import {
  Activity,
  AlertCircle,
  AlertTriangle,
  Archive,
  ArrowLeft,
  BadgeCheck,
  Ban,
  BookOpen,
  Boxes,
  Building2,
  Calendar,
  Check,
  CheckCircle,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Cloud,
  Code2,
  Cpu,
  Database,
  Download,
  Import,
  SquareArrowOutUpRight,
  ExternalLink,
  Eye,
  FileImage,
  FileText,
  Globe,
  HardDrive,
  Layers,
  LayoutGrid,
  List as ListIcon,
  History,
  Link2,
  Loader2,
  MapPin,
  MessageSquare,
  Network,
  Pencil,
  Plus,
  Search,
  Send,
  Server,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Tag,
  Trash2,
  Upload,
  UserCheck,
  UserCog,
  Users,
  X,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router";
import { useTheme } from "next-themes";
import {
  abortImportSession,
  addInformationType,
  patchInformationType,
  deleteInformationType,
  addInventoryItem,
  patchInventoryItem,
  deleteInventoryItem,
  addLeveragedAuthorization,
  addSspComponent,
  addSspControlImplementation,
  addSspInterconnection,
  addSspSystemUser,
  bulkUpdateImplementedRequirements,
  putSspIrSetParameters,
  commitImportSession,
  createSSP,
  createSSPFromApplication,
  createSSPFromService,
  createSspSupportingEvidence,
  deleteLeveragedAuthorization,
  deleteSSP,
  renameDocument,
  deleteSspComponent,
  deleteSspControlImplementation,
  deleteSspInterconnection,
  deleteSspSupportingEvidence,
  deleteSspSystemUser,
  downloadSupportingEvidenceFile,
  exportCatalogDoc,
  getControl,
  getDocument,
  getExportDownloadUrl,
  getExportJob,
  getImportSession,
  getProfileByDocumentId,
  getSspFull,
  getSspProfile,
  getSspProfileMatchedComponents,
  getSystemCharacteristics,
  importComponentToSsp,
  patchSspComponent,
  listComponentControls,
  importSSP,
  listBaselineControls,
  listComponentControlImplementations,
  listComponentInventoryItems,
  listInformationTypes,
  listInventoryItems,
  listLeveragedAuthorizations,
  listCompProfiles,
  listSspComponents,
  listSspControlImplementations,
  listSspInterconnections,
  listSspLocations,
  listSspParties,
  listSspResponsibleParties,
  listSspRoles,
  listSSPs,
  getAuthHeaders,
  listSspSupportingEvidence,
  listSspSystemUsers,
  previewSupportingEvidenceFile,
  replaceSspLocations,
  replaceSspParties,
  replaceSspResponsibleParties,
  replaceSspRoles,
  resolveAsset,
  setSystemCharacteristics,
  transitionDocument,
  updateImplementedRequirement,
  updateLeveragedAuthorization,
  updateSspInterconnection,
  updateSspSystemUser,
  uploadSspSupportingEvidenceFile,
  validateImport,
  type AssetResolutionResponse,
  type BaselineControlResponse,
  type BulkImplementedRequirementPayload,
  type ComponentDefinitionSummaryResponse,
  type ComponentResponse,
  type ControlOrigination,
  type ControlResponse,
  type DocumentResponse,
  type ImplementationStatus,
  type ImplementedRequirementDetailResponse,
  type ImportComponentsResponse,
  type ImportJobResponse,
  type ImportSessionResponse,
  type InformationTypeResponse,
  type InventoryItemResponse,
  type LeveragedAuthorizationResponse,
  type LeveragedAuthorizationStatus,
  type ProfileDetailResponse,
  type ProfileResponse,
  type SspComponentResponse,
  type SspControlImplementationRawResponse,
  type SspControlImplementationResponse,
  type SspFullResponse,
  type ComponentControlRow,
  type PatchSspComponentPayload,
  type SspInterconnectionResponse,
  type SspLocationPayload,
  type SspLocationResponse,
  type SspPartyPayload,
  type SspPartyResponse,
  type SspResponse,
  type SspResponsiblePartyPayload,
  type SspResponsiblePartyResponse,
  type SspRolePayload,
  type SspRoleResponse,
  type SspSummaryResponse,
  type SspSystemUserResponse,
  type SystemCharacteristicsResponse,
  type ValidateResponse,
} from "../../api/frameworksApi";
import { getAPI, downloadFile } from "../../api/frameworksApi";
import { API_URL } from "../../constants";
import { useToastStore } from "../../stores/toastStore";
import { useSspInheritance } from "../../hooks/useSspInheritance";
import { RBACButton, RBACAction } from "../components/RBACButton";
import { useRBACPermission } from "../../hooks/useRBACPermission";
import { DataTable } from "../components/DataTable";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import { Dropdown } from "../components/Dropdown";
import { StatusBadge } from "../components/StatusBadge";
import {
  EntityCardGrid,
  StatTile,
  StatTiles,
  MetaDates,
  TypeBadge,
  CardActions,
  TitleTooltip,
} from "../components/EntityCardGrid";
import { InheritancePickerModal } from "./SSP/components/InheritancePickerModal";
import { SSPDetailJourney, type JourneyMetrics } from "./SSP/components/SSPDetailJourney";
import { ControlImplementationsTab } from "./SSP/tabs/ControlImplementationsTab";
import {
  FormDatePicker,
  FormField,
  FormInput,
  FormSelect,
  FormTextarea,
  Modal,
  ModalFooterButtons,
  ModalShell,
} from "../components/Modal";
import { PageSizeSelector } from "../components/PageSizeSelector";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Tip,
} from "../components/ui/tooltip";

// ── OSCAL Document Lifecycle (REQ-5.1) ────────────────────
type LifecycleStatus =
  | "draft"
  | "in_review"
  | "approved"
  | "published"
  | "superseded"
  | "withdrawn";
const LIFECYCLE_STAGES: {
  key: LifecycleStatus;
  label: string;
}[] = [
    { key: "draft", label: "Draft" },
    { key: "in_review", label: "In Review" },
    { key: "approved", label: "Approved" },
    { key: "published", label: "Published" },
  ];

const COMPONENT_TYPES: { value: string; label: string }[] = [
  { value: "software", label: "Software" },
  { value: "hardware", label: "Hardware" },
  { value: "service", label: "Service" },
  { value: "policy", label: "Policy" },
  { value: "process", label: "Process" },
  { value: "procedure", label: "Procedure" },
  { value: "plan", label: "Plan" },
  { value: "guidance", label: "Guidance" },
  { value: "standard", label: "Standard" },
  { value: "validation", label: "Validation" },
];

// ── SSP Cylinder Layers ───────────────────────────────────
type SspLayerKey =
  | "application"
  | "data"
  | "runtime"
  | "compute"
  | "storage"
  | "platform"
  | "network";

// Component detail panel pagination sizes
const COMP_DETAIL_PAGE_SIZE = 25; // default rows shown per Prev/Next page
const COMP_DETAIL_PAGE_SIZE_OPTIONS = [10, 25, 50, 100].map((n) => ({
  value: String(n),
  label: String(n),
}));

const SSP_LAYERS: {
  key: SspLayerKey;
  label: string;
  icon: typeof LayoutGrid;
  gradient: string;
  dot: string;
  // [top-highlight, mid, side-shadow] hex values for the SVG cylinder ring
  colors: [string, string, string];
}[] = [
    {
      key: "application",
      label: "Application",
      icon: LayoutGrid,
      gradient: "from-purple-500 to-fuchsia-500",
      dot: "bg-purple-400",
      colors: ["#9B4DFF", "#7B3FF2", "#5F2EEA"],
    },
    {
      key: "data",
      label: "Data",
      icon: Database,
      gradient: "from-pink-500 to-rose-500",
      dot: "bg-pink-400",
      colors: ["#FF4FA5", "#D63384", "#A61E6A"],
    },
    {
      key: "runtime",
      label: "Runtime",
      icon: Code2,
      gradient: "from-red-500 to-orange-500",
      dot: "bg-red-400",
      colors: ["#FF5C7A", "#E63963", "#C9184A"],
    },
    {
      key: "compute",
      label: "Compute",
      icon: Cpu,
      gradient: "from-orange-500 to-amber-500",
      dot: "bg-orange-400",
      colors: ["#FF9F1C", "#F77F00", "#D66B00"],
    },
    {
      key: "storage",
      label: "Storage",
      icon: HardDrive,
      gradient: "from-lime-500 to-emerald-500",
      dot: "bg-emerald-400",
      colors: ["#8AC926", "#6BAF00", "#4F8C00"],
    },
    {
      key: "platform",
      label: "Datacenter / Platform",
      icon: Server,
      gradient: "from-teal-500 to-cyan-500",
      dot: "bg-teal-400",
      colors: ["#2EC4B6", "#1AA6A0", "#0F8B8D"],
    },
    {
      key: "network",
      label: "Network",
      icon: Network,
      gradient: "from-sky-500 to-blue-500",
      dot: "bg-sky-400",
      colors: ["#4D8DFF", "#3366FF", "#2451D6"],
    },
  ];

// Determine which SSP layer a component belongs to.
// Prefers sspLayerSlug from API, then explicit "Layer" / "SSP Layer" / "Group Name" props, then maps by componentType.
function getComponentLayer(comp: SspComponentResponse): SspLayerKey {
  // First: use sspLayerSlug directly from the API response
  if (comp.sspLayerSlug) {
    const slug = comp.sspLayerSlug.toLowerCase().trim();
    if (slug === "application") return "application";
    if (slug === "data") return "data";
    if (slug === "runtime") return "runtime";
    if (slug === "compute") return "compute";
    if (slug === "storage") return "storage";
    if (slug === "platform" || slug === "datacenter") return "platform";
    if (slug === "network") return "network";
  }

  const propLayer =
    comp.props?.find(
      (p) =>
        p.name?.toLowerCase() === "layer" ||
        p.name?.toLowerCase() === "ssp layer" ||
        p.name?.toLowerCase() === "ssp-layer",
    )?.value ||
    comp.props?.find((p) => p.name === "Group Name")?.value ||
    "";
  const norm = propLayer.toLowerCase().replace(/\s*ssp\s*/g, "").trim();
  if (norm.includes("application")) return "application";
  if (norm.includes("data")) return "data";
  if (norm.includes("runtime")) return "runtime";
  if (norm.includes("compute")) return "compute";
  if (norm.includes("storage")) return "storage";
  if (norm.includes("datacenter") || norm.includes("platform"))
    return "platform";
  if (norm.includes("network")) return "network";

  const t = (comp.componentType || "").toLowerCase();
  if (t === "hardware") return "compute";
  if (t === "service") return "application";
  if (
    t === "software" ||
    t === "policy" ||
    t === "process" ||
    t === "procedure" ||
    t === "plan" ||
    t === "guidance" ||
    t === "standard" ||
    t === "validation"
  )
    return "application";
  return "application";
}

// ── displayStatus color map ──────────────────────────────
const DISPLAY_STATUS_STYLES: Record<string, string> = {
  CLOUD_DISCOVERED:
    "bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300",
  ON_PREM_DISCOVERED:
    "bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-300",
  CONTROLS_ASSIGNED:
    "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300",
  IMPLEMENTED:
    "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  INHERITED:
    "bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300",
};

function getDisplayStatusStyle(displayStatus?: string | null): string {
  if (!displayStatus) return "bg-muted text-muted-foreground";
  return (
    DISPLAY_STATUS_STYLES[displayStatus] ?? "bg-muted text-muted-foreground"
  );
}

function getControlCountLabel(comp: SspComponentResponse): string {
  const mapped = comp.implementedControlCount ?? 0;
  const confirmed = comp.fullyImplementedControlCount ?? 0;
  if (comp.displayStatus === "CONTROLS_ASSIGNED" && mapped > 0) {
    if (confirmed > 0) {
      return `${confirmed} of ${mapped} controls implemented`;
    }
    return `${mapped} ${mapped === 1 ? "control" : "controls"} assigned`;
  }
  return `${mapped} Controls`;
}

const SYSTEM_STATUSES: { value: string; label: string }[] = [
  { value: "operational", label: "Operational" },
  { value: "under-development", label: "Under Development" },
  { value: "disposition", label: "Disposition" },
  { value: "other", label: "Other" },
];

const IMPLEMENTATION_STATUSES: { value: string; label: string }[] = [
  { value: "implemented", label: "Implemented" },
  { value: "partial", label: "Partially Implemented" },
  { value: "planned", label: "Planned" },
  { value: "alternative", label: "Alternative" },
  { value: "not-implemented", label: "Not Implemented" },
  { value: "not applicable", label: "Not Applicable" },
];

const CONTROL_ORIGINATIONS: { value: string; label: string }[] = [
  { value: "system-specific", label: "System Specific" },
  { value: "inherited", label: "Inherited" },
  { value: "customer-configured", label: "Customer Configured" },
  { value: "customer", label: "Customer" },
  { value: "customer-responsibility", label: "Customer Responsibility" },
  { value: "hybrid", label: "Hybrid" },
  { value: "shared", label: "Shared" },
  { value: "provider-specific", label: "Provider Specific" },
  { value: "provider", label: "Provider" },
];

// ── Tab definitions ───────────────────────────────────────
type SspInheritanceType = "" | "application" | "service";

const SSP_INHERITANCE_TYPE_OPTIONS: {
  value: SspInheritanceType;
  label: string;
}[] = [
    { value: "application", label: "Application" },
    { value: "service", label: "Service" },
  ];

const tabs = [
  "System Characteristics",
  "Information Types",
  "Components",
  "Inventory",
  "Control Implementations",
  "System Users",
  "Interconnections",
  "Locations",
  "Roles & Parties",
  "Leveraged Authorizations",
] as const;
type TabId = (typeof tabs)[number];

// ── Tab tooltips ──────────────────────────────────────────
const TAB_DESCRIPTIONS: Record<TabId, string> = {
  "System Characteristics":
    "Defines the system’s identity, purpose, and security posture within its authorization boundary. It describes what the system does, where it operates, and the type of data and workloads it handles. This section establishes the baseline context auditors and engineers rely on to understand scope and criticality.",
  "Information Types":
    "Identifies the categories of data processed, stored, or transmitted by the system, along with their sensitivity and regulatory impact. It maps data types to confidentiality, integrity, and availability requirements. This ensures controls are aligned with actual data risk, not assumptions.",
  Components:
    "Breaks down the system into logical and physical components such as services, infrastructure, and platforms. Each component represents a control enforcement boundary where security responsibilities are applied. This view connects architecture directly to compliance execution.",
  Inventory:
    "Maintains a real-time list of discovered assets under each component, including systems, users, and resources. It tracks metadata like source, timestamps, and environment classification. This ensures compliance is evaluated on actual assets, not static assumptions.",
  "Control Implementations":
    "Manage how each security control is implemented — author what your system provides for consumers (Provider Export), discover and inherit controls from other systems (Consumer Inheritance), and write satisfied statements for shared responsibilities.",
  "System Users":
    "Captures all users and identities interacting with the system, including roles, privileges, and access boundaries. It highlights privileged accounts and enforces least privilege principles. This section is critical for validating access control and identity governance.",
  Interconnections:
    "Documents all external systems, services, and dependencies that interact with the system. It defines trust boundaries, data flows, and security considerations for each connection. This ensures risks from integrations and third-party dependencies are explicitly managed.",
  Locations:
    "Specifies where system components and data are hosted, including regions, data centers, or cloud zones. It highlights jurisdictional and compliance implications tied to geography. This is essential for regulatory alignment and data residency requirements.",
  "Roles & Parties":
    "Defines organizational roles, responsibilities, and stakeholders involved in system operation and security. It maps ownership for controls, components, and remediation actions. This ensures accountability is clearly assigned and auditable.",
  "Leveraged Authorizations":
    "Identifies external systems or services whose existing authorizations are reused or inherited. It explains the scope and limitations of relying on these authorizations. This prevents over-assumption of compliance and clarifies shared responsibility boundaries.",
};

/** Convert ISO / YYYY-MM-DD to DD/MM/YYYY for display */
function toddmmyyyy(date: string): string {
  const ymd = date.split("T")[0];
  const [yyyy, mm, dd] = ymd.split("-");
  return `${dd}-${mm}-${yyyy}`;
}

/** Format tab name from to title case */
function formatTabName(tabName: string): string {
  return tabName
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatSecurityLevel(level?: string | null): string {
  if (!level) return "";

  return level.charAt(0).toUpperCase() + level.slice(1).toLowerCase();
}

const SYS_LEVEL_STYLES: Record<string, string> = {
  high: "bg-red-50 text-red-600 border-red-200 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/30",
  moderate:
    "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/30",
  low: "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/30",
};

function SysLevelPill({ level }: { level: string | null | undefined }) {
  if (!level) return <span className="text-sm text-muted-foreground">—</span>;
  const key = level.toLowerCase();
  const cls = SYS_LEVEL_STYLES[key] || SYS_LEVEL_STYLES.low;
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border capitalize ${cls}`}
    >
      {key}
    </span>
  );
}

const SYS_STATUS_STYLES: Record<string, string> = {
  operational:
    "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/30",
  "under-development":
    "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/30",
  "under-major-modification":
    "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/30",
  disposition:
    "bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-500/10 dark:text-slate-300 dark:border-slate-500/30",
  other:
    "bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-500/10 dark:text-slate-300 dark:border-slate-500/30",
};

function SysStatusPill({ status }: { status: string | null | undefined }) {
  if (!status) return <span className="text-sm text-muted-foreground">—</span>;
  const key = status.toLowerCase();
  const cls = SYS_STATUS_STYLES[key] || SYS_STATUS_STYLES.other;
  const label = key.replace(/-/g, " ");
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold border capitalize ${cls}`}
    >
      {label}
    </span>
  );
}

function SysCharRow({
  icon: Icon,
  label,
  children,
}: {
  icon: LucideIcon;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[240px_1fr] gap-2 sm:gap-4 py-3 items-start">
      <div className="flex items-center gap-2.5">
        <div className="flex items-center justify-center size-7 rounded-md bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex-shrink-0">
          <Icon className="size-3.5" />
        </div>
        <span className="text-xs font-medium text-muted-foreground">
          {label}
        </span>
      </div>
      <div className="text-sm text-foreground/90 min-w-0 sm:pt-1">
        {children}
      </div>
    </div>
  );
}

function ImpactPill({ level }: { level: string | null | undefined }) {
  const key = (level || "").toLowerCase();
  const cls = SYS_LEVEL_STYLES[key] || SYS_LEVEL_STYLES.low;
  const label = key ? key.charAt(0).toUpperCase() + key.slice(1) : "—";
  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border min-w-[90px] justify-center ${cls}`}
    >
      {label}
    </span>
  );
}

type ParameterDisplayRow = {
  paramId: string;
  label?: string;
  catalogValues: string[];
  profileValues: string[];
  componentValues: string[];
  constraints: string[];
  guidelines: string[];
  isOverridden?: boolean;
};

function getBaselineControlId(control: BaselineControlResponse): string {
  const raw = control as unknown as Record<string, unknown>;
  return String(
    control.controlId ||
    control.control_id ||
    raw.control_id ||
    raw["control-id"] ||
    raw.id ||
    "",
  );
}

function textValues(values: unknown): string[] {
  const list = Array.isArray(values) ? values : values == null ? [] : [values];
  return list
    .map((value) => {
      if (value == null) return "";
      if (typeof value === "string") return value;
      if (typeof value === "number" || typeof value === "boolean")
        return String(value);
      if (typeof value === "object") {
        const record = value as Record<string, unknown>;
        return String(
          record.prose ?? record.description ?? record.value ?? record.id ?? "",
        );
      }
      return String(value);
    })
    .filter(Boolean);
}

function getParamId(param: Record<string, unknown>): string {
  return String(param.paramId ?? param.param_id ?? param["param-id"] ?? "");
}

function getNestedParamSource(
  param: Record<string, unknown>,
  ...keys: string[]
): Record<string, unknown> | null {
  for (const key of keys) {
    const value = param[key];
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return value as Record<string, unknown>;
    }
  }
  return null;
}

function sourceValues(
  param: Record<string, unknown>,
  sourceKey: string,
  ...directKeys: string[]
): string[] {
  const source = getNestedParamSource(param, sourceKey);
  const sourceResult = source
    ? textValues(
      source.values ??
      source.value ??
      source.vals ??
      source["set-values"] ??
      source.setValues,
    )
    : textValues(param[sourceKey]);
  if (sourceResult.length > 0) return sourceResult;

  for (const key of directKeys) {
    const directResult = textValues(param[key]);
    if (directResult.length > 0) return directResult;
  }

  return [];
}

function collectParamRecords(source: unknown): Record<string, unknown>[] {
  const records: Record<string, unknown>[] = [];
  const visited = new Set<unknown>();

  const walk = (value: unknown) => {
    if (!value || typeof value !== "object" || visited.has(value)) return;
    visited.add(value);

    if (Array.isArray(value)) {
      value.forEach(walk);
      return;
    }

    const record = value as Record<string, unknown>;
    if (getParamId(record)) records.push(record);
    Object.values(record).forEach(walk);
  };

  walk(source);
  return records;
}

function findBaselineControlForId(
  controls: BaselineControlResponse[],
  controlId: string,
): BaselineControlResponse | undefined {
  const normalizedId = controlId.trim().toLowerCase();
  if (!normalizedId) return undefined;

  return controls.find((control) => {
    const raw = control as unknown as Record<string, unknown>;
    const candidateIds = [
      getBaselineControlId(control),
      control.controlDetails?.control_id,
      String(raw.controlId ?? ""),
      String(raw.control_id ?? ""),
      String(raw["control-id"] ?? ""),
      ...(control.implementedRequirements ?? []).map((ir) => ir.controlId),
    ];
    return candidateIds.some(
      (candidate) => candidate?.trim().toLowerCase() === normalizedId,
    );
  });
}

function buildParameterRows(
  control: BaselineControlResponse | undefined,
  implementationParams: { paramId: string; values: string }[],
): ParameterDisplayRow[] {
  const componentValueByParam = new Map(
    implementationParams
      .filter((param) => param.paramId?.trim())
      .map((param) => [param.paramId, textValues(param.values.split(","))]),
  );

  const rows = new Map<string, ParameterDisplayRow>();
  const upsert = (row: ParameterDisplayRow) => {
    const existing = rows.get(row.paramId);
    rows.set(row.paramId, {
      paramId: row.paramId,
      label: row.label || existing?.label,
      catalogValues: row.catalogValues.length
        ? row.catalogValues
        : (existing?.catalogValues ?? []),
      profileValues: row.profileValues.length
        ? row.profileValues
        : (existing?.profileValues ?? []),
      componentValues: row.componentValues.length
        ? row.componentValues
        : (existing?.componentValues ??
          componentValueByParam.get(row.paramId) ??
          []),
      constraints: row.constraints.length
        ? row.constraints
        : (existing?.constraints ?? []),
      guidelines: row.guidelines.length
        ? row.guidelines
        : (existing?.guidelines ?? []),
      isOverridden: row.isOverridden ?? existing?.isOverridden,
    });
  };

  const consumedParamRecords = new Set<Record<string, unknown>>();
  const addNestedParameter = (param: Record<string, unknown>) => {
    consumedParamRecords.add(param);
    const paramId = getParamId(param);
    if (!paramId.trim()) return;
    const catalogSource = getNestedParamSource(param, "catalog");
    const profileSource = getNestedParamSource(param, "profile");
    const componentSource = getNestedParamSource(
      param,
      "componentDefinition",
      "component_definition",
      "component",
      "implementation",
    );
    const componentSourceValues = componentSource
      ? textValues(
        componentSource.values ??
        componentSource.value ??
        componentSource.vals ??
        componentSource["set-values"] ??
        componentSource.setValues,
      )
      : [];
    upsert({
      paramId,
      label: String(
        param.label ?? profileSource?.label ?? catalogSource?.label ?? "",
      ),
      catalogValues: sourceValues(
        param,
        "catalog",
        "catalogValues",
        "catalog_values",
        "catalogValue",
        "catalog_value",
      ),
      profileValues: sourceValues(
        param,
        "profile",
        "profileValues",
        "profile_values",
        "profileValue",
        "profile_value",
      ),
      componentValues:
        componentSourceValues.length > 0
          ? componentSourceValues
          : sourceValues(
            param,
            "componentDefinition",
            "component_definition",
            "componentDefinitionValues",
            "component_definition_values",
            "componentDefinitionValue",
            "component_definition_value",
            "componentValues",
            "component_values",
            "implementationValues",
            "implementation_values",
          ).length > 0
            ? sourceValues(
              param,
              "componentDefinition",
              "component_definition",
              "componentDefinitionValues",
              "component_definition_values",
              "componentDefinitionValue",
              "component_definition_value",
              "componentValues",
              "component_values",
              "implementationValues",
              "implementation_values",
            )
            : (componentValueByParam.get(paramId) ?? []),
      constraints: textValues(profileSource?.constraints).length
        ? textValues(profileSource?.constraints)
        : textValues(catalogSource?.constraints),
      guidelines: textValues(profileSource?.guidelines).length
        ? textValues(profileSource?.guidelines)
        : textValues(catalogSource?.guidelines),
      isOverridden: Boolean(param.isOverridden ?? param.is_overridden),
    });
  };

  ((control?.params ?? []) as Record<string, unknown>[]).forEach(
    addNestedParameter,
  );
  collectParamRecords(control).forEach((param) => {
    if (!consumedParamRecords.has(param)) addNestedParameter(param);
  });

  (control?.parameters ?? []).forEach((param) => {
    if (!param.paramId?.trim()) return;
    upsert({
      paramId: param.paramId,
      label: param.label,
      catalogValues: textValues(param.catalogValues),
      profileValues: textValues(param.profileValues),
      componentValues: componentValueByParam.get(param.paramId) ?? [],
      constraints: textValues(param.constraints),
      guidelines: [],
    });
  });

  (
    (control?.controlDetails?.params ?? []) as Record<string, unknown>[]
  ).forEach((param) => {
    const paramId = getParamId(param);
    if (!paramId.trim()) return;
    upsert({
      paramId,
      label: String(param.label ?? ""),
      catalogValues: textValues(param.values),
      profileValues: [],
      componentValues: componentValueByParam.get(paramId) ?? [],
      constraints: [],
      guidelines: textValues(param.guidelines),
    });
  });

  implementationParams.forEach((param) => {
    if (!param.paramId?.trim() || rows.has(param.paramId)) return;
    upsert({
      paramId: param.paramId,
      catalogValues: [],
      profileValues: [],
      componentValues: componentValueByParam.get(param.paramId) ?? [],
      constraints: [],
      guidelines: [],
    });
  });

  return Array.from(rows.values());
}

export function SSPEditor() {
  const navigate = useNavigate();
  const { id: sspIdFromUrl } = useParams<{ id: string }>();
  const { theme } = useTheme();
  const isDark = theme !== "light";

  const [activeTab, setActiveTab] = useState<TabId>("System Characteristics");
  const [selectedSSPId, setSelectedSSPId] = useState<string | null>(null);

  // Tab scroll state
  const tabsContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollArrows = useCallback(() => {
    const el = tabsContainerRef.current;
    if (!el) return;
    const sl = Math.round(el.scrollLeft);
    setCanScrollLeft(sl > 0);
    setCanScrollRight(sl + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = tabsContainerRef.current;
    if (!el) return;
    const raf = requestAnimationFrame(updateScrollArrows);
    el.addEventListener("scroll", updateScrollArrows, { passive: true });
    window.addEventListener("resize", updateScrollArrows);
    // ResizeObserver fires when the tab bar renders or its width changes
    const ro = new ResizeObserver(() => updateScrollArrows());
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      el.removeEventListener("scroll", updateScrollArrows);
      window.removeEventListener("resize", updateScrollArrows);
      ro.disconnect();
    };
  }, [updateScrollArrows, selectedSSPId]);

  const scrollTabs = (direction: "left" | "right") => {
    const el = tabsContainerRef.current;
    if (!el) return;
    el.scrollBy({
      left: direction === "left" ? -200 : 200,
      behavior: "smooth",
    });
    // Poll to update arrows during smooth scroll animation
    let frame = 0;
    const poll = () => {
      updateScrollArrows();
      frame++;
      if (frame < 20) requestAnimationFrame(poll);
    };
    requestAnimationFrame(poll);
  };

  // List state
  const [sspList, setSspList] = useState<SspSummaryResponse[]>([]);
  const [sspDocTitles, setSspDocTitles] = useState<Record<string, string>>({});
  // Inline SSP title (document) rename — only before publish
  const [editingSspTitle, setEditingSspTitle] = useState(false);
  const [sspTitleDraft, setSspTitleDraft] = useState("");
  const [savingSspTitle, setSavingSspTitle] = useState(false);
  const [sspLifecycles, setSspLifecycles] = useState<
    Record<string, LifecycleStatus>
  >({});
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState<string | null>(null);

  // List search + pagination state
  const [sspSearchQuery, setSspSearchQuery] = useState("");
  const [sspStatusFilter, setSspStatusFilter] = useState<string>("");
  const [sspCurrentPage, setSspCurrentPage] = useState(0);
  const [sspPageSize, setSspPageSize] = useState(12);
  const [sspSortKey, setSspSortKey] = useState<string | null>("updated");
  const [sspSortDir, setSspSortDir] = useState<"asc" | "desc">("desc");
  const [sspViewMode, setSspViewMode] = useState<"card" | "list">("card");

  const handleSspSort = (key: string) => {
    if (sspSortKey === key)
      setSspSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSspSortKey(key);
      setSspSortDir("asc");
    }
    setSspCurrentPage(0);
  };

  const handleSspSearchChange = (query: string) => {
    setSspSearchQuery(query);
    setSspCurrentPage(0);
  };

  const filteredSspList = useMemo(() => {
    const q = sspSearchQuery.trim().toLowerCase();
    const byStatus = sspStatusFilter
      ? sspList.filter(
        (ssp) =>
          (sspLifecycles[ssp.documentId] || "draft") === sspStatusFilter,
      )
      : sspList;
    if (!q) return byStatus;
    return byStatus.filter((ssp) => {
      const title = (sspDocTitles[ssp.documentId] || "").toLowerCase();
      if (title.includes(q)) return true;
      if (ssp.documentId.toLowerCase().includes(q)) return true;
      if (ssp.importProfileHref?.toLowerCase().includes(q)) return true;
      return false;
    });
  }, [sspList, sspDocTitles, sspLifecycles, sspSearchQuery, sspStatusFilter]);

  const sortedSspList = useMemo(() => {
    if (!sspSortKey) return filteredSspList;
    return [...filteredSspList].sort((a, b) => {
      let aVal = "";
      let bVal = "";
      if (sspSortKey === "title") {
        aVal = sspDocTitles[a.documentId] || a.documentId;
        bVal = sspDocTitles[b.documentId] || b.documentId;
      } else if (sspSortKey === "profile") {
        aVal = (a as any).profileName || "";
        bVal = (b as any).profileName || "";
      } else if (sspSortKey === "sourceType") {
        aVal = (a as any).sourceType || "";
        bVal = (b as any).sourceType || "";
      } else if (sspSortKey === "sourceName") {
        aVal = (a as any).sourceName || "";
        bVal = (b as any).sourceName || "";
      } else if (sspSortKey === "status") {
        aVal = sspLifecycles[a.documentId] || "draft";
        bVal = sspLifecycles[b.documentId] || "draft";
      } else if (sspSortKey === "updated") {
        aVal = (a as any).updatedAt || (a as any).createdAt || "";
        bVal = (b as any).updatedAt || (b as any).createdAt || "";
      } else if (sspSortKey === "created") {
        aVal = (a as any).createdAt || "";
        bVal = (b as any).createdAt || "";
      }
      return sspSortDir === "asc"
        ? aVal.localeCompare(bVal)
        : bVal.localeCompare(aVal);
    });
  }, [filteredSspList, sspSortKey, sspSortDir, sspDocTitles, sspLifecycles]);

  const paginatedSspList = useMemo(() => {
    const start = sspCurrentPage * sspPageSize;
    return sortedSspList.slice(start, start + sspPageSize);
  }, [sortedSspList, sspCurrentPage, sspPageSize]);

  // Detail state
  const [sspDetail, setSspDetail] = useState<SspResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // "New SSP" modal
  const [showNewSSP, setShowNewSSP] = useState(false);
  const [showInheritancePicker, setShowInheritancePicker] = useState(false);
  const [creating, setCreating] = useState(false);
  const [sspForm, setSspForm] = useState({
    title: "",
    importProfileHref: "",
    owningOrgUnitId: "",
  });
  const [sspInheritanceType, setSspInheritanceType] =
    useState<SspInheritanceType>("");
  const [sspSelectedAppId, setSspSelectedAppId] = useState("");
  const [sspSelectedServiceId, setSspSelectedServiceId] = useState("");
  const [inheritedIds, setInheritedIds] = useState<string[]>([]);
  const [profiles, setProfiles] = useState<ProfileResponse[]>([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const sspInheritanceMode =
    sspInheritanceType === "application" || sspInheritanceType === "service"
      ? sspInheritanceType
      : null;
  // Bumped on every "New SSP" modal open so the hook refetches and we
  // pick up freshly-created service SSPs (which flip isUsed from false
  // → true on the next /applications response).
  const [sspInheritanceRefreshTick, setSspInheritanceRefreshTick] = useState(0);
  const { data: sspInheritance, loading: sspInheritanceLoading } =
    useSspInheritance(sspInheritanceMode, sspInheritanceRefreshTick);
  const sspApplicationOptions = useMemo(
    () =>
      sspInheritance.applications.map((app) => ({
        // Use applicationId (UUID) as the option value — appCode can
        // collide across applications (e.g. two apps both labelled
        // "A360"), which would auto-select the first match. UUID is
        // guaranteed unique per app.
        value: app.applicationId,
        label: app.applicationName,
      })),
    [sspInheritance.applications],
  );
  const sspServiceOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: { value: string; label: string }[] = [];
    for (const app of sspInheritance.applications) {
      for (const svc of app.services ?? []) {
        if (seen.has(svc.serviceId)) continue;
        seen.add(svc.serviceId);
        options.push({ value: svc.serviceId, label: svc.name });
      }
    }
    return options;
  }, [sspInheritance.applications]);
  // For each service of the selected application, find its provider SSP (if any)
  // by cross-referencing sspList on sourceType="service" + sourceId=serviceId.
  const availableProviderSsps = useMemo(() => {
    if (sspInheritanceType !== "application" || !sspSelectedAppId) return [];
    const app = sspInheritance.applications.find(
      (a) => a.applicationId === sspSelectedAppId,
    );
    return (app?.services ?? []).map((svc) => {
      // Match by serviceId (primary) OR by service name (fallback).
      // sourceType is case-insensitive — API may return "SERVICE" or "service".
      const providerSsp = sspList.find(
        (s) =>
          s.sourceType?.toLowerCase() === "service" &&
          (s.sourceId === svc.serviceId ||
            s.sourceName?.toLowerCase() === svc.name?.toLowerCase()),
      );
      const lifecycle = providerSsp
        ? (providerSsp.lifecycleStatus || sspLifecycles[providerSsp.documentId] || "draft").toLowerCase()
        : null;
      const title = providerSsp
        ? (sspDocTitles[providerSsp.documentId] || providerSsp.sourceName || providerSsp.title || providerSsp.documentId)
        : null;
      return {
        serviceId: svc.serviceId,
        serviceName: svc.name,
        sspId: providerSsp?.id ?? null,
        sspTitle: title,
        lifecycleStatus: lifecycle,
        isPublished: lifecycle === "published",
      };
    });
  }, [sspInheritanceType, sspSelectedAppId, sspInheritance.applications, sspList, sspLifecycles, sspDocTitles]);

  const sspTypeSelectionIncomplete =
    !sspInheritanceType ||
    (sspInheritanceType === "application" && !sspSelectedAppId) ||
    (sspInheritanceType === "service" && !sspSelectedServiceId);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState<SspSummaryResponse | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);

  // Export modal
  const [showExport, setShowExport] = useState(false);
  const [exportingDoc, setExportingDoc] = useState(false);
  const [exportTargetDocId, setExportTargetDocId] = useState<string | null>(
    null,
  );

  // SSP OSCAL Import pipeline state
  const [showSspImport, setShowSspImport] = useState(false);
  const [sspImportStep, setSspImportStep] = useState<
    "select" | "url" | "importing" | "findings"
  >("select");
  const [sspImportFile, setSspImportFile] = useState<File | null>(null);
  const [sspImportUrl, setSspImportUrl] = useState("");
  const [sspImportMode, setSspImportMode] = useState<"full" | "delta">("full");
  const [sspImportError, setSspImportError] = useState<string | null>(null);
  const [sspImportErrorTitle, setSspImportErrorTitle] = useState<string | null>(
    null,
  );
  const [sspImportResult, setSspImportResult] =
    useState<ImportJobResponse | null>(null);
  const [sspValidateResult, setSspValidateResult] =
    useState<ValidateResponse | null>(null);
  const [sspValidating, setSspValidating] = useState(false);

  // Import Session modal state
  const [showSspSession, setShowSspSession] = useState(false);
  const [sspSessionData, setSspSessionData] =
    useState<ImportSessionResponse | null>(null);
  const [sspSessionLoading, setSspSessionLoading] = useState(false);
  const [sspSessionError, setSspSessionError] = useState<string | null>(null);
  const [sspCommitting, setSspCommitting] = useState(false);
  const [sspAborting, setSspAborting] = useState(false);

  // System Characteristics state
  const [sysChars, setSysChars] =
    useState<SystemCharacteristicsResponse | null>(null);
  const [sysCharsLoading, setSysCharsLoading] = useState(false);
  const [editingSysChars, setEditingSysChars] = useState(false);
  const [savingSysChars, setSavingSysChars] = useState(false);
  const [sysCharsForm, setSysCharsForm] = useState({
    systemName: "",
    systemNameShort: "",
    description: "",
    securitySensitivityLevel: "",
    systemType: "",
    authorizationType: "",
    deploymentModel: "",
    serviceModel: "",
    systemIds: [] as { "identifier-type": string; id: string }[],
    authorizationBoundary: "",
    networkArchitecture: "",
    dataFlow: "",
    confidentiality: "",
    integrity: "",
    availability: "",
    status: "",
    statusRemarks: "",
    dateAuthorized: "",
  });

  // Diagram uploads (backed by supporting-evidence API)
  const [networkArchDiagram, setNetworkArchDiagram] = useState<{
    file?: File;
    evidenceId?: string; // SSP link ID (for delete)
    contentEvidenceId?: string; // content-registry ID (for preview/download)
    fileName?: string;
  } | null>(null);
  const [dataFlowDiagram, setDataFlowDiagram] = useState<{
    file?: File;
    evidenceId?: string;
    contentEvidenceId?: string;
    fileName?: string;
  } | null>(null);
  const [viewingDiagram, setViewingDiagram] = useState<{
    title: string;
    contentEvidenceId: string;
    fileName?: string;
  } | null>(null);
  const [uploadingDiagram, setUploadingDiagram] = useState<string | null>(null); // "network" | "dataflow" | null

  // Information Types state
  const [infoTypes, setInfoTypes] = useState<InformationTypeResponse[]>([]);
  const [infoTypesLoading, setInfoTypesLoading] = useState(false);
  const [showAddInfoType, setShowAddInfoType] = useState(false);
  const [addingInfoType, setAddingInfoType] = useState(false);
  const [infoTypeForm, setInfoTypeForm] = useState({
    title: "",
    description: "",
    confidentiality: "",
    integrity: "",
    availability: "",
  });

  // Components state
  const [sspComponents, setSspComponents] = useState<SspComponentResponse[]>(
    [],
  );
  const [componentsLoading, setComponentsLoading] = useState(false);
  const [showAddComponent, setShowAddComponent] = useState(false);
  const [addingComponent, setAddingComponent] = useState(false);
  const [componentForm, setComponentForm] = useState({
    title: "",
    componentType: "",
    description: "",
    purpose: "",
    status: "",
  });
  const [deletingComponentId, setDeletingComponentId] = useState<string | null>(
    null,
  );
  const [componentDeleteError, setComponentDeleteError] = useState<
    string | null
  >(null);
  const [componentSearchFilter, setComponentSearchFilter] = useState("");
  const [componentTypeFilter, setComponentTypeFilter] = useState("");
  const [componentGroupFilter, setComponentGroupFilter] = useState("");
  // Cylindrical layer view: which SSP layer is currently focused
  const [selectedLayerKey, setSelectedLayerKey] =
    useState<SspLayerKey>("application");
  // Component detail view
  const [selectedComponentUuid, setSelectedComponentUuid] = useState<
    string | null
  >(null);
  const [compDetailTab, setCompDetailTab] = useState<"inventory" | "controls">(
    "inventory",
  );
  const [compInventory, setCompInventory] = useState<InventoryItemResponse[]>(
    [],
  );
  const [compControls, setCompControls] = useState<ComponentControlRow[]>([]);
  const [compDetailLoading, setCompDetailLoading] = useState(false);
  // Component detail pagination: assets + controls both paginated client-side
  const [compInvPage, setCompInvPage] = useState(0);
  const [compCtrlPage, setCompCtrlPage] = useState(0);
  const [compPageSize, setCompPageSize] = useState(COMP_DETAIL_PAGE_SIZE);

  // Information Types editing state
  const [editingInfoTypeId, setEditingInfoTypeId] = useState<string | null>(null);
  const [savingInfoType, setSavingInfoType] = useState(false);
  const [editInfoTypeForm, setEditInfoTypeForm] = useState({
    title: "",
    description: "",
    confidentiality: "",
    integrity: "",
    availability: "",
  });

  // Inventory editing state
  const [editingInventoryId, setEditingInventoryId] = useState<string | null>(null);
  const [savingInventoryItem, setSavingInventoryItem] = useState(false);
  const [editInventoryForm, setEditInventoryForm] = useState({
    description: "",
    componentId: "",
    identifiers: [] as { identifierType: string; identifierValue: string }[],
  });

  // Component editing state
  const [editingComponentId, setEditingComponentId] = useState<string | null>(null);
  const [savingComponent, setSavingComponent] = useState(false);
  const [editComponentForm, setEditComponentForm] = useState({
    title: "",
    componentType: "",
    description: "",
    purpose: "",
    status: "",
    statusRemarks: "",
  });

  // Inventory state
  const [inventoryItems, setInventoryItems] = useState<InventoryItemResponse[]>(
    [],
  );
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [showAddAsset, setShowAddAsset] = useState(false);
  const [addingAsset, setAddingAsset] = useState(false);
  const [assetForm, setAssetForm] = useState({
    componentId: "",
    description: "",
    identifiers: [{ identifierType: "", identifierValue: "" }] as {
      identifierType: string;
      identifierValue: string;
    }[],
  });

  // Control Implementations state
  const [controlImpls, setControlImpls] = useState<
    SspControlImplementationResponse[]
  >([]);
  const [controlImplsLoading, setControlImplsLoading] = useState(false);
  const [controlIdFilter, setControlIdFilter] = useState("");
  const [sspProfile, setSspProfile] = useState<ProfileDetailResponse | null>(
    null,
  );

  // Asset Resolution state
  const [resolveQuery, setResolveQuery] = useState("");
  const [resolving, setResolving] = useState(false);
  const [resolveResult, setResolveResult] =
    useState<AssetResolutionResponse | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);

  // Leveraged Authorizations state
  const [leveragedAuths, setLeveragedAuths] = useState<
    LeveragedAuthorizationResponse[]
  >([]);
  const [leveragedAuthsLoading, setLeveragedAuthsLoading] = useState(false);
  const [showAddLevAuth, setShowAddLevAuth] = useState(false);
  const [addingLevAuth, setAddingLevAuth] = useState(false);
  const [levAuthForm, setLevAuthForm] = useState({
    title: "",
    partyUuid: "",
    dateAuthorized: "",
  });
  const [editingLevAuthId, setEditingLevAuthId] = useState<string | null>(null);
  const [savingLevAuth, setSavingLevAuth] = useState(false);
  const [updatingLevAuthStatusId, setUpdatingLevAuthStatusId] = useState<string | null>(null);
  const [openLevAuthMenuId, setOpenLevAuthMenuId] = useState<string | null>(null);
  const [editLevAuthForm, setEditLevAuthForm] = useState({
    title: "",
    partyUuid: "",
    dateAuthorized: "",
  });
  const [deletingLevAuthId, setDeletingLevAuthId] = useState<string | null>(
    null,
  );

  // System Users state
  const [systemUsers, setSystemUsers] = useState<SspSystemUserResponse[]>([]);
  const [systemUsersLoading, setSystemUsersLoading] = useState(false);
  const [showAddSystemUser, setShowAddSystemUser] = useState(false);
  const [addingSystemUser, setAddingSystemUser] = useState(false);
  const [systemUserForm, setSystemUserForm] = useState({
    title: "",
    shortName: "",
    description: "",
    roleIds: "",
    privilegeTitle: "",
    functionsPerformed: "",
  });
  const [deletingSystemUserId, setDeletingSystemUserId] = useState<
    string | null
  >(null);
  const [editingSystemUserId, setEditingSystemUserId] = useState<string | null>(
    null,
  );
  const [savingSystemUser, setSavingSystemUser] = useState(false);
  const [editSystemUserForm, setEditSystemUserForm] = useState({
    title: "",
    shortName: "",
    description: "",
    roleIds: "",
    privilegeTitle: "",
    functionsPerformed: "",
  });

  // Interconnections state
  const [interconnections, setInterconnections] = useState<
    SspInterconnectionResponse[]
  >([]);
  const [interconnectionsLoading, setInterconnectionsLoading] = useState(false);
  const [showAddInterconnection, setShowAddInterconnection] = useState(false);
  const [addingInterconnection, setAddingInterconnection] = useState(false);
  const defaultInterconnectionForm = {
    title: "",
    description: "",
    direction: "" as "incoming" | "outgoing" | "incoming-outgoing" | "",
    remoteSystemName: "",
    responsibleRoles: "",
    protocols: [
      { name: "", portRanges: [{ start: "", end: "", transport: "tcp" }] },
    ] as {
      name: string;
      portRanges: { start: string; end: string; transport: string }[];
    }[],
  };
  const [interconnectionForm, setInterconnectionForm] = useState(
    defaultInterconnectionForm,
  );
  const [deletingInterconnectionId, setDeletingInterconnectionId] = useState<
    string | null
  >(null);
  const [editingInterconnectionId, setEditingInterconnectionId] = useState<
    string | null
  >(null);
  const [savingInterconnection, setSavingInterconnection] = useState(false);
  const [editInterconnectionForm, setEditInterconnectionForm] = useState({
    ...defaultInterconnectionForm,
  });

  // Baseline Controls state
  const [baselineControls, setBaselineControls] = useState<
    BaselineControlResponse[]
  >([]);
  const [baselineControlsLoading, setBaselineControlsLoading] = useState(false);

  // Roles, Parties state
  const [roles, setRoles] = useState<SspRoleResponse[]>([]);
  const [parties, setParties] = useState<SspPartyResponse[]>([]);
  // Locations state
  const [locations, setLocations] = useState<SspLocationResponse[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [responsibleParties, setResponsibleParties] = useState<
    SspResponsiblePartyResponse[]
  >([]);
  const [rolesPartiesLoading, setRolesPartiesLoading] = useState(false);
  const [showAddRole, setShowAddRole] = useState(false);
  const [addingRole, setAddingRole] = useState(false);
  const [roleForm, setRoleForm] = useState({
    roleId: "",
    title: "",
    shortName: "",
    description: "",
  });
  const [showAddParty, setShowAddParty] = useState(false);
  const [addingParty, setAddingParty] = useState(false);
  const [partyForm, setPartyForm] = useState({
    type: "person" as "person" | "organization",
    name: "",
    shortName: "",
    email: "",
  });
  const [showAssignRP, setShowAssignRP] = useState(false);
  const [assigningRP, setAssigningRP] = useState(false);
  const [rpForm, setRpForm] = useState({ roleId: "", partyUuids: "" });
  const [deletingRoleId, setDeletingRoleId] = useState<string | null>(null);
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [savingRole, setSavingRole] = useState(false);
  const [editRoleForm, setEditRoleForm] = useState({
    roleId: "",
    title: "",
    shortName: "",
    description: "",
  });
  const [deletingPartyId, setDeletingPartyId] = useState<string | null>(null);
  const [editingPartyId, setEditingPartyId] = useState<string | null>(null);
  const [savingParty, setSavingParty] = useState(false);
  const [editPartyForm, setEditPartyForm] = useState({
    type: "person" as "person" | "organization",
    name: "",
    shortName: "",
    email: "",
  });
  const [deletingRPId, setDeletingRPId] = useState<string | null>(null);
  const [showAddLocation, setShowAddLocation] = useState(false);
  const [addingLocation, setAddingLocation] = useState(false);
  const [locationForm, setLocationForm] = useState({
    title: "",
    addressLines: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
  });
  const [deletingLocationId, setDeletingLocationId] = useState<string | null>(
    null,
  );
  const [editingLocationId, setEditingLocationId] = useState<string | null>(
    null,
  );
  const [savingLocation, setSavingLocation] = useState(false);
  const [editLocationForm, setEditLocationForm] = useState({
    title: "",
    addressLines: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
  });

  // ── Tab-level search & pagination state ──
  const [tabSearch, setTabSearch] = useState("");

  // Separate pagination state for each tab
  const [infoTypesPagination, setInfoTypesPagination] = useState({ page: 1, pageSize: 10 });
  const [inventoryPagination, setInventoryPagination] = useState({ page: 1, pageSize: 10 });
  const [levAuthPagination, setLevAuthPagination] = useState({ page: 1, pageSize: 10 });
  const [systemUserPagination, setSystemUserPagination] = useState({ page: 1, pageSize: 10 });
  const [interconnectionPagination, setInterconnectionPagination] = useState({ page: 1, pageSize: 10 });
  const [locationPagination, setLocationPagination] = useState({ page: 1, pageSize: 10 });
  const [controlsPagination, setControlsPagination] = useState({ page: 1, pageSize: 10 });
  const [rolesPagination, setRolesPagination] = useState({ page: 1, pageSize: 10 });
  const [partiesPagination, setPartiesPagination] = useState({ page: 1, pageSize: 10 });
  const [historyPagination, setHistoryPagination] = useState({ page: 1, pageSize: 10 });

  // Helper to get current tab's pagination
  const getTabPagination = () => {
    switch (activeTab) {
      case "Information Types": return infoTypesPagination;
      case "Inventory": return inventoryPagination;
      case "Level Auth": return levAuthPagination;
      case "System Users": return systemUserPagination;
      case "Interconnections": return interconnectionPagination;
      case "Locations": return locationPagination;
      case "Baseline Controls": return controlsPagination;
      case "Roles & Parties": return rolesPagination;
      case "History": return historyPagination;
      default: return { page: 1, pageSize: 10 };
    }
  };

  // Helper to set current tab's pagination
  const setCurrentTabPagination = (pagination: { page: number; pageSize: number }) => {
    switch (activeTab) {
      case "Information Types": return setInfoTypesPagination(pagination);
      case "Inventory": return setInventoryPagination(pagination);
      case "Level Auth": return setLevAuthPagination(pagination);
      case "System Users": return setSystemUserPagination(pagination);
      case "Interconnections": return setInterconnectionPagination(pagination);
      case "Locations": return setLocationPagination(pagination);
      case "Baseline Controls": return setControlsPagination(pagination);
      case "Roles & Parties": return setRolesPagination(pagination);
      case "History": return setHistoryPagination(pagination);
    }
  };

  const tabPage = getTabPagination().page;
  const tabPageSize = getTabPagination().pageSize;
  const setTabPage = (page: number) => setCurrentTabPagination({ ...getTabPagination(), page });
  const setTabPageSize = (pageSize: number) => {
    setCurrentTabPagination({ page: 1, pageSize });
  };

  // Helper function for pagination page window
  const getPageWindow = (page: number, totalPages: number): (number | "dots")[] => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }
    const current = page;
    const out: (number | "dots")[] = [1];
    const start = Math.max(2, current - 1);
    const end = Math.min(totalPages - 1, current + 1);
    if (start > 2) out.push("dots");
    for (let p = start; p <= end; p++) out.push(p);
    if (end < totalPages - 1) out.push("dots");
    out.push(totalPages);
    return out;
  };

  // Reset tab search when switching tabs (but keep pagination)
  useEffect(() => {
    setTabSearch("");
    setComponentSearchFilter("");
    setControlIdFilter("");
  }, [activeTab]);

  // Sync tabSearch with Components/Control Implementations existing filters
  useEffect(() => {
    if (activeTab === "Components") setComponentSearchFilter(tabSearch);
    if (activeTab === "Control Implementations") setControlIdFilter(tabSearch);
  }, [tabSearch, activeTab]);

  // Control Implementation CRUD state
  const [showAddControlImpl, setShowAddControlImpl] = useState(false);
  const [addingControlImpl, setAddingControlImpl] = useState(false);
  const [controlImplForm, setControlImplForm] = useState({
    componentIds: [] as string[],
    source: "",
    controlId: "",
    description: "",
    implementationStatus: "" as ImplementationStatus | "",
    controlOrigination: "" as ControlOrigination | "",
    responsibleRoleIds: "",
    setParameters: [] as { paramId: string; values: string; remarks: string }[],
    statements: [] as {
      statementId: string;
      description: string;
      sortOrder: string;
    }[],
  });
  // Resolved catalog control selection for Add Control Implementation
  const [profileControlMap, setProfileControlMap] = useState<
    Record<string, { controlName: string; groupName: string }>
  >({});
  const [ciControls, setCiControls] = useState<ControlResponse[]>([]);
  const [ciCatalogId, setCiCatalogId] = useState<string>("");
  const [ciLoadingControls, setCiLoadingControls] = useState(false);
  const [ciSelectedControlDetail, setCiSelectedControlDetail] =
    useState<ControlResponse | null>(null);
  const [ciLoadingControlDetail, setCiLoadingControlDetail] = useState(false);
  const [editingControlImplId, setEditingControlImplId] = useState<
    string | null
  >(null);
  const [editingCiId, setEditingCiId] = useState<string | null>(null);
  /** The single CI id for this SSP — backend enforces one CI per SSP */
  const [singleCiId, setSingleCiId] = useState<string | null>(null);
  const [savingControlImpl, setSavingControlImpl] = useState(false);
  const [showEditControlImpl, setShowEditControlImpl] = useState(false);
  const [editControlImplForm, setEditControlImplForm] = useState({
    controlId: "",
    description: "",
    implementationStatus: "" as ImplementationStatus | "",
    controlOrigination: "" as ControlOrigination | "",
    responsibleRoleIds: "",
    setParameters: [] as { paramId: string; values: string }[],
    byComponents: [] as {
      componentUuid: string;
      description: string;
      implementationStatus: string;
      responsibleRoles: string;
    }[],
    statements: [] as {
      statementId: string;
      description: string;
      sortOrder: number;
    }[],
    sortOrder: 0,
  });
  const [deletingControlImplId, setDeletingControlImplId] = useState<
    string | null
  >(null);

  // Lifecycle state
  const [lifecycleData, setLifecycleData] = useState<DocumentResponse | null>(
    null,
  );
  const [transitioningLifecycle, setTransitioningLifecycle] = useState(false);
  const [showTransitionModal, setShowTransitionModal] = useState(false);
  const [transitionTarget, setTransitionTarget] = useState("");
  const [transitionComment, setTransitionComment] = useState("");

  const normalizeSspLifecycle = (status?: string | null): LifecycleStatus =>
    (status || "draft").toLowerCase() as LifecycleStatus;

  const getSelectedSspLifecycle = (): LifecycleStatus =>
    normalizeSspLifecycle(
      lifecycleData?.lifecycleStatus ||
      (sspDetail?.documentId
        ? sspLifecycles[sspDetail.documentId]
        : undefined),
    );

  const isSelectedSspPublished = () =>
    getSelectedSspLifecycle() === "published";

  const getSspSummaryLifecycle = (ssp: SspSummaryResponse | null) =>
    normalizeSspLifecycle(
      ssp?.lifecycleStatus ||
      (ssp?.documentId ? sspLifecycles[ssp.documentId] : undefined),
    );

  // Organization state for SSP import
  const [accessibleOrgs, setAccessibleOrgs] = useState<any[]>([]);
  const [accessibleOrgsLoading, setAccessibleOrgsLoading] = useState(false);
  const [importOrgId, setImportOrgId] = useState("");

  // CDEF Import state
  const [showImportComponent, setShowImportComponent] = useState(false);
  const [cdefList, setCdefList] = useState<
    ComponentDefinitionSummaryResponse[]
  >([]);
  const [cdefComponents, setCdefComponents] = useState<
    (ComponentResponse & { cdefTitle: string })[]
  >([]);
  const [cdefLoading, setCdefLoading] = useState(false);
  const [selectedImportComponentIds, setSelectedImportComponentIds] = useState<
    Set<string>
  >(new Set());
  const [importingComponents, setImportingComponents] = useState(false);
  const [importedComponentIds, setImportedComponentIds] = useState<Set<string>>(
    new Set(),
  );

  // Post-import control status popup
  const [postImportIRs, setPostImportIRs] = useState<
    {
      ciId: string;
      componentId: string;
      componentTitle: string;
      ir: ImplementedRequirementDetailResponse;
    }[]
  >([]);
  const [postImportComponentTitle, setPostImportComponentTitle] = useState("");
  const [showPostImportPopup, setShowPostImportPopup] = useState(false);
  const [postImportStatuses, setPostImportStatuses] = useState<
    Record<string, ImplementationStatus>
  >({});
  const [postImportDescriptions, setPostImportDescriptions] = useState<
    Record<string, string>
  >({});
  const [postImportStmtEdits, setPostImportStmtEdits] = useState<
    Record<
      string,
      { statementId: string; description: string; sortOrder: number }[]
    >
  >({});
  const [postImportControlDetails, setPostImportControlDetails] = useState<
    Record<string, ControlResponse>
  >({});
  const [postImportParamEdits, setPostImportParamEdits] = useState<
    Record<string, { paramId: string; values: string }[]>
  >({});
  const [savingIrId, setSavingIrId] = useState<string | null>(null);
  const [savedIrIds, setSavedIrIds] = useState<Set<string>>(new Set());
  const [postImportLoading, setPostImportLoading] = useState(false);
  const [postImportLoadingControlId, setPostImportLoadingControlId] = useState<
    string | null
  >(null);
  const [postImportSelectedIdx, setPostImportSelectedIdx] = useState<
    number | null
  >(null);
  const [postImportSearch, setPostImportSearch] = useState("");
  const [excludedIrIds, setExcludedIrIds] = useState<Set<string>>(new Set());

  // Generic delete confirmation
  const [pendingDelete, setPendingDelete] = useState<{
    type: string;
    id: string;
    title: string;
    action: () => Promise<void>;
  } | null>(null);
  const [pendingDeleting, setPendingDeleting] = useState(false);

  const confirmPendingDelete = async () => {
    if (!pendingDelete) return;
    setPendingDeleting(true);
    try {
      await pendingDelete.action();
    } finally {
      setPendingDeleting(false);
      setPendingDelete(null);
    }
  };

  const requestDelete = (
    type: string,
    id: string,
    title: string,
    action: () => Promise<void>,
  ) => {
    if (isSelectedSspPublished()) {
      useToastStore
        .getState()
        .showError(
          "Error",
          "Published SSPs are read-only and cannot be changed",
        );
      return;
    }
    setPendingDelete({ type, id, title, action });
  };

  // Global error
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (error) {
      useToastStore.getState().showError("Error", error);
      setError(null);
    }
  }, [error]);

  // ── Fetch SSP list ──────────────────────────────────────
  const fetchSSPList = useCallback(async () => {
    setListLoading(true);
    setListError(null);
    try {
      const res = await listSSPs();
      setSspList(res.data);
      // Use title and lifecycleStatus directly from the SSP list response
      setSspDocTitles(
        Object.fromEntries(
          res.data.map((ssp) => [ssp.documentId, ssp.title || ssp.documentId]),
        ),
      );
      setSspLifecycles(
        Object.fromEntries(
          res.data.map((ssp) => [
            ssp.documentId,
            normalizeSspLifecycle(ssp.lifecycleStatus),
          ]),
        ),
      );
    } catch (err) {
      setListError(err instanceof Error ? err.message : "Failed to load SSPs");
    } finally {
      setListLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSSPList();
  }, [fetchSSPList]);

  // ── Fetch accessible organizations ───────────────────────────────────
  const fetchAccessibleOrgs = async (module: string, action: string) => {
    setAccessibleOrgsLoading(true);
    try {
      const res = await getAPI(
        `${API_URL.ACCESSIBLE_ORGS}?module=${module}&action=${action}`,
      );
      const orgs = res?.data?.organizations ?? [];
      setAccessibleOrgs(orgs);
      if (orgs.length === 1) {
        setImportOrgId(orgs[0].id);
      }
    } catch {
      setAccessibleOrgs([]);
    } finally {
      setAccessibleOrgsLoading(false);
    }
  };

  // ── Fetch SSP detail (single /full call) ───────────────
  const fetchSSPDetail = useCallback(async (id: string) => {
    setDetailLoading(true);
    setError(null);
    try {
      const res = await getSspFull(id);
      const full: SspFullResponse = res.data;
      const { ssp } = full;

      setSspDetail(ssp);
      setSysChars(ssp.systemCharacteristics ?? null);
      setSspComponents(ssp.systemImplementation?.components ?? []);
      setInventoryItems(ssp.systemImplementation?.inventoryItems ?? []);
      setSystemUsers(ssp.systemImplementation?.users ?? []);
      setInterconnections(ssp.systemImplementation?.interconnections ?? []);
      setLeveragedAuths(ssp.leveragedAuthorizations ?? []);
      setInfoTypes(full.informationTypes ?? []);
      setRoles(full.roles ?? []);
      setParties(full.parties ?? []);
      setLocations(full.locations ?? []);
      setResponsibleParties(full.responsibleParties ?? []);
      const rawBaseline = full.baselineControls as any;
      setBaselineControls(Array.isArray(rawBaseline) ? rawBaseline : (rawBaseline?.controls ?? []));

      // Flatten raw control implementations (same logic as listSspControlImplementations)
      const flatCIs: SspControlImplementationResponse[] = [];
      for (const ci of ssp.controlImplementations ?? []) {
        for (const ir of ci.implementedRequirements ?? []) {
          flatCIs.push({
            id: ir.id,
            irUuid: ir.irUuid,
            controlId: ir.controlId,
            componentId: ci.componentId ?? null,
            source: ci.source,
            description: ir.description ?? ci.description,
            implementationStatus: ir.implementationStatus,
            controlOrigination: ir.origination,
            setParameters: ir.setParameters ?? [],
            byComponents: ir.byComponents ?? [],
            statements: ir.statements ?? [],
            sortOrder: ir.sortOrder,
            createdAt: ir.createdAt,
            updatedAt: ir.updatedAt,
            ciId: ci.id,
          });
        }
      }
      setControlImpls(flatCIs);
    } catch (err: any) {
      const detail = err?.response?.data?.errors?.[0]?.detail;
      setError(detail ?? (err instanceof Error ? err.message : "Failed to load SSP details"));
    } finally {
      setDetailLoading(false);
    }
  }, []);

  /* ── Load SSP from URL parameter on mount ── */
  useEffect(() => {
    if (!sspIdFromUrl) return;

    let cancelled = false;

    const loadSSPFromUrl = async () => {
      const existingSSP = sspList.find((s) => s.id === sspIdFromUrl);
      if (existingSSP && !cancelled) {
        setSelectedSSPId(sspIdFromUrl);
        fetchSSPDetail(sspIdFromUrl);
      } else if (!cancelled) {
        // Directly set the ID and let the existing useEffect handle fetching
        setSelectedSSPId(sspIdFromUrl);
        fetchSSPDetail(sspIdFromUrl);
      }
    };

    loadSSPFromUrl();
    return () => {
      cancelled = true;
    };
  }, [sspIdFromUrl, sspList, fetchSSPDetail]);

  useEffect(() => {
    if (selectedSSPId) {
      fetchSSPDetail(selectedSSPId);
    }
  }, [selectedSSPId, fetchSSPDetail]);

  // Fetch lifecycle once we have the document ID from SSP detail
  useEffect(() => {
    if (sspDetail?.documentId) {
      fetchLifecycle(sspDetail.documentId);
    }
  }, [sspDetail?.documentId]);

  // ── Fetch tab data when tab changes ─────────────────────
  // Most data is pre-loaded by fetchSSPDetail via GET /ssps/{id}/full.
  // Supporting Evidence is not included in /full so is still fetched on tab visit.
  // Component UUID selection is reset when navigating to Components.
  useEffect(() => {
    if (!selectedSSPId) return;
    if (activeTab === "System Characteristics") {
      fetchSupportingEvidence(selectedSSPId);
    } else if (activeTab === "Components") {
      setSelectedComponentUuid(null);
      setComponentDeleteError(null);
    }
  }, [activeTab, selectedSSPId]);

  // ── Tab data fetchers ───────────────────────────────────
  const fetchSystemCharacteristics = async (id: string) => {
    setSysCharsLoading(true);
    try {
      const res = await getSystemCharacteristics(id);
      setSysChars(res.data);
    } catch {
      setSysChars(null);
    } finally {
      setSysCharsLoading(false);
    }
  };

  const fetchInformationTypes = async (id: string) => {
    setInfoTypesLoading(true);
    try {
      const res = await listInformationTypes(id);
      setInfoTypes(res.data);
    } catch {
      setInfoTypes([]);
    } finally {
      setInfoTypesLoading(false);
    }
  };

  const fetchComponents = async (id: string) => {
    setComponentsLoading(true);
    try {
      const res = await listSspComponents(id);
      setSspComponents(res.data);
    } catch {
      setSspComponents([]);
    } finally {
      setComponentsLoading(false);
    }
  };

  const fetchInventory = async (id: string) => {
    setInventoryLoading(true);
    try {
      const res = await listInventoryItems(id);
      setInventoryItems(res.data);
    } catch {
      setInventoryItems([]);
    } finally {
      setInventoryLoading(false);
    }
  };

  const fetchControlImplementations = async (
    id: string,
    controlId?: string,
  ) => {
    setControlImplsLoading(true);
    try {
      const res = await listSspControlImplementations(id, controlId);
      setControlImpls(res.data);
      // Backend enforces one CI per SSP — capture it for reuse
      if (res.data.length > 0 && res.data[0].ciId) {
        setSingleCiId(res.data[0].ciId);
      }
    } catch {
      setControlImpls([]);
    } finally {
      setControlImplsLoading(false);
    }
  };

  const fetchSspProfile = async () => {
    if (!sspDetail?.importProfileHref) return;
    const documentId = sspDetail.importProfileHref.replace(/^#/, "");
    try {
      const res = await getProfileByDocumentId(documentId);
      setSspProfile(res.data);
    } catch {
      setSspProfile(null);
    }
  };

  const fetchLeveragedAuthorizations = async (id: string) => {
    setLeveragedAuthsLoading(true);
    try {
      const res = await listLeveragedAuthorizations(id);
      setLeveragedAuths(res.data);
    } catch {
      setLeveragedAuths([]);
    } finally {
      setLeveragedAuthsLoading(false);
    }
  };

  const fetchLocations = async (id: string) => {
    setLocationsLoading(true);
    try {
      const res = await listSspLocations(id);
      setLocations(res.data);
    } catch {
      setLocations([]);
    } finally {
      setLocationsLoading(false);
    }
  };

  const fetchRolesAndParties = async (id: string) => {
    setRolesPartiesLoading(true);
    try {
      const [rolesRes, partiesRes, rpRes] = await Promise.all([
        listSspRoles(id),
        listSspParties(id),
        listSspResponsibleParties(id),
      ]);
      setRoles(rolesRes.data);
      setParties(partiesRes.data);
      setResponsibleParties(rpRes.data);
    } catch {
      setRoles([]);
      setParties([]);
      setResponsibleParties([]);
    } finally {
      setRolesPartiesLoading(false);
    }
  };

  const fetchSystemUsers = async (id: string) => {
    setSystemUsersLoading(true);
    try {
      const res = await listSspSystemUsers(id);
      setSystemUsers(res.data);
    } catch {
      setSystemUsers([]);
    } finally {
      setSystemUsersLoading(false);
    }
  };

  const fetchInterconnections = async (id: string) => {
    setInterconnectionsLoading(true);
    try {
      const res = await listSspInterconnections(id);
      setInterconnections(res.data);
    } catch {
      setInterconnections([]);
    } finally {
      setInterconnectionsLoading(false);
    }
  };

  const fetchBaselineControls = async (id: string) => {
    setBaselineControlsLoading(true);
    try {
      const res = await listBaselineControls(id);
      const raw = res.data as any;
      setBaselineControls(Array.isArray(raw) ? raw : (raw?.controls ?? []));
    } catch {
      setBaselineControls([]);
    } finally {
      setBaselineControlsLoading(false);
    }
  };

  useEffect(() => {
    if (!showEditControlImpl || !selectedSSPId) return;

    let cancelled = false;
    listBaselineControls(selectedSSPId)
      .then((res) => {
        if (!cancelled) {
          const raw = res.data as any;
          setBaselineControls(Array.isArray(raw) ? raw : (raw?.controls ?? []));
        }
      })
      .catch(() => {
        // Keep the existing baseline data if a modal refresh fails.
      });

    return () => {
      cancelled = true;
    };
  }, [showEditControlImpl, selectedSSPId, editControlImplForm.controlId]);

  const fetchLifecycle = async (documentId: string) => {
    try {
      const res = await getDocument(documentId);
      setLifecycleData(res.data);
    } catch {
      setLifecycleData(null);
    }
  };

  // ── Fetch Profiles for SSP creation ─────────────────────
  const fetchProfiles = async () => {
    setLoadingProfiles(true);
    try {
      const res = await listCompProfiles();
      setProfiles(res.data);
    } catch {
      setProfiles([]);
    } finally {
      setLoadingProfiles(false);
    }
  };

  // ── Create SSP ──────────────────────────────────────────
  // Application-mode picks route through POST /ssps/from-application; the
  // plain endpoint is used for service mode (no API contract yet) and any
  // bare creation path. owning_org_unit_id comes from the chosen
  // organisation (form value or the only accessible org).
  const handleCreateSSP = async (inheritance?: {
    applicationId?: string;
    serviceId?: string;
    componentIds?: string[];
    providerSspIds?: string[];
  }) => {
    if (
      !sspForm.title ||
      !sspForm.importProfileHref ||
      sspTypeSelectionIncomplete
    )
      return;
    setCreating(true);
    try {
      const owningOrgUnitId =
        sspForm.owningOrgUnitId ||
        (accessibleOrgs.length === 1 ? accessibleOrgs[0].id : undefined);
      let createdId: string | null = null;
      if (inheritance?.applicationId && owningOrgUnitId) {
        const res = await createSSPFromApplication({
          title: sspForm.title,
          import_profile_href: sspForm.importProfileHref,
          owning_org_unit_id: owningOrgUnitId,
          application_id: inheritance.applicationId,
          component_ids: inheritance.componentIds ?? [],
          ...(inheritance.providerSspIds?.length
            ? { provider_ssp_ids: inheritance.providerSspIds }
            : {}),
        });
        createdId = res.data?.id ?? null;
      } else if (inheritance?.serviceId && owningOrgUnitId) {
        await createSSPFromService({
          title: sspForm.title,
          import_profile_href: sspForm.importProfileHref,
          owning_org_unit_id: owningOrgUnitId,
          service_id: inheritance.serviceId,
          component_ids: inheritance.componentIds ?? [],
        });
      } else {
        await createSSP({
          title: sspForm.title,
          importProfileHref: sspForm.importProfileHref,
          owningOrgUnitId,
        });
      }
      setShowNewSSP(false);
      setSspForm({ title: "", importProfileHref: "", owningOrgUnitId: "" });
      setSspInheritanceType("");
      setSspSelectedAppId("");
      setSspSelectedServiceId("");
      // Invalidate the cached inheritance data — the just-created SSP
      // changes isUsed/usedBySsps for one of the services.
      setSspInheritanceRefreshTick((t) => t + 1);
      await fetchSSPList();
      // Navigate directly to the new SSP so the user lands on a pre-wired LA tab.
      if (createdId) navigate(`/ssp/${createdId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create SSP");
    } finally {
      setCreating(false);
    }
  };

  // ── Delete SSP ──────────────────────────────────────────
  const handleDeleteSSP = async () => {
    if (!deleteTarget) return;
    if (getSspSummaryLifecycle(deleteTarget) === "published") {
      useToastStore
        .getState()
        .showError("Error", "Published SSPs cannot be deleted");
      setDeleteTarget(null);
      return;
    }
    setDeleting(true);
    try {
      await deleteSSP(deleteTarget.id, "Deleted from SSP Editor");
      useToastStore
        .getState()
        .showSuccess("Success", `SSP "${deleteTarget.title}" deleted`);
      setDeleteTarget(null);
      if (selectedSSPId === deleteTarget.id) {
        navigate("/ssp");
        setSelectedSSPId(null);
        setSspDetail(null);
      }
      await fetchSSPList();
    } catch (err: any) {
      const detail = err?.response?.data?.errors?.[0]?.detail;
      setError(detail ?? (err instanceof Error ? err.message : "Failed to delete SSP"));
    } finally {
      setDeleting(false);
    }
  };

  // ── Save System Characteristics ─────────────────────────
  const handleSaveSysChars = async () => {
    if (!selectedSSPId || !sysCharsForm.systemName) return;
    setSavingSysChars(true);
    try {
      const res = await setSystemCharacteristics(selectedSSPId, {
        systemName: sysCharsForm.systemName,
        systemNameShort: sysCharsForm.systemNameShort || undefined,
        description: sysCharsForm.description || undefined,
        securitySensitivityLevel:
          sysCharsForm.securitySensitivityLevel || undefined,
        systemType: sysCharsForm.systemType || undefined,
        authorizationType: sysCharsForm.authorizationType || undefined,
        deploymentModel: sysCharsForm.deploymentModel || undefined,
        serviceModel: sysCharsForm.serviceModel || undefined,
        systemIds:
          sysCharsForm.systemIds.length > 0
            ? sysCharsForm.systemIds.filter((s) => s.id)
            : [],
        authorizationBoundary: sysCharsForm.authorizationBoundary
          ? { description: sysCharsForm.authorizationBoundary }
          : undefined,
        networkArchitecture: sysCharsForm.networkArchitecture
          ? { description: sysCharsForm.networkArchitecture }
          : undefined,
        dataFlow: sysCharsForm.dataFlow
          ? { description: sysCharsForm.dataFlow }
          : undefined,
        securityImpactLevel:
          sysCharsForm.confidentiality ||
            sysCharsForm.integrity ||
            sysCharsForm.availability
            ? {
              "security-objective-confidentiality":
                sysCharsForm.confidentiality || "low",
              "security-objective-integrity": sysCharsForm.integrity || "low",
              "security-objective-availability":
                sysCharsForm.availability || "low",
            }
            : undefined,
        status: sysCharsForm.status || undefined,
        statusRemarks: sysCharsForm.statusRemarks || undefined,
        dateAuthorized: sysCharsForm.dateAuthorized
          ? `${sysCharsForm.dateAuthorized}T00:00:00.000000000Z`
          : undefined,
      });
      setSysChars(res.data);
      setEditingSysChars(false);
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `System characteristics for "${sysCharsForm.systemName}" saved`,
        );
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to save system characteristics",
      );
    } finally {
      setSavingSysChars(false);
    }
  };

  // ── Fetch existing supporting evidence (diagrams) ──────
  const fetchSupportingEvidence = useCallback(async (sspId: string) => {
    try {
      const res = await listSspSupportingEvidence(sspId);
      const evidenceList = res.data || [];
      for (const ev of evidenceList) {
        if (ev.tags?.includes("network-architecture") && ev.hasFile) {
          setNetworkArchDiagram({
            evidenceId: ev.id,
            contentEvidenceId: ev.evidenceId,
            fileName: ev.fileName || ev.title,
          });
        }
        if (ev.tags?.includes("data-flow") && ev.hasFile) {
          setDataFlowDiagram({
            evidenceId: ev.id,
            contentEvidenceId: ev.evidenceId,
            fileName: ev.fileName || ev.title,
          });
        }
      }
    } catch {
      // Evidence not available yet — not critical
    }
  }, []);

  // ── Upload diagram via supporting-evidence API ────────
  const handleDiagramUpload = async (
    file: File,
    type: "network" | "dataflow",
  ) => {
    if (!selectedSSPId) return;
    const tag = type === "network" ? "network-architecture" : "data-flow";
    const title =
      type === "network" ? "Network Architecture Diagram" : "Data Flow Diagram";
    setUploadingDiagram(type);
    try {
      // Step 1: Create the evidence record
      const createRes = await createSspSupportingEvidence(selectedSSPId, {
        title,
        description: `${title} for this SSP`,
        tags: [tag],
      });
      const linkId = createRes.data.id;
      const contentEvidenceId = createRes.data.evidenceId;

      // Step 2: Upload the file
      await uploadSspSupportingEvidenceFile(selectedSSPId, linkId, file);

      const diagramInfo = {
        file,
        evidenceId: linkId,
        contentEvidenceId,
        fileName: file.name,
      };
      if (type === "network") {
        setNetworkArchDiagram(diagramInfo);
      } else {
        setDataFlowDiagram(diagramInfo);
      }
      useToastStore
        .getState()
        .showSuccess("Success", `Uploaded "${file.name}" for ${title}`);
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : `Failed to upload ${title}`,
        );
    } finally {
      setUploadingDiagram(null);
    }
  };

  // ── Delete diagram evidence ───────────────────────────
  const handleDiagramDelete = async (type: "network" | "dataflow") => {
    if (!selectedSSPId) return;
    const diagram = type === "network" ? networkArchDiagram : dataFlowDiagram;
    if (!diagram?.evidenceId) {
      // Local-only, just clear
      if (diagram?.file) URL.revokeObjectURL(diagram.url);
      if (type === "network") setNetworkArchDiagram(null);
      else setDataFlowDiagram(null);
      return;
    }
    try {
      await deleteSspSupportingEvidence(selectedSSPId, diagram.evidenceId);
      if (diagram.file) URL.revokeObjectURL(diagram.url);
      if (type === "network") setNetworkArchDiagram(null);
      else setDataFlowDiagram(null);
      useToastStore.getState().showSuccess("Success", "Diagram removed");
    } catch (err) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Failed to remove diagram",
        );
    }
  };

  // ── Add Information Type ────────────────────────────────
  const handleAddInfoType = async () => {
    if (!selectedSSPId || !infoTypeForm.title) return;
    setAddingInfoType(true);
    try {
      await addInformationType(selectedSSPId, {
        title: infoTypeForm.title,
        description: infoTypeForm.description || undefined,
        confidentialityImpact: infoTypeForm.confidentiality
          ? { base: infoTypeForm.confidentiality }
          : undefined,
        integrityImpact: infoTypeForm.integrity
          ? { base: infoTypeForm.integrity }
          : undefined,
        availabilityImpact: infoTypeForm.availability
          ? { base: infoTypeForm.availability }
          : undefined,
        sortOrder: infoTypes.length,
      });
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `Information type "${infoTypeForm.title}" added`,
        );
      setShowAddInfoType(false);
      setInfoTypeForm({
        title: "",
        description: "",
        confidentiality: "",
        integrity: "",
        availability: "",
      });
      await fetchInformationTypes(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to add information type",
      );
    } finally {
      setAddingInfoType(false);
    }
  };

  // ── Add Component ───────────────────────────────────────
  const handleAddComponent = async () => {
    if (!selectedSSPId || !componentForm.title || !componentForm.componentType)
      return;
    setAddingComponent(true);
    try {
      await addSspComponent(selectedSSPId, {
        title: componentForm.title,
        componentType: componentForm.componentType,
        description: componentForm.description || undefined,
        purpose: componentForm.purpose || undefined,
        status: componentForm.status || undefined,
        sortOrder: sspComponents.length,
      });
      useToastStore
        .getState()
        .showSuccess("Success", `Component "${componentForm.title}" added`);
      setShowAddComponent(false);
      setComponentForm({
        title: "",
        componentType: "",
        description: "",
        purpose: "",
        status: "",
      });
      await fetchComponents(selectedSSPId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add component");
    } finally {
      setAddingComponent(false);
    }
  };

  // ── Delete Component ────────────────────────────────────
  const handleDeleteComponent = async (componentUuid: string) => {
    if (!selectedSSPId) return;
    setDeletingComponentId(componentUuid);
    setComponentDeleteError(null);
    try {
      await deleteSspComponent(selectedSSPId, componentUuid);
      useToastStore.getState().showSuccess("Success", "Component deleted");
      await fetchComponents(selectedSSPId);
    } catch (err: any) {
      const detail = err?.response?.data?.errors?.[0]?.detail;
      const msg = detail ?? (err instanceof Error ? err.message : "Failed to delete component");
      setComponentDeleteError(msg);
    } finally {
      setDeletingComponentId(null);
    }
  };

  // ── Component Detail View ─────────────────────────────
  const handleOpenComponentDetail = async (componentId: string) => {
    if (!selectedSSPId) return;
    setSelectedComponentUuid(componentId);
    setCompDetailTab("inventory");
    setCompDetailLoading(true);
    setCompInvPage(0);
    setCompCtrlPage(0);
    try {
      const [invRes, ctrlRes] = await Promise.all([
        listComponentInventoryItems(selectedSSPId, componentId),
        listComponentControls(selectedSSPId, componentId),
      ]);
      setCompInventory(invRes.data ?? []);
      setCompControls(ctrlRes.data ?? []);
    } catch {
      setCompInventory([]);
      setCompControls([]);
    } finally {
      setCompDetailLoading(false);
    }
  };

  // ── Edit Component ─────────────────────────────────────
  const handleUpdateComponent = async () => {
    if (!selectedSSPId || !editingComponentId || !editComponentForm.title) return;
    setSavingComponent(true);
    try {
      const payload: PatchSspComponentPayload = {
        title: editComponentForm.title,
        componentType: editComponentForm.componentType || undefined,
        description: editComponentForm.description || null,
        purpose: editComponentForm.purpose || null,
        status: editComponentForm.status || null,
        statusRemarks: editComponentForm.statusRemarks || null,
      };
      await patchSspComponent(selectedSSPId, editingComponentId, payload);
      useToastStore.getState().showSuccess("Success", `Component "${editComponentForm.title}" updated`);
      setEditingComponentId(null);
      await fetchComponents(selectedSSPId);
    } catch (err: any) {
      const detail = err?.response?.data?.errors?.[0]?.detail;
      setError(detail ?? (err instanceof Error ? err.message : "Failed to update component"));
    } finally {
      setSavingComponent(false);
    }
  };

  // ── Edit Information Type ──────────────────────────────
  const handleUpdateInfoType = async () => {
    if (!selectedSSPId || !editingInfoTypeId || !editInfoTypeForm.title) return;
    setSavingInfoType(true);
    try {
      await patchInformationType(selectedSSPId, editingInfoTypeId, {
        title: editInfoTypeForm.title,
        description: editInfoTypeForm.description || undefined,
        confidentialityImpact: editInfoTypeForm.confidentiality ? { base: editInfoTypeForm.confidentiality } : undefined,
        integrityImpact: editInfoTypeForm.integrity ? { base: editInfoTypeForm.integrity } : undefined,
        availabilityImpact: editInfoTypeForm.availability ? { base: editInfoTypeForm.availability } : undefined,
      });
      useToastStore.getState().showSuccess("Success", `Information type "${editInfoTypeForm.title}" updated`);
      setEditingInfoTypeId(null);
      await fetchInformationTypes(selectedSSPId);
    } catch (err: any) {
      const detail = err?.response?.data?.errors?.[0]?.detail;
      setError(detail ?? (err instanceof Error ? err.message : "Failed to update information type"));
    } finally {
      setSavingInfoType(false);
    }
  };

  const handleDeleteInfoType = async (infoTypeId: string) => {
    if (!selectedSSPId) return;
    try {
      await deleteInformationType(selectedSSPId, infoTypeId);
      useToastStore.getState().showSuccess("Success", "Information type deleted");
      await fetchInformationTypes(selectedSSPId);
    } catch (err: any) {
      const detail = err?.response?.data?.errors?.[0]?.detail;
      setError(detail ?? (err instanceof Error ? err.message : "Failed to delete information type"));
    }
  };

  // ── Edit Inventory Item ────────────────────────────────
  const handleUpdateInventoryItem = async () => {
    if (!selectedSSPId || !editingInventoryId || !editInventoryForm.description) return;
    setSavingInventoryItem(true);
    try {
      const selectedComp = sspComponents.find((c) => c.id === editInventoryForm.componentId);
      await patchInventoryItem(selectedSSPId, editingInventoryId, {
        description: editInventoryForm.description,
        component_id: editInventoryForm.componentId || undefined,
        implementedComponents: selectedComp ? [{ "component-uuid": selectedComp.componentUuid }] : undefined,
        assetIdentifiers: editInventoryForm.identifiers.filter((i) => i.identifierType && i.identifierValue),
      });
      useToastStore.getState().showSuccess("Success", "Inventory item updated");
      setEditingInventoryId(null);
      await fetchInventory(selectedSSPId);
    } catch (err: any) {
      const detail = err?.response?.data?.errors?.[0]?.detail;
      setError(detail ?? (err instanceof Error ? err.message : "Failed to update inventory item"));
    } finally {
      setSavingInventoryItem(false);
    }
  };

  const handleDeleteInventoryItem = async (itemId: string) => {
    if (!selectedSSPId) return;
    try {
      await deleteInventoryItem(selectedSSPId, itemId);
      useToastStore.getState().showSuccess("Success", "Inventory item deleted");
      await fetchInventory(selectedSSPId);
    } catch (err: any) {
      const detail = err?.response?.data?.errors?.[0]?.detail;
      setError(detail ?? (err instanceof Error ? err.message : "Failed to delete inventory item"));
    }
  };

  // ── Add Inventory Item ──────────────────────────────────
  const handleAddAsset = async () => {
    if (!selectedSSPId || !assetForm.description || !assetForm.componentId)
      return;
    setAddingAsset(true);
    try {
      const selectedComp = sspComponents.find(
        (c) => c.id === assetForm.componentId,
      );
      await addInventoryItem(selectedSSPId, {
        description: assetForm.description,
        component_id: assetForm.componentId,
        implementedComponents: selectedComp
          ? [{ "component-uuid": selectedComp.componentUuid }]
          : [],
        assetIdentifiers: assetForm.identifiers.filter(
          (id) => id.identifierType && id.identifierValue,
        ),
        sortOrder: inventoryItems.length,
      });
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `Inventory item "${assetForm.description}" added`,
        );
      setShowAddAsset(false);
      setAssetForm({
        componentId: "",
        description: "",
        identifiers: [{ identifierType: "", identifierValue: "" }],
      });
      await fetchInventory(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to add inventory item",
      );
    } finally {
      setAddingAsset(false);
    }
  };

  // ── Resolve Asset ───────────────────────────────────────
  const handleResolveAsset = async () => {
    if (!selectedSSPId || !resolveQuery) return;
    setResolving(true);
    setResolveError(null);
    setResolveResult(null);
    try {
      const res = await resolveAsset(selectedSSPId, resolveQuery);
      setResolveResult(res.data);
    } catch (err) {
      setResolveError(
        err instanceof Error ? err.message : "Failed to resolve asset",
      );
    } finally {
      setResolving(false);
    }
  };

  // ── Add Leveraged Authorization ────────────────────────
  const handleAddLevAuth = async () => {
    if (!selectedSSPId || !levAuthForm.title) return;
    setAddingLevAuth(true);
    try {
      await addLeveragedAuthorization(selectedSSPId, {
        leveragedUuid: null,
        title: levAuthForm.title,
        partyUuid: levAuthForm.partyUuid || undefined,
        dateAuthorized: levAuthForm.dateAuthorized
          ? `${levAuthForm.dateAuthorized}T00:00:00.000000000Z`
          : undefined,
        sortOrder: leveragedAuths.length,
      });
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `Leveraged authorization "${levAuthForm.title}" added`,
        );
      setShowAddLevAuth(false);
      setLevAuthForm({
        title: "",
        partyUuid: "",
        dateAuthorized: "",
      });
      await fetchLeveragedAuthorizations(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to add leveraged authorization",
      );
    } finally {
      setAddingLevAuth(false);
    }
  };

  // ── Update Leveraged Authorization ────────────────────
  const handleUpdateLevAuth = async () => {
    if (!selectedSSPId || !editingLevAuthId || !editLevAuthForm.title) return;
    setSavingLevAuth(true);
    try {
      await updateLeveragedAuthorization(selectedSSPId, editingLevAuthId, {
        title: editLevAuthForm.title,
        partyUuid: editLevAuthForm.partyUuid || undefined,
        dateAuthorized: editLevAuthForm.dateAuthorized
          ? `${editLevAuthForm.dateAuthorized}T00:00:00.000000000Z`
          : undefined,
      });
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `Leveraged authorization "${editLevAuthForm.title}" updated`,
        );
      setEditingLevAuthId(null);
      await fetchLeveragedAuthorizations(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to update leveraged authorization",
      );
    } finally {
      setSavingLevAuth(false);
    }
  };

  // ── Update Leveraged Authorization Status ─────────────
  const handleUpdateLevAuthStatus = async (authId: string, status: LeveragedAuthorizationStatus) => {
    if (!selectedSSPId) return;
    setUpdatingLevAuthStatusId(authId);
    try {
      await updateLeveragedAuthorization(selectedSSPId, authId, { status });
      setLeveragedAuths((prev) => prev.map((a) => a.id === authId ? { ...a, status } : a));
      useToastStore.getState().showSuccess("Updated", `Authorization marked as ${status}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update authorization status");
    } finally {
      setUpdatingLevAuthStatusId(null);
    }
  };

  // ── Delete Leveraged Authorization ────────────────────
  const handleDeleteLevAuth = async (authId: string) => {
    if (!selectedSSPId) return;
    setDeletingLevAuthId(authId);
    try {
      await deleteLeveragedAuthorization(selectedSSPId, authId);
      useToastStore
        .getState()
        .showSuccess("Success", "Leveraged authorization deleted");
      await fetchLeveragedAuthorizations(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to delete leveraged authorization",
      );
    } finally {
      setDeletingLevAuthId(null);
    }
  };

  // ── Roles & Parties CRUD (PUT replace-all) ─────────────
  const handleAddRole = async () => {
    if (!selectedSSPId || !roleForm.roleId || !roleForm.title) return;
    setAddingRole(true);
    try {
      const newRoles: SspRolePayload[] = [
        ...roles.map((r) => ({
          roleId: r.roleId,
          title: r.title,
          shortName: r.shortName,
          description: r.description,
          sortOrder: r.sortOrder,
        })),
        {
          roleId: roleForm.roleId,
          title: roleForm.title,
          shortName: roleForm.shortName || null,
          description: roleForm.description || null,
          sortOrder: roles.length,
        },
      ];
      await replaceSspRoles(selectedSSPId, newRoles);
      useToastStore
        .getState()
        .showSuccess("Success", `Role "${roleForm.title}" added`);
      setShowAddRole(false);
      setRoleForm({ roleId: "", title: "", shortName: "", description: "" });
      await fetchRolesAndParties(selectedSSPId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add role");
    } finally {
      setAddingRole(false);
    }
  };

  const handleDeleteRole = async (roleDbId: string) => {
    if (!selectedSSPId) return;
    setDeletingRoleId(roleDbId);
    try {
      const newRoles: SspRolePayload[] = roles
        .filter((r) => r.id !== roleDbId)
        .map((r, i) => ({
          roleId: r.roleId,
          title: r.title,
          shortName: r.shortName,
          description: r.description,
          sortOrder: i,
        }));
      await replaceSspRoles(selectedSSPId, newRoles);
      useToastStore.getState().showSuccess("Success", "Role deleted");
      await fetchRolesAndParties(selectedSSPId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete role");
    } finally {
      setDeletingRoleId(null);
    }
  };

  const handleAddParty = async () => {
    if (!selectedSSPId || !partyForm.name) return;
    setAddingParty(true);
    try {
      const newParties: SspPartyPayload[] = [
        ...parties.map((p) => ({
          partyUuid: p.partyUuid,
          partyType: p.partyType,
          name: p.name,
          shortName: p.shortName,
          emailAddresses: p.emailAddresses,
          telephoneNumbers: p.telephoneNumbers,
          memberOfOrganizations: p.memberOfOrganizations,
          externalIds: p.externalIds,
          sortOrder: p.sortOrder,
        })),
        {
          partyType: partyForm.type,
          name: partyForm.name,
          shortName: partyForm.shortName || null,
          emailAddresses: partyForm.email ? [partyForm.email] : [],
          telephoneNumbers: [],
          memberOfOrganizations: [],
          sortOrder: parties.length,
        },
      ];
      await replaceSspParties(selectedSSPId, newParties);
      useToastStore
        .getState()
        .showSuccess("Success", `Party "${partyForm.name}" added`);
      setShowAddParty(false);
      setPartyForm({ type: "person", name: "", shortName: "", email: "" });
      await fetchRolesAndParties(selectedSSPId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add party");
    } finally {
      setAddingParty(false);
    }
  };

  const handleDeleteParty = async (partyDbId: string) => {
    if (!selectedSSPId) return;
    setDeletingPartyId(partyDbId);
    try {
      const newParties: SspPartyPayload[] = parties
        .filter((p) => p.id !== partyDbId)
        .map((p, i) => ({
          partyUuid: p.partyUuid,
          partyType: p.partyType,
          name: p.name,
          shortName: p.shortName,
          emailAddresses: p.emailAddresses,
          telephoneNumbers: p.telephoneNumbers,
          memberOfOrganizations: p.memberOfOrganizations,
          externalIds: p.externalIds,
          sortOrder: i,
        }));
      await replaceSspParties(selectedSSPId, newParties);
      useToastStore.getState().showSuccess("Success", "Party deleted");
      await fetchRolesAndParties(selectedSSPId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete party");
    } finally {
      setDeletingPartyId(null);
    }
  };

  const handleAssignRP = async () => {
    if (!selectedSSPId || !rpForm.roleId || !rpForm.partyUuids) return;
    setAssigningRP(true);
    try {
      const newEntry: SspResponsiblePartyPayload = {
        roleId: rpForm.roleId,
        partyUuids: rpForm.partyUuids
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean),
      };
      const existing: SspResponsiblePartyPayload[] = responsibleParties
        .filter((rp) => rp.roleId !== rpForm.roleId)
        .map((rp) => ({ roleId: rp.roleId, partyUuids: rp.partyUuids }));
      await replaceSspResponsibleParties(selectedSSPId, [
        ...existing,
        newEntry,
      ]);
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `Responsible party assigned to role "${rpForm.roleId}"`,
        );
      setShowAssignRP(false);
      setRpForm({ roleId: "", partyUuids: "" });
      await fetchRolesAndParties(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : "Failed to assign responsible party",
      );
    } finally {
      setAssigningRP(false);
    }
  };

  const handleDeleteRP = async (rpId: string) => {
    if (!selectedSSPId) return;
    setDeletingRPId(rpId);
    try {
      const newRPs: SspResponsiblePartyPayload[] = responsibleParties
        .filter((rp) => rp.id !== rpId)
        .map((rp) => ({ roleId: rp.roleId, partyUuids: rp.partyUuids }));
      await replaceSspResponsibleParties(selectedSSPId, newRPs);
      useToastStore
        .getState()
        .showSuccess("Success", "Responsible party assignment removed");
      await fetchRolesAndParties(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to remove assignment",
      );
    } finally {
      setDeletingRPId(null);
    }
  };

  const handleUpdateRole = async () => {
    if (!selectedSSPId || !editingRoleId || !editRoleForm.title) return;
    setSavingRole(true);
    try {
      const newRoles: SspRolePayload[] = roles.map((r) => {
        if (r.id === editingRoleId) {
          return {
            roleId: r.roleId,
            title: editRoleForm.title,
            shortName: editRoleForm.shortName || null,
            description: editRoleForm.description || null,
            sortOrder: r.sortOrder,
          };
        }
        return {
          roleId: r.roleId,
          title: r.title,
          shortName: r.shortName,
          description: r.description,
          sortOrder: r.sortOrder,
        };
      });
      await replaceSspRoles(selectedSSPId, newRoles);
      useToastStore
        .getState()
        .showSuccess("Success", `Role "${editRoleForm.title}" updated`);
      setEditingRoleId(null);
      await fetchRolesAndParties(selectedSSPId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update role");
    } finally {
      setSavingRole(false);
    }
  };

  const handleUpdateParty = async () => {
    if (!selectedSSPId || !editingPartyId || !editPartyForm.name) return;
    setSavingParty(true);
    try {
      const newParties: SspPartyPayload[] = parties.map((p) => {
        if (p.id === editingPartyId) {
          return {
            partyUuid: p.partyUuid,
            partyType: editPartyForm.type,
            name: editPartyForm.name,
            shortName: editPartyForm.shortName || null,
            emailAddresses: editPartyForm.email ? [editPartyForm.email] : [],
            telephoneNumbers: p.telephoneNumbers,
            memberOfOrganizations: p.memberOfOrganizations,
            externalIds: p.externalIds,
            sortOrder: p.sortOrder,
          };
        }
        return {
          partyUuid: p.partyUuid,
          partyType: p.partyType,
          name: p.name,
          shortName: p.shortName,
          emailAddresses: p.emailAddresses,
          telephoneNumbers: p.telephoneNumbers,
          memberOfOrganizations: p.memberOfOrganizations,
          externalIds: p.externalIds,
          sortOrder: p.sortOrder,
        };
      });
      await replaceSspParties(selectedSSPId, newParties);
      useToastStore
        .getState()
        .showSuccess("Success", `Party "${editPartyForm.name}" updated`);
      setEditingPartyId(null);
      await fetchRolesAndParties(selectedSSPId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update party");
    } finally {
      setSavingParty(false);
    }
  };

  // ── Locations CRUD (PUT replace-all) ────────────────────
  const handleAddLocation = async () => {
    if (!selectedSSPId || !locationForm.title) return;
    setAddingLocation(true);
    try {
      const newLocations: SspLocationPayload[] = [
        ...locations.map((l) => ({
          locationUuid: l.locationUuid,
          title: l.title,
          addressLines: l.addressLines,
          city: l.city,
          state: l.state,
          postalCode: l.postalCode,
          country: l.country,
          latitude: l.latitude,
          longitude: l.longitude,
          sortOrder: l.sortOrder,
        })),
        {
          title: locationForm.title,
          addressLines: locationForm.addressLines
            ? locationForm.addressLines.split("\n").filter(Boolean)
            : [],
          city: locationForm.city || null,
          state: locationForm.state || null,
          postalCode: locationForm.postalCode || null,
          country: locationForm.country || null,
          sortOrder: locations.length,
        },
      ];
      await replaceSspLocations(selectedSSPId, newLocations);
      useToastStore
        .getState()
        .showSuccess("Success", `Location "${locationForm.title}" added`);
      setShowAddLocation(false);
      setLocationForm({
        title: "",
        addressLines: "",
        city: "",
        state: "",
        postalCode: "",
        country: "",
      });
      await fetchLocations(selectedSSPId);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to add location");
    } finally {
      setAddingLocation(false);
    }
  };

  const handleDeleteLocation = async (locationDbId: string) => {
    if (!selectedSSPId) return;
    setDeletingLocationId(locationDbId);
    try {
      const newLocations: SspLocationPayload[] = locations
        .filter((l) => l.id !== locationDbId)
        .map((l, i) => ({
          locationUuid: l.locationUuid,
          title: l.title,
          addressLines: l.addressLines,
          city: l.city,
          state: l.state,
          postalCode: l.postalCode,
          country: l.country,
          latitude: l.latitude,
          longitude: l.longitude,
          sortOrder: i,
        }));
      await replaceSspLocations(selectedSSPId, newLocations);
      useToastStore.getState().showSuccess("Success", "Location deleted");
      await fetchLocations(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to delete location",
      );
    } finally {
      setDeletingLocationId(null);
    }
  };

  const handleUpdateLocation = async () => {
    if (!selectedSSPId || !editingLocationId || !editLocationForm.title) return;
    setSavingLocation(true);
    try {
      const newLocations: SspLocationPayload[] = locations.map((l) => {
        if (l.id === editingLocationId) {
          return {
            locationUuid: l.locationUuid,
            title: editLocationForm.title,
            addressLines: editLocationForm.addressLines
              ? editLocationForm.addressLines.split("\n").filter(Boolean)
              : [],
            city: editLocationForm.city || null,
            state: editLocationForm.state || null,
            postalCode: editLocationForm.postalCode || null,
            country: editLocationForm.country || null,
            sortOrder: l.sortOrder,
          };
        }
        return {
          locationUuid: l.locationUuid,
          title: l.title,
          addressLines: l.addressLines,
          city: l.city,
          state: l.state,
          postalCode: l.postalCode,
          country: l.country,
          latitude: l.latitude,
          longitude: l.longitude,
          sortOrder: l.sortOrder,
        };
      });
      await replaceSspLocations(selectedSSPId, newLocations);
      useToastStore
        .getState()
        .showSuccess("Success", `Location "${editLocationForm.title}" updated`);
      setEditingLocationId(null);
      await fetchLocations(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update location",
      );
    } finally {
      setSavingLocation(false);
    }
  };

  // ── System Users CRUD ──────────────────────────────────
  const handleAddSystemUser = async () => {
    if (!selectedSSPId || !systemUserForm.title) return;
    setAddingSystemUser(true);
    try {
      const authorizedPrivileges = systemUserForm.privilegeTitle
        ? [
          {
            title: systemUserForm.privilegeTitle,
            "functions-performed": systemUserForm.functionsPerformed
              ? systemUserForm.functionsPerformed
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
              : [],
          },
        ]
        : [];
      await addSspSystemUser(selectedSSPId, {
        title: systemUserForm.title,
        shortName: systemUserForm.shortName || undefined,
        description: systemUserForm.description || undefined,
        roleIds: systemUserForm.roleIds
          ? systemUserForm.roleIds
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
          : [],
        authorizedPrivileges,
        sortOrder: systemUsers.length,
      });
      useToastStore
        .getState()
        .showSuccess("Success", `System user "${systemUserForm.title}" added`);
      setShowAddSystemUser(false);
      setSystemUserForm({
        title: "",
        shortName: "",
        description: "",
        roleIds: "",
        privilegeTitle: "",
        functionsPerformed: "",
      });
      await fetchSystemUsers(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to add system user",
      );
    } finally {
      setAddingSystemUser(false);
    }
  };

  const handleDeleteSystemUser = async (userId: string) => {
    if (!selectedSSPId) return;
    setDeletingSystemUserId(userId);
    try {
      await deleteSspSystemUser(selectedSSPId, userId);
      useToastStore.getState().showSuccess("Success", "System user deleted");
      await fetchSystemUsers(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to delete system user",
      );
    } finally {
      setDeletingSystemUserId(null);
    }
  };

  const handleUpdateSystemUser = async () => {
    if (!selectedSSPId || !editingSystemUserId || !editSystemUserForm.title)
      return;
    setSavingSystemUser(true);
    try {
      const authorizedPrivileges = editSystemUserForm.privilegeTitle
        ? [
          {
            title: editSystemUserForm.privilegeTitle,
            functions_performed: editSystemUserForm.functionsPerformed
              ? editSystemUserForm.functionsPerformed
                .split(",")
                .map((s) => s.trim())
                .filter(Boolean)
              : [],
          },
        ]
        : undefined;
      await updateSspSystemUser(selectedSSPId, editingSystemUserId, {
        title: editSystemUserForm.title,
        shortName: editSystemUserForm.shortName || undefined,
        description: editSystemUserForm.description || undefined,
        roleIds: editSystemUserForm.roleIds
          ? editSystemUserForm.roleIds
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
          : undefined,
        authorizedPrivileges,
      });
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `System user "${editSystemUserForm.title}" updated`,
        );
      setEditingSystemUserId(null);
      await fetchSystemUsers(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update system user",
      );
    } finally {
      setSavingSystemUser(false);
    }
  };

  // ── Interconnections CRUD ─────────────────────────────
  const handleAddInterconnection = async () => {
    if (!selectedSSPId || !interconnectionForm.title) return;
    setAddingInterconnection(true);
    try {
      const filteredProtocols = interconnectionForm.protocols
        .filter((p) => p.name.trim())
        .map((p) => ({
          name: p.name,
          port_ranges: p.portRanges
            .filter((pr) => pr.start)
            .map((pr) => ({
              start: Number(pr.start),
              end: Number(pr.end || pr.start),
              transport: pr.transport || "tcp",
            })),
        }));
      const filteredRoles = interconnectionForm.responsibleRoles
        .split(",")
        .map((r) => r.trim())
        .filter(Boolean)
        .map((r) => ({ role_id: r }));
      await addSspInterconnection(selectedSSPId, {
        title: interconnectionForm.title,
        description: interconnectionForm.description || undefined,
        direction:
          (interconnectionForm.direction as
            | "incoming"
            | "outgoing"
            | "incoming-outgoing") || "outgoing",
        remoteSystemName: interconnectionForm.remoteSystemName || undefined,
        responsibleRoles: filteredRoles.length > 0 ? filteredRoles : undefined,
        protocols: filteredProtocols.length > 0 ? filteredProtocols : undefined,
        sortOrder: interconnections.length,
      });
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `Interconnection "${interconnectionForm.title}" added`,
        );
      setShowAddInterconnection(false);
      setInterconnectionForm({
        ...defaultInterconnectionForm,
        protocols: [
          { name: "", portRanges: [{ start: "", end: "", transport: "tcp" }] },
        ],
      });
      await fetchInterconnections(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to add interconnection",
      );
    } finally {
      setAddingInterconnection(false);
    }
  };

  const handleDeleteInterconnection = async (connId: string) => {
    if (!selectedSSPId) return;
    setDeletingInterconnectionId(connId);
    try {
      await deleteSspInterconnection(selectedSSPId, connId);
      useToastStore
        .getState()
        .showSuccess("Success", "Interconnection deleted");
      await fetchInterconnections(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to delete interconnection",
      );
    } finally {
      setDeletingInterconnectionId(null);
    }
  };

  const handleUpdateInterconnection = async () => {
    if (
      !selectedSSPId ||
      !editingInterconnectionId ||
      !editInterconnectionForm.title
    )
      return;
    setSavingInterconnection(true);
    try {
      const filteredProtocols = editInterconnectionForm.protocols
        .filter((p) => p.name.trim())
        .map((p) => ({
          name: p.name,
          port_ranges: p.portRanges
            .filter((pr) => pr.start)
            .map((pr) => ({
              start: Number(pr.start),
              end: Number(pr.end || pr.start),
              transport: pr.transport || "tcp",
            })),
        }));
      const filteredRoles = editInterconnectionForm.responsibleRoles
        .split(",")
        .map((r) => r.trim())
        .filter(Boolean)
        .map((r) => ({ role_id: r }));
      await updateSspInterconnection(selectedSSPId, editingInterconnectionId, {
        title: editInterconnectionForm.title,
        description: editInterconnectionForm.description || undefined,
        direction:
          (editInterconnectionForm.direction as
            | "incoming"
            | "outgoing"
            | "incoming-outgoing") || undefined,
        remoteSystemName: editInterconnectionForm.remoteSystemName || undefined,
        responsibleRoles: filteredRoles.length > 0 ? filteredRoles : undefined,
        protocols: filteredProtocols.length > 0 ? filteredProtocols : undefined,
      });
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `Interconnection "${editInterconnectionForm.title}" updated`,
        );
      setEditingInterconnectionId(null);
      await fetchInterconnections(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update interconnection",
      );
    } finally {
      setSavingInterconnection(false);
    }
  };

  // ── Fetch controls from profile for Add Control Implementation ──
  const fetchCiResolvedControls = async () => {
    setCiControls([]);
    setCiCatalogId("");
    setProfileControlMap({});
    setControlImplForm((f) => ({
      ...f,
      controlId: "",
      description: "",
      setParameters: [],
      statements: [],
    }));
    if (!selectedSSPId) return;
    setCiLoadingControls(true);
    try {
      // Use /ssps/{id}/profile to get the linked profile directly
      const profileRes = await getSspProfile(selectedSSPId);
      const profileDetail = profileRes.data;
      setControlImplForm((f) => ({
        ...f,
        source: sspDetail?.importProfileHref || "",
      }));
      // Extract catalog ID from profile imports
      const catalogId = profileDetail.imports?.[0]?.href?.replace(/^#/, "");
      if (catalogId) setCiCatalogId(catalogId);

      // Build control name map from controlDetails
      const controlMap: Record<
        string,
        { controlName: string; groupName: string }
      > = {};
      for (const imp of profileDetail.imports || []) {
        for (const cd of (imp as any).controlDetails || []) {
          if (cd.controlName) {
            controlMap[cd.controlId] = {
              controlName: cd.controlName,
              groupName: cd.groupName || "",
            };
          }
        }
      }
      setProfileControlMap(controlMap);

      // Build controls list: prefer controlDetails, fallback to with-ids
      const controls: ControlResponse[] = [];
      for (const imp of profileDetail.imports || []) {
        const details = (imp as any).controlDetails || [];
        if (details.length > 0) {
          // Use controlDetails - skip group-level IDs (those without controlName)
          for (const cd of details) {
            if (cd.controlName) {
              controls.push({
                id: cd.controlId,
                controlId: cd.controlId,
                title: cd.controlName,
              } as ControlResponse);
            }
          }
        } else {
          // Fallback: use with-ids from includeControls
          for (const ic of imp.includeControls || []) {
            for (const controlId of ic["with-ids"] || []) {
              controls.push({
                id: controlId,
                controlId,
                title:
                  controlMap[controlId]?.controlName || controlId.toUpperCase(),
              } as ControlResponse);
            }
          }
        }
      }

      // Deduplicate and sort controls
      const uniqueControls = Array.from(
        new Map(controls.map((c) => [c.controlId, c])).values(),
      ).sort((a, b) => a.controlId.localeCompare(b.controlId));
      setCiControls(uniqueControls);
    } catch {
      setCiControls([]);
    } finally {
      setCiLoadingControls(false);
    }
  };

  // ── Control Implementation CRUD ──────────────────────────
  const handleAddControlImpl = async () => {
    if (
      !selectedSSPId ||
      controlImplForm.componentIds.length === 0 ||
      !controlImplForm.source ||
      !controlImplForm.controlId ||
      !controlImplForm.implementationStatus
    )
      return;
    setAddingControlImpl(true);
    try {
      for (const compId of controlImplForm.componentIds) {
        await addSspControlImplementation(selectedSSPId, {
          source: controlImplForm.source,
          controlId: controlImplForm.controlId,
          component_id: compId,
          description: controlImplForm.description || undefined,
          implementationStatus:
            controlImplForm.implementationStatus as ImplementationStatus,
          controlOrigination:
            (controlImplForm.controlOrigination as ControlOrigination) ||
            undefined,
          responsibleRoleIds: controlImplForm.responsibleRoleIds
            ? controlImplForm.responsibleRoleIds
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
            : undefined,
          setParameters: controlImplForm.setParameters
            .filter((p) => p.paramId && p.values)
            .map((p) => ({
              "param-id": p.paramId,
              values: p.values
                .split(",")
                .map((v) => v.trim())
                .filter(Boolean),
              remarks: p.remarks || undefined,
            })),
          statements: controlImplForm.statements
            .filter((s) => s.statementId)
            .map((s) => ({
              statementId: s.statementId,
              description: s.description || undefined,
              sortOrder: s.sortOrder ? Number(s.sortOrder) : undefined,
            })),
        });
      }
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `Control implementation for "${controlImplForm.controlId}" added`,
        );
      setShowAddControlImpl(false);
      setControlImplForm({
        componentIds: [],
        source: "",
        controlId: "",
        description: "",
        implementationStatus: "",
        controlOrigination: "",
        responsibleRoleIds: "",
        setParameters: [],
        statements: [],
      });
      setCiControls([]);
      setCiSelectedControlDetail(null);
      await fetchControlImplementations(
        selectedSSPId,
        controlIdFilter || undefined,
      );
    } catch (err: any) {
      const detail = err?.response?.data?.errors?.[0]?.detail;
      setError(detail ?? (err instanceof Error ? err.message : "Failed to add control implementation"));
    } finally {
      setAddingControlImpl(false);
    }
  };

  const handleUpdateControlImpl = async () => {
    if (
      !selectedSSPId ||
      !editControlImplForm.controlId ||
      !editControlImplForm.implementationStatus
    )
      return;

    setSavingControlImpl(true);
    try {
      const payload: BulkImplementedRequirementPayload[] = [
        {
          controlId: editControlImplForm.controlId,
          implementationStatus:
            editControlImplForm.implementationStatus as ImplementationStatus,
          origination:
            (editControlImplForm.controlOrigination as ControlOrigination) ||
            undefined,
          description: editControlImplForm.description || undefined,
          responsibleRoles: editControlImplForm.responsibleRoleIds
            ? editControlImplForm.responsibleRoleIds
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
              .map((roleId) => ({ "role-id": roleId }))
            : undefined,
          byComponents:
            editControlImplForm.byComponents.length > 0
              ? editControlImplForm.byComponents
                .filter((bc) => bc.componentUuid)
                .map((bc, idx) => ({
                  componentUuid: bc.componentUuid,
                  description: bc.description || undefined,
                  implementationStatus: bc.implementationStatus || undefined,
                  sortOrder: idx,
                }))
              : [],
          statements:
            editControlImplForm.statements.length > 0
              ? editControlImplForm.statements
                .filter((s) => s.statementId)
                .map((s) => ({
                  statementId: s.statementId,
                  description: s.description || undefined,
                  sortOrder: s.sortOrder,
                }))
              : [],
          sortOrder: editControlImplForm.sortOrder,
        },
      ];

      // Use the ciId captured when the modal opened; fall back to searching controlImpls
      let ciId: string | undefined =
        editingCiId ||
        (editingControlImplId
          ? controlImpls.find((ci) => ci.id === editingControlImplId)?.ciId
          : controlImpls.find(
            (ci) => ci.controlId === editControlImplForm.controlId,
          )?.ciId) ||
        undefined;

      // One CI per SSP — use the known single CI rather than creating a new one
      if (!ciId) {
        ciId = singleCiId || undefined;
      }

      await bulkUpdateImplementedRequirements(selectedSSPId, ciId, payload);

      // Save SSP-level parameter overrides via the scoped endpoint (existing IR only)
      if (editingControlImplId && editControlImplForm.setParameters.length > 0) {
        const paramPayload = editControlImplForm.setParameters
          .filter((p) => p.paramId.trim())
          .map((p) => ({
            "param-id": p.paramId,
            values: p.values.split(",").map((v) => v.trim()).filter(Boolean),
          }));
        await putSspIrSetParameters(selectedSSPId, editingControlImplId, paramPayload);
      }

      setEditingControlImplId(null);
      setEditingCiId(null);
      setShowEditControlImpl(false);
      useToastStore
        .getState()
        .showSuccess("Success", "Control implementation saved");
      await fetchControlImplementations(
        selectedSSPId,
        controlIdFilter || undefined,
      );
      if (selectedSSPId) fetchBaselineControls(selectedSSPId);
    } catch (err: any) {
      const detail = err?.response?.data?.errors?.[0]?.detail;
      useToastStore
        .getState()
        .showError(
          "Error",
          detail ?? (err instanceof Error ? err.message : "Failed to save control implementation"),
        );
    } finally {
      setSavingControlImpl(false);
    }
  };

  const handleDeleteControlImpl = async (ciId: string) => {
    if (!selectedSSPId) return;
    const confirmed = window.confirm(
      "Delete this control implementation?\n\nThis also removes its implemented requirements and cannot be undone.",
    );
    if (!confirmed) return;
    setDeletingControlImplId(ciId);
    try {
      await deleteSspControlImplementation(selectedSSPId, ciId);
      useToastStore
        .getState()
        .showSuccess("Success", "Control implementation deleted");
      await fetchControlImplementations(
        selectedSSPId,
        controlIdFilter || undefined,
      );
    } catch (err: any) {
      const detail = err?.response?.data?.errors?.[0]?.detail;
      setError(detail ?? (err instanceof Error ? err.message : "Failed to delete control implementation"));
    } finally {
      setDeletingControlImplId(null);
    }
  };

  // ── Lifecycle transitions ─────────────────────────────────
  const handleLifecycleTransition = async () => {
    if (!sspDetail?.documentId || !transitionTarget) return;
    setTransitioningLifecycle(true);
    try {
      await transitionDocument(sspDetail.documentId, {
        targetStatus: transitionTarget,
        comments: transitionComment || undefined,
      });
      await fetchLifecycle(sspDetail.documentId);
      setShowTransitionModal(false);
      setTransitionTarget("");
      setTransitionComment("");
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `Status changed to "${transitionTarget.replace(/_/g, " ")}"`,
        );
    } catch (err: any) {
      const detail = err?.response?.data?.errors?.[0]?.detail;
      useToastStore
        .getState()
        .showError(
          "Error",
          detail ?? (err instanceof Error ? err.message : "Failed to transition lifecycle"),
        );
    } finally {
      setTransitioningLifecycle(false);
    }
  };

  // ── OSCAL Export (real API) ───────────────────────────────
  // ── SSP Export (generic export API) ──────────────────────
  const handleExport = async (format: "json" | "xml" | "yaml") => {
    const docId = exportTargetDocId || sspDetail?.documentId;
    if (!docId) return;
    setExportingDoc(true);
    setShowExport(false);
    try {
      const sspName = sspDetail?.title || "ssp";
      const res = await exportCatalogDoc(docId, format);
      let job = res.data;
      const getJobId = (j: typeof job) => j.jobId || j.id || "";
      if (job.status === "pending") {
        for (let i = 0; i < 30; i++) {
          await new Promise((r) => setTimeout(r, 2000));
          const poll = await getExportJob(getJobId(job));
          job = poll.data;
          if (job.status !== "pending") break;
        }
      }
      if (job.status !== "completed") {
        useToastStore
          .getState()
          .showError(
            "Error",
            job.status === "failed"
              ? "Export failed"
              : "Export timed out - please try again",
          );
        return;
      }
      const downloadUrl =
        job.downloadUrl || getExportDownloadUrl(getJobId(job));
      const downloadRes = await downloadFile(downloadUrl);
      if (!downloadRes) throw new Error("No response from download");

      // Extract documentTitle from response headers
      let documentTitle = sspName;
      const xDocumentTitle = downloadRes.headers?.["x-document-title"];
      if (xDocumentTitle) {
        documentTitle = xDocumentTitle;
      } else {
        // Try to extract from Content-Disposition header
        const contentDisposition =
          downloadRes.headers?.["content-disposition"] || "";
        const filenameMatch = contentDisposition.match(
          /filename\*?=(?:"([^"]*)"|([^;,\s]*))/,
        );
        if (filenameMatch && (filenameMatch[1] || filenameMatch[2])) {
          const filename = (filenameMatch[1] || filenameMatch[2]).split(".")[0];
          documentTitle = filename;
        }
      }

      // Get MIME type from API response Content-Type header first
      let mimeType = downloadRes.headers?.["content-type"] || "";
      if (!mimeType) {
        // Fallback: use selected format
        const mimeTypes: Record<string, string> = {
          json: "application/json",
          xml: "application/xml",
          yaml: "application/yaml",
        };
        mimeType = mimeTypes[format] || "application/octet-stream";
      }
      const blob = new Blob([downloadRes.data], { type: mimeType });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${documentTitle}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      useToastStore
        .getState()
        .showSuccess("Success", "Export ready - download started");
    } catch (err: unknown) {
      useToastStore
        .getState()
        .showError(
          "Error",
          err instanceof Error ? err.message : "Export failed",
        );
    } finally {
      setExportingDoc(false);
      setExportTargetDocId(null);
    }
  };

  // ── SSP OSCAL Import handlers ──────────────────────────
  const sspStartValidation = async (file: File) => {
    setSspValidating(true);
    setSspImportError(null);
    setSspValidateResult(null);
    try {
      const res = await validateImport(file);
      if (
        !["system-security-plan", "ssp"].includes(
          res.data.documentType.toLowerCase(),
        )
      ) {
        const msg = `Expected a system-security-plan but got "${res.data.documentType}"`;
        setSspImportError(msg);
        useToastStore.getState().showError("Error", msg);
      } else {
        setSspValidateResult(res.data);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Validation failed";
      setSspImportError(msg);
      useToastStore.getState().showError("Error", msg);
    } finally {
      setSspValidating(false);
    }
  };

  const sspIsAllowedUrl = (url: string): boolean =>
    /\.(json|yaml|yml|xml)(\?.*)?$/i.test(url.split("#")[0]);

  const sspToRawUrl = (url: string): string => {
    const ghMatch = url.match(
      /^https?:\/\/github\.com\/([^/]+\/[^/]+)\/blob\/(.+)$/,
    );
    if (ghMatch)
      return `https://raw.githubusercontent.com/${ghMatch[1]}/${ghMatch[2]}`;
    return url;
  };

  const sspFetchUrl = async () => {
    if (!sspImportUrl.trim()) return;
    if (!sspIsAllowedUrl(sspImportUrl.trim())) {
      const msg = "URL must point to a .json, .yaml, or .xml file";
      setSspImportError(msg);
      useToastStore.getState().showError("Error", msg);
      return;
    }
    setSspValidating(true);
    setSspImportError(null);
    setSspValidateResult(null);
    setSspImportFile(null);
    try {
      const rawUrl = sspToRawUrl(sspImportUrl.trim());
      const response = await fetch(rawUrl);
      if (!response.ok)
        throw new Error(
          `Failed to fetch: ${response.status} ${response.statusText}`,
        );
      const blob = await response.blob();
      const filename = sspImportUrl.split("/").pop() || "document";
      const file = new File([blob], filename, {
        type: blob.type || "application/octet-stream",
      });
      setSspImportFile(file);
      const res = await validateImport(file);
      if (
        !["system-security-plan", "ssp"].includes(
          res.data.documentType.toLowerCase(),
        )
      ) {
        const msg = `Expected a system-security-plan but got "${res.data.documentType}"`;
        setSspImportError(msg);
        useToastStore.getState().showError("Error", msg);
      } else {
        setSspValidateResult(res.data);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to fetch";
      setSspImportError(msg);
      useToastStore.getState().showError("Error", msg);
    } finally {
      setSspValidating(false);
    }
  };

  const sspRunImport = async () => {
    if (!sspImportFile) return;
    setSspImportStep("importing");
    setSspImportError(null);
    try {
      const res = await importSSP(
        sspImportFile,
        sspImportMode,
        importOrgId || undefined,
      );
      setSspImportResult(res.data);
      setShowSspImport(false);
      sspOpenSessionModal(res.data.id);
    } catch (err: unknown) {
      const title =
        err instanceof Error && err.name !== "Error"
          ? err.name
          : "Import Failed";
      const msg =
        err instanceof Error ? err.message : "This file already exists";
      setSspImportErrorTitle(title);
      setSspImportError(msg);
      setSspImportStep("findings");
      useToastStore.getState().showError(title, msg);
    }
  };

  const sspOpenSessionModal = async (sessionId: string) => {
    setShowSspSession(true);
    setSspSessionData(null);
    setSspSessionError(null);
    setSspSessionLoading(true);
    setSspCommitting(false);
    setSspAborting(false);
    try {
      const res = await getImportSession(sessionId);
      setSspSessionData(res.data);
    } catch (err: unknown) {
      setSspSessionError(
        err instanceof Error ? err.message : "Failed to fetch session",
      );
    } finally {
      setSspSessionLoading(false);
    }
  };

  useEffect(() => {
    if (!showSspSession || !sspSessionData) return;
    const status = sspSessionData.jobStatus.toLowerCase();
    if (status === "validated" || status === "failed" || status === "committed")
      return;
    const timer = setInterval(async () => {
      try {
        const res = await getImportSession(sspSessionData.id);
        setSspSessionData(res.data);
      } catch {
        /* silently ignore */
      }
    }, 10000);
    return () => clearInterval(timer);
  }, [showSspSession, sspSessionData]);

  const handleSspCommitSession = async () => {
    if (!sspSessionData) return;
    setSspCommitting(true);
    setSspSessionError(null);
    try {
      await commitImportSession(sspSessionData.id, importOrgId || undefined);
      fetchSSPList();
      useToastStore
        .getState()
        .showSuccess("Success", "SSP Imported Successfully");
      setShowSspSession(false);
      setSspSessionData(null);
      resetSspImport();
    } catch (err: unknown) {
      setSspSessionError(err instanceof Error ? err.message : "Commit failed");
    } finally {
      setSspCommitting(false);
    }
  };

  const handleSspAbortSession = async () => {
    if (!sspSessionData) return;
    setSspAborting(true);
    setSspSessionError(null);
    try {
      await abortImportSession(sspSessionData.id);
      setShowSspSession(false);
      setSspSessionData(null);
      resetSspImport();
      fetchSSPList();
    } catch (err: unknown) {
      setSspSessionError(err instanceof Error ? err.message : "Abort failed");
    } finally {
      setSspAborting(false);
    }
  };

  const resetSspImport = () => {
    setSspImportStep("select");
    setSspImportFile(null);
    setSspImportUrl("");
    setSspImportMode("full");
    setSspValidateResult(null);
    setSspValidating(false);
    setSspImportError(null);
    setSspImportErrorTitle(null);
    setSspImportResult(null);
    setImportOrgId("");
  };

  // ── CDEF Import ───────────────────────────────────────────
  const handleOpenImport = async () => {
    if (!selectedSSPId) return;
    setShowImportComponent(true);
    setCdefLoading(true);
    setCdefComponents([]);
    setSelectedImportComponentIds(new Set());
    setImportedComponentIds(new Set());
    try {
      const res = await getSspProfileMatchedComponents(selectedSSPId);
      setCdefComponents(
        res.data.map((comp) => ({
          ...comp,
          cdefTitle:
            (comp as any).cdefTitle ||
            (comp as any).definitionTitle ||
            comp.title,
        })),
      );
    } catch {
      setCdefComponents([]);
    } finally {
      setCdefLoading(false);
    }
  };

  const handleImportComponents = async (sourceComponentIds: string[]) => {
    if (!selectedSSPId || sourceComponentIds.length === 0) return;
    setImportingComponents(true);
    try {
      const importRes = await importComponentToSsp(
        selectedSSPId,
        sourceComponentIds,
      );
      await fetchComponents(selectedSSPId);
      setImportedComponentIds((prev) => {
        const next = new Set(prev);
        sourceComponentIds.forEach((id) => next.add(id));
        return next;
      });

      const importedComponents: ImportComponentsResponse =
        importRes.data?.components || importRes.data || [];
      const compTitle =
        importedComponents.length === 1
          ? importedComponents[0]?.title || "Imported Component"
          : `${importedComponents.length} Imported Components`;

      // Reset stale state and show popup
      setPostImportSelectedIdx(null);
      setPostImportSearch("");
      setSavedIrIds(new Set());
      setExcludedIrIds(new Set());
      setPostImportComponentTitle(compTitle);
      setShowPostImportPopup(true);
      setPostImportLoading(true);

      // Build the post-import control list from the import response.
      // Some API responses return imported controls in
      // sourceControlImplementations while controlImplementations is empty.
      const allIRs: {
        ciId: string;
        componentId: string;
        componentTitle: string;
        ir: ImplementedRequirementDetailResponse;
      }[] = [];

      for (const importedComp of importedComponents) {
        const componentTitle = importedComp?.title || "Imported Component";
        const importedControlGroups =
          importedComp.controlImplementations &&
            importedComp.controlImplementations.length > 0
            ? importedComp.controlImplementations
            : (importedComp.sourceControlImplementations || []).map((ci) => ({
              id: ci.id,
              implementedRequirements: (ci.implementedRequirements || []).map(
                (ir) => ({
                  id: ir.id,
                  controlId: ir.controlId,
                  implementationStatus: "planned",
                  origination: "system-specific",
                  description: "",
                  statements: [],
                  setParameters: [],
                  sortOrder: 0,
                  createdAt: "",
                  updatedAt: "",
                }),
              ),
            }));

        for (const ci of importedControlGroups) {
          for (const ir of ci.implementedRequirements || []) {
            allIRs.push({
              ciId: ci.id,
              componentId: importedComp.id,
              componentTitle,
              ir: {
                id: ir.id,
                controlId: ir.controlId,
                implementationStatus:
                  (ir as any).implementationStatus || "planned",
                origination: (ir as any).origination || "system-specific",
                description: ir.description || "",
                statements: (ir as any).statements || [],
                setParameters: (ir as any).setParameters || [],
                sortOrder: (ir as any).sortOrder || 0,
                createdAt: (ir as any).createdAt || "",
                updatedAt: (ir as any).updatedAt || "",
              } as ImplementedRequirementDetailResponse,
            });
          }
        }
      }

      // Build form state
      const statuses: Record<string, ImplementationStatus> = {};
      const descriptions: Record<string, string> = {};
      const stmtEdits: Record<
        string,
        { statementId: string; description: string; sortOrder: number }[]
      > = {};
      const paramEdits: Record<string, { paramId: string; values: string }[]> =
        {};

      allIRs.forEach(({ ir }) => {
        statuses[ir.id] =
          (ir.implementationStatus as ImplementationStatus) || "planned";
        descriptions[ir.id] = ir.description || "";
        stmtEdits[ir.id] = (ir.statements || []).map((s) => ({
          statementId: s.statementId,
          description: s.description || "",
          sortOrder: s.sortOrder,
        }));
        if (ir.setParameters && ir.setParameters.length > 0) {
          paramEdits[ir.id] = ir.setParameters.map((p) => ({
            paramId: p["param-id"],
            values: p.values.join(", "),
          }));
        }
      });

      setPostImportIRs(allIRs);
      setExcludedIrIds(new Set());
      setPostImportStatuses(statuses);
      setPostImportDescriptions(descriptions);
      setPostImportStmtEdits(stmtEdits);
      setPostImportControlDetails({});
      setPostImportParamEdits(paramEdits);
      setPostImportLoading(false);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to import components",
      );
      setPostImportLoading(false);
      setShowPostImportPopup(false);
    } finally {
      setImportingComponents(false);
    }
  };

  // Save a single IR's status, statements, and params
  const handleSaveSingleIR = async (
    ciId: string,
    ir: ImplementedRequirementDetailResponse,
  ) => {
    if (!selectedSSPId) return;
    setSavingIrId(ir.id);
    try {
      const newStatus = postImportStatuses[ir.id];
      const newStmts = postImportStmtEdits[ir.id];
      const newParams = postImportParamEdits[ir.id];

      const setParamsPayload = newParams
        ?.filter((p) => p.values.trim())
        .map((p) => ({
          "param-id": p.paramId,
          values: p.values
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean),
        }));

      await updateImplementedRequirement(selectedSSPId, ciId, {
        controlId: ir.controlId,
        implementationStatus:
          newStatus ||
          (ir.implementationStatus as ImplementationStatus) ||
          "planned",
        origination:
          (ir.origination as ControlOrigination) || "system-specific",
        description: postImportDescriptions[ir.id]?.trim() || undefined,
        ...(newStmts && newStmts.length > 0 ? { statements: newStmts } : {}),
        ...(setParamsPayload && setParamsPayload.length > 0
          ? { setParameters: setParamsPayload }
          : {}),
      });

      setSavedIrIds((prev) => new Set(prev).add(ir.id));

      // Refresh control implementations in background
      const ciRes = await listSspControlImplementations(selectedSSPId);
      setControlImpls(ciRes.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update control");
    } finally {
      setSavingIrId(null);
    }
  };

  // Bulk save all IRs from post-import
  const [bulkSaving, setBulkSaving] = useState(false);
  const handleBulkSaveAll = async () => {
    const includedIRs = postImportIRs.filter(
      ({ ir }) => !excludedIrIds.has(ir.id),
    );
    if (!selectedSSPId || includedIRs.length === 0) return;
    setBulkSaving(true);
    try {
      // Group IRs by ciId
      const grouped: Record<
        string,
        { ciId: string; irs: typeof postImportIRs }
      > = {};
      for (const item of includedIRs) {
        if (!grouped[item.ciId])
          grouped[item.ciId] = { ciId: item.ciId, irs: [] };
        grouped[item.ciId].irs.push(item);
      }

      for (const [ciId, group] of Object.entries(grouped)) {
        const payload: BulkImplementedRequirementPayload[] = group.irs.map(
          ({ ir }, idx) => {
            const newStatus = postImportStatuses[ir.id];
            const newStmts = postImportStmtEdits[ir.id];
            const newParams = postImportParamEdits[ir.id];
            return {
              controlId: ir.controlId,
              implementationStatus:
                newStatus ||
                (ir.implementationStatus as ImplementationStatus) ||
                "planned",
              origination:
                (ir.origination as ControlOrigination) || "system-specific",
              description: postImportDescriptions[ir.id]?.trim() || undefined,
              setParameters:
                newParams
                  ?.filter((p) => p.values.trim())
                  .map((p) => ({
                    "param-id": p.paramId,
                    values: p.values
                      .split(",")
                      .map((v) => v.trim())
                      .filter(Boolean),
                  })) || [],
              statements: newStmts?.length
                ? newStmts.map((s) => ({
                  statementId: s.statementId,
                  description: s.description || undefined,
                  sortOrder: s.sortOrder,
                }))
                : [],
              sortOrder: idx,
            };
          },
        );
        await bulkUpdateImplementedRequirements(selectedSSPId, ciId, payload);
      }

      setSavedIrIds(new Set(includedIRs.map(({ ir }) => ir.id)));
      useToastStore
        .getState()
        .showSuccess(
          "Success",
          `${includedIRs.length} controls saved successfully`,
        );
      // Refresh control implementations
      await fetchControlImplementations(selectedSSPId);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to bulk save controls",
      );
    } finally {
      setBulkSaving(false);
    }
  };

  // Close both import and post-import popups
  const closePostImportPopups = () => {
    setShowPostImportPopup(false);
    setShowImportComponent(false);
    setSelectedImportComponentIds(new Set());
    setPostImportIRs([]);
    setPostImportStatuses({});
    setPostImportDescriptions({});
    setPostImportStmtEdits({});
    setPostImportControlDetails({});
    setPostImportParamEdits({});
    setSavedIrIds(new Set());
    setExcludedIrIds(new Set());
    setPostImportLoadingControlId(null);
    setPostImportSelectedIdx(null);
    setPostImportSearch("");
  };

  const handleSelectPostImportControl = async (index: number) => {
    setPostImportSelectedIdx(index);
    const selected = postImportIRs[index];
    if (!selected || !ciCatalogId) return;

    const { ir } = selected;
    if (postImportControlDetails[ir.controlId]) return;

    setPostImportLoadingControlId(ir.controlId);
    try {
      const res = await getControl(ciCatalogId, ir.controlId);
      setPostImportControlDetails((prev) => ({
        ...prev,
        [ir.controlId]: res.data,
      }));
      setPostImportParamEdits((prev) => {
        if ((prev[ir.id] || []).length > 0) return prev;
        if (!res.data.params?.length) return prev;
        return {
          ...prev,
          [ir.id]: res.data.params.map((param) => ({
            paramId: param.paramId,
            values: "",
          })),
        };
      });
      setPostImportStmtEdits((prev) => {
        if ((prev[ir.id] || []).length > 0) return prev;
        let sortOrder = 0;
        const statements = res.data.parts
          .filter((part) => part.name === "statement")
          .flatMap((part) => [
            {
              statementId: part.partId,
              description: part.prose || "",
              sortOrder: sortOrder++,
            },
            ...part.parts.map((sub) => ({
              statementId: sub.partId,
              description: sub.prose || "",
              sortOrder: sortOrder++,
            })),
          ]);
        return statements.length > 0 ? { ...prev, [ir.id]: statements } : prev;
      });
    } catch {
      // Keep the control selectable even if detail fetch fails.
    } finally {
      setPostImportLoadingControlId((current) =>
        current === ir.controlId ? null : current,
      );
    }
  };

  // ── Components: filter + group by Group Name ─────────────
  const componentGroups = useMemo(() => {
    let filtered = sspComponents;
    if (componentSearchFilter) {
      const q = componentSearchFilter.toLowerCase();
      filtered = filtered.filter(
        (c) =>
          c.title.toLowerCase().includes(q) ||
          (c.description ?? "").toLowerCase().includes(q),
      );
    }
    if (componentTypeFilter) {
      filtered = filtered.filter(
        (c) => c.componentType === componentTypeFilter,
      );
    }
    if (componentGroupFilter) {
      filtered = filtered.filter((c) => {
        const gn = c.props?.find((p) => p.name === "Group Name")?.value ?? "";
        return gn === componentGroupFilter;
      });
    }
    // Group by "Group Name" prop
    const groups: Record<string, typeof filtered> = {};
    for (const comp of filtered) {
      const gn =
        comp.props?.find((p) => p.name === "Group Name")?.value || "Ungrouped";
      if (!groups[gn]) groups[gn] = [];
      groups[gn].push(comp);
    }
    return groups;
  }, [
    sspComponents,
    componentSearchFilter,
    componentTypeFilter,
    componentGroupFilter,
  ]);

  const availableGroupNames = useMemo(() => {
    const names = new Set<string>();
    for (const c of sspComponents) {
      const gn = c.props?.find((p) => p.name === "Group Name")?.value;
      if (gn) names.add(gn);
    }
    return Array.from(names).sort();
  }, [sspComponents]);

  const availableComponentTypes = useMemo(() => {
    const types = new Set<string>();
    for (const c of sspComponents) {
      if (c.componentType) types.add(c.componentType);
    }
    return Array.from(types).sort();
  }, [sspComponents]);

  // ── Lifecycle derived state ───────────────────────────────
  const lifecycle = getSelectedSspLifecycle();
  const isPublishedSsp = lifecycle === "published";

  useEffect(() => {
    if (!isPublishedSsp) return;
    setPendingDelete(null);
    setShowImportComponent(false);
    setShowAddInfoType(false);
    setShowAddComponent(false);
    setShowAddAsset(false);
    setShowAddLevAuth(false);
    setShowAddSystemUser(false);
    setShowAddInterconnection(false);
    setShowAddRole(false);
    setShowAddParty(false);
    setShowAssignRP(false);
    setShowAddLocation(false);
    setShowAddControlImpl(false);
    setShowEditControlImpl(false);
    setEditingSysChars(false);
    setEditingLevAuthId(null);
    setEditingSystemUserId(null);
    setEditingInterconnectionId(null);
    setEditingRoleId(null);
    setEditingPartyId(null);
    setEditingLocationId(null);
    setEditingControlImplId(null);
    setEditingSspTitle(false);
  }, [isPublishedSsp]);

  // ── SSP title (document) rename — allowed only before publish ──
  const currentSspDocumentId =
    sspList.find((s) => s.id === selectedSSPId)?.documentId ?? "";
  const currentSspTitle =
    (currentSspDocumentId ? sspDocTitles[currentSspDocumentId] : "") ||
    sspDetail?.title ||
    "";
  const handleRenameSsp = async () => {
    const newTitle = sspTitleDraft.trim();
    if (!currentSspDocumentId || !newTitle || newTitle === currentSspTitle) {
      setEditingSspTitle(false);
      return;
    }
    setSavingSspTitle(true);
    try {
      await renameDocument(currentSspDocumentId, newTitle);
      setSspDocTitles((prev) => ({ ...prev, [currentSspDocumentId]: newTitle }));
      setSspDetail((prev) => (prev ? { ...prev, title: newTitle } : prev));
      useToastStore.getState().showSuccess("Success", "SSP title updated");
      setEditingSspTitle(false);
    } catch (err: any) {
      const detail = err?.response?.data?.errors?.[0]?.detail;
      setError(
        detail ?? (err instanceof Error ? err.message : "Failed to rename SSP"),
      );
    } finally {
      setSavingSspTitle(false);
    }
  };

  const currentStageIdx = LIFECYCLE_STAGES.findIndex(
    (s) => s.key === lifecycle,
  );

  // Lifecycle action buttons per status (matching Profiles pattern)
  const lifecycleActions: Record<
    string,
    { label: string; target: string; color: string; icon: React.ReactNode }[]
  > = {
    draft: [
      {
        label: "Submit for Review",
        target: "in_review",
        color: "bg-blue-600 hover:bg-blue-700 shadow-blue-500/20",
        icon: <Send className="size-3.5" />,
      },
    ],
    in_review: [
      {
        label: "Approve",
        target: "approved",
        color: "bg-emerald-600 hover:bg-emerald-700 shadow-emerald-500/20",
        icon: <ShieldCheck className="size-3.5" />,
      },
      {
        label: "Reject",
        target: "draft",
        color: "bg-red-600 hover:bg-red-700 shadow-red-500/20",
        icon: <XCircle className="size-3.5" />,
      },
    ],
    approved: [
      {
        label: "Publish",
        target: "published",
        color: "bg-purple-600 hover:bg-purple-700 shadow-purple-500/20",
        icon: <Globe className="size-3.5" />,
      },
    ],
    published: [
      {
        label: "Supersede",
        target: "superseded",
        color: "bg-orange-600 hover:bg-orange-700 shadow-orange-500/20",
        icon: <Archive className="size-3.5" />,
      },
      {
        label: "Withdraw",
        target: "withdrawn",
        color: "bg-rose-700 hover:bg-rose-800 shadow-rose-500/20",
        icon: <Ban className="size-3.5" />,
      },
    ],
    rejected: [
      {
        label: "Submit for Review",
        target: "in_review",
        color: "bg-blue-600 hover:bg-blue-700 shadow-blue-500/20",
        icon: <Send className="size-3.5" />,
      },
    ],
  };

  // Get lifecycle actions for current state (permission-based filtering is done at render time)
  const hasUpdatePermission = useRBACPermission("ssp", "update");
  const hasApprovePermission = useRBACPermission("ssp", "approve");
  const hasPublishPermission = useRBACPermission("ssp", "publish");
  const currentLifecycleActions = (lifecycleActions[lifecycle] ?? []).filter((a) => {
    // Show "Submit for Review" only if user has update permission
    if (a.label === "Submit for Review") return hasUpdatePermission;
    // Show "Approve" and "Reject" only if user has approve permission
    if (["Approve", "Reject"].includes(a.label)) return hasApprovePermission;
    // Show "Publish" only if user has publish permission
    if (a.label === "Publish") return hasPublishPermission;
    // Show "Supersede" and "Withdraw" only if user has update permission
    if (["Supersede", "Withdraw"].includes(a.label)) return hasUpdatePermission;
    return true;
  });

  const openTransitionModal = (target: string) => {
    setTransitionTarget(target);
    setTransitionComment("");
    setShowTransitionModal(true);
  };

  // ── Loading spinner helper ──────────────────────────────
  const LoadingSpinner = ({ text = "Loading" }: { text?: string }) => (
    <div className="flex items-center justify-center py-12">
      <Loader2 className="size-5 text-indigo-600 animate-spin" />
      <span className="ml-3 text-sm text-muted-foreground">{text}</span>
    </div>
  );

  // ══════════════════════════════════════════════════════════
  // LOADING SKELETON (when navigating via URL)
  // ══════════════════════════════════════════════════════════
  if (sspIdFromUrl && !selectedSSPId && detailLoading) {
    const LoadingSpinner = ({ text = "Loading" }: { text?: string }) => (
      <div className="flex flex-col items-center gap-3 p-8">
        <Loader2 className="size-8 text-muted-foreground animate-spin" />
        <p className="text-sm text-muted-foreground">{text}</p>
      </div>
    );
    return <LoadingSpinner text="Loading SSP details" />;
  }

  // ══════════════════════════════════════════════════════════
  // LIST VIEW
  // ══════════════════════════════════════════════════════════
  if (!selectedSSPId && !sspIdFromUrl) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div>
            <h1>System Security Plans</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Document and manage the implementation of security controls across
              the system.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <RBACAction module="ssp" action="create">
              <button
                onClick={() => {
                  resetSspImport();
                  setShowSspImport(true);
                  fetchAccessibleOrgs("ssp", "create");
                }}
                className="flex items-center gap-2 px-3.5 py-2 rounded-xl border border-indigo-500 dark:border-indigo-400 text-sm font-bold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-all hover:-translate-y-px"
              >
                <Import className="size-4" /> Import SSP
              </button>
              <button
                onClick={() => {
                  fetchProfiles();
                  fetchAccessibleOrgs("ssp", "create");
                  // Full reset on every "New SSP" click so the modal opens
                  // fresh — title, profile, org, type and downstream picks
                  // never leak from a previous attempt.
                  setSspForm({
                    title: "",
                    importProfileHref: "",
                    owningOrgUnitId: "",
                  });
                  setSspInheritanceType("");
                  setSspSelectedAppId("");
                  setSspSelectedServiceId("");
                  setInheritedIds([]);
                  // Force a fresh inheritance fetch so any SSP created in
                  // a prior modal session shows up with isUsed=true.
                  setSspInheritanceRefreshTick((t) => t + 1);
                  setShowNewSSP(true);
                }}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-all shadow-[0_4px_14px_rgba(79,70,229,0.3)] dark:shadow-[0_4px_14px_rgba(139,92,246,0.3)] hover:-translate-y-px"
              >
                <Plus className="size-4" />
                New SSP
              </button>
            </RBACAction>
          </div>
        </div>

        {/* Error banner */}
        {listError && (
          <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-xl text-sm text-red-700 dark:text-red-400">
            <AlertCircle className="size-4 flex-shrink-0" />
            <span>{listError}</span>
            <button
              onClick={fetchSSPList}
              className="ml-auto text-xs font-bold underline"
            >
              Retry
            </button>
          </div>
        )}

        {/* Search + filters + Sort + View toggle */}
        {!listLoading && !creating && sspList.length > 0 && (
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[260px] max-w-md">
              <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground/50 pointer-events-none" />
              <input
                type="text"
                placeholder="Search SSPs by name, source, profile or document ID"
                value={sspSearchQuery}
                onChange={(e) => handleSspSearchChange(e.target.value)}
                className="w-full pl-10 pr-10 py-2.5 text-sm bg-card rounded-xl border border-border outline-none focus:border-indigo-500 dark:focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 dark:focus:ring-indigo-500/10 transition-all placeholder:text-muted-foreground/50"
              />
              {sspSearchQuery && (
                <button
                  onClick={() => handleSspSearchChange("")}
                  className="absolute right-3.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="size-4" />
                </button>
              )}
            </div>
            <Dropdown
              size="sm"
              variant="pill"
              value={sspStatusFilter}
              onChange={(v) => {
                setSspStatusFilter(v);
                setSspCurrentPage(0);
              }}
              options={[
                { value: "", label: "All Status" },
                { value: "draft", label: "Draft", dot: "bg-slate-400" },
                { value: "in_review", label: "In Review", dot: "bg-blue-500" },
                { value: "approved", label: "Approved", dot: "bg-emerald-500" },
                { value: "published", label: "Published", dot: "bg-emerald-500" },
                { value: "rejected", label: "Rejected", dot: "bg-red-500" },
                { value: "superseded", label: "Superseded", dot: "bg-orange-500" },
                { value: "withdrawn", label: "Withdrawn", dot: "bg-rose-700" },
              ]}
            />
            <div className="flex items-center gap-2 ml-auto flex-shrink-0">
              <Dropdown
                value={`${sspSortKey ?? "updated"}_${sspSortDir}`}
                onChange={(v) => {
                  const [k, d] = v.split("_");
                  setSspSortKey(k);
                  setSspSortDir(d as "asc" | "desc");
                  setSspCurrentPage(0);
                }}
                options={[
                  { value: "updated_desc", label: "Sort by: Updated On" },
                  { value: "created_desc", label: "Sort by: Created On" },
                  { value: "title_asc", label: "Sort by: Name (A-Z)" },
                  { value: "title_desc", label: "Sort by: Name (Z-A)" },
                  { value: "status_asc", label: "Sort by: Status" },
                ]}
                size="sm"
                align="end"
                className="!min-w-[200px]"
              />
              <label className="text-sm text-muted-foreground whitespace-nowrap">
                SSPs per page
              </label>
              <Dropdown
                value={String(sspPageSize)}
                onChange={(v) => {
                  setSspPageSize(Number(v));
                  setSspCurrentPage(0);
                }}
                options={[12, 24, 48, 96].map((n) => ({
                  value: String(n),
                  label: String(n),
                }))}
                size="sm"
                align="end"
                className="!min-w-[80px]"
              />
            </div>
          </div>
        )}

        {/* SSP — Card grid view (Catalog-style via EntityCardGrid) */}
        {sspViewMode === "card" && (
          <EntityCardGrid
            items={paginatedSspList}
            loading={listLoading || creating}
            getKey={(ssp) => (ssp as { id: string }).id}
            getStatus={(ssp) =>
              (sspLifecycles[(ssp as { documentId: string }).documentId] ||
                "draft") as LifecycleStatus
            }
            onOpen={(ssp) => {
              const id = (ssp as { id: string }).id;
              navigate(`/ssp/${id}`);
              setSelectedSSPId(id);
              setActiveTab("System Characteristics");
            }}
            page={sspCurrentPage}
            pageSize={sspPageSize}
            totalItems={filteredSspList.length}
            onPageChange={setSspCurrentPage}
            itemLabel="SSPs"
            emptyIcon={<Shield className="size-8 text-[#cbd5e1] dark:text-white/30" />}
            emptyText="No System Security Plans found"
            renderTitle={(ssp) => {
              const r = ssp as { title?: string; documentId: string };
              const title =
                r.title || sspDocTitles[r.documentId] || r.documentId;
              return (
                <div className="flex items-center gap-2 min-w-0">
                  <div className="size-7 rounded-lg bg-gradient-to-br from-violet-100 to-violet-50 dark:from-violet-500/25 dark:to-violet-500/10 ring-1 ring-violet-200/60 dark:ring-violet-500/20 flex items-center justify-center shrink-0">
                    <Shield className="size-3.5 text-violet-600 dark:text-violet-400" />
                  </div>
                  <TitleTooltip text={title}>
                    <h3 className="text-[13px] font-bold leading-snug tracking-[-0.01em] text-foreground truncate group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                      {title}
                    </h3>
                  </TitleTooltip>
                </div>
              );
            }}
            renderBadges={(ssp) => {
              const sourceType = (ssp as { sourceType?: string }).sourceType;
              if (!sourceType) return null;
              return (
                <span className="inline-flex items-center gap-1 whitespace-nowrap px-1.5 py-0.5 rounded-md text-[10px] font-bold tracking-wider border capitalize bg-indigo-50 text-indigo-700 border-indigo-200/70 dark:bg-indigo-500/10 dark:text-indigo-300 dark:border-indigo-500/20">
                  <Building2 className="size-3" />
                  {sourceType.toLowerCase()}
                </span>
              );
            }}
            renderStats={(ssp) => {
              const r = ssp as {
                sourceType?: string;
                sourceName?: string;
                profileName?: string;
              };
              const sourceDisplay = r.sourceName || "—";
              const profileDisplay = r.profileName || "—";
              return (
                <StatTiles>
                  <StatTile
                    icon={<Building2 className="size-3.5" />}
                    accent="indigo"
                    value={sourceDisplay}
                    label="Source"
                    tooltip={sourceDisplay}
                  />
                  <div className="flex flex-1 items-center justify-center px-2.5 py-2 min-w-0">
                    {r.profileName ? (
                      <Tip
                        label={profileDisplay}
                        className="flex min-w-0 max-w-full"
                        contentClassName="max-w-[200px] whitespace-normal break-words text-left leading-snug px-2 py-1 text-[10px]"
                      >
                        <span className="flex items-center px-2 py-1 rounded-md bg-violet-50 dark:bg-violet-500/10 ring-1 ring-violet-200 dark:ring-violet-500/20 text-violet-700 dark:text-violet-300 text-[12px] font-semibold min-w-0 max-w-full">
                          <span className="truncate">{r.profileName}</span>
                        </span>
                      </Tip>
                    ) : (
                      <span className="text-[12px] text-muted-foreground">—</span>
                    )}
                  </div>
                </StatTiles>
              );
            }}
            renderDates={(ssp) => {
              const r = ssp as { createdAt?: string; updatedAt?: string };
              const created = r.createdAt
                ? new Date(r.createdAt).toLocaleDateString("en-GB")
                : "—";
              const updated = new Date(
                r.updatedAt || r.createdAt || Date.now(),
              ).toLocaleDateString("en-GB");
              return <MetaDates created={created} updated={updated} />;
            }}
            renderFooterActions={(ssp) => {
              const rowLifecycle = getSspSummaryLifecycle(
                ssp as SspSummaryResponse,
              );
              return (
                <CardActions
                  deleteModule="ssp"
                  exportTooltip="Export SSP"
                  deleteTooltip="Delete SSP"
                  deleteDisabledTooltip="Only draft SSPs can be deleted"
                  canDelete={rowLifecycle !== "published"}
                  deleting={deleting}
                  onExport={() => {
                    setExportTargetDocId(
                      (ssp as { documentId: string }).documentId,
                    );
                    setShowExport(true);
                  }}
                  onDelete={() => setDeleteTarget(ssp as SspSummaryResponse)}
                />
              );
            }}
          />
        )}


        {/* SSP — Table (list) view */}
        {sspViewMode === "list" && (
          <DataTable
            columns={[
              {
                key: "sno",
                header: "#",
                align: "center",
                render: (_row, idx) => (
                  <span className="text-muted-foreground font-medium">
                    {sspCurrentPage * sspPageSize + idx + 1}
                  </span>
                ),
              },
              {
                key: "title",
                header: "SSP Name",
                sortable: true,
                align: "center",
                cellClassName: "text-left [&>div]:justify-start",
                ellipsis: 2,
                tooltip: (row) =>
                  (row as any).title ||
                  sspDocTitles[(row as any).documentId] ||
                  (row as any).documentId ||
                  "",
                render: (row) => (
                  <span className="font-medium">
                    {(row as any).title ||
                      sspDocTitles[(row as any).documentId] ||
                      (row as any).documentId}
                  </span>
                ),
              },
              {
                key: "sourceType",
                header: "Source Type",
                sortable: true,
                align: "center",
                render: (row) => {
                  const type = (row as any).sourceType as string | undefined;
                  if (!type)
                    return <span className="text-muted-foreground">-</span>;
                  return (
                    <span className="inline-flex items-center justify-center w-[110px] text-[10px] font-bold tracking-wide capitalize px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 ring-1 ring-indigo-200 dark:ring-indigo-500/20">
                      {type.toLowerCase()}
                    </span>
                  );
                },
              },
              {
                key: "sourceName",
                header: "Source Name",
                sortable: true,
                align: "center",
                cellClassName: "text-left [&>div]:justify-start",
                render: (row) => {
                  const name = (row as any).sourceName as string | undefined;
                  return name ? (
                    <span className="text-xs font-medium">{name}</span>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  );
                },
              },
              {
                key: "profile",
                header: "Profile",
                sortable: true,
                align: "center",
                cellClassName: "text-left [&>div]:justify-start",
                render: (row) =>
                  (row as any).profileName ? (
                    <span className="text-foreground font-medium text-xs">
                      {(row as any).profileName}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  ),
              },
              {
                key: "status",
                header: "Status",
                sortable: true,
                align: "center",
                render: (row) => {
                  const lc = (sspLifecycles[(row as any).documentId] ||
                    "draft") as LifecycleStatus;
                  return <StatusBadge status={lc as Parameters<typeof StatusBadge>[0]["status"]} />;
                },
              },
              {
                key: "created",
                header: "Created On",
                sortable: true,
                align: "center",
                render: (row) => (
                  <span className="text-muted-foreground">
                    {new Date((row as any).createdAt).toLocaleDateString("en-GB")}
                  </span>
                ),
              },
              {
                key: "updated",
                header: "Updated On",
                sortable: true,
                align: "center",
                render: (row) => (
                  <span className="text-muted-foreground">
                    {new Date(
                      (row as any).updatedAt || (row as any).createdAt,
                    ).toLocaleDateString("en-GB")}
                  </span>
                ),
              },
              {
                key: "action",
                header: "Action",
                align: "center",
                render: (row) => {
                  const rowLifecycle = getSspSummaryLifecycle(
                    row as SspSummaryResponse,
                  );
                  return (
                    <div className="flex items-center justify-center gap-1">
                      <button
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          setExportTargetDocId((row as any).documentId);
                          setShowExport(true);
                        }}
                        className="p-1 rounded hover:bg-accent text-muted-foreground hover:text-indigo-600 transition-colors"
                        title="Export"
                      >
                        <SquareArrowOutUpRight className="size-4" />
                      </button>
                      <RBACAction module="ssp" action="delete">
                        <button
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            setDeleteTarget(row as any);
                          }}
                          disabled={deleting || rowLifecycle === "published"}
                          className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-500/10 text-muted-foreground hover:text-red-600 transition-colors disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
                          title={
                            rowLifecycle === "published"
                              ? "Only draft SSPs can be deleted"
                              : "Delete"
                          }
                        >
                          <Trash2 className="size-4" />
                        </button>
                      </RBACAction>
                    </div>
                  );
                },
              },
            ]}
            data={paginatedSspList}
            rowKey={(r) => (r as any).id}
            onRowClick={(row) => {
              navigate(`/ssp/${(row as any).id}`);
              setSelectedSSPId((row as any).id);
              setActiveTab("System Characteristics");
            }}
            loading={listLoading || creating}
            emptyText="No System Security Plans found"
            page={sspCurrentPage}
            pageSize={sspPageSize}
            totalItems={filteredSspList.length}
            onPageChange={setSspCurrentPage}
            itemLabel="SSPs"
            sortKey={sspSortKey}
            sortDir={sspSortDir}
            onSort={handleSspSort}
          />
        )}

        {/* New SSP Modal */}
        <Modal
          open={showNewSSP}
          onClose={() => {
            // Reset every field on close so the next "New SSP" click
            // never sees stale state — title, profile, org, type, picks.
            setShowNewSSP(false);
            setSspForm({
              title: "",
              importProfileHref: "",
              owningOrgUnitId: "",
            });
            setSspInheritanceType("");
            setSspSelectedAppId("");
            setSspSelectedServiceId("");
            setInheritedIds([]);
          }}
          title="New System Security Plan"
          description="Create a new OSCAL-native SSP."
          size="md"
          footer={
            <ModalFooterButtons
              onCancel={() => {
                setShowNewSSP(false);
                setSspForm({
                  title: "",
                  importProfileHref: "",
                  owningOrgUnitId: "",
                });
                setSspInheritanceType("");
                setSspSelectedAppId("");
                setSspSelectedServiceId("");
                setInheritedIds([]);
              }}
              onSubmit={async () => {
                // Service mode → no second popup. The BE wants every
                // component of the chosen service as component_ids, so we
                // resolve them here and fire the create call directly.
                if (sspInheritanceType === "service" && sspSelectedServiceId) {
                  const svc = sspInheritance.applications
                    .flatMap((a) => a.services ?? [])
                    .find((s) => s.serviceId === sspSelectedServiceId);
                  const componentIds =
                    svc?.components?.map((c) => c.componentId) ?? [];
                  setShowNewSSP(false);
                  await handleCreateSSP({
                    serviceId: sspSelectedServiceId,
                    componentIds,
                  });
                  return;
                }
                // Application mode → only show the picker (modal #2) if the
                // selected app has SERVICES to inherit from. If there are no
                // services, skip the picker entirely and create directly from
                // this popup. Auto-include any direct components on the app
                // (BE requires component_ids to be non-empty for
                // /ssps/from-application).
                if (sspInheritanceType === "application" && sspSelectedAppId) {
                  const app = sspInheritance.applications.find(
                    (a) => a.applicationId === sspSelectedAppId,
                  );
                  const hasServices = (app?.services?.length ?? 0) > 0;
                  if (!hasServices) {
                    const directComponentIds =
                      app?.components?.map((c) => c.componentId) ?? [];
                    setShowNewSSP(false);
                    await handleCreateSSP({
                      applicationId: sspSelectedAppId,
                      componentIds: directComponentIds,
                    });
                    return;
                  }
                }
                // Otherwise → keep the picker step so the user can choose
                // which services/components to inherit.
                setShowNewSSP(false);
                setShowInheritancePicker(true);
              }}
              submitLabel={(() => {
                if (creating) return "Creating SSP";
                if (sspInheritanceType === "service") return "Create SSP";
                if (sspInheritanceType === "application" && sspSelectedAppId) {
                  const app = sspInheritance.applications.find(
                    (a) => a.applicationId === sspSelectedAppId,
                  );
                  const hasServices = (app?.services?.length ?? 0) > 0;
                  if (!hasServices) return "Create SSP";
                }
                return "Next";
              })()}
              submitDisabled={
                !sspForm.title ||
                !sspForm.importProfileHref ||
                sspTypeSelectionIncomplete ||
                (accessibleOrgs.length > 1 && !sspForm.owningOrgUnitId) ||
                creating
              }
            />
          }
        >
          <FormField label="SSP Name" required>
            <FormInput
              placeholder="e.g. Production Cloud Platform SSP"
              value={sspForm.title}
              onChange={(v) => setSspForm((f) => ({ ...f, title: v }))}
            />
          </FormField>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              label="Profile"
              required
              hint="Select the profile baseline for this SSP"
            >
              {loadingProfiles ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                  <Loader2 className="size-4 animate-spin" />
                  Loading profiles
                </div>
              ) : (
                <FormSelect
                  options={profiles
                    .filter((p) => p.lifecycleStatus === "published")
                    .map((p) => ({
                      value: `#${p.documentId}`,
                      label: p.title,
                    }))}
                  placeholder="Select a profile"
                  value={sspForm.importProfileHref}
                  onChange={(v) =>
                    setSspForm((f) => ({ ...f, importProfileHref: v }))
                  }
                />
              )}
            </FormField>
            <FormField label="Owning Unit" required>
              {accessibleOrgsLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                  <Loader2 className="size-4 animate-spin" />
                  Loading organizations
                </div>
              ) : accessibleOrgs.length === 0 ? (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200/50 dark:border-red-500/20">
                  <AlertTriangle className="size-4 text-red-500 flex-shrink-0" />
                  <span className="text-sm text-red-600 dark:text-red-400">
                    No accessible organizations.
                  </span>
                </div>
              ) : accessibleOrgs.length === 1 ? (
                <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#f5f7fb] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.07] text-sm">
                  <span className="font-medium">{accessibleOrgs[0].name}</span>
                  <span className="text-muted-foreground">
                    ({accessibleOrgs[0].shortName})
                  </span>
                </div>
              ) : (
                <FormSelect
                  options={accessibleOrgs.map((o) => ({
                    value: o.id,
                    label: `${o.name} (${o.shortName})`,
                  }))}
                  value={sspForm.owningOrgUnitId}
                  onChange={(v) =>
                    setSspForm((f) => ({ ...f, owningOrgUnitId: v }))
                  }
                  placeholder="Select organization"
                />
              )}
            </FormField>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              label="Type"
              required
              hint="Select SSP implementation type"
            >
              <FormSelect
                options={SSP_INHERITANCE_TYPE_OPTIONS}
                placeholder="Select type"
                value={sspInheritanceType}
                onChange={(v) => {
                  setSspInheritanceType(v as SspInheritanceType);
                  setSspSelectedAppId("");
                  setSspSelectedServiceId("");
                }}
              />
            </FormField>
            {sspInheritanceType === "application" && (
              <FormField
                label="Application"
                required
                hint="Choose linked application"
              >
                {sspInheritanceLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                    <Loader2 className="size-4 animate-spin" />
                    Loading applications
                  </div>
                ) : (
                  <FormSelect
                    options={sspApplicationOptions}
                    placeholder="Select an application"
                    value={sspSelectedAppId}
                    onChange={setSspSelectedAppId}
                  />
                )}
              </FormField>
            )}
            {sspInheritanceType === "service" && (
              <FormField label="Service" required hint="Choose linked service">
                {sspInheritanceLoading ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                    <Loader2 className="size-4 animate-spin" />
                    Loading services
                  </div>
                ) : (
                  <FormSelect
                    options={sspServiceOptions}
                    placeholder="Select a service"
                    value={sspSelectedServiceId}
                    onChange={setSspSelectedServiceId}
                  />
                )}
              </FormField>
            )}
          </div>

        </Modal>

        <InheritancePickerModal
          open={
            showInheritancePicker &&
            (sspInheritanceType === "application" ||
              sspInheritanceType === "service")
          }
          mode={sspInheritanceType === "service" ? "service" : "application"}
          selectedAppId={sspSelectedAppId}
          selectedServiceId={sspSelectedServiceId}
          serviceProviderSsps={availableProviderSsps}
          onClose={() => {
            setShowInheritancePicker(false);
            setShowNewSSP(true);
          }}
          onConfirm={async (ids, meta) => {
            // Application-mode → POST /ssps/from-application
            // Service-mode     → POST /ssps/from-service
            // Resolve UUIDs from SSPEditor's own sspInheritance data as a
            // fallback in case the picker's parallel fetch hasn't settled.
            setInheritedIds(ids);
            setShowInheritancePicker(false);
            // sspSelectedAppId is now the applicationId UUID directly
            // (the dropdown was switched off appCode to avoid collisions).
            const resolvedAppId = meta.applicationId || sspSelectedAppId;
            const resolvedServiceId = meta.serviceId || sspSelectedServiceId;
            if (sspInheritanceType === "application" && resolvedAppId) {
              await handleCreateSSP({
                applicationId: resolvedAppId,
                componentIds: ids,
                providerSspIds: meta.providerSspIds ?? [],
              });
            } else if (sspInheritanceType === "service" && resolvedServiceId) {
              await handleCreateSSP({
                serviceId: resolvedServiceId,
                componentIds: ids,
              });
            } else {
              await handleCreateSSP();
            }
          }}
          creating={creating}
        />

        {/* Delete Confirmation */}
        <DeleteConfirmDialog
          open={!!deleteTarget}
          type="SSP"
          name={
            deleteTarget
              ? sspDocTitles[deleteTarget.documentId] || deleteTarget.documentId
              : ""
          }
          deleting={deleting}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => handleDeleteSSP()}
        />

        {/* ── Import SSP Modal (list view) ── */}
        <Modal
          open={showSspImport}
          onClose={() => {
            setShowSspImport(false);
            resetSspImport();
          }}
          title="Import System Security Plan"
          description="Upload an OSCAL SSP document (.json, .xml, .yaml)"
          size="md"
          footer={
            sspImportStep === "select" ? (
              <div className="flex justify-end w-full gap-2">
                <button
                  onClick={() => {
                    setShowSspImport(false);
                    resetSspImport();
                  }}
                  className="px-4 py-2 rounded-lg bg-muted text-sm font-medium hover:bg-accent transition-colors"
                >
                  Cancel
                </button>
                {sspImportFile && (
                  <button
                    onClick={sspRunImport}
                    disabled={!sspValidateResult || sspValidating}
                    className={`px-4 py-2 rounded-lg text-white text-sm font-bold transition-colors shadow-md ${!sspValidateResult || sspValidating
                      ? "bg-indigo-400 dark:bg-indigo-400 opacity-50 cursor-not-allowed"
                      : "bg-indigo-600 dark:bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-700"
                      }`}
                  >
                    Import
                  </button>
                )}
              </div>
            ) : sspImportStep === "url" ? (
              <div className="flex justify-end w-full gap-2">
                <button
                  onClick={() => {
                    setSspImportStep("select");
                    setSspImportUrl("");
                    setSspImportFile(null);
                    setSspValidateResult(null);
                    setSspImportError(null);
                    setSspValidating(false);
                  }}
                  className="px-4 py-2 rounded-lg bg-muted text-sm font-medium hover:bg-accent transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={sspRunImport}
                  disabled={!sspValidateResult || sspValidating}
                  className={`px-4 py-2 rounded-lg text-white text-sm font-bold transition-colors shadow-md ${!sspValidateResult || sspValidating
                    ? "bg-indigo-400 dark:bg-indigo-400 opacity-50 cursor-not-allowed"
                    : "bg-indigo-600 dark:bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-700"
                    }`}
                >
                  Import
                </button>
              </div>
            ) : sspImportStep === "findings" ? (
              <div className="flex items-center justify-end w-full gap-3">
                <button
                  onClick={() => {
                    setShowSspImport(false);
                    resetSspImport();
                  }}
                  className="px-3 py-1.5 rounded-md border border-border text-sm hover:bg-accent transition-colors"
                >
                  Close
                </button>
                <button
                  onClick={resetSspImport}
                  className="flex items-center gap-2 px-4 py-1.5 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  Try Again
                </button>
              </div>
            ) : null
          }
        >
          {/* Step: Select source */}
          {sspImportStep === "select" && (
            <div className="py-6 flex flex-col items-center gap-6">
              <input
                id="ssp-import-file-input-list"
                type="file"
                accept=".json,.yaml,.yml,.xml"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setSspImportFile(file);
                    setSspValidateResult(null);
                    setSspImportError(null);
                    sspStartValidation(file);
                  }
                }}
              />
              <div className="w-full">
                <p className="text-xs font-medium text-muted-foreground mb-2">
                  Organization
                </p>
                {accessibleOrgsLoading ? (
                  <div className="flex items-center gap-2 py-2">
                    <Loader2 className="size-4 animate-spin text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">
                      Loading
                    </span>
                  </div>
                ) : accessibleOrgs.length === 0 ? (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200/50 dark:border-red-500/20">
                    <AlertTriangle className="size-4 text-red-500 flex-shrink-0" />
                    <span className="text-sm text-red-600 dark:text-red-400">
                      No accessible organizations.
                    </span>
                  </div>
                ) : accessibleOrgs.length === 1 ? (
                  <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[#f5f7fb] dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.07] text-sm">
                    <span className="font-medium">
                      {accessibleOrgs[0].name}
                    </span>
                    <span className="text-muted-foreground">
                      ({accessibleOrgs[0].shortName})
                    </span>
                  </div>
                ) : (
                  <FormSelect
                    options={accessibleOrgs.map((o) => ({
                      value: o.id,
                      label: `${o.name} (${o.shortName})`,
                    }))}
                    value={importOrgId}
                    onChange={setImportOrgId}
                    placeholder="Select organization"
                  />
                )}
              </div>
              <p className="text-sm font-semibold text-foreground">
                Select a source to add from
              </p>
              <div className="flex items-center justify-center gap-8">
                <button
                  onClick={() =>
                    document
                      .getElementById("ssp-import-file-input-list")
                      ?.click()
                  }
                  className="flex flex-col items-center gap-3 group"
                >
                  <div className="size-20 rounded-full bg-muted flex items-center justify-center group-hover:bg-accent transition-colors">
                    <Upload className="size-8 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </div>
                  <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                    Upload file
                  </span>
                </button>
                <button
                  onClick={() => setSspImportStep("url")}
                  className="flex flex-col items-center gap-3 group"
                >
                  <div className="size-20 rounded-full bg-muted flex items-center justify-center group-hover:bg-accent transition-colors">
                    <Link2 className="size-8 text-muted-foreground group-hover:text-foreground transition-colors" />
                  </div>
                  <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                    Source link
                  </span>
                </button>
              </div>
              {sspImportFile && (
                <div
                  className={`rounded-lg border w-full ${sspValidateResult
                    ? "border-emerald-200 dark:border-emerald-500/20 bg-emerald-50 dark:bg-emerald-500/10"
                    : sspImportError
                      ? "border-red-200 dark:border-red-500/20 bg-red-50 dark:bg-red-500/10"
                      : "border-border bg-muted"
                    }`}
                >
                  <div className="flex items-center gap-3 px-4 py-3">
                    <FileText
                      className={`size-6 flex-shrink-0 ${sspValidateResult ? "text-emerald-500" : sspImportError ? "text-red-500" : "text-muted-foreground"}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {sspImportFile.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {(sspImportFile.size / 1024).toFixed(1)} KB
                      </p>
                      {sspValidating && (
                        <p className="text-xs text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5 mt-1">
                          <Loader2 className="size-3 animate-spin" />{" "}
                          Validating…
                        </p>
                      )}
                      {!sspValidating && sspValidateResult && (
                        <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5 mt-1">
                          <CheckCircle className="size-3" /> Validated -{" "}
                          {sspValidateResult.documentType}, OSCAL{" "}
                          {sspValidateResult.oscalVersion}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setSspImportFile(null);
                        setSspValidateResult(null);
                        setSspImportError(null);
                        setSspValidating(false);
                      }}
                      className="p-1 rounded hover:bg-accent flex-shrink-0"
                    >
                      <X className="size-4 text-muted-foreground" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step: URL input */}
          {sspImportStep === "url" && (
            <div className="space-y-4">
              <FormField
                label="Document URL"
                required
                hint="URL must end in .json, .yaml, or .xml"
              >
                <div className="flex gap-2">
                  <div className="flex-1">
                    <FormInput
                      placeholder="https://example.com/ssp.json"
                      value={sspImportUrl}
                      onChange={(v) => {
                        setSspImportUrl(v);
                        setSspValidateResult(null);
                        setSspImportError(null);
                      }}
                      disabled={sspValidating}
                    />
                  </div>
                  <button
                    onClick={sspFetchUrl}
                    disabled={!sspImportUrl.trim() || sspValidating}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${!sspImportUrl.trim() || sspValidating
                      ? "bg-muted text-muted-foreground cursor-not-allowed"
                      : "bg-indigo-600 dark:bg-indigo-600 text-white hover:bg-indigo-700 dark:hover:bg-indigo-700"
                      }`}
                  >
                    {sspValidating ? (
                      <span className="flex items-center gap-1.5">
                        <Loader2 className="size-3.5 animate-spin" /> Fetching…
                      </span>
                    ) : (
                      "Fetch"
                    )}
                  </button>
                </div>
              </FormField>
              {(sspValidating || sspValidateResult) && (
                <div
                  className={`rounded-lg border w-full ${sspValidateResult
                    ? "border-emerald-200 dark:border-emerald-500/20 bg-emerald-50 dark:bg-emerald-500/10"
                    : sspImportError
                      ? "border-red-200 dark:border-red-500/20 bg-red-50 dark:bg-red-500/10"
                      : "border-border bg-muted"
                    }`}
                >
                  <div className="flex items-center gap-3 px-4 py-3">
                    <Link2
                      className={`size-6 flex-shrink-0 ${sspValidateResult ? "text-emerald-500" : "text-muted-foreground"}`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">
                        {sspImportUrl.split("/").pop() || sspImportUrl}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {sspImportUrl}
                      </p>
                      {sspValidating && (
                        <p className="text-xs text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5 mt-1">
                          <Loader2 className="size-3 animate-spin" /> Fetching &
                          validating…
                        </p>
                      )}
                      {!sspValidating && sspValidateResult && (
                        <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5 mt-1">
                          <CheckCircle className="size-3" /> Validated -{" "}
                          {sspValidateResult.documentType}, OSCAL{" "}
                          {sspValidateResult.oscalVersion}
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => {
                        setSspValidateResult(null);
                        setSspImportError(null);
                        setSspValidating(false);
                      }}
                      className="p-1 rounded hover:bg-accent flex-shrink-0"
                    >
                      <X className="size-4 text-muted-foreground" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Importing spinner */}
          {sspImportStep === "importing" && (
            <div className="py-12 flex flex-col items-center gap-4">
              <Loader2 className="size-10 animate-spin text-indigo-500 dark:text-indigo-500" />
              <p className="text-sm font-medium">Importing document…</p>
              <p className="text-xs text-muted-foreground text-center">
                Uploading and processing your OSCAL SSP. This may take a moment
                for large files.
              </p>
            </div>
          )}

          {/* Error findings */}
          {sspImportStep === "findings" && (
            <div className="space-y-4">
              <div className="flex items-start gap-2 px-3 py-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-700 dark:text-red-400">
                <AlertTriangle className="size-4 mt-0.5 flex-shrink-0" />
                <div className="space-y-1">
                  <p className="text-xs">
                    <span className="font-semibold">Error:</span>{" "}
                    {sspImportErrorTitle || "Import Failed"}
                  </p>
                  <p className="text-xs">
                    <span className="font-semibold">Message:</span>{" "}
                    {sspImportError}
                  </p>
                </div>
              </div>
            </div>
          )}
        </Modal>

        {/* ── Import Session Modal (list view) ── */}
        <Modal
          open={showSspSession}
          onClose={() => {
            setShowSspSession(false);
            setSspSessionData(null);
          }}
          title={
            sspSessionData
              ? sspSessionData.jobStatus.toLowerCase() === "committed"
                ? "Import Complete"
                : sspSessionData.jobStatus.toLowerCase() === "validated"
                  ? "Commit SSP"
                  : sspSessionData.jobStatus.toLowerCase() === "failed" ||
                    sspSessionData.jobStatus.toLowerCase() === "aborted"
                    ? "Import Failed"
                    : "Importing SSP"
              : "Import SSP"
          }
          size="md"
          closable={false}
          footer={
            sspSessionData ? (
              <div className="flex justify-end w-full gap-2">
                {sspSessionData.jobStatus.toLowerCase() === "committed" ? (
                  <button
                    onClick={() => {
                      setShowSspSession(false);
                      setSspSessionData(null);
                      resetSspImport();
                      fetchSSPList();
                    }}
                    className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors shadow-md"
                  >
                    Done
                  </button>
                ) : sspSessionData.jobStatus.toLowerCase() === "failed" ||
                  sspSessionData.jobStatus.toLowerCase() === "aborted" ? (
                  <button
                    onClick={() => {
                      setShowSspSession(false);
                      setSspSessionData(null);
                      resetSspImport();
                    }}
                    className="px-4 py-2 rounded-lg bg-muted text-sm font-medium hover:bg-accent transition-colors"
                  >
                    Close
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleSspAbortSession}
                      disabled={sspAborting || sspCommitting}
                      className="px-4 py-2 rounded-lg border border-red-200 dark:border-red-500/20 text-red-600 dark:text-red-400 text-sm font-medium hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors disabled:opacity-40"
                    >
                      {sspAborting ? "Cancelling…" : "Cancel"}
                    </button>
                    <button
                      onClick={handleSspCommitSession}
                      disabled={
                        sspCommitting ||
                        sspAborting ||
                        sspSessionData.jobStatus.toLowerCase() !== "validated"
                      }
                      className={`px-4 py-2 rounded-lg text-white text-sm font-bold transition-colors shadow-md ${sspCommitting ||
                        sspAborting ||
                        sspSessionData.jobStatus.toLowerCase() !== "validated"
                        ? "bg-indigo-400 dark:bg-indigo-400 opacity-50 cursor-not-allowed"
                        : "bg-indigo-600 dark:bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-700"
                        }`}
                    >
                      {sspCommitting ? "Committing…" : "Commit"}
                    </button>
                  </>
                )}
              </div>
            ) : null
          }
        >
          {sspSessionLoading && (
            <div className="py-12 flex flex-col items-center gap-4">
              <Loader2 className="size-10 animate-spin text-indigo-500 dark:text-indigo-500" />
              <p className="text-sm font-medium">Loading session…</p>
            </div>
          )}
          {sspSessionError && (
            <div className="flex items-start gap-2 px-3 py-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-700 dark:text-red-400 mb-4">
              <AlertTriangle className="size-4 mt-0.5 flex-shrink-0" />
              <p className="text-xs">{sspSessionError}</p>
            </div>
          )}
          {sspSessionData && (
            <div className="space-y-5">
              {sspCommitting && (
                <div className="py-6 flex flex-col items-center gap-3">
                  <Loader2 className="size-8 animate-spin text-indigo-500 dark:text-indigo-500" />
                  <p className="text-sm font-medium">Committing import…</p>
                  <div className="w-full max-w-xs h-1.5 rounded-full bg-muted overflow-hidden">
                    <div
                      className="h-full rounded-full bg-indigo-500 dark:bg-indigo-500 animate-pulse"
                      style={{ width: "70%" }}
                    />
                  </div>
                </div>
              )}
              {!sspCommitting && (
                <>
                  <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
                    <div className="flex items-center justify-between py-1.5 border-b border-border">
                      <span className="text-xs text-muted-foreground">
                        Status
                      </span>
                      <span
                        className={`text-sm font-semibold capitalize ${sspSessionData.jobStatus.toLowerCase() === "committed"
                          ? "text-emerald-600 dark:text-emerald-400"
                          : sspSessionData.jobStatus.toLowerCase() ===
                            "failed" ||
                            sspSessionData.jobStatus.toLowerCase() ===
                            "aborted"
                            ? "text-red-600 dark:text-red-400"
                            : sspSessionData.jobStatus.toLowerCase() ===
                              "validated"
                              ? "text-indigo-600 dark:text-indigo-400"
                              : "text-amber-600 dark:text-amber-400"
                          }`}
                      >
                        {sspSessionData.jobStatus}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-1.5 border-b border-border">
                      <span className="text-xs text-muted-foreground">
                        Job ID
                      </span>
                      <code className="text-xs font-mono text-foreground">
                        {sspSessionData.id}
                      </code>
                    </div>
                  </div>
                  {sspSessionData.findings.length > 0 && (
                    <div className="rounded-lg border border-border p-4">
                      <h4 className="text-xs font-bold text-muted-foreground mb-2">
                        Findings ({sspSessionData.findings.length})
                      </h4>
                      <div className="space-y-2">
                        {sspSessionData.findings.map((f) => (
                          <div
                            key={f.id}
                            className="flex items-start gap-2 text-xs"
                          >
                            <span
                              className={`px-1.5 py-0.5 rounded font-bold text-[10px] ${f.severity === "info"
                                ? "bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400"
                                : f.severity === "warning"
                                  ? "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400"
                                  : "bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400"
                                }`}
                            >
                              {f.severity}
                            </span>
                            <div className="flex-1">
                              <p className="font-medium">{f.title}</p>
                              <p className="text-muted-foreground">
                                {f.detail}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </Modal>

        {/* Export Modal (List View) */}
        <Modal
          open={showExport}
          onClose={() => {
            setShowExport(false);
            setExportTargetDocId(null);
          }}
          title="Export"
          description="Choose a file format to export this System Security Plan."
          size="sm"
        >
          <div className="grid grid-cols-3 gap-3">
            {(["json", "xml", "yaml"] as const).map((fmt) => (
              <button
                key={fmt}
                onClick={() => handleExport(fmt)}
                disabled={exportingDoc}
                className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:border-indigo-400 dark:hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors group disabled:opacity-40"
              >
                {exportingDoc ? (
                  <Loader2 className="size-6 animate-spin text-muted-foreground" />
                ) : (
                  <Upload className="size-6 text-muted-foreground group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors" />
                )}
                <span className="text-sm font-semibold group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                  {fmt.toUpperCase()}
                </span>
              </button>
            ))}
          </div>
        </Modal>

        {/* Export Pulse Overlay (List View) */}
        {exportingDoc && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 backdrop-blur-sm">
            <div className="flex flex-col items-center gap-4 bg-card rounded-2xl px-10 py-8 shadow-2xl">
              <div className="relative flex items-center justify-center">
                <div className="absolute size-16 rounded-full bg-indigo-500/20 dark:bg-indigo-500/20 animate-ping" />
                <div className="absolute size-12 rounded-full bg-indigo-500/30 dark:bg-indigo-500/30 animate-pulse" />
                <Upload className="relative size-6 text-indigo-600 dark:text-indigo-400" />
              </div>
              <p className="text-sm font-semibold text-foreground mt-2">
                Exporting…
              </p>
              <p className="text-xs text-muted-foreground">
                Please wait while we prepare your file
              </p>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════
  // DETAIL VIEW
  // ══════════════════════════════════════════════════════════

  if (detailLoading) {
    return <LoadingSpinner text="Loading SSP details" />;
  }

  // Tab-specific add buttons
  const tabAddBtn: Partial<
    Record<TabId, { label: string; action: () => void }>
  > = {
    "Information Types": {
      label: "New Info Type",
      action: () => setShowAddInfoType(true),
    },
    "Leveraged Authorizations": {
      label: "New Authorization",
      action: () => setShowAddLevAuth(true),
    },
    "System Users": {
      label: "New User",
      action: () => setShowAddSystemUser(true),
    },
    Interconnections: {
      label: "New Interconnection",
      action: () => setShowAddInterconnection(true),
    },
    Locations: {
      label: "New Location",
      action: () => setShowAddLocation(true),
    },
  };
  const currentAddBtn = tabAddBtn[activeTab];

  // ── Generic tab filter + paginate helper ──
  function filterAndPaginate<T>(items: T[], searchFields: (keyof T)[]) {
    const q = tabSearch.toLowerCase();
    const filtered = q
      ? items.filter((item) =>
        searchFields.some((f) =>
          String(item[f] ?? "")
            .toLowerCase()
            .includes(q),
        ),
      )
      : items;
    const paginated = filtered.slice(
      (tabPage - 1) * tabPageSize,
      tabPage * tabPageSize,
    );
    return { filtered, paginated };
  }

  const { filtered: filtInfoTypes, paginated: pagInfoTypes } =
    filterAndPaginate(infoTypes, ["title" as keyof InformationTypeResponse]);
  const { filtered: filtInventory, paginated: pagInventory } =
    filterAndPaginate(inventoryItems, [
      "description" as keyof InventoryItemResponse,
    ]);
  const { filtered: filtSystemUsers, paginated: pagSystemUsers } =
    filterAndPaginate(systemUsers, ["title" as keyof SspSystemUserResponse]);
  const { filtered: filtInterconnections, paginated: pagInterconnections } =
    filterAndPaginate(interconnections, [
      "title" as keyof SspInterconnectionResponse,
    ]);
  const { filtered: filtLocations, paginated: pagLocations } =
    filterAndPaginate(locations, ["title" as keyof SspLocationResponse]);
  const { filtered: filtLevAuths, paginated: pagLevAuths } = filterAndPaginate(
    leveragedAuths,
    ["title" as keyof LeveragedAuthorizationResponse],
  );

  // SSP Journey metrics — counts and implementation status breakdown derived from loaded state
  const journeyMetrics: JourneyMetrics = (() => {
    const counts = {
      implemented: 0,
      partial: 0,
      planned: 0,
      alternative: 0,
      notImplemented: 0,
      notApplicable: 0,
    };
    // Each unique baseline control with at least one "implemented" requirement counts as implemented.
    const baselineById = new Map<string, string>();
    baselineControls?.forEach((bc: any) => {
      const ctrlId = bc?.controlId || bc?.ciId || bc?.id;
      if (ctrlId) baselineById.set(String(ctrlId), bc?.implementationStatus || "not-implemented");
    });
    // Overlay the live status from controlImpls when present
    controlImpls?.forEach((ci: any) => {
      const cid = ci?.ciId || ci?.controlId;
      if (!cid) return;
      const status = (ci?.implementationStatus || ci?.status || "").toString().toLowerCase();
      if (status) baselineById.set(String(cid), status);
    });
    baselineById.forEach((status) => {
      const s = status.toLowerCase();
      if (s === "implemented") counts.implemented++;
      else if (s === "partial") counts.partial++;
      else if (s === "planned") counts.planned++;
      else if (s === "alternative") counts.alternative++;
      else if (s === "not applicable" || s === "not-applicable" || s === "na") counts.notApplicable++;
      else counts.notImplemented++;
    });

    return {
      implementationCounts: counts,
      totalBaselineControls: baselineById.size,
      controlImplsCount: controlImpls?.length || 0,
      componentsCount: sspComponents?.length || 0,
      infoTypesCount: infoTypes?.length || 0,
      inventoryCount: inventoryItems?.length || 0,
      systemUsersCount: systemUsers?.length || 0,
      interconnectionsCount: interconnections?.length || 0,
      locationsCount: locations?.length || 0,
      partiesCount: parties?.length || 0,
      leveragedAuthsCount: leveragedAuths?.length || 0,
      hasSystemCharacteristics: Boolean(sysChars?.systemName),
    };
  })();

  return (
    <div className="space-y-3">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => {
            navigate("/ssp");
            setSelectedSSPId(null);
            setSspDetail(null);
          }}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="size-3.5" />
          System Security Plans
        </button>
        <ChevronRight className="size-3.5 text-muted-foreground" />
        <span className="text-sm font-medium truncate">
          {sspDocTitles[
            sspList.find((s) => s.id === selectedSSPId)?.documentId ?? ""
          ] ||
            sspDetail?.title ||
            `SSP`}
        </span>
        {/* Lifecycle stepper */}
        <div className="ml-3 flex items-center gap-1">
          {LIFECYCLE_STAGES.map((stage, idx) => {
            const isCurrent = stage.key === lifecycle;
            const isPast = currentStageIdx >= 0 && idx < currentStageIdx;
            return (
              <React.Fragment key={stage.key}>
                <div
                  className={`flex items-center gap-1 ${isCurrent ? "text-foreground" : isPast ? "text-emerald-600 dark:text-emerald-400" : "text-muted-foreground/40"}`}
                >
                  <div
                    className={`size-2 rounded-full ${isCurrent ? "bg-blue-500 ring-2 ring-blue-500/30" : isPast ? "bg-emerald-500" : "bg-muted-foreground/20"}`}
                  />
                  <span
                    className={`text-[10px] ${isCurrent ? "font-bold" : "font-medium"}`}
                  >
                    {stage.label}
                  </span>
                </div>
                {idx < LIFECYCLE_STAGES.length - 1 && (
                  <div
                    className={`w-4 h-px ${isPast ? "bg-emerald-500/50" : "bg-muted-foreground/15"}`}
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-lg border border-border bg-card text-sm text-muted-foreground">
          <span>Something went wrong.</span>
          <button
            onClick={() => setError(null)}
            className="ml-auto text-xs underline hover:no-underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* SSP Hero Header */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        {/* Top section: title + actions */}
        <div className="px-4 pt-3 pb-2.5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="size-9 rounded-lg bg-indigo-50 dark:bg-indigo-500/15 flex items-center justify-center flex-shrink-0">
                <Shield className="size-4 text-indigo-600 dark:text-indigo-400" />
              </div>
              <div>
                {editingSspTitle ? (
                  <div className="flex items-center gap-1.5">
                    <input
                      autoFocus
                      value={sspTitleDraft}
                      onChange={(e) => setSspTitleDraft(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleRenameSsp();
                        if (e.key === "Escape") setEditingSspTitle(false);
                      }}
                      maxLength={255}
                      disabled={savingSspTitle}
                      className="text-base font-black px-2 py-0.5 rounded border border-indigo-400 bg-card outline-none focus:ring-1 focus:ring-indigo-500 min-w-[220px]"
                    />
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={handleRenameSsp}
                          disabled={savingSspTitle || !sspTitleDraft.trim()}
                          className="p-1 rounded hover:bg-green-50 dark:hover:bg-green-500/10 text-green-600 dark:text-green-400 transition-colors disabled:opacity-40"
                        >
                          {savingSspTitle ? (
                            <Loader2 className="size-4 animate-spin" />
                          ) : (
                            <Check className="size-4" />
                          )}
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Save</TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <button
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => setEditingSspTitle(false)}
                          disabled={savingSspTitle}
                          className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-500/10 text-red-600 dark:text-red-400 transition-colors disabled:opacity-40"
                        >
                          <X className="size-4" />
                        </button>
                      </TooltipTrigger>
                      <TooltipContent>Cancel</TooltipContent>
                    </Tooltip>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <h2 className="text-base font-black text-foreground tracking-[-0.3px] leading-tight">
                      {currentSspTitle || `SSP`}
                    </h2>
                    {!isPublishedSsp && (
                      <RBACAction module="ssp" action="update">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => {
                                setSspTitleDraft(currentSspTitle);
                                setEditingSspTitle(true);
                              }}
                              className="p-1 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                            >
                              <Pencil className="size-3.5" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>Rename SSP</TooltipContent>
                        </Tooltip>
                      </RBACAction>
                    )}
                  </div>
                )}
                <p className="text-[11px] text-muted-foreground mt-0.5">
                  System Security Plan
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {/* Lifecycle transition buttons */}
              {currentLifecycleActions.map((action) => {
                const button = (
                  <button
                    key={action.target}
                    onClick={() => openTransitionModal(action.target)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-white text-xs font-semibold transition-colors shadow-md ${action.color}`}
                  >
                    {action.icon}
                    {action.label}
                  </button>
                );

                // Determine required permission based on action label
                if (["Approve", "Reject"].includes(action.label)) {
                  return (
                    <RBACAction key={action.target} module="ssp" action="approve">
                      {button}
                    </RBACAction>
                  );
                }
                if (action.label === "Publish") {
                  return (
                    <RBACAction key={action.target} module="ssp" action="publish">
                      {button}
                    </RBACAction>
                  );
                }
                if (action.label === "Submit for Review") {
                  return (
                    <RBACAction key={action.target} module="ssp" action="update">
                      {button}
                    </RBACAction>
                  );
                }
                if (["Supersede", "Withdraw"].includes(action.label)) {
                  return (
                    <RBACAction key={action.target} module="ssp" action="update">
                      {button}
                    </RBACAction>
                  );
                }
                return button;
              })}
            </div>
          </div>
        </div>

        {/* Info row: profile, status, system ID, lifecycle, updated */}
        <div className="px-4 py-2 bg-muted/30 border-t border-border">
          <div className="flex items-center gap-4 flex-wrap">
            {/* Profile */}
            {(() => {
              const currentSsp = sspList.find((s) => s.id === selectedSSPId);
              const profName = currentSsp?.profileName;
              return profName ? (
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold text-foreground tracking-wide">
                    Profile
                  </span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                    {profName}
                  </span>
                </div>
              ) : null;
            })()}

            {/* Source name — shown as "Application Name" or "Service Name" based on sourceType */}
            {(() => {
              const currentSsp = sspList.find((s) => s.id === selectedSSPId);
              const srcName = currentSsp?.sourceName;
              const srcType = (currentSsp?.sourceType || "").toLowerCase();
              if (!srcName) return null;
              const label =
                srcType === "application"
                  ? "Application Name"
                  : srcType === "service"
                    ? "Service Name"
                    : "Source";
              return (
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-bold text-foreground tracking-wide">
                    {label}
                  </span>
                  <span className="text-xs text-foreground">
                    {srcName}
                  </span>
                </div>
              );
            })()}

            {/* Status from system characteristics */}
            {sysChars?.status && (
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold text-foreground tracking-wide">
                  Status
                </span>
                <span className="text-xs font-medium px-2.5 py-1 rounded bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400">
                  {SYSTEM_STATUSES.find((s) => s.value === sysChars.status)?.label || sysChars.status}
                </span>
              </div>
            )}

            {/* System ID from system characteristics */}
            {sysChars?.systemIds && sysChars.systemIds.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-[11px] font-bold text-foreground tracking-wide">
                  System ID
                </span>
                <span className="text-xs font-mono font-medium px-2 py-0.5 rounded bg-muted text-foreground break-all">
                  {String(
                    sysChars.systemIds[0]?.id || sysChars.systemIds[0] || "",
                  )}
                </span>
              </div>
            )}

            {/* Lifecycle status */}
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-bold text-foreground tracking-wide">
                Lifecycle
              </span>
              <StatusBadge status={lifecycle as Parameters<typeof StatusBadge>[0]["status"]} />
            </div>

            {/* Updated date */}
            {sspDetail && (
              <span className="text-xs text-muted-foreground ml-auto">
                Updated:{" "}
                {new Date(sspDetail.updatedAt).toLocaleDateString("en-GB")}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* SSP Journey — primary navigation (replaces text tabs) */}
      <SSPDetailJourney
        activeTab={activeTab}
        onTabChange={(tab) => setActiveTab(tab as typeof activeTab)}
        metrics={journeyMetrics}
      />

      {/* Search + Rows per page + Add button – below tabs */}
      {(() => {
        const searchTabs = [
          "Information Types",
          "Components",
          "Inventory",
          "System Users",
          "Interconnections",
          "Locations",
          "Leveraged Authorizations",
        ];
        const paginatedTabs = searchTabs;
        const showSearch = searchTabs.includes(activeTab);
        // "Rows per page" is hidden on the Components tab (commented out per
        // request). It still shows on the other paginated tabs.
        const showRowsPerPage =
          paginatedTabs.includes(activeTab) && activeTab !== "Components";
        const canShowCurrentAddBtn = Boolean(currentAddBtn && !isPublishedSsp);
        if (!showSearch && !showRowsPerPage && !canShowCurrentAddBtn)
          return null;
        return (
          <div className="flex items-center gap-4">
            {showSearch && (
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground/50 pointer-events-none" />
                <input
                  type="text"
                  placeholder={`Search ${formatTabName(activeTab)}`}
                  value={tabSearch}
                  onChange={(e) => {
                    setTabSearch(e.target.value);
                    setTabPage(1);
                  }}
                  className="w-full pl-9 pr-4 py-2 text-sm bg-card border border-border rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-indigo-500/20 focus:border-indigo-500 dark:focus:border-indigo-500 transition-all placeholder:text-muted-foreground/50"
                />
              </div>
            )}
            {showRowsPerPage && (
              <div
                className={`flex items-center gap-2 flex-shrink-0 ${canShowCurrentAddBtn ? "" : "ml-auto"
                  }`}
              >
                <label className="text-sm text-muted-foreground whitespace-nowrap">
                  Rows per page
                </label>
                <Dropdown
                  value={String(tabPageSize)}
                  onChange={(v) => {
                    setCurrentTabPagination({ page: 1, pageSize: Number(v) });
                  }}
                  options={[10, 25, 50, 100].map((n) => ({
                    value: String(n),
                    label: String(n),
                  }))}
                  size="sm"
                  align="end"
                  className="!min-w-[80px]"
                />
              </div>
            )}
            {canShowCurrentAddBtn && currentAddBtn && (
              <div className="flex items-center gap-2 flex-shrink-0 ml-auto">
                <button
                  onClick={currentAddBtn.action}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors"
                >
                  <Plus className="size-3" />
                  {currentAddBtn.label}
                </button>
              </div>
            )}
          </div>
        );
      })()}

      {/* ═══ System Characteristics Tab ═══ */}
      {activeTab === "System Characteristics" && (
        <>
          {sysCharsLoading ? (
            <LoadingSpinner text="Loading system characteristics" />
          ) : sysChars ? (
            <div className="rounded-lg border border-border bg-card p-5 space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="flex items-center justify-center size-8 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                    <Shield className="size-4" />
                  </div>
                  <h3 className="text-sm font-bold text-foreground">
                    System Characteristics
                  </h3>
                </div>
                {!editingSysChars && !isPublishedSsp && (
                  <button
                    onClick={() => {
                      setSysCharsForm({
                        systemName: sysChars.systemName || "",
                        systemNameShort: sysChars.systemNameShort || "",
                        description: sysChars.description || "",
                        securitySensitivityLevel:
                          sysChars.securitySensitivityLevel || "",
                        systemType: sysChars.systemType || "",
                        authorizationType: sysChars.authorizationType || "",
                        deploymentModel: sysChars.deploymentModel || "",
                        serviceModel: sysChars.serviceModel || "",
                        systemIds: sysChars.systemIds?.length
                          ? sysChars.systemIds
                          : [],
                        authorizationBoundary:
                          sysChars.authorizationBoundary?.description || "",
                        networkArchitecture:
                          sysChars.networkArchitecture?.description || "",
                        dataFlow: sysChars.dataFlow?.description || "",
                        confidentiality:
                          sysChars.securityImpactLevel?.[
                          "security-objective-confidentiality"
                          ] || "",
                        integrity:
                          sysChars.securityImpactLevel?.[
                          "security-objective-integrity"
                          ] || "",
                        availability:
                          sysChars.securityImpactLevel?.[
                          "security-objective-availability"
                          ] || "",
                        status: sysChars.status || "",
                        statusRemarks: sysChars.statusRemarks || "",
                        dateAuthorized: sysChars.dateAuthorized
                          ? sysChars.dateAuthorized.split("T")[0]
                          : "",
                      });
                      setEditingSysChars(true);
                    }}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors"
                  >
                    <Pencil className="size-3" /> Edit
                  </button>
                )}
              </div>

              {editingSysChars ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="System Name" required>
                      <FormInput
                        value={sysCharsForm.systemName}
                        onChange={(v) =>
                          setSysCharsForm((f) => ({ ...f, systemName: v }))
                        }
                        placeholder="Full system name"
                      />
                    </FormField>
                    <FormField label="Short Name">
                      <FormInput
                        value={sysCharsForm.systemNameShort}
                        onChange={(v) =>
                          setSysCharsForm((f) => ({ ...f, systemNameShort: v }))
                        }
                        placeholder="Acronym"
                      />
                    </FormField>
                  </div>
                  <FormField label="Description">
                    <FormTextarea
                      value={sysCharsForm.description}
                      onChange={(v) =>
                        setSysCharsForm((f) => ({ ...f, description: v }))
                      }
                      placeholder="System description"
                      rows={2}
                    />
                  </FormField>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="System Type">
                      <FormSelect
                        options={[
                          {
                            value: "general-support-system",
                            label: "General Support System",
                          },
                          {
                            value: "major-application",
                            label: "Major Application",
                          },
                          {
                            value: "minor-application",
                            label: "Minor Application",
                          },
                          {
                            value: "cloud-service-provider",
                            label: "Cloud Service Provider",
                          },
                        ]}
                        value={sysCharsForm.systemType}
                        onChange={(v) =>
                          setSysCharsForm((f) => ({ ...f, systemType: v }))
                        }
                        placeholder="Select type"
                      />
                    </FormField>
                    <FormField label="Authorization Type">
                      <FormSelect
                        options={[
                          { value: "fisma", label: "FISMA" },
                          { value: "fedramp", label: "FedRAMP" },
                          { value: "dod-rmf", label: "DoD RMF" },
                          { value: "stateramp", label: "StateRAMP" },
                          { value: "other", label: "Other" },
                        ]}
                        value={sysCharsForm.authorizationType}
                        onChange={(v) =>
                          setSysCharsForm((f) => ({
                            ...f,
                            authorizationType: v,
                          }))
                        }
                        placeholder="Select auth type"
                      />
                    </FormField>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="Deployment Model">
                      <FormSelect
                        options={[
                          { value: "public-cloud", label: "Public Cloud" },
                          { value: "private-cloud", label: "Private Cloud" },
                          {
                            value: "government-cloud",
                            label: "Government Cloud",
                          },
                          { value: "hybrid", label: "Hybrid" },
                          { value: "on-premise", label: "On-Premise" },
                        ]}
                        value={sysCharsForm.deploymentModel}
                        onChange={(v) =>
                          setSysCharsForm((f) => ({ ...f, deploymentModel: v }))
                        }
                        placeholder="Select deployment model"
                      />
                    </FormField>
                    <FormField label="Service Model">
                      <FormSelect
                        options={[
                          { value: "saas", label: "SaaS" },
                          { value: "paas", label: "PaaS" },
                          { value: "iaas", label: "IaaS" },
                        ]}
                        value={sysCharsForm.serviceModel}
                        onChange={(v) =>
                          setSysCharsForm((f) => ({ ...f, serviceModel: v }))
                        }
                        placeholder="Select service model"
                      />
                    </FormField>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="Security Sensitivity Level">
                      <FormSelect
                        options={[
                          { value: "low", label: "Low" },
                          { value: "moderate", label: "Moderate" },
                          { value: "high", label: "High" },
                        ]}
                        value={sysCharsForm.securitySensitivityLevel}
                        onChange={(v) =>
                          setSysCharsForm((f) => ({
                            ...f,
                            securitySensitivityLevel: v,
                          }))
                        }
                        placeholder="Select level"
                      />
                    </FormField>
                    <FormField label="System Status">
                      <FormSelect
                        options={SYSTEM_STATUSES}
                        value={sysCharsForm.status}
                        onChange={(v) =>
                          setSysCharsForm((f) => ({ ...f, status: v }))
                        }
                        placeholder="Select status"
                      />
                    </FormField>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <label className="text-xs font-semibold text-muted-foreground">
                        System IDs
                      </label>
                      <button
                        type="button"
                        onClick={() =>
                          setSysCharsForm((f) => ({
                            ...f,
                            systemIds: [
                              ...f.systemIds,
                              {
                                "identifier-type":
                                  "https://ietf.org/rfc/rfc4122",
                                id: "",
                              },
                            ],
                          }))
                        }
                        className="text-xs font-bold text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 transition-colors flex items-center gap-1"
                      >
                        <Plus className="size-3" /> Add ID
                      </button>
                    </div>
                    {sysCharsForm.systemIds.map((sid, idx) => (
                      <div
                        key={idx}
                        className="grid grid-cols-[1fr_1fr_auto] gap-3 items-end"
                      >
                        <FormField label={idx === 0 ? "Identifier Type" : ""}>
                          <FormInput
                            value={sid["identifier-type"]}
                            onChange={(v) =>
                              setSysCharsForm((f) => {
                                const updated = [...f.systemIds];
                                updated[idx] = {
                                  ...updated[idx],
                                  "identifier-type": v,
                                };
                                return { ...f, systemIds: updated };
                              })
                            }
                            placeholder="https://ietf.org/rfc/rfc4122"
                          />
                        </FormField>
                        <FormField label={idx === 0 ? "ID" : ""}>
                          <FormInput
                            value={sid.id}
                            onChange={(v) =>
                              setSysCharsForm((f) => {
                                const updated = [...f.systemIds];
                                updated[idx] = { ...updated[idx], id: v };
                                return { ...f, systemIds: updated };
                              })
                            }
                            placeholder="550e8400-e29b-41d4-a716-446655440000"
                          />
                        </FormField>
                        <button
                          type="button"
                          onClick={() =>
                            setSysCharsForm((f) => ({
                              ...f,
                              systemIds: f.systemIds.filter(
                                (_, i) => i !== idx,
                              ),
                            }))
                          }
                          className="p-1.5 rounded hover:bg-red-100 dark:hover:bg-red-500/20 text-red-500 transition-colors mb-0.5"
                        >
                          <Trash2 className="size-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <FormField label="Authorization Boundary">
                    <FormTextarea
                      value={sysCharsForm.authorizationBoundary}
                      onChange={(v) =>
                        setSysCharsForm((f) => ({
                          ...f,
                          authorizationBoundary: v,
                        }))
                      }
                      placeholder="Describe the authorization boundary"
                      rows={2}
                    />
                  </FormField>
                  {/* Network Architecture - description + diagram */}
                  <div className="rounded-xl border border-border p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-bold text-foreground tracking-wider">
                        Network Architecture
                      </h4>
                      <div className="flex items-center gap-1.5">
                        {networkArchDiagram?.contentEvidenceId && (
                          <button
                            type="button"
                            onClick={() =>
                              setViewingDiagram({
                                title: "Network Architecture",
                                contentEvidenceId:
                                  networkArchDiagram.contentEvidenceId!,
                                fileName: networkArchDiagram.fileName,
                              })
                            }
                            className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors"
                          >
                            <Eye className="size-3" /> View
                          </button>
                        )}
                        <label
                          className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-indigo-600 dark:bg-indigo-600 text-white hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors ${uploadingDiagram === "network" ? "opacity-50 pointer-events-none" : "cursor-pointer"}`}
                        >
                          {uploadingDiagram === "network" ? (
                            <Loader2 className="size-3 animate-spin" />
                          ) : (
                            <Upload className="size-3" />
                          )}
                          {uploadingDiagram === "network"
                            ? "Uploading"
                            : networkArchDiagram
                              ? "Replace Diagram"
                              : "Upload Diagram"}
                          <input
                            type="file"
                            accept="image/*,.pdf,.doc,.docx"
                            className="hidden"
                            disabled={uploadingDiagram === "network"}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleDiagramUpload(file, "network");
                              e.target.value = "";
                            }}
                          />
                        </label>
                      </div>
                    </div>
                    <FormTextarea
                      value={sysCharsForm.networkArchitecture}
                      onChange={(v) =>
                        setSysCharsForm((f) => ({
                          ...f,
                          networkArchitecture: v,
                        }))
                      }
                      placeholder="Describe the network architecture"
                      rows={2}
                    />
                    {networkArchDiagram && (
                      <div className="flex items-center gap-2 p-2 rounded-md bg-muted/50">
                        <FileImage className="size-4 text-muted-foreground flex-shrink-0" />
                        <span className="text-xs truncate flex-1">
                          {networkArchDiagram.fileName ||
                            networkArchDiagram.file?.name ||
                            "Diagram"}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleDiagramDelete("network")}
                          className="p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-500/20 text-muted-foreground hover:text-red-600 transition-colors"
                        >
                          <X className="size-3" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Data Flow - description + diagram */}
                  <div className="rounded-xl border border-border p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <h4 className="text-xs font-bold text-foreground tracking-wider">
                        Data Flow
                      </h4>
                      <div className="flex items-center gap-1.5">
                        {dataFlowDiagram?.contentEvidenceId && (
                          <button
                            type="button"
                            onClick={() =>
                              setViewingDiagram({
                                title: "Data Flow",
                                contentEvidenceId:
                                  dataFlowDiagram.contentEvidenceId!,
                                fileName: dataFlowDiagram.fileName,
                              })
                            }
                            className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors"
                          >
                            <Eye className="size-3" /> View
                          </button>
                        )}
                        <label
                          className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium bg-indigo-600 dark:bg-indigo-600 text-white hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors ${uploadingDiagram === "dataflow" ? "opacity-50 pointer-events-none" : "cursor-pointer"}`}
                        >
                          {uploadingDiagram === "dataflow" ? (
                            <Loader2 className="size-3 animate-spin" />
                          ) : (
                            <Upload className="size-3" />
                          )}
                          {uploadingDiagram === "dataflow"
                            ? "Uploading"
                            : dataFlowDiagram
                              ? "Replace Diagram"
                              : "Upload Diagram"}
                          <input
                            type="file"
                            accept="image/*,.pdf,.doc,.docx"
                            className="hidden"
                            disabled={uploadingDiagram === "dataflow"}
                            onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (file) handleDiagramUpload(file, "dataflow");
                              e.target.value = "";
                            }}
                          />
                        </label>
                      </div>
                    </div>
                    <FormTextarea
                      value={sysCharsForm.dataFlow}
                      onChange={(v) =>
                        setSysCharsForm((f) => ({ ...f, dataFlow: v }))
                      }
                      placeholder="Describe data flows"
                      rows={2}
                    />
                    {dataFlowDiagram && (
                      <div className="flex items-center gap-2 p-2 rounded-md bg-muted/50">
                        <FileImage className="size-4 text-muted-foreground flex-shrink-0" />
                        <span className="text-xs truncate flex-1">
                          {dataFlowDiagram.fileName ||
                            dataFlowDiagram.file?.name ||
                            "Diagram"}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleDiagramDelete("dataflow")}
                          className="p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-500/20 text-muted-foreground hover:text-red-600 transition-colors"
                        >
                          <X className="size-3" />
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <FormField label="Confidentiality">
                      <FormSelect
                        options={[
                          { value: "low", label: "Low" },
                          { value: "moderate", label: "Moderate" },
                          { value: "high", label: "High" },
                        ]}
                        value={sysCharsForm.confidentiality}
                        onChange={(v) =>
                          setSysCharsForm((f) => ({ ...f, confidentiality: v }))
                        }
                        placeholder="Select"
                      />
                    </FormField>
                    <FormField label="Integrity">
                      <FormSelect
                        options={[
                          { value: "low", label: "Low" },
                          { value: "moderate", label: "Moderate" },
                          { value: "high", label: "High" },
                        ]}
                        value={sysCharsForm.integrity}
                        onChange={(v) =>
                          setSysCharsForm((f) => ({ ...f, integrity: v }))
                        }
                        placeholder="Select"
                      />
                    </FormField>
                    <FormField label="Availability">
                      <FormSelect
                        options={[
                          { value: "low", label: "Low" },
                          { value: "moderate", label: "Moderate" },
                          { value: "high", label: "High" },
                        ]}
                        value={sysCharsForm.availability}
                        onChange={(v) =>
                          setSysCharsForm((f) => ({ ...f, availability: v }))
                        }
                        placeholder="Select"
                      />
                    </FormField>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField label="Status Remarks">
                      <FormInput
                        value={sysCharsForm.statusRemarks}
                        onChange={(v) =>
                          setSysCharsForm((f) => ({ ...f, statusRemarks: v }))
                        }
                        placeholder="Status remarks"
                      />
                    </FormField>
                    <FormField label="Date Authorized">
                      <FormDatePicker
                        value={sysCharsForm.dateAuthorized}
                        onChange={(v) =>
                          setSysCharsForm((f) => ({ ...f, dateAuthorized: v }))
                        }
                      />
                    </FormField>
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                    <button
                      onClick={() => setEditingSysChars(false)}
                      className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-accent transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveSysChars}
                      disabled={!sysCharsForm.systemName || savingSysChars}
                      className="px-3 py-1.5 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-50"
                    >
                      {savingSysChars ? "Saving" : "Save"}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="divide-y divide-border">
                    {sysChars.systemName && (
                      <SysCharRow icon={Server} label="System Name">
                        <span className="font-medium text-foreground">
                          {sysChars.systemName}
                        </span>
                      </SysCharRow>
                    )}
                    {sysChars.systemNameShort && (
                      <SysCharRow icon={Tag} label="Short Name">
                        <span className="font-medium text-foreground">
                          {sysChars.systemNameShort}
                        </span>
                      </SysCharRow>
                    )}
                    {sysChars.description && (
                      <SysCharRow icon={FileText} label="Description">
                        <p className="leading-relaxed">
                          {sysChars.description}
                        </p>
                      </SysCharRow>
                    )}
                    {sysChars.securitySensitivityLevel && (
                      <SysCharRow
                        icon={ShieldAlert}
                        label="Security Sensitivity Level"
                      >
                        <SysLevelPill
                          level={sysChars.securitySensitivityLevel}
                        />
                      </SysCharRow>
                    )}
                    {sysChars.systemType && (
                      <SysCharRow icon={Cpu} label="System Type">
                        <span className="capitalize">
                          {sysChars.systemType.replace(/-/g, " ")}
                        </span>
                      </SysCharRow>
                    )}
                    {sysChars.authorizationType && (
                      <SysCharRow icon={BadgeCheck} label="Authorization Type">
                        <span className="capitalize">
                          {sysChars.authorizationType.replace(/-/g, " ")}
                        </span>
                      </SysCharRow>
                    )}
                    {sysChars.deploymentModel && (
                      <SysCharRow icon={Cloud} label="Deployment Model">
                        <span className="capitalize">
                          {sysChars.deploymentModel.replace(/-/g, " ")}
                        </span>
                      </SysCharRow>
                    )}
                    {sysChars.serviceModel && (
                      <SysCharRow icon={Layers} label="Service Model">
                        <span className="capitalize">
                          {sysChars.serviceModel}
                        </span>
                      </SysCharRow>
                    )}
                    {sysChars.authorizationBoundary?.description && (
                      <SysCharRow icon={Boxes} label="Authorization Boundary">
                        <p className="leading-relaxed">
                          {sysChars.authorizationBoundary.description}
                        </p>
                      </SysCharRow>
                    )}
                    {sysChars.securityImpactLevel && (
                      <SysCharRow
                        icon={ShieldCheck}
                        label="Security Impact Level"
                      >
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">
                              Confidentiality:
                            </span>
                            <SysLevelPill
                              level={
                                sysChars.securityImpactLevel[
                                "security-objective-confidentiality"
                                ]
                              }
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">
                              Integrity:
                            </span>
                            <SysLevelPill
                              level={
                                sysChars.securityImpactLevel[
                                "security-objective-integrity"
                                ]
                              }
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">
                              Availability:
                            </span>
                            <SysLevelPill
                              level={
                                sysChars.securityImpactLevel[
                                "security-objective-availability"
                                ]
                              }
                            />
                          </div>
                        </div>
                      </SysCharRow>
                    )}
                    {sysChars.status && (
                      <SysCharRow icon={Activity} label="Status">
                        <SysStatusPill status={sysChars.status} />
                      </SysCharRow>
                    )}
                    {sysChars.statusRemarks && (
                      <SysCharRow icon={MessageSquare} label="Status Remarks">
                        <p className="leading-relaxed">
                          {sysChars.statusRemarks}
                        </p>
                      </SysCharRow>
                    )}
                    {sysChars.dateAuthorized && (
                      <SysCharRow icon={Calendar} label="Date Authorized">
                        <span className="inline-flex items-center gap-1.5">
                          <Calendar className="size-3.5 text-muted-foreground" />
                          {toddmmyyyy(sysChars.dateAuthorized)}
                        </span>
                      </SysCharRow>
                    )}
                  </div>

                  {/* Network Architecture - combined read-only */}
                  {(sysChars.networkArchitecture?.description ||
                    networkArchDiagram) && (
                      <div className="mt-4 rounded-xl border border-border bg-muted/20 p-4 space-y-3">
                        <div className="flex items-center gap-2">
                          <div className="flex items-center justify-center size-7 rounded-md bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                            <Network className="size-3.5" />
                          </div>
                          <h4 className="text-sm font-bold text-foreground">
                            Network Architecture
                          </h4>
                        </div>
                        {sysChars.networkArchitecture?.description && (
                          <p className="text-sm text-foreground/80 leading-relaxed">
                            {sysChars.networkArchitecture.description}
                          </p>
                        )}
                        {networkArchDiagram?.contentEvidenceId && (
                          <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card">
                            <FileImage className="size-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                            <span className="text-xs truncate flex-1">
                              {networkArchDiagram.fileName ||
                                networkArchDiagram.file?.name ||
                                "Diagram"}
                            </span>
                            <button
                              onClick={() =>
                                setViewingDiagram({
                                  title: "Network Architecture",
                                  contentEvidenceId:
                                    networkArchDiagram.contentEvidenceId!,
                                  fileName: networkArchDiagram.fileName,
                                })
                              }
                              className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors"
                            >
                              <Eye className="size-3" /> View
                            </button>
                          </div>
                        )}
                      </div>
                    )}

                  {/* Data Flow - combined read-only */}
                  {(sysChars.dataFlow?.description || dataFlowDiagram) && (
                    <div className="mt-4 rounded-xl border border-border bg-muted/20 p-4 space-y-3">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center justify-center size-7 rounded-md bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                          <Activity className="size-3.5" />
                        </div>
                        <h4 className="text-sm font-bold text-foreground">
                          Data Flow
                        </h4>
                      </div>
                      {sysChars.dataFlow?.description && (
                        <p className="text-sm text-foreground/80 leading-relaxed">
                          {sysChars.dataFlow.description}
                        </p>
                      )}
                      {dataFlowDiagram && (
                        <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-card">
                          <FileImage className="size-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                          <span className="text-xs truncate flex-1">
                            {dataFlowDiagram.fileName ||
                              dataFlowDiagram.file?.name ||
                              "Diagram"}
                          </span>
                          <button
                            onClick={() =>
                              setViewingDiagram({
                                title: "Data Flow",
                                contentEvidenceId:
                                  dataFlowDiagram.contentEvidenceId!,
                                fileName: dataFlowDiagram.fileName,
                              })
                            }
                            className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors"
                          >
                            <Eye className="size-3" /> View
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          ) : (
            <div className="rounded-lg border border-border bg-card p-5">
              <div className="text-center py-8">
                <Shield className="size-8 text-muted-foreground mb-3 mx-auto" />
                <p className="text-sm font-medium">
                  No system characteristics defined
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Set up system characteristics for this SSP
                </p>
                {!isPublishedSsp && (
                  <button
                    onClick={() => {
                      setSysCharsForm({
                        systemName: "",
                        systemNameShort: "",
                        description: "",
                        securitySensitivityLevel: "",
                        systemType: "",
                        authorizationType: "",
                        deploymentModel: "",
                        serviceModel: "",
                        systemIds: [],
                        authorizationBoundary: "",
                        networkArchitecture: "",
                        dataFlow: "",
                        confidentiality: "",
                        integrity: "",
                        availability: "",
                        status: "",
                        statusRemarks: "",
                        dateAuthorized: "",
                      });
                      setEditingSysChars(true);
                      setSysChars({} as SystemCharacteristicsResponse);
                    }}
                    className="mt-3 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors mx-auto"
                  >
                    <Plus className="size-3" />
                    Set Characteristics
                  </button>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══ Information Types Tab ═══ */}
      {activeTab === "Information Types" && (
        <>
          {infoTypesLoading ? (
            <LoadingSpinner text="Loading information types" />
          ) : infoTypes.length > 0 ? (
            <>
              <div className="rounded-lg border border-border bg-card overflow-hidden">
                {/* Header */}
                <div className="hidden md:grid grid-cols-[52px_1fr_400px_44px] gap-4 px-5 py-3.5 border-b border-border bg-muted/30">
                  <div />
                  <div>
                    <p className="text-[13px] font-medium text-[#1e293b] dark:text-white">
                      Information Type
                    </p>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-[13px] font-medium text-[#1e293b] dark:text-white text-center">
                    <span>Confidentiality</span>
                    <span>Integrity</span>
                    <span>Availability</span>
                  </div>
                  <div />
                </div>

                <div className="divide-y divide-border">
                  {pagInfoTypes.map((it) => (
                    <div key={it.id} className="px-5 py-3.5 hover:bg-muted/40 transition-colors">
                      {editingInfoTypeId === it.id ? (
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <FormField label="Title" required>
                              <FormInput
                                value={editInfoTypeForm.title}
                                onChange={(v) => setEditInfoTypeForm((f) => ({ ...f, title: v }))}
                                placeholder="Information type title"
                              />
                            </FormField>
                            <FormField label="Description">
                              <FormInput
                                value={editInfoTypeForm.description}
                                onChange={(v) => setEditInfoTypeForm((f) => ({ ...f, description: v }))}
                                placeholder="Description"
                              />
                            </FormField>
                          </div>
                          <div className="grid grid-cols-3 gap-3">
                            {(["confidentiality", "integrity", "availability"] as const).map((field) => (
                              <FormField key={field} label={field.charAt(0).toUpperCase() + field.slice(1)}>
                                <FormSelect
                                  options={[
                                    { label: "Low", value: "low" },
                                    { label: "Moderate", value: "moderate" },
                                    { label: "High", value: "high" },
                                  ]}
                                  value={editInfoTypeForm[field]}
                                  onChange={(v) => setEditInfoTypeForm((f) => ({ ...f, [field]: v }))}
                                  placeholder="Select level"
                                />
                              </FormField>
                            ))}
                          </div>
                          <div className="flex items-center gap-2 pt-1">
                            <button
                              onClick={() => setEditingInfoTypeId(null)}
                              className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-accent transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleUpdateInfoType}
                              disabled={!editInfoTypeForm.title || savingInfoType}
                              className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 transition-colors disabled:opacity-50"
                            >
                              {savingInfoType ? "Saving" : "Save"}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="grid grid-cols-[40px_1fr] md:grid-cols-[52px_1fr_400px_44px] gap-3 md:gap-4 items-center">
                          <div className="flex items-center justify-center size-10 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex-shrink-0">
                            <Database className="size-5" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-foreground">{it.title}</p>
                            {it.description && (
                              <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{it.description}</p>
                            )}
                          </div>
                          <div className="grid grid-cols-3 gap-2 col-span-2 md:col-span-1">
                            {(["confidentiality", "integrity", "availability"] as const).map((field) => {
                              const impact = field === "confidentiality" ? it.confidentialityImpact : field === "integrity" ? it.integrityImpact : it.availabilityImpact;
                              return (
                                <div key={field} className="flex justify-center">
                                  <ImpactPill level={impact?.base} />
                                </div>
                              );
                            })}
                          </div>
                          {!isPublishedSsp ? (
                            <div className="flex items-center gap-1 justify-end flex-shrink-0 col-span-2 md:col-span-1">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={() => {
                                      setEditingInfoTypeId(it.id);
                                      setEditInfoTypeForm({
                                        title: it.title,
                                        description: it.description || "",
                                        confidentiality: it.confidentialityImpact?.base || "",
                                        integrity: it.integrityImpact?.base || "",
                                        availability: it.availabilityImpact?.base || "",
                                      });
                                    }}
                                    className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                                  >
                                    <Pencil className="size-3.5" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>Edit</TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={() => requestDelete("Information Type", it.id, it.title, () => handleDeleteInfoType(it.id))}
                                    className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-500/10 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-colors"
                                  >
                                    <Trash2 className="size-3.5" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>Delete</TooltipContent>
                              </Tooltip>
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              {filtInfoTypes.length > 0 && (
                <div className="flex items-center justify-between gap-2 px-4 py-2 border-t border-[#e2e8f0] dark:border-white/[0.06] bg-[#fafbfc] dark:bg-white/[0.015] flex-shrink-0">
                  <p className="text-[11px] leading-tight text-[#475569] dark:text-white/70">
                    Showing{" "}
                    <span className="font-semibold text-[#334155] dark:text-white/90">
                      {Math.min((tabPage - 1) * tabPageSize + 1, filtInfoTypes.length)}–{Math.min(tabPage * tabPageSize, filtInfoTypes.length)}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold text-[#334155] dark:text-white/90">
                      {filtInfoTypes.length}
                    </span>
                  </p>
                  {Math.ceil(filtInfoTypes.length / tabPageSize) > 1 && (
                    <div className="flex items-center gap-0.5">
                      <button
                        disabled={tabPage === 1}
                        onClick={() => setCurrentTabPagination({ page: Math.max(1, tabPage - 1), pageSize: tabPageSize })}
                        className="p-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronLeft className="size-3" />
                      </button>

                      {getPageWindow(tabPage, Math.ceil(filtInfoTypes.length / tabPageSize)).map((p, idx) =>
                        p === "dots" ? (
                          <span
                            key={`dots-${idx}`}
                            className="w-6 h-6 flex items-center justify-center text-xs text-[#475569] dark:text-white/70 select-none"
                          >
                            …
                          </span>
                        ) : (
                          <button
                            key={p}
                            onClick={() => setCurrentTabPagination({ page: p as number, pageSize: tabPageSize })}
                            className={`w-6 h-6 text-xs font-medium rounded-md transition-colors ${p === tabPage
                              ? "bg-indigo-600 dark:bg-indigo-600 text-white"
                              : "text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04]"
                              }`}
                          >
                            {p}
                          </button>
                        ),
                      )}

                      <button
                        disabled={tabPage >= Math.ceil(filtInfoTypes.length / tabPageSize)}
                        onClick={() => setCurrentTabPagination({ page: Math.min(Math.ceil(filtInfoTypes.length / tabPageSize), tabPage + 1), pageSize: tabPageSize })}
                        className="p-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronRight className="size-3" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="rounded-lg border border-border bg-card p-5 text-center py-12">
              <Shield className="size-8 text-muted-foreground mb-3 mx-auto" />
              <p className="text-sm font-medium">
                No information types defined
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Add information types processed by this system
              </p>
            </div>
          )}
        </>
      )}

      {/* ═══ Inventory Tab ═══ */}
      {activeTab === "Inventory" && (
        <>
          {inventoryLoading ? (
            <LoadingSpinner text="Loading inventory" />
          ) : inventoryItems.length > 0 ? (
            <>
              <div className="rounded-lg border border-border bg-card overflow-hidden">
                <div className="divide-y divide-border">
                  {pagInventory.map((item) => (
                    <div key={item.id} className="px-5 py-3.5 hover:bg-muted/40 transition-colors">
                      {editingInventoryId === item.id ? (
                        <div className="space-y-3">
                          <FormField label="Description" required>
                            <FormTextarea
                              value={editInventoryForm.description}
                              onChange={(v) => setEditInventoryForm((f) => ({ ...f, description: v }))}
                              placeholder="Asset description"
                            />
                          </FormField>
                          <FormField label="Component">
                            <FormSelect
                              options={sspComponents.map((c) => ({ label: c.title, value: c.id }))}
                              value={editInventoryForm.componentId}
                              onChange={(v) => setEditInventoryForm((f) => ({ ...f, componentId: v }))}
                              placeholder="Select component"
                            />
                          </FormField>
                          <div className="flex items-center gap-2 pt-1">
                            <button
                              onClick={() => setEditingInventoryId(null)}
                              className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-accent transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleUpdateInventoryItem}
                              disabled={!editInventoryForm.description || savingInventoryItem}
                              className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 transition-colors disabled:opacity-50"
                            >
                              {savingInventoryItem ? "Saving" : "Save"}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start gap-4">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-foreground break-words">{item.description}</p>
                            <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                              {item.componentName && (
                                <span className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-500/20 capitalize">
                                  {item.componentName}
                                </span>
                              )}
                              {item.assetIdentifiers.map((ai) => {
                                const key = ai.identifierType.toLowerCase();
                                const tint =
                                  /host|fqdn|dns/.test(key)
                                    ? "bg-blue-50 border-blue-100 text-blue-700 dark:bg-blue-500/10 dark:border-blue-500/20 dark:text-blue-400"
                                    : /ip|network|address/.test(key)
                                      ? "bg-emerald-50 border-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:border-emerald-500/20 dark:text-emerald-400"
                                      : /type|category|class/.test(key)
                                        ? "bg-amber-50 border-amber-100 text-amber-700 dark:bg-amber-500/10 dark:border-amber-500/20 dark:text-amber-400"
                                        : /mac/.test(key)
                                          ? "bg-rose-50 border-rose-100 text-rose-700 dark:bg-rose-500/10 dark:border-rose-500/20 dark:text-rose-400"
                                          : /uuid|id/.test(key)
                                            ? "bg-violet-50 border-violet-100 text-violet-700 dark:bg-violet-500/10 dark:border-violet-500/20 dark:text-violet-400"
                                            : "bg-slate-50 border-slate-200 text-slate-700 dark:bg-slate-500/10 dark:border-slate-500/20 dark:text-slate-300";
                                return (
                                  <span
                                    key={ai.id}
                                    className={`inline-flex items-start gap-1.5 px-2 py-0.5 rounded-md border text-[11px] font-mono max-w-full ${tint}`}
                                  >
                                    <span className="font-semibold tracking-wide text-[10px] flex-shrink-0 capitalize opacity-80">
                                      {ai.identifierType.replace(/[_-]/g, " ")}:
                                    </span>
                                    <span className="break-all min-w-0">
                                      {ai.identifierValue}
                                    </span>
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                          <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5 flex-shrink-0 mt-0.5">
                            <Calendar className="size-3.5" />
                            <span>{toddmmyyyy(item.updatedAt || item.createdAt)}</span>
                          </div>
                          {!isPublishedSsp && (
                            <div className="flex items-center gap-1 flex-shrink-0">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={() => {
                                      setEditingInventoryId(item.id);
                                      setEditInventoryForm({
                                        description: item.description,
                                        componentId: item.componentId || "",
                                        identifiers: item.assetIdentifiers.map((ai) => ({
                                          identifierType: ai.identifierType,
                                          identifierValue: ai.identifierValue,
                                        })),
                                      });
                                    }}
                                    className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                                  >
                                    <Pencil className="size-3.5" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>Edit</TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={() => requestDelete("Inventory Item", item.id, item.description, () => handleDeleteInventoryItem(item.id))}
                                    className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-500/10 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-colors"
                                  >
                                    <Trash2 className="size-3.5" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>Delete</TooltipContent>
                              </Tooltip>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              {filtInventory.length > 0 && (
                <div className="flex items-center justify-between gap-2 px-4 py-2 border-t border-[#e2e8f0] dark:border-white/[0.06] bg-[#fafbfc] dark:bg-white/[0.015] flex-shrink-0">
                  <p className="text-[11px] leading-tight text-[#475569] dark:text-white/70">
                    Showing{" "}
                    <span className="font-semibold text-[#334155] dark:text-white/90">
                      {Math.min((tabPage - 1) * tabPageSize + 1, filtInventory.length)}–{Math.min(tabPage * tabPageSize, filtInventory.length)}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold text-[#334155] dark:text-white/90">
                      {filtInventory.length}
                    </span>
                  </p>
                  {Math.ceil(filtInventory.length / tabPageSize) > 1 && (
                    <div className="flex items-center gap-0.5">
                      <button
                        disabled={tabPage === 1}
                        onClick={() => setCurrentTabPagination({ page: Math.max(1, tabPage - 1), pageSize: tabPageSize })}
                        className="p-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronLeft className="size-3" />
                      </button>

                      {getPageWindow(tabPage, Math.ceil(filtInventory.length / tabPageSize)).map((p, idx) =>
                        p === "dots" ? (
                          <span
                            key={`dots-${idx}`}
                            className="w-6 h-6 flex items-center justify-center text-xs text-[#475569] dark:text-white/70 select-none"
                          >
                            …
                          </span>
                        ) : (
                          <button
                            key={p}
                            onClick={() => setCurrentTabPagination({ page: p as number, pageSize: tabPageSize })}
                            className={`w-6 h-6 text-xs font-medium rounded-md transition-colors ${p === tabPage
                              ? "bg-indigo-600 dark:bg-indigo-600 text-white"
                              : "text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04]"
                              }`}
                          >
                            {p}
                          </button>
                        ),
                      )}

                      <button
                        disabled={tabPage >= Math.ceil(filtInventory.length / tabPageSize)}
                        onClick={() => setCurrentTabPagination({ page: Math.min(Math.ceil(filtInventory.length / tabPageSize), tabPage + 1), pageSize: tabPageSize })}
                        className="p-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronRight className="size-3" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="rounded-lg border border-border bg-card p-5 text-center py-12">
              <Server className="size-8 text-muted-foreground mb-3 mx-auto" />
              <p className="text-sm font-medium">No inventory items</p>
              <p className="text-xs text-muted-foreground mt-1">
                Add assets to the system inventory
              </p>
            </div>
          )}
        </>
      )}

      {/* ═══ Components Tab ═══ */}
      {activeTab === "Components" && (
        <>
          {/* ── Component Detail View ── */}
          {selectedComponentUuid ? (
            (() => {
              const comp = sspComponents.find((c) => c.componentUuid === selectedComponentUuid || c.id === selectedComponentUuid);
              // ── Assets pagination (client-side) ──
              const invStart = compInvPage * compPageSize;
              const shownInventory = compInventory.slice(
                invStart,
                invStart + compPageSize,
              );
              const invTotal = compInventory.length;
              const invHasNext =
                (compInvPage + 1) * compPageSize < compInventory.length;
              // ── Controls pagination (client-side) ──
              const ctrlStart = compCtrlPage * compPageSize;
              const shownControls = compControls.slice(
                ctrlStart,
                ctrlStart + compPageSize,
              );
              const ctrlTotal = compControls.length;
              const ctrlHasNext =
                (compCtrlPage + 1) * compPageSize < ctrlTotal;
              return (
                <div className="space-y-4">
                  {/* Back button + header */}
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => { setSelectedComponentUuid(null); setEditingComponentId(null); }}
                      className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <ChevronLeft className="size-3.5" /> Back to components
                    </button>
                  </div>
                  {comp && (
                    <div className="rounded-lg border border-border bg-card p-4">
                      <div className="flex items-start gap-3">
                        <div className="size-9 rounded-md bg-muted flex items-center justify-center flex-shrink-0">
                          <Server className="size-4 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold">{comp.title}</p>
                          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-muted text-muted-foreground capitalize">{comp.componentType}</span>
                            {(comp.displayStatusLabel || comp.displayStatus) && (
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${getDisplayStatusStyle(comp.displayStatus)}`}>
                                {comp.displayStatusLabel || comp.displayStatus}
                              </span>
                            )}
                          </div>
                          {comp.description && <p className="text-xs text-muted-foreground mt-1">{comp.description}</p>}
                        </div>
                      </div>
                    </div>
                  )}
                  {/* Sub-tabs */}
                  <div className="flex items-center gap-1 border-b border-border">
                    {(["inventory", "controls"] as const).map((t) => (
                      <button
                        key={t}
                        onClick={() => setCompDetailTab(t)}
                        className={`px-3 py-2 text-xs font-medium border-b-2 transition-colors capitalize ${compDetailTab === t ? "border-indigo-500 text-indigo-600 dark:text-indigo-400" : "border-transparent text-muted-foreground hover:text-foreground"}`}
                      >
                        {t === "inventory" ? `Assets${comp?.assetCount != null ? ` (${comp.assetCount})` : ""}` : `Controls${comp?.implementedControlCount != null ? ` (${comp.implementedControlCount})` : ""}`}
                      </button>
                    ))}
                  </div>
                  {compDetailLoading ? (
                    <LoadingSpinner text="Loading" />
                  ) : compDetailTab === "inventory" ? (
                    compInventory.length > 0 ? (
                      <div className="rounded-lg border border-border bg-card flex flex-col h-[460px] overflow-hidden">
                        {/* Header: title + rows per page (top-right) */}
                        <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-border flex-shrink-0">
                          <span className="text-xs font-semibold text-foreground">Assets</span>
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-muted-foreground">Rows per page</span>
                            <Dropdown
                              value={String(compPageSize)}
                              onChange={(v) => { setCompPageSize(Number(v)); setCompInvPage(0); setCompCtrlPage(0); }}
                              options={COMP_DETAIL_PAGE_SIZE_OPTIONS}
                              variant="pill"
                            />
                          </div>
                        </div>
                        {/* Scrollable body */}
                        <div className="flex-1 overflow-y-auto divide-y divide-border">
                          {shownInventory.map((item) => (
                            <div key={item.id} className="flex items-start gap-3 px-4 py-3">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium">{item.description}</p>
                                <div className="flex items-center gap-2 mt-1 flex-wrap">
                                  {item.assetIdentifiers.map((ai) => (
                                    <span key={ai.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-muted text-[10px] font-mono">
                                      <span className="text-muted-foreground">{ai.identifierType}:</span> {ai.identifierValue}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                        {/* Footer: showing + pager */}
                        <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-2.5 border-t border-border flex-shrink-0">
                          <p className="text-xs text-muted-foreground">
                            Showing{" "}
                            <span className="font-semibold text-[#334155] dark:text-white/90">
                              {invStart + 1}–{invStart + shownInventory.length}
                            </span>{" "}
                            of{" "}
                            <span className="font-semibold text-[#334155] dark:text-white/90">
                              {invTotal}
                            </span>
                          </p>
                          {(compInvPage > 0 || invHasNext) && (
                            <div className="flex items-center gap-2">
                              <button
                                disabled={compInvPage === 0}
                                onClick={() => setCompInvPage((p) => Math.max(0, p - 1))}
                                className="flex items-center gap-1 px-2.5 py-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-xs text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                              >
                                <ChevronLeft className="size-3" /> Prev
                              </button>
                              <span className="text-xs text-muted-foreground">
                                Page {compInvPage + 1}
                              </span>
                              <button
                                disabled={!invHasNext}
                                onClick={() => setCompInvPage((p) => p + 1)}
                                className="flex items-center gap-1 px-2.5 py-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-xs text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                              >
                                Next <ChevronRight className="size-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-8">No assets linked to this component.</p>
                    )
                  ) : (
                    compControls.length > 0 ? (
                      <div className="rounded-lg border border-border bg-card flex flex-col h-[460px] overflow-hidden">
                        {/* Header: title + rows per page (top-right) */}
                        <div className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-border flex-shrink-0">
                          <span className="text-xs font-semibold text-foreground">Controls</span>
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs text-muted-foreground">Rows per page</span>
                            <Dropdown
                              value={String(compPageSize)}
                              onChange={(v) => { setCompPageSize(Number(v)); setCompInvPage(0); setCompCtrlPage(0); }}
                              options={COMP_DETAIL_PAGE_SIZE_OPTIONS}
                              variant="pill"
                            />
                          </div>
                        </div>
                        {/* Column headers */}
                        <div className="grid grid-cols-[90px_minmax(0,1fr)_minmax(0,2fr)_150px] gap-4 px-4 py-2 bg-muted/40 border-b border-border text-[10px] font-bold tracking-wider text-muted-foreground flex-shrink-0">
                          <span>Control ID</span>
                          <span>Control Title</span>
                          <span>Statement</span>
                          <span className="text-center">Implementation Status</span>
                        </div>
                        {/* Scrollable body */}
                        <div className="flex-1 overflow-y-auto divide-y divide-border">
                          {shownControls.map((ctrl) => {
                            const controlTitle = ctrl.controlTitle ?? ctrl.title ?? null;
                            const s = (ctrl.implementationStatus || "").toLowerCase();
                            const statusStyle =
                              s === "implemented"
                                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400"
                                : s === "partial"
                                  ? "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400"
                                  : s === "planned"
                                    ? "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400"
                                    : s === "alternative"
                                      ? "bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400"
                                      : s === "not-implemented"
                                        ? "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-400"
                                        : "bg-muted text-muted-foreground";
                            return (
                              <div key={ctrl.implementedRequirementId} className="grid grid-cols-[90px_minmax(0,1fr)_minmax(0,2fr)_150px] gap-4 items-center px-4 py-3">
                                <span className="text-xs font-mono font-bold text-indigo-600 dark:text-indigo-400 truncate">{ctrl.controlId?.toUpperCase()}</span>
                                <p className="text-xs font-semibold text-foreground truncate" title={controlTitle || ""}>{controlTitle || "—"}</p>
                                <p className="text-[11px] text-muted-foreground truncate" title={ctrl.statement || ctrl.description || ""}>{ctrl.statement || ctrl.description || "—"}</p>
                                <div className="flex justify-center">
                                  {ctrl.implementationStatus ? (
                                    <span className={`inline-flex items-center justify-center w-[110px] px-3 py-0.5 rounded-full text-[10px] font-bold ${statusStyle}`}>
                                      {ctrl.implementationStatus.replace(/[-_]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                                    </span>
                                  ) : (
                                    <span className="text-muted-foreground text-[10px]">—</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        {/* Footer: showing + pager */}
                        <div className="flex items-center justify-between gap-3 flex-wrap px-4 py-2.5 border-t border-border flex-shrink-0">
                          <p className="text-xs text-muted-foreground">
                            Showing{" "}
                            <span className="font-semibold text-[#334155] dark:text-white/90">
                              {ctrlStart + 1}–{ctrlStart + shownControls.length}
                            </span>{" "}
                            of{" "}
                            <span className="font-semibold text-[#334155] dark:text-white/90">
                              {ctrlTotal}
                            </span>
                          </p>
                          {(compCtrlPage > 0 || ctrlHasNext) && (
                            <div className="flex items-center gap-2">
                              <button
                                disabled={compCtrlPage === 0}
                                onClick={() => setCompCtrlPage((p) => Math.max(0, p - 1))}
                                className="flex items-center gap-1 px-2.5 py-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-xs text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                              >
                                <ChevronLeft className="size-3" /> Prev
                              </button>
                              <span className="text-xs text-muted-foreground">
                                Page {compCtrlPage + 1}
                              </span>
                              <button
                                disabled={!ctrlHasNext}
                                onClick={() => setCompCtrlPage((p) => p + 1)}
                                className="flex items-center gap-1 px-2.5 py-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-xs text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                              >
                                Next <ChevronRight className="size-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-8">No controls implemented by this component.</p>
                    )
                  )}
                </div>
              );
            })()
          ) : componentsLoading ? (
            <LoadingSpinner text="Loading components" />
          ) : sspComponents.length > 0 ? (
            <div className="space-y-4">
              {/* Component delete error banner */}
              {componentDeleteError && (
                <div className="bg-red-50 dark:bg-red-500/10 border border-red-300 dark:border-red-500/30 text-red-800 dark:text-red-300 rounded-md p-3 text-sm flex items-start gap-2">
                  <AlertCircle className="size-4 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    {componentDeleteError}
                    <button onClick={() => setComponentDeleteError(null)} className="ml-2 text-xs underline">Dismiss</button>
                  </div>
                </div>
              )}

              {/* Filter bar */}
              <div className="flex items-center gap-3 flex-wrap">
                {availableComponentTypes.length > 1 && (
                  <Dropdown
                    value={componentTypeFilter}
                    onChange={(v) => setComponentTypeFilter(v)}
                    options={[{ value: "", label: "All Types" }, ...availableComponentTypes.map((t) => ({ value: t, label: t }))]}
                    variant="pill"
                  />
                )}
                {availableGroupNames.length > 1 && (
                  <Dropdown
                    value={componentGroupFilter}
                    onChange={(v) => setComponentGroupFilter(v)}
                    options={[{ value: "", label: "All Groups" }, ...availableGroupNames.map((g) => ({ value: g, label: g }))]}
                    variant="pill"
                  />
                )}
                {(componentSearchFilter || componentTypeFilter || componentGroupFilter) && (
                  <button
                    onClick={() => { setComponentSearchFilter(""); setComponentTypeFilter(""); setComponentGroupFilter(""); }}
                    className="px-2.5 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Clear filters
                  </button>
                )}
              </div>

              {/* ── Cylindrical SSP Layer View ── */}
              {(() => {
                // Flatten currently-filtered components from componentGroups
                const filteredComps = Object.values(componentGroups).flat();
                // Bucket by SSP layer
                const layerBuckets: Record<SspLayerKey, typeof filteredComps> = {
                  application: [],
                  data: [],
                  runtime: [],
                  compute: [],
                  storage: [],
                  platform: [],
                  network: [],
                };
                for (const comp of filteredComps) {
                  layerBuckets[getComponentLayer(comp)].push(comp);
                }
                const totalComponents = filteredComps.length;
                const layersWithItems = SSP_LAYERS.filter(
                  (l) => layerBuckets[l.key].length > 0,
                ).length;
                const coveragePct =
                  SSP_LAYERS.length > 0
                    ? Math.round((layersWithItems / SSP_LAYERS.length) * 100)
                    : 0;
                const activeLayer =
                  SSP_LAYERS.find((l) => l.key === selectedLayerKey) ??
                  SSP_LAYERS[0];
                const layerComps = layerBuckets[activeLayer.key];

                if (totalComponents === 0) {
                  return (
                    <p className="text-sm text-muted-foreground text-center py-8">
                      No components match the current filters.
                    </p>
                  );
                }

                return (
                  <div className="rounded-2xl border border-slate-200 dark:border-slate-800/60 bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-slate-950 dark:via-[#0c1126] dark:to-slate-950 p-4 sm:p-6 text-slate-900 dark:text-white shadow-xl overflow-hidden">
                    <div className="grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)_minmax(0,1.05fr)] gap-5">
                      {/* ── Component Overview ── */}
                      <div className="rounded-xl bg-slate-100/70 dark:bg-white/[0.03] border border-slate-200 dark:border-white/[0.08] p-4 flex flex-col gap-3">
                        <div>
                          <h4 className="text-sm font-bold text-slate-900 dark:text-white">
                            Component Overview
                          </h4>
                          <p className="text-[11px] text-slate-600 dark:text-white/60 leading-relaxed mt-1">
                            All components organized across {SSP_LAYERS.length}{" "}
                            security plan layers
                          </p>
                        </div>
                        <div className="grid gap-2">
                          <div className="flex items-center gap-3 rounded-lg bg-white dark:bg-white/[0.04] border border-slate-200 dark:border-white/[0.06] px-3 py-2.5">
                            <div className="size-9 rounded-md bg-indigo-100 dark:bg-indigo-500/15 border border-indigo-300 dark:border-indigo-400/30 flex items-center justify-center flex-shrink-0">
                              <Boxes className="size-4 text-indigo-600 dark:text-indigo-300" />
                            </div>
                            <div>
                              <p className="text-base font-bold leading-none">
                                {totalComponents}
                              </p>
                              <p className="text-[10px] tracking-wider text-slate-500 dark:text-white/60 mt-1">
                                Total Components
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 rounded-lg bg-white dark:bg-white/[0.04] border border-slate-200 dark:border-white/[0.06] px-3 py-2.5">
                            <div className="size-9 rounded-md bg-emerald-100 dark:bg-emerald-500/15 border border-emerald-300 dark:border-emerald-400/30 flex items-center justify-center flex-shrink-0">
                              <Layers className="size-4 text-emerald-600 dark:text-emerald-300" />
                            </div>
                            <div>
                              <p className="text-base font-bold leading-none">
                                {SSP_LAYERS.length}
                              </p>
                              <p className="text-[10px] tracking-wider text-slate-500 dark:text-white/60 mt-1">
                                SSP Layers
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 rounded-lg bg-white dark:bg-white/[0.04] border border-slate-200 dark:border-white/[0.06] px-3 py-2.5">
                            <div className="size-9 rounded-md bg-violet-100 dark:bg-violet-500/15 border border-violet-300 dark:border-violet-400/30 flex items-center justify-center flex-shrink-0">
                              <ShieldCheck className="size-4 text-violet-600 dark:text-violet-300" />
                            </div>
                            <div>
                              <p className="text-base font-bold leading-none">
                                {coveragePct}%
                              </p>
                              <p className="text-[10px] tracking-wider text-slate-500 dark:text-white/60 mt-1">
                                Coverage
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* ── SSP Layer Rows (flat cards with subtle gradient tint) ── */}
                      <div className="relative flex flex-col justify-center py-2 mx-auto w-full">
                        <div className="relative w-full flex flex-col gap-2.5">
                          {SSP_LAYERS.map((layer) => {
                            const count = layerBuckets[layer.key].length;
                            const active = selectedLayerKey === layer.key;
                            const Icon = layer.icon;
                            const [, mid] = layer.colors;
                            return (
                              <button
                                key={layer.key}
                                onClick={() =>
                                  setSelectedLayerKey(layer.key)
                                }
                                className="group relative block w-full rounded-2xl border transition-all duration-300 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent overflow-hidden"
                                style={{
                                  background: isDark
                                    ? `linear-gradient(90deg, ${mid}26 0%, ${mid}10 50%, ${mid}22 100%)`
                                    : `linear-gradient(90deg, ${mid}18 0%, ${mid}08 50%, ${mid}14 100%)`,
                                  borderColor: active
                                    ? mid
                                    : isDark
                                      ? `${mid}45`
                                      : `${mid}35`,
                                  boxShadow: active
                                    ? isDark
                                      ? `0 10px 28px -10px ${mid}80, 0 0 0 1px ${mid}66 inset`
                                      : `0 10px 24px -10px ${mid}60, 0 0 0 1px ${mid}55 inset`
                                    : undefined,
                                  transform: active
                                    ? "translateX(4px) scale(1.01)"
                                    : undefined,
                                }}
                              >
                                {/* Left accent bar — expands on hover/active (transition only during hover, so leaving snaps back) */}
                                <span
                                  aria-hidden
                                  className={`pointer-events-none absolute left-0 top-0 h-full rounded-l-2xl group-hover:transition-all group-hover:duration-300 group-hover:ease-out ${active
                                    ? "w-1"
                                    : "w-0 group-hover:w-1"
                                    }`}
                                  style={{ background: mid }}
                                />
                                {/* Sheen sweep on hover */}
                                <span
                                  aria-hidden
                                  className="pointer-events-none absolute inset-y-0 left-0 w-1/3 -translate-x-full skew-x-12 bg-gradient-to-r from-transparent via-white/20 to-transparent group-hover:translate-x-[320%] group-hover:transition-transform group-hover:duration-[900ms] group-hover:ease-out"
                                />
                                <div className="relative flex items-center justify-between gap-3 px-4 py-3.5">
                                  <div className="flex items-center gap-3 min-w-0">
                                    <div
                                      className="size-10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:transition-transform group-hover:duration-300 group-hover:ease-out group-hover:scale-110"
                                      style={{
                                        background: isDark
                                          ? `${mid}2a`
                                          : `${mid}1c`,
                                        border: `1px solid ${mid}55`,
                                      }}
                                    >
                                      <Icon
                                        className="size-5"
                                        style={{ color: mid }}
                                      />
                                    </div>
                                    <span
                                      className={`text-[14px] font-bold tracking-tight truncate ${isDark
                                        ? "text-white"
                                        : "text-slate-800"
                                        }`}
                                    >
                                      {layer.label}
                                    </span>
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    <span
                                      className="inline-flex items-center justify-center min-w-[30px] h-7 px-2.5 rounded-full text-[13px] font-bold tabular-nums group-hover:transition-transform group-hover:duration-300 group-hover:ease-out group-hover:scale-110"
                                      style={{
                                        background: isDark
                                          ? `${mid}22`
                                          : `${mid}18`,
                                        color: mid,
                                        border: `1px solid ${mid}40`,
                                      }}
                                    >
                                      {count}
                                    </span>
                                    <ChevronRight
                                      className={`size-4 group-hover:transition-transform group-hover:duration-300 group-hover:ease-out ${active
                                        ? "translate-x-1"
                                        : "group-hover:translate-x-1"
                                        }`}
                                      style={{
                                        color: isDark
                                          ? "rgba(255,255,255,0.65)"
                                          : "rgba(71,85,105,0.75)",
                                      }}
                                    />
                                  </div>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* ── Selected Layer Components ── */}
                      <div className="rounded-xl bg-slate-100/70 dark:bg-white/[0.03] border border-slate-200 dark:border-white/[0.08] p-4 flex flex-col">
                        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span
                              className={`size-2 rounded-full ${activeLayer.dot}`}
                            />
                            <h4 className="text-sm font-bold text-slate-900 dark:text-white truncate">
                              Components in {activeLayer.label}
                            </h4>
                          </div>
                          <span className="text-[10px] font-bold tracking-wider text-slate-500 dark:text-white/60">
                            {layerComps.length}{" "}
                            {layerComps.length === 1
                              ? "Component"
                              : "Components"}
                          </span>
                        </div>
                        {layerComps.length === 0 ? (
                          <div className="flex-1 flex flex-col items-center justify-center text-center py-10 px-3">
                            <Server className="size-7 text-slate-400 dark:text-white/30 mb-2" />
                            <p className="text-sm font-medium text-slate-700 dark:text-white/80">
                              No components in this layer
                            </p>
                            <p className="text-[11px] text-slate-500 dark:text-white/50 mt-1">
                              Add components and tag them with this layer
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
                            {layerComps.map((comp) =>
                              editingComponentId === comp.id ? (
                                <div
                                  key={comp.id}
                                  className="rounded-lg bg-white dark:bg-white/[0.04] border border-slate-200 dark:border-white/[0.1] p-3 space-y-3 text-foreground"
                                >
                                  <div className="grid grid-cols-2 gap-3">
                                    <FormField label="Title" required>
                                      <FormInput
                                        value={editComponentForm.title}
                                        onChange={(v) =>
                                          setEditComponentForm((f) => ({
                                            ...f,
                                            title: v,
                                          }))
                                        }
                                        placeholder="Component title"
                                      />
                                    </FormField>
                                    <FormField label="Type">
                                      <FormSelect
                                        options={COMPONENT_TYPES}
                                        value={editComponentForm.componentType}
                                        onChange={(v) =>
                                          setEditComponentForm((f) => ({
                                            ...f,
                                            componentType: v,
                                          }))
                                        }
                                        placeholder="Select type"
                                      />
                                    </FormField>
                                  </div>
                                  <FormField label="Description">
                                    <FormTextarea
                                      value={editComponentForm.description}
                                      onChange={(v) =>
                                        setEditComponentForm((f) => ({
                                          ...f,
                                          description: v,
                                        }))
                                      }
                                      placeholder="Description"
                                    />
                                  </FormField>
                                  <div className="grid grid-cols-2 gap-3">
                                    <FormField label="Purpose">
                                      <FormInput
                                        value={editComponentForm.purpose}
                                        onChange={(v) =>
                                          setEditComponentForm((f) => ({
                                            ...f,
                                            purpose: v,
                                          }))
                                        }
                                        placeholder="Purpose"
                                      />
                                    </FormField>
                                    <FormField label="Status">
                                      <FormSelect
                                        options={SYSTEM_STATUSES}
                                        value={editComponentForm.status}
                                        onChange={(v) =>
                                          setEditComponentForm((f) => ({
                                            ...f,
                                            status: v,
                                          }))
                                        }
                                        placeholder="Select status"
                                      />
                                    </FormField>
                                  </div>
                                  <div className="flex items-center gap-2 pt-1">
                                    <button
                                      onClick={() =>
                                        setEditingComponentId(null)
                                      }
                                      className="px-3 py-1.5 rounded-lg border border-slate-300 dark:border-white/15 text-xs font-medium text-slate-700 dark:text-white hover:bg-slate-100 dark:hover:bg-white/[0.06] transition-colors"
                                    >
                                      Cancel
                                    </button>
                                    <button
                                      onClick={handleUpdateComponent}
                                      disabled={
                                        !editComponentForm.title ||
                                        savingComponent
                                      }
                                      className="px-3 py-1.5 rounded-lg bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 transition-colors disabled:opacity-50"
                                    >
                                      {savingComponent ? "Saving" : "Save"}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div
                                  key={comp.id}
                                  className="group rounded-lg bg-white dark:bg-white/[0.04] hover:bg-slate-50 dark:hover:bg-white/[0.07] border border-slate-200 dark:border-white/[0.08] hover:border-slate-300 dark:hover:border-white/[0.18] p-3 transition-all"
                                >
                                  <div className="flex items-start gap-3">
                                    <div
                                      className={`size-9 rounded-md bg-gradient-to-br ${activeLayer.gradient} flex items-center justify-center flex-shrink-0 shadow-inner`}
                                    >
                                      <Server className="size-4 text-white" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <button
                                        onClick={() =>
                                          handleOpenComponentDetail(comp.id)
                                        }
                                        className="text-sm font-semibold text-slate-900 dark:text-white hover:text-indigo-600 dark:hover:text-indigo-300 transition-colors text-left truncate w-full"
                                      >
                                        {comp.title}
                                      </button>
                                      <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-100 dark:bg-white/[0.08] text-slate-700 dark:text-white/80 capitalize">
                                          {comp.componentType}
                                        </span>
                                        {(comp.implementedControlCount ?? 0) > 0 &&
                                          (comp.displayStatusLabel || comp.displayStatus) && (
                                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${getDisplayStatusStyle(comp.displayStatus)}`}>
                                              {comp.displayStatusLabel || comp.displayStatus}
                                            </span>
                                          )}
                                      </div>
                                    </div>
                                    {!isPublishedSsp && (
                                      <div className="flex items-center gap-1 flex-shrink-0 opacity-70 group-hover:opacity-100 transition-opacity">
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <button
                                              onClick={() => {
                                                setEditingComponentId(comp.id);
                                                setEditComponentForm({
                                                  title: comp.title,
                                                  componentType:
                                                    comp.componentType || "",
                                                  description:
                                                    comp.description || "",
                                                  purpose: comp.purpose || "",
                                                  status: comp.status || "",
                                                  statusRemarks:
                                                    comp.statusRemarks || "",
                                                });
                                              }}
                                              className="p-1.5 rounded-md hover:bg-slate-100 dark:hover:bg-white/[0.1] text-slate-500 dark:text-white/70 hover:text-slate-900 dark:hover:text-white transition-colors"
                                            >
                                              <Pencil className="size-3.5" />
                                            </button>
                                          </TooltipTrigger>
                                          <TooltipContent>Edit</TooltipContent>
                                        </Tooltip>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <button
                                              onClick={() =>
                                                requestDelete(
                                                  "Component",
                                                  comp.componentUuid,
                                                  comp.title,
                                                  () =>
                                                    handleDeleteComponent(
                                                      comp.componentUuid,
                                                    ),
                                                )
                                              }
                                              disabled={
                                                deletingComponentId ===
                                                comp.componentUuid
                                              }
                                              className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-500/15 text-slate-500 dark:text-white/70 hover:text-red-600 dark:hover:text-red-300 transition-colors disabled:opacity-50"
                                            >
                                              <Trash2 className="size-3.5" />
                                            </button>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            Delete
                                          </TooltipContent>
                                        </Tooltip>
                                      </div>
                                    )}
                                  </div>
                                  {comp.description && (
                                    <p className="text-[11px] text-slate-600 dark:text-white/60 leading-relaxed line-clamp-2 mt-2">
                                      {comp.description}
                                    </p>
                                  )}
                                  <div className="flex items-center gap-4 mt-2 pt-2 border-t border-slate-200 dark:border-white/[0.08]">
                                    <button
                                      onClick={() =>
                                        handleOpenComponentDetail(comp.id)
                                      }
                                      className="flex items-center gap-1 text-[11px] text-slate-600 dark:text-white/60 hover:text-slate-900 dark:hover:text-white transition-colors"
                                    >
                                      <span className="font-bold text-slate-900 dark:text-white">
                                        {comp.assetCount ?? 0}
                                      </span>{" "}
                                      Assets
                                    </button>
                                    <button
                                      onClick={() => {
                                        handleOpenComponentDetail(comp.id);
                                        setCompDetailTab("controls");
                                      }}
                                      className="flex items-center gap-1 text-[11px] text-slate-600 dark:text-white/60 hover:text-slate-900 dark:hover:text-white transition-colors"
                                    >
                                      {comp.displayStatus === "CONTROLS_ASSIGNED" ? (
                                        <span>{getControlCountLabel(comp)}</span>
                                      ) : (
                                        <>
                                          <span className="font-bold text-slate-900 dark:text-white">
                                            {comp.implementedControlCount ?? 0}
                                          </span>{" "}
                                          Controls
                                        </>
                                      )}
                                    </button>
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          ) : (
            <div className="rounded-lg border border-border bg-card p-5 text-center py-12">
              <Server className="size-8 text-muted-foreground mb-3 mx-auto" />
              <p className="text-sm font-medium">No components defined</p>
              <p className="text-xs text-muted-foreground mt-1">Add components to this SSP</p>
            </div>
          )}
        </>
      )}

      {/* ═══ Control Implementations Tab ═══ */}
      {activeTab === "Control Implementations" && (
        <ControlImplementationsTab
          sspId={selectedSSPId!}
          sourceType={sspList.find((s) => s.id === selectedSSPId)?.sourceType}
          lifecycleStatus={getSelectedSspLifecycle()}
        />
      )}

      {/* ═══ LEGACY BLOCK REMOVED — kept below for reference, delete after QA ═══ */}
      {false && (
        <>
          {/* Coverage summary from baseline controls */}
          {!baselineControlsLoading &&
            baselineControls.length > 0 &&
            (() => {
              const total = baselineControls.length;
              const implemented = baselineControls.filter(
                (c) => c.implementationStatus?.toLowerCase() === "implemented",
              ).length;
              const partial = baselineControls.filter((c) => {
                const s = c.implementationStatus?.toLowerCase();
                return s === "partial" || s === "partially-implemented";
              }).length;
              const planned = baselineControls.filter(
                (c) => c.implementationStatus?.toLowerCase() === "planned",
              ).length;
              const notImplemented = baselineControls.filter((c) => {
                const s = c.implementationStatus?.toLowerCase();
                return (
                  s === "not-implemented" ||
                  s === "not implemented" ||
                  !c.implementationStatus
                );
              }).length;
              const other =
                total - implemented - partial - planned - notImplemented;
              const coveredPct =
                total > 0
                  ? Math.round(((implemented + partial) / total) * 100)
                  : 0;
              return (
                <div className="rounded-xl border border-border bg-card p-5 space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-bold text-foreground">
                      {implemented + partial} of {total} controls Implemented (
                      {coveredPct}%)
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden flex">
                    {implemented > 0 && (
                      <div
                        className="bg-emerald-500 transition-all"
                        style={{ width: `${(implemented / total) * 100}%` }}
                      />
                    )}
                    {partial > 0 && (
                      <div
                        className="bg-amber-500 transition-all"
                        style={{ width: `${(partial / total) * 100}%` }}
                      />
                    )}
                    {planned > 0 && (
                      <div
                        className="bg-blue-400 transition-all"
                        style={{ width: `${(planned / total) * 100}%` }}
                      />
                    )}
                    {notImplemented > 0 && (
                      <div
                        className="bg-slate-300 dark:bg-slate-600 transition-all"
                        style={{ width: `${(notImplemented / total) * 100}%` }}
                      />
                    )}
                    {other > 0 && (
                      <div
                        className="bg-red-400 transition-all"
                        style={{ width: `${(other / total) * 100}%` }}
                      />
                    )}
                  </div>
                  <div className="flex items-center gap-4 flex-wrap">
                    <span className="flex items-center gap-1.5 text-xs">
                      <span className="size-2.5 rounded-full bg-emerald-500" />
                      <span className="font-medium">{implemented}</span>{" "}
                      Implemented
                    </span>
                    <span className="flex items-center gap-1.5 text-xs">
                      <span className="size-2.5 rounded-full bg-amber-500" />
                      <span className="font-medium">{partial}</span> Partial
                    </span>
                    <span className="flex items-center gap-1.5 text-xs">
                      <span className="size-2.5 rounded-full bg-blue-400" />
                      <span className="font-medium">{planned}</span> Planned
                    </span>
                    <span className="flex items-center gap-1.5 text-xs">
                      <span className="size-2.5 rounded-full bg-slate-300 dark:bg-slate-600" />
                      <span className="font-medium">{notImplemented}</span> Not
                      Implemented
                    </span>
                  </div>
                </div>
              );
            })()}

          {baselineControlsLoading ? (
            <LoadingSpinner text="Loading control implementations" />
          ) : baselineControls.length > 0 ? (
            (() => {
              const filtered = baselineControls.filter((bc) => {
                const controlId = getBaselineControlId(bc);
                return (
                  !tabSearch ||
                  controlId.toLowerCase().includes(tabSearch.toLowerCase()) ||
                  (
                    bc.controlTitle ||
                    bc.controlDetails?.title ||
                    bc.title ||
                    ""
                  )
                    .toLowerCase()
                    .includes(tabSearch.toLowerCase())
                );
              });
              const paginated = filtered.slice(
                (tabPage - 1) * tabPageSize,
                tabPage * tabPageSize,
              );
              return (
                <>
                  <div className="rounded-xl border border-border bg-card overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm table-fixed">
                        <colgroup>
                          <col className="w-[40px]" />
                          <col className="w-[90px]" />
                          <col style={{ width: "22%" }} />
                          <col style={{ width: "26%" }} />
                          <col className="w-[100px]" />
                          <col style={{ width: "18%" }} />
                          <col className="w-[50px]" />
                        </colgroup>
                        <thead>
                          <tr className="border-b border-border bg-muted/40">
                            <th className="px-3 py-3 text-center text-[11px] font-bold text-muted-foreground tracking-wider">
                              #
                            </th>
                            <th className="px-3 py-3 text-left text-[11px] font-bold text-muted-foreground tracking-wider">
                              Control Id
                            </th>
                            <th className="px-3 py-3 text-left text-[11px] font-bold text-muted-foreground tracking-wider">
                              Title
                            </th>
                            <th className="px-3 py-3 text-left text-[11px] font-bold text-muted-foreground tracking-wider">
                              Statement
                            </th>
                            <th className="px-3 py-3 text-center text-[11px] font-bold text-muted-foreground tracking-wider">
                              Status
                            </th>
                            <th className="px-3 py-3 text-left text-[11px] font-bold text-muted-foreground tracking-wider">
                              Component
                            </th>
                            {!isPublishedSsp && (
                              <th className="px-3 py-3 text-center text-[11px] font-bold text-muted-foreground tracking-wider">
                                Action
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {paginated.map((bc, idx) => {
                            const controlId = getBaselineControlId(bc);
                            const ir = (bc.implementedRequirements || [])[0];
                            // Get statement from controlDetails.parts (available for all controls)
                            const stmtPart = (
                              bc.controlDetails as any
                            )?.parts?.find((p: any) => p.name === "statement");
                            const stmtText =
                              stmtPart?.prose ||
                              ir?.statements?.[0]?.description ||
                              (ir as any)?.description ||
                              "";
                            // Get all component names from byComponents
                            const byComps: {
                              componentUuid: string;
                              componentName?: string;
                            }[] = (ir as any)?.byComponents || [];
                            const compNames = byComps
                              .map(
                                (bc) =>
                                  bc.componentName ||
                                  sspComponents.find(
                                    (c) => c.componentUuid === bc.componentUuid,
                                  )?.title,
                              )
                              .filter(Boolean) as string[];
                            return (
                              <tr
                                key={controlId}
                                className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
                              >
                                <td className="px-3 py-3.5 text-center text-xs text-muted-foreground">
                                  {(tabPage - 1) * tabPageSize + idx + 1}
                                </td>
                                <td className="px-3 py-3.5">
                                  <span className="px-2 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 text-[10px] font-black text-indigo-600 dark:text-indigo-400">
                                    {controlId?.toUpperCase()}
                                  </span>
                                </td>
                                <td className="px-3 py-3.5 text-sm font-medium text-foreground">
                                  {bc.controlTitle ||
                                    bc.controlDetails?.title ||
                                    "-"}
                                </td>
                                <td className="px-3 py-3.5 text-xs text-muted-foreground">
                                  <p className="line-clamp-2">
                                    {stmtText || "-"}
                                  </p>
                                </td>
                                <td className="px-3 py-3.5 text-center">
                                  {bc.implementationStatus ? (
                                    <span
                                      className={`px-1.5 py-0.5 rounded text-[10px] font-bold capitalize whitespace-nowrap ${(() => {
                                        const s =
                                          bc.implementationStatus.toLowerCase();
                                        return s === "implemented"
                                          ? "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                                          : s === "partial" ||
                                            s === "partially-implemented"
                                            ? "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400"
                                            : s === "planned"
                                              ? "bg-blue-50 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400"
                                              : s === "alternative"
                                                ? "bg-purple-50 dark:bg-purple-500/10 text-purple-700 dark:text-purple-400"
                                                : s === "not applicable"
                                                  ? "bg-gray-50 dark:bg-gray-500/10 text-gray-700 dark:text-gray-400"
                                                  : "bg-slate-100 dark:bg-white/[0.05] text-slate-600 dark:text-white/50";
                                      })()}`}
                                    >
                                      {bc.implementationStatus.replace(
                                        /-/g,
                                        " ",
                                      )}
                                    </span>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">
                                      -
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-3.5">
                                  {compNames.length > 0 ? (
                                    <div className="flex flex-col gap-1">
                                      {compNames.map((name) => (
                                        <span
                                          key={name}
                                          className="inline-block px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 text-[10px] font-bold text-indigo-600 dark:text-indigo-400 whitespace-nowrap"
                                        >
                                          {name}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">
                                      -
                                    </span>
                                  )}
                                </td>
                                <td className="px-3 py-3.5 text-center">
                                  {!isPublishedSsp && (
                                    <button
                                      onClick={() => {
                                        const ci = ir
                                          ? controlImpls.find(
                                            (c) => c.id === ir.id,
                                          ) ||
                                          controlImpls.find(
                                            (c) => c.controlId === controlId,
                                          )
                                          : controlImpls.find(
                                            (c) => c.controlId === controlId,
                                          );
                                        if (ci) {
                                          setEditingControlImplId(ci.id);
                                          setEditingCiId(ci.ciId || null);
                                          setEditControlImplForm({
                                            controlId: ci.controlId || "",
                                            description: ci.description || "",
                                            implementationStatus:
                                              (IMPLEMENTATION_STATUSES.find(
                                                (s) =>
                                                  s.value ===
                                                  String(
                                                    ci.implementationStatus ||
                                                    "",
                                                  ).toLowerCase(),
                                              )?.value ||
                                                ci.implementationStatus ||
                                                "") as
                                              | ImplementationStatus
                                              | "",
                                            controlOrigination: String(
                                              ci.controlOrigination || "",
                                            ) as ControlOrigination | "",
                                            responsibleRoleIds: (ci.responsibleRoles ?? [])
                                              .map((r) => r["role-id"])
                                              .join(", "),
                                            setParameters: (
                                              ci.setParameters ?? []
                                            ).map((p) => ({
                                              paramId: p["param-id"],
                                              values: (p.values ?? []).join(
                                                ", ",
                                              ),
                                            })),
                                            byComponents: (
                                              ci.byComponents ?? []
                                            ).map(
                                              (
                                                byc: Record<string, unknown>,
                                              ) => ({
                                                componentUuid: String(
                                                  byc.componentUuid ?? "",
                                                ),
                                                description: String(
                                                  byc.description ?? "",
                                                ),
                                                implementationStatus: String(
                                                  byc.implementationStatus ??
                                                  "",
                                                ),
                                                responsibleRoles: "",
                                              }),
                                            ),
                                            statements: (
                                              ci.statements ?? []
                                            ).map((s) => ({
                                              statementId: s.statementId,
                                              description:
                                                ((s as Record<string, unknown>)
                                                  .description as string) ?? "",
                                              sortOrder: s.sortOrder,
                                            })),
                                            sortOrder: ci.sortOrder ?? 0,
                                          });
                                        } else {
                                          setEditingControlImplId(null);
                                          setEditingCiId(null);
                                          setEditControlImplForm({
                                            controlId,
                                            description:
                                              bc.controlTitle ||
                                              bc.controlDetails?.title ||
                                              bc.title ||
                                              "",
                                            implementationStatus:
                                              (IMPLEMENTATION_STATUSES.find(
                                                (s) =>
                                                  s.value ===
                                                  String(
                                                    bc.implementationStatus ||
                                                    "planned",
                                                  ).toLowerCase(),
                                              )?.value || "planned") as
                                              | ImplementationStatus
                                              | "",
                                            controlOrigination: "",
                                            responsibleRoleIds: "",
                                            setParameters: [],
                                            byComponents: [],
                                            statements: [],
                                            sortOrder: 0,
                                          });
                                        }
                                        setShowEditControlImpl(true);
                                      }}
                                      className="p-1.5 rounded-lg text-muted-foreground hover:text-indigo-600 dark:hover:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors"
                                    >
                                      <Pencil className="size-3.5" />
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  {filtered.length > tabPageSize && (
                    <PageSizeSelector
                      page={tabPage}
                      pageSize={tabPageSize}
                      total={filtered.length}
                      onPageChange={setTabPage}
                      onPageSizeChange={(s) => {
                        setTabPageSize(s);
                        setTabPage(1);
                      }}
                    />
                  )}
                </>
              );
            })()
          ) : (
            <div className="rounded-lg border border-border bg-card p-5 text-center py-12">
              <Shield className="size-8 text-muted-foreground mb-3 mx-auto" />
              <p className="text-sm font-medium">
                No control implementations found
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {tabSearch
                  ? `No controls match "${tabSearch}"`
                  : "No baseline controls defined for this SSP"}
              </p>
            </div>
          )}
        </>
      )}

      {/* ═══ Leveraged Authorizations Tab ═══ */}
      {activeTab === "Leveraged Authorizations" && (
        <>
          {leveragedAuthsLoading ? (
            <LoadingSpinner text="Loading leveraged authorizations" />
          ) : leveragedAuths.length > 0 ? (
            <>
              {leveragedAuths.some((a) => a.status === "revoked") && (
                <div className="flex items-center gap-2 px-4 py-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-lg text-xs text-red-700 dark:text-red-400 font-medium">
                  <XCircle className="size-4 shrink-0" />
                  One or more provider authorizations have been revoked. Review inherited controls in the Control Implementations tab.
                </div>
              )}
              <div className="rounded-lg border border-border bg-card divide-y divide-border overflow-hidden">
                {pagLevAuths.map((auth) => (
                  <div
                    key={auth.id}
                    className={`px-5 py-4 transition-colors ${auth.status === "revoked"
                      ? "bg-red-50/30 dark:bg-red-500/5"
                      : auth.status === "expired"
                        ? "bg-amber-50/30 dark:bg-amber-500/5"
                        : "hover:bg-muted/40"
                      }`}
                  >
                    {editingLevAuthId === auth.id ? (
                      /* ── Inline edit form ── */
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <FormField label="Title" required>
                            <FormInput
                              value={editLevAuthForm.title}
                              onChange={(v) =>
                                setEditLevAuthForm((f) => ({ ...f, title: v }))
                              }
                              placeholder="Authorization title"
                            />
                          </FormField>
                          <FormField label="Authorizing Party">
                            <FormSelect
                              options={[
                                { label: "— None —", value: "" },
                                ...parties.map((p) => ({
                                  label: `${p.name} (${p.partyType})`,
                                  value: p.partyUuid,
                                })),
                              ]}
                              placeholder="Select a party"
                              value={editLevAuthForm.partyUuid}
                              onChange={(v) =>
                                setEditLevAuthForm((f) => ({
                                  ...f,
                                  partyUuid: v,
                                }))
                              }
                            />
                          </FormField>
                        </div>
                        <FormField label="Date Authorized">
                          <FormDatePicker
                            value={editLevAuthForm.dateAuthorized}
                            onChange={(v) =>
                              setEditLevAuthForm((f) => ({
                                ...f,
                                dateAuthorized: v,
                              }))
                            }
                          />
                        </FormField>
                        <div className="flex items-center gap-2 pt-1">
                          <button
                            onClick={() => setEditingLevAuthId(null)}
                            className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-accent transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleUpdateLevAuth}
                            disabled={!editLevAuthForm.title || savingLevAuth}
                            className="px-3 py-1.5 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-50"
                          >
                            {savingLevAuth ? "Saving" : "Save"}
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* ── Read-only display ── */
                      <div className="space-y-2">
                        {/* Revoked / expired banners */}
                        {auth.status === "revoked" && (
                          <div className="flex items-center gap-2 px-3 py-2 rounded bg-red-100 dark:bg-red-500/15 text-red-700 dark:text-red-400 text-xs font-medium">
                            <XCircle className="size-3.5 shrink-0" />
                            This leveraged authorization has been revoked. Inherited controls from this provider may no longer be valid.
                          </div>
                        )}
                        {auth.status === "expired" && (
                          <div className="flex items-center gap-2 px-3 py-2 rounded bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 text-xs font-medium">
                            <AlertTriangle className="size-3.5 shrink-0" />
                            This leveraged authorization has expired. Review with the provider to renew.
                          </div>
                        )}
                        <div className="flex items-start gap-3">
                          <div className="flex items-center justify-center size-10 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex-shrink-0 mt-0.5 ring-1 ring-indigo-100 dark:ring-indigo-500/20">
                            <Link2 className="size-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-semibold text-foreground">{auth.title}</p>
                              {/* Status badge */}
                              {(!auth.status || auth.status === "active") && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20">
                                  <CheckCircle2 className="size-2.5" /> Active
                                </span>
                              )}
                              {auth.status === "revoked" && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-100 dark:bg-red-500/10 dark:text-red-400 dark:border-red-500/20">
                                  <XCircle className="size-2.5" /> Revoked
                                </span>
                              )}
                              {auth.status === "expired" && (
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-100 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20">
                                  <Clock className="size-2.5" /> Expired
                                </span>
                              )}
                            </div>
                            <div className="flex items-center gap-x-4 gap-y-1.5 mt-2 flex-wrap text-xs">
                              {auth.partyUuid && (
                                <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                                  <Users className="size-3.5 text-sky-600 dark:text-sky-400" />
                                  <span className="text-muted-foreground">Party:</span>
                                  <span className="font-mono text-foreground">{auth.partyUuid.slice(0, 12)}…</span>
                                </span>
                              )}
                              {auth.dateAuthorized && (
                                <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                                  <Calendar className="size-3.5 text-indigo-600 dark:text-indigo-400" />
                                  <span className="text-muted-foreground">Authorized:</span>
                                  <span className="text-foreground font-medium">{toddmmyyyy(auth.dateAuthorized)}</span>
                                </span>
                              )}
                              {auth.sourceSspVersion && (
                                <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                                  <Tag className="size-3.5 text-violet-600 dark:text-violet-400" />
                                  <span className="text-muted-foreground">Version:</span>
                                  <span className="text-foreground font-medium">{auth.sourceSspVersion}</span>
                                </span>
                              )}
                              <span className="inline-flex items-center gap-1.5 text-muted-foreground">
                                <ShieldCheck className="size-3.5 text-emerald-600 dark:text-emerald-400" />
                                <span className="text-muted-foreground">Scope:</span>
                                <span className="text-foreground font-medium">
                                  {auth.controlScope ? auth.controlScope.join(", ") : "All controls"}
                                </span>
                              </span>
                            </div>
                          </div>
                          {/* Update Status dropdown (non-terminal only) */}
                          {auth.status !== "revoked" && auth.status !== "expired" && (
                            <div className="relative shrink-0">
                              <button
                                onClick={() => setOpenLevAuthMenuId(openLevAuthMenuId === auth.id ? null : auth.id)}
                                className="flex items-center gap-1 px-2.5 py-1.5 text-xs border border-border rounded-lg hover:bg-muted transition-colors"
                              >
                                Update Status <ChevronDown className="size-3" />
                              </button>
                              {openLevAuthMenuId === auth.id && (
                                <div className="absolute right-0 top-full mt-1 z-10 bg-popover border border-border rounded-lg shadow-lg min-w-[160px] overflow-hidden">
                                  <button
                                    onClick={() => { setOpenLevAuthMenuId(null); handleUpdateLevAuthStatus(auth.id, "revoked"); }}
                                    disabled={updatingLevAuthStatusId === auth.id}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors"
                                  >
                                    <XCircle className="size-3.5" /> Mark as Revoked
                                  </button>
                                  <button
                                    onClick={() => { setOpenLevAuthMenuId(null); handleUpdateLevAuthStatus(auth.id, "expired"); }}
                                    disabled={updatingLevAuthStatusId === auth.id}
                                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-500/10 transition-colors"
                                  >
                                    <Clock className="size-3.5" /> Mark as Expired
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                          {updatingLevAuthStatusId === auth.id && (
                            <Loader2 className="size-4 animate-spin text-muted-foreground shrink-0" />
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              {filtLevAuths.length > 0 && (
                <div className="flex items-center justify-between gap-2 px-4 py-2 border-t border-[#e2e8f0] dark:border-white/[0.06] bg-[#fafbfc] dark:bg-white/[0.015] flex-shrink-0">
                  <p className="text-[11px] leading-tight text-[#475569] dark:text-white/70">
                    Showing{" "}
                    <span className="font-semibold text-[#334155] dark:text-white/90">
                      {Math.min((tabPage - 1) * tabPageSize + 1, filtLevAuths.length)}–{Math.min(tabPage * tabPageSize, filtLevAuths.length)}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold text-[#334155] dark:text-white/90">
                      {filtLevAuths.length}
                    </span>
                  </p>
                  {Math.ceil(filtLevAuths.length / tabPageSize) > 1 && (
                    <div className="flex items-center gap-0.5">
                      <button
                        disabled={tabPage === 1}
                        onClick={() => setCurrentTabPagination({ page: Math.max(1, tabPage - 1), pageSize: tabPageSize })}
                        className="p-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronLeft className="size-3" />
                      </button>

                      {getPageWindow(tabPage, Math.ceil(filtLevAuths.length / tabPageSize)).map((p, idx) =>
                        p === "dots" ? (
                          <span
                            key={`dots-${idx}`}
                            className="w-6 h-6 flex items-center justify-center text-xs text-[#475569] dark:text-white/70 select-none"
                          >
                            …
                          </span>
                        ) : (
                          <button
                            key={p}
                            onClick={() => setCurrentTabPagination({ page: p as number, pageSize: tabPageSize })}
                            className={`w-6 h-6 text-xs font-medium rounded-md transition-colors ${p === tabPage
                              ? "bg-indigo-600 dark:bg-indigo-600 text-white"
                              : "text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04]"
                              }`}
                          >
                            {p}
                          </button>
                        ),
                      )}

                      <button
                        disabled={tabPage >= Math.ceil(filtLevAuths.length / tabPageSize)}
                        onClick={() => setCurrentTabPagination({ page: Math.min(Math.ceil(filtLevAuths.length / tabPageSize), tabPage + 1), pageSize: tabPageSize })}
                        className="p-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronRight className="size-3" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="rounded-lg border border-border bg-card p-5 text-center py-12">
              <ExternalLink className="size-8 text-muted-foreground mb-3 mx-auto" />
              <p className="text-sm font-medium">No leveraged authorizations</p>
              <p className="text-xs text-muted-foreground mt-1">
                Reference authorizations from other systems that this SSP
                inherits controls from
              </p>
            </div>
          )}
        </>
      )}

      {/* ═══ System Users Tab ═══ */}
      {activeTab === "System Users" && (
        <>
          {systemUsersLoading ? (
            <LoadingSpinner text="Loading system users" />
          ) : systemUsers.length > 0 ? (
            <>
              <div className="rounded-lg border border-border bg-card overflow-hidden">
                {/* Header */}
                <div className="hidden md:grid grid-cols-[1fr_1fr_140px_44px] gap-4 px-5 py-3.5 border-b border-border bg-muted/30">
                  <p className="text-[13px] font-medium text-[#1e293b] dark:text-white">User</p>
                  <p className="text-[13px] font-medium text-[#1e293b] dark:text-white">Responsibilities</p>
                  <p className="text-[13px] font-medium text-[#1e293b] dark:text-white">Last Updated</p>
                  <div />
                </div>

                <div className="divide-y divide-border">
                  {pagSystemUsers.map((user) => (
                    <div
                      key={user.id}
                      className="px-5 py-4 hover:bg-muted/40 transition-colors"
                    >
                      {editingSystemUserId === user.id ? (
                        /* ── Inline edit form ── */
                        <div className="space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <FormField label="Title" required>
                              <FormInput
                                value={editSystemUserForm.title}
                                onChange={(v) =>
                                  setEditSystemUserForm((f) => ({
                                    ...f,
                                    title: v,
                                  }))
                                }
                                placeholder="User title"
                              />
                            </FormField>
                            <FormField label="Short Name">
                              <FormInput
                                value={editSystemUserForm.shortName}
                                onChange={(v) =>
                                  setEditSystemUserForm((f) => ({
                                    ...f,
                                    shortName: v,
                                  }))
                                }
                                placeholder="Short name"
                              />
                            </FormField>
                          </div>
                          <FormField label="Description">
                            <FormTextarea
                              value={editSystemUserForm.description}
                              onChange={(v) =>
                                setEditSystemUserForm((f) => ({
                                  ...f,
                                  description: v,
                                }))
                              }
                              placeholder="Description"
                            />
                          </FormField>
                          <FormField label="Role IDs (comma-separated)">
                            <FormInput
                              value={editSystemUserForm.roleIds}
                              onChange={(v) =>
                                setEditSystemUserForm((f) => ({
                                  ...f,
                                  roleIds: v,
                                }))
                              }
                              placeholder="role-1, role-2"
                            />
                          </FormField>
                          <div className="grid grid-cols-2 gap-3">
                            <FormField label="Privilege Title">
                              <FormInput
                                value={editSystemUserForm.privilegeTitle}
                                onChange={(v) =>
                                  setEditSystemUserForm((f) => ({
                                    ...f,
                                    privilegeTitle: v,
                                  }))
                                }
                                placeholder="Privilege title"
                              />
                            </FormField>
                            <FormField label="Functions Performed (comma-separated)">
                              <FormInput
                                value={editSystemUserForm.functionsPerformed}
                                onChange={(v) =>
                                  setEditSystemUserForm((f) => ({
                                    ...f,
                                    functionsPerformed: v,
                                  }))
                                }
                                placeholder="func-1, func-2"
                              />
                            </FormField>
                          </div>
                          <div className="flex items-center gap-2 pt-1">
                            <button
                              onClick={() => setEditingSystemUserId(null)}
                              className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-accent transition-colors"
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleUpdateSystemUser}
                              disabled={
                                !editSystemUserForm.title || savingSystemUser
                              }
                              className="px-3 py-1.5 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-50"
                            >
                              {savingSystemUser ? "Saving" : "Save"}
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* ── Read-only display ── */
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_140px_44px] gap-3 md:gap-4 items-start">
                          {/* User column */}
                          <div className="flex items-start gap-3 min-w-0">
                            <div className="flex items-center justify-center size-10 rounded-full bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex-shrink-0 ring-1 ring-indigo-100 dark:ring-indigo-500/20">
                              <UserCog className="size-5" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-semibold text-foreground">{user.title}</p>
                              {user.description && (
                                <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                                  {user.description}
                                </p>
                              )}
                              {(user.shortName || (user.roleIds && user.roleIds.length > 0)) && (
                                <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                                  {user.shortName && (
                                    <span className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-500/20">
                                      {user.shortName}
                                    </span>
                                  )}
                                  {user.roleIds?.map((rid) => (
                                    <span
                                      key={rid}
                                      className="inline-flex items-center text-[10px] font-mono px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border"
                                    >
                                      {rid}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Responsibilities column */}
                          <div className="min-w-0 md:pt-0.5">
                            {user.authorizedPrivileges?.length > 0 ? (
                              <div className="space-y-2">
                                {user.authorizedPrivileges.map((priv, i) => (
                                  <div key={i} className="min-w-0">
                                    <div className="flex items-start gap-2">
                                      <ShieldCheck className="size-3.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                                      <div className="min-w-0 flex-1">
                                        <p className="text-sm font-semibold text-foreground">{priv.title}</p>
                                        {(
                                          (priv as { functions_performed?: string[]; "functions-performed"?: string[] }).functions_performed ||
                                          (priv as { functions_performed?: string[]; "functions-performed"?: string[] })["functions-performed"]
                                        )?.length ? (
                                          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                                            {(
                                              (priv as { functions_performed?: string[]; "functions-performed"?: string[] }).functions_performed ||
                                              (priv as { functions_performed?: string[]; "functions-performed"?: string[] })["functions-performed"]
                                            )?.join(", ")}
                                          </p>
                                        ) : null}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">—</span>
                            )}
                          </div>

                          {/* Last updated column */}
                          <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5 md:pt-1">
                            <Calendar className="size-3.5 flex-shrink-0" />
                            <span>{toddmmyyyy(user.updatedAt)}</span>
                          </div>

                          {/* Actions */}
                          {!isPublishedSsp ? (
                            <div className="flex items-center gap-1 flex-shrink-0 justify-end">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={() => {
                                      setEditingSystemUserId(user.id);
                                      setEditSystemUserForm({
                                        title: user.title,
                                        shortName: user.shortName || "",
                                        description: user.description || "",
                                        roleIds: user.roleIds?.join(", ") || "",
                                        privilegeTitle: user.authorizedPrivileges?.[0]?.title || "",
                                        functionsPerformed:
                                          (user.authorizedPrivileges?.[0]?.["functions-performed"] as string[] | undefined)?.join(", ") || "",
                                      });
                                    }}
                                    className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                                  >
                                    <Pencil className="size-3.5" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>Edit</TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={() => handleDeleteSystemUser(user.id)}
                                    disabled={deletingSystemUserId === user.id}
                                    className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-500/10 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-50"
                                  >
                                    <Trash2 className="size-3.5" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>Delete</TooltipContent>
                              </Tooltip>
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
              {filtSystemUsers.length > 0 && (
                <div className="flex items-center justify-between gap-2 px-4 py-2 border-t border-[#e2e8f0] dark:border-white/[0.06] bg-[#fafbfc] dark:bg-white/[0.015] flex-shrink-0">
                  <p className="text-[11px] leading-tight text-[#475569] dark:text-white/70">
                    Showing{" "}
                    <span className="font-semibold text-[#334155] dark:text-white/90">
                      {Math.min((tabPage - 1) * tabPageSize + 1, filtSystemUsers.length)}–{Math.min(tabPage * tabPageSize, filtSystemUsers.length)}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold text-[#334155] dark:text-white/90">
                      {filtSystemUsers.length}
                    </span>
                  </p>
                  {Math.ceil(filtSystemUsers.length / tabPageSize) > 1 && (
                    <div className="flex items-center gap-0.5">
                      <button
                        disabled={tabPage === 1}
                        onClick={() => setCurrentTabPagination({ page: Math.max(1, tabPage - 1), pageSize: tabPageSize })}
                        className="p-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronLeft className="size-3" />
                      </button>

                      {getPageWindow(tabPage, Math.ceil(filtSystemUsers.length / tabPageSize)).map((p, idx) =>
                        p === "dots" ? (
                          <span
                            key={`dots-${idx}`}
                            className="w-6 h-6 flex items-center justify-center text-xs text-[#475569] dark:text-white/70 select-none"
                          >
                            …
                          </span>
                        ) : (
                          <button
                            key={p}
                            onClick={() => setCurrentTabPagination({ page: p as number, pageSize: tabPageSize })}
                            className={`w-6 h-6 text-xs font-medium rounded-md transition-colors ${p === tabPage
                              ? "bg-indigo-600 dark:bg-indigo-600 text-white"
                              : "text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04]"
                              }`}
                          >
                            {p}
                          </button>
                        ),
                      )}

                      <button
                        disabled={tabPage >= Math.ceil(filtSystemUsers.length / tabPageSize)}
                        onClick={() => setCurrentTabPagination({ page: Math.min(Math.ceil(filtSystemUsers.length / tabPageSize), tabPage + 1), pageSize: tabPageSize })}
                        className="p-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronRight className="size-3" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="rounded-lg border border-border bg-card p-5 text-center py-12">
              <UserCog className="size-8 text-muted-foreground mb-3 mx-auto" />
              <p className="text-sm font-medium">No system users defined</p>
              <p className="text-xs text-muted-foreground mt-1">
                Add user types and their authorized privileges
              </p>
            </div>
          )}
        </>
      )}

      {/* ═══ Interconnections Tab ═══ */}
      {activeTab === "Interconnections" && (
        <>
          {interconnectionsLoading ? (
            <LoadingSpinner text="Loading interconnections" />
          ) : interconnections.length > 0 ? (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {pagInterconnections.map((conn) => (
                  <div
                    key={conn.id}
                    className="rounded-xl border border-border bg-card p-4 hover:shadow-md hover:border-indigo-200 dark:hover:border-indigo-500/30 transition-all"
                  >
                    {editingInterconnectionId === conn.id ? (
                      /* ── Inline edit form ── */
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <FormField label="Title" required>
                            <FormInput
                              value={editInterconnectionForm.title}
                              onChange={(v) =>
                                setEditInterconnectionForm((f) => ({
                                  ...f,
                                  title: v,
                                }))
                              }
                              placeholder="Interconnection title"
                            />
                          </FormField>
                          <FormField label="Direction">
                            <FormSelect
                              options={[
                                { label: "Incoming", value: "incoming" },
                                { label: "Outgoing", value: "outgoing" },
                                {
                                  label: "Incoming-Outgoing",
                                  value: "incoming-outgoing",
                                },
                              ]}
                              value={editInterconnectionForm.direction}
                              onChange={(v) =>
                                setEditInterconnectionForm((f) => ({
                                  ...f,
                                  direction: v as typeof f.direction,
                                }))
                              }
                              placeholder="Select direction"
                            />
                          </FormField>
                        </div>
                        <FormField label="Description">
                          <FormTextarea
                            value={editInterconnectionForm.description}
                            onChange={(v) =>
                              setEditInterconnectionForm((f) => ({
                                ...f,
                                description: v,
                              }))
                            }
                            placeholder="Description"
                          />
                        </FormField>
                        <div className="grid grid-cols-2 gap-3">
                          <FormField label="Remote System Name">
                            <FormInput
                              value={editInterconnectionForm.remoteSystemName}
                              onChange={(v) =>
                                setEditInterconnectionForm((f) => ({
                                  ...f,
                                  remoteSystemName: v,
                                }))
                              }
                              placeholder="Remote system"
                            />
                          </FormField>
                          <FormField label="Responsible Roles (comma-separated)">
                            <FormInput
                              value={editInterconnectionForm.responsibleRoles}
                              onChange={(v) =>
                                setEditInterconnectionForm((f) => ({
                                  ...f,
                                  responsibleRoles: v,
                                }))
                              }
                              placeholder="role-1, role-2"
                            />
                          </FormField>
                        </div>
                        <div className="flex items-center gap-2 pt-1">
                          <button
                            onClick={() => setEditingInterconnectionId(null)}
                            className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-accent transition-colors"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleUpdateInterconnection}
                            disabled={
                              !editInterconnectionForm.title ||
                              savingInterconnection
                            }
                            className="px-3 py-1.5 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-50"
                          >
                            {savingInterconnection ? "Saving" : "Save"}
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* ── Read-only card display ── */
                      (() => {
                        const dirKey = (conn.direction || "").toLowerCase();
                        const dirTint = /incoming.*outgoing/.test(dirKey)
                          ? "bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-500/10 dark:text-violet-400 dark:border-violet-500/30"
                          : dirKey === "incoming"
                            ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/30"
                            : dirKey === "outgoing"
                              ? "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/30"
                              : "bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-500/10 dark:text-slate-300 dark:border-slate-500/30";
                        return (
                          <div className="flex flex-col gap-3 h-full">
                            {/* Header */}
                            <div className="flex items-start gap-3">
                              <div className="flex items-center justify-center size-10 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex-shrink-0 ring-1 ring-indigo-100 dark:ring-indigo-500/20">
                                <Link2 className="size-5" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-start justify-between gap-2">
                                  <p className="text-sm font-semibold text-foreground break-words">{conn.title}</p>
                                  <span className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border capitalize flex-shrink-0 ${dirTint}`}>
                                    {conn.direction?.replace(/-/g, " ")}
                                  </span>
                                </div>
                                {conn.description && (
                                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                                    {conn.description}
                                  </p>
                                )}
                              </div>
                              {!isPublishedSsp && (
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        onClick={() => {
                                          setEditingInterconnectionId(conn.id);
                                          setEditInterconnectionForm({
                                            title: conn.title,
                                            description: conn.description || "",
                                            direction: conn.direction as any,
                                            remoteSystemName: conn.remoteSystemName || "",
                                            responsibleRoles:
                                              conn.responsibleRoles?.map((r) => r.role_id).join(", ") || "",
                                            protocols: conn.protocols?.length
                                              ? conn.protocols
                                              : [{ name: "", portRanges: [{ start: "", end: "", transport: "tcp" }] }],
                                          });
                                        }}
                                        className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                                      >
                                        <Pencil className="size-3.5" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent>Edit</TooltipContent>
                                  </Tooltip>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        onClick={() => handleDeleteInterconnection(conn.id)}
                                        disabled={deletingInterconnectionId === conn.id}
                                        className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-500/10 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-50"
                                      >
                                        <Trash2 className="size-3.5" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent>Delete</TooltipContent>
                                  </Tooltip>
                                </div>
                              )}
                            </div>

                            {/* Meta */}
                            {(conn.remoteSystemName || (conn.responsibleRoles && conn.responsibleRoles.length > 0)) && (
                              <div className="flex flex-col gap-1.5 pl-[52px]">
                                {conn.remoteSystemName && (
                                  <div className="flex items-center gap-2 text-xs">
                                    <Globe className="size-3.5 text-cyan-600 dark:text-cyan-400 flex-shrink-0" />
                                    <span className="text-muted-foreground">Remote:</span>
                                    <span className="text-foreground font-medium break-words min-w-0">{conn.remoteSystemName}</span>
                                  </div>
                                )}
                                {conn.responsibleRoles?.length > 0 && (
                                  <div className="flex items-start gap-2 text-xs">
                                    <UserCheck className="size-3.5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                                    <span className="text-muted-foreground flex-shrink-0">Roles:</span>
                                    <div className="flex flex-wrap gap-1">
                                      {conn.responsibleRoles.map((r) => (
                                        <span
                                          key={r.role_id}
                                          className="inline-flex items-center text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground border border-border"
                                        >
                                          {r.role_id}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Protocols */}
                            {conn.protocols?.length > 0 && (
                              <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-border/60 mt-auto">
                                <span className="text-[10px] text-muted-foreground font-medium">Protocols:</span>
                                {conn.protocols.map((p, i) => {
                                  const pn = (p.name || "").toLowerCase();
                                  const pTint = /https|tls|ssl/.test(pn)
                                    ? "bg-emerald-50 text-emerald-700 border-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-400 dark:border-emerald-500/20"
                                    : /saml|oidc|oauth|ldap/.test(pn)
                                      ? "bg-violet-50 text-violet-700 border-violet-100 dark:bg-violet-500/10 dark:text-violet-400 dark:border-violet-500/20"
                                      : /syslog|log/.test(pn)
                                        ? "bg-amber-50 text-amber-700 border-amber-100 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20"
                                        : /jdbc|sql|db/.test(pn)
                                          ? "bg-blue-50 text-blue-700 border-blue-100 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20"
                                          : /http|rest|api/.test(pn)
                                            ? "bg-cyan-50 text-cyan-700 border-cyan-100 dark:bg-cyan-500/10 dark:text-cyan-400 dark:border-cyan-500/20"
                                            : "bg-slate-50 text-slate-700 border-slate-200 dark:bg-slate-500/10 dark:text-slate-300 dark:border-slate-500/20";
                                  return (
                                    <span
                                      key={i}
                                      className={`inline-flex items-center text-[10px] font-mono font-semibold px-2 py-0.5 rounded-md border ${pTint}`}
                                    >
                                      {p.name}
                                      {p.portRanges?.[0]?.start && `:${p.portRanges[0].start}`}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })()
                    )}
                  </div>
                ))}
              </div>
              {filtInterconnections.length > 0 && (
                <div className="flex items-center justify-between gap-2 px-4 py-2 border-t border-[#e2e8f0] dark:border-white/[0.06] bg-[#fafbfc] dark:bg-white/[0.015] flex-shrink-0">
                  <p className="text-[11px] leading-tight text-[#475569] dark:text-white/70">
                    Showing{" "}
                    <span className="font-semibold text-[#334155] dark:text-white/90">
                      {Math.min((tabPage - 1) * tabPageSize + 1, filtInterconnections.length)}–{Math.min(tabPage * tabPageSize, filtInterconnections.length)}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold text-[#334155] dark:text-white/90">
                      {filtInterconnections.length}
                    </span>
                  </p>
                  {Math.ceil(filtInterconnections.length / tabPageSize) > 1 && (
                    <div className="flex items-center gap-0.5">
                      <button
                        disabled={tabPage === 1}
                        onClick={() => setCurrentTabPagination({ page: Math.max(1, tabPage - 1), pageSize: tabPageSize })}
                        className="p-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronLeft className="size-3" />
                      </button>

                      {getPageWindow(tabPage, Math.ceil(filtInterconnections.length / tabPageSize)).map((p, idx) =>
                        p === "dots" ? (
                          <span
                            key={`dots-${idx}`}
                            className="w-6 h-6 flex items-center justify-center text-xs text-[#475569] dark:text-white/70 select-none"
                          >
                            …
                          </span>
                        ) : (
                          <button
                            key={p}
                            onClick={() => setCurrentTabPagination({ page: p as number, pageSize: tabPageSize })}
                            className={`w-6 h-6 text-xs font-medium rounded-md transition-colors ${p === tabPage
                              ? "bg-indigo-600 dark:bg-indigo-600 text-white"
                              : "text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04]"
                              }`}
                          >
                            {p}
                          </button>
                        ),
                      )}

                      <button
                        disabled={tabPage >= Math.ceil(filtInterconnections.length / tabPageSize)}
                        onClick={() => setCurrentTabPagination({ page: Math.min(Math.ceil(filtInterconnections.length / tabPageSize), tabPage + 1), pageSize: tabPageSize })}
                        className="p-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronRight className="size-3" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="rounded-lg border border-border bg-card p-5 text-center py-12">
              <Link2 className="size-8 text-muted-foreground mb-3 mx-auto" />
              <p className="text-sm font-medium">No interconnections defined</p>
              <p className="text-xs text-muted-foreground mt-1">
                Add system interconnections and external integrations
              </p>
            </div>
          )}
        </>
      )}

      {/* ═══ Baseline Controls Tab ═══ */}
      {activeTab === "Baseline Controls" && (
        <>
          {baselineControlsLoading ? (
            <LoadingSpinner text="Loading baseline controls" />
          ) : baselineControls.length > 0 ? (
            <div className="rounded-lg border border-border bg-card divide-y divide-border overflow-hidden">
              <div className="px-5 py-3 bg-muted/40 text-xs font-bold text-muted-foreground tracking-wider">
                {baselineControls.length} controls from profile baseline
              </div>
              {baselineControls.map((ctrl) => {
                const controlId = getBaselineControlId(ctrl);
                const parameterRows = buildParameterRows(ctrl, []);
                return (
                  <div
                    key={controlId}
                    className="px-5 py-3 hover:bg-muted/40 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="px-2 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 text-[10px] font-black text-indigo-600 dark:text-indigo-400">
                        {controlId?.toUpperCase()}
                      </span>
                      <span className="text-sm font-medium flex-1 min-w-0 truncate">
                        {ctrl.title}
                      </span>
                      {ctrl.family && (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-muted text-muted-foreground flex-shrink-0">
                          {ctrl.family}
                        </span>
                      )}
                    </div>
                    {parameterRows.length > 0 && (
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        {parameterRows.map((p) => (
                          <span
                            key={p.paramId}
                            className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
                          >
                            {p.paramId}
                            {p.profileValues.length > 0
                              ? ` = ${p.profileValues.join(", ")}`
                              : ""}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="rounded-lg border border-border bg-card p-5 text-center py-12">
              <BookOpen className="size-8 text-muted-foreground mb-3 mx-auto" />
              <p className="text-sm font-medium">No baseline controls</p>
              <p className="text-xs text-muted-foreground mt-1">
                Baseline controls are resolved from the SSP's linked profile
              </p>
            </div>
          )}
        </>
      )}

      {/* ═══ Roles & Parties Tab ═══ */}
      {activeTab === "Roles & Parties" && (
        <>
          {rolesPartiesLoading ? (
            <LoadingSpinner text="Loading roles & parties" />
          ) : (
            <div className="space-y-6">
              {/* Roles Section */}
              <div className="rounded-lg border border-border bg-card p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold flex items-center gap-2">
                    <span className="flex items-center justify-center size-8 rounded-lg bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-400 ring-1 ring-violet-100 dark:ring-violet-500/20">
                      <UserCheck className="size-4" />
                    </span>
                    <span className="text-foreground">Roles</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-400 border border-violet-100 dark:border-violet-500/20">
                      {roles.length}
                    </span>
                  </h3>
                  {!isPublishedSsp && (
                    <button
                      onClick={() => setShowAddRole(true)}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-[10px] font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors"
                    >
                      <Plus className="size-3" /> Add Role
                    </button>
                  )}
                </div>
                {roles.length > 0 ? (
                  <div className="space-y-4">
                    {/* Pagination controls for roles */}
                    {roles.length > 10 && (
                      <div className="flex items-center justify-between pb-3 border-b border-border">
                        <div className="flex items-center gap-2">
                          <label className="text-xs dark:text-white text-foreground whitespace-nowrap">Per page</label>
                          <Dropdown
                            value={String(rolesPagination.pageSize)}
                            onChange={(v) => setRolesPagination({ page: 1, pageSize: Number(v) })}
                            options={[5, 10, 25, 50].map((n) => ({
                              value: String(n),
                              label: String(n),
                            }))}
                            size="sm"
                            align="start"
                            className="!min-w-[70px]"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {Math.min((rolesPagination.page - 1) * rolesPagination.pageSize + 1, roles.length)}–{Math.min(rolesPagination.page * rolesPagination.pageSize, roles.length)} of {roles.length}
                        </p>
                      </div>
                    )}

                    {/* Roles list */}
                    <div className="divide-y divide-border">
                      {roles
                        .slice((rolesPagination.page - 1) * rolesPagination.pageSize, rolesPagination.page * rolesPagination.pageSize)
                        .map((role) => (
                          <div key={role.id} className="py-2.5">
                            {editingRoleId === role.id ? (
                              <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                  <FormField label="Title" required>
                                    <FormInput
                                      value={editRoleForm.title}
                                      onChange={(v) =>
                                        setEditRoleForm((f) => ({ ...f, title: v }))
                                      }
                                      placeholder="Role title"
                                    />
                                  </FormField>
                                  <FormField label="Short Name">
                                    <FormInput
                                      value={editRoleForm.shortName}
                                      onChange={(v) =>
                                        setEditRoleForm((f) => ({
                                          ...f,
                                          shortName: v,
                                        }))
                                      }
                                      placeholder="Short name"
                                    />
                                  </FormField>
                                </div>
                                <FormField label="Description">
                                  <FormTextarea
                                    value={editRoleForm.description}
                                    onChange={(v) =>
                                      setEditRoleForm((f) => ({
                                        ...f,
                                        description: v,
                                      }))
                                    }
                                    placeholder="Description"
                                  />
                                </FormField>
                                <div className="flex items-center gap-2 pt-1">
                                  <button
                                    onClick={() => setEditingRoleId(null)}
                                    className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-accent transition-colors"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={handleUpdateRole}
                                    disabled={!editRoleForm.title || savingRole}
                                    className="px-3 py-1.5 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-50"
                                  >
                                    {savingRole ? "Saving" : "Save"}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-start gap-3">
                                <div className="flex items-center justify-center size-9 rounded-lg bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-400 flex-shrink-0 mt-0.5">
                                  <UserCheck className="size-4" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-foreground">
                                    {role.title}
                                  </p>
                                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    <span className="inline-flex items-center text-[10px] font-mono font-semibold px-2 py-0.5 rounded-md bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-400 border border-violet-100 dark:border-violet-500/20">
                                      {role.roleId}
                                    </span>
                                    {role.shortName && (
                                      <span className="text-xs text-muted-foreground">
                                        {role.shortName}
                                      </span>
                                    )}
                                  </div>
                                  {role.description && (
                                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                                      {role.description}
                                    </p>
                                  )}
                                </div>
                                <div
                                  className={`flex items-center gap-1 flex-shrink-0 ${isPublishedSsp ? "hidden" : ""}`}
                                >
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        onClick={() => {
                                          setEditingRoleId(role.id);
                                          setEditRoleForm({
                                            roleId: role.roleId,
                                            title: role.title,
                                            shortName: role.shortName || "",
                                            description: role.description || "",
                                          });
                                        }}
                                        className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                                      >
                                        <Pencil className="size-3.5" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent>Edit</TooltipContent>
                                  </Tooltip>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        onClick={() =>
                                          requestDelete(
                                            "Role",
                                            role.id,
                                            role.title,
                                            () => handleDeleteRole(role.id),
                                          )
                                        }
                                        disabled={deletingRoleId === role.id}
                                        className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-500/10 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-50"
                                      >
                                        <Trash2 className="size-3.5" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent>Delete</TooltipContent>
                                  </Tooltip>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                    </div>

                    {/* Pagination buttons for roles */}
                    {Math.ceil(roles.length / rolesPagination.pageSize) > 1 && (
                      <div className="flex items-center justify-center gap-2 pt-3 border-t border-border">
                        <button
                          onClick={() => setRolesPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
                          disabled={rolesPagination.page === 1}
                          className="p-1.5 rounded-lg border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronLeft className="size-3.5" />
                        </button>
                        <span className="text-xs text-muted-foreground">
                          Page {rolesPagination.page} of {Math.ceil(roles.length / rolesPagination.pageSize)}
                        </span>
                        <button
                          onClick={() => setRolesPagination((p) => ({ ...p, page: Math.min(Math.ceil(roles.length / rolesPagination.pageSize), p.page + 1) }))}
                          disabled={rolesPagination.page >= Math.ceil(roles.length / rolesPagination.pageSize)}
                          className="p-1.5 rounded-lg border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronRight className="size-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground text-center py-4">
                    No roles defined. Add roles like ISSO, System Owner, AO.
                  </p>
                )}
              </div>

              {/* Parties Section */}
              <div className="rounded-lg border border-border bg-card p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold flex items-center gap-2">
                    <span className="flex items-center justify-center size-8 rounded-lg bg-sky-50 dark:bg-sky-500/10 text-sky-600 dark:text-sky-400 ring-1 ring-sky-100 dark:ring-sky-500/20">
                      <Users className="size-4" />
                    </span>
                    <span className="text-foreground">Parties</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-400 border border-sky-100 dark:border-sky-500/20">
                      {parties.length}
                    </span>
                  </h3>
                  {!isPublishedSsp && (
                    <button
                      onClick={() => setShowAddParty(true)}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-[10px] font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors"
                    >
                      <Plus className="size-3" /> Add Party
                    </button>
                  )}
                </div>
                {parties.length > 0 ? (
                  <div className="space-y-4">
                    {/* Pagination controls for parties */}
                    {parties.length > 10 && (
                      <div className="flex items-center justify-between pb-3 border-b border-border">
                        <div className="flex items-center gap-2">
                          <label className="text-xs dark:text-white text-foreground whitespace-nowrap">Per page</label>
                          <Dropdown
                            value={String(partiesPagination.pageSize)}
                            onChange={(v) => setPartiesPagination({ page: 1, pageSize: Number(v) })}
                            options={[5, 10, 25, 50].map((n) => ({
                              value: String(n),
                              label: String(n),
                            }))}
                            size="sm"
                            align="start"
                            className="!min-w-[70px]"
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {Math.min((partiesPagination.page - 1) * partiesPagination.pageSize + 1, parties.length)}–{Math.min(partiesPagination.page * partiesPagination.pageSize, parties.length)} of {parties.length}
                        </p>
                      </div>
                    )}

                    {/* Parties list */}
                    <div className="divide-y divide-border">
                      {parties
                        .slice((partiesPagination.page - 1) * partiesPagination.pageSize, partiesPagination.page * partiesPagination.pageSize)
                        .map((party) => (
                          <div key={party.id} className="py-2.5">
                            {editingPartyId === party.id ? (
                              <div className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                  <FormField label="Name" required>
                                    <FormInput
                                      value={editPartyForm.name}
                                      onChange={(v) =>
                                        setEditPartyForm((f) => ({ ...f, name: v }))
                                      }
                                      placeholder="Party name"
                                    />
                                  </FormField>
                                  <FormField label="Type">
                                    <FormSelect
                                      options={[
                                        { label: "Person", value: "person" },
                                        {
                                          label: "Organization",
                                          value: "organization",
                                        },
                                      ]}
                                      value={editPartyForm.type}
                                      onChange={(v) =>
                                        setEditPartyForm((f) => ({
                                          ...f,
                                          type: v as "person" | "organization",
                                        }))
                                      }
                                      placeholder="Select type"
                                    />
                                  </FormField>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                  <FormField label="Short Name">
                                    <FormInput
                                      value={editPartyForm.shortName}
                                      onChange={(v) =>
                                        setEditPartyForm((f) => ({
                                          ...f,
                                          shortName: v,
                                        }))
                                      }
                                      placeholder="Short name"
                                    />
                                  </FormField>
                                  <FormField label="Email">
                                    <FormInput
                                      value={editPartyForm.email}
                                      onChange={(v) =>
                                        setEditPartyForm((f) => ({
                                          ...f,
                                          email: v,
                                        }))
                                      }
                                      placeholder="Email address"
                                    />
                                  </FormField>
                                </div>
                                <div className="flex items-center gap-2 pt-1">
                                  <button
                                    onClick={() => setEditingPartyId(null)}
                                    className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-accent transition-colors"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={handleUpdateParty}
                                    disabled={!editPartyForm.name || savingParty}
                                    className="px-3 py-1.5 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-50"
                                  >
                                    {savingParty ? "Saving" : "Save"}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-start gap-3">
                                {party.partyType === "organization" ? (
                                  <div className="size-9 rounded-lg bg-amber-50 dark:bg-amber-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <Building2 className="size-4 text-amber-600 dark:text-amber-400" />
                                  </div>
                                ) : (
                                  <div className="size-9 rounded-lg bg-sky-50 dark:bg-sky-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <Users className="size-4 text-sky-600 dark:text-sky-400" />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-semibold text-foreground">
                                    {party.name}
                                  </p>
                                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                                    <span className={`inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full border capitalize ${party.partyType === "organization"
                                      ? "bg-amber-50 text-amber-700 border-amber-100 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20"
                                      : "bg-sky-50 text-sky-700 border-sky-100 dark:bg-sky-500/10 dark:text-sky-400 dark:border-sky-500/20"
                                      }`}>
                                      {party.partyType}
                                    </span>
                                    {party.shortName && (
                                      <span className="text-xs text-muted-foreground">
                                        {party.shortName}
                                      </span>
                                    )}
                                    {party.emailAddresses.length > 0 && (
                                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                        <span className="size-1 rounded-full bg-muted-foreground/50" />
                                        {party.emailAddresses[0]}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div
                                  className={`flex items-center gap-1 flex-shrink-0 ${isPublishedSsp ? "hidden" : ""}`}
                                >
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        onClick={() => {
                                          setEditingPartyId(party.id);
                                          setEditPartyForm({
                                            type: party.partyType as
                                              | "person"
                                              | "organization",
                                            name: party.name,
                                            shortName: party.shortName || "",
                                            email: party.emailAddresses[0] || "",
                                          });
                                        }}
                                        className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                                      >
                                        <Pencil className="size-3.5" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent>Edit</TooltipContent>
                                  </Tooltip>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <button
                                        onClick={() =>
                                          requestDelete(
                                            "Party",
                                            party.id,
                                            party.name,
                                            () => handleDeleteParty(party.id),
                                          )
                                        }
                                        disabled={deletingPartyId === party.id}
                                        className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-500/10 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-50"
                                      >
                                        <Trash2 className="size-3.5" />
                                      </button>
                                    </TooltipTrigger>
                                    <TooltipContent>Delete</TooltipContent>
                                  </Tooltip>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                    </div>

                    {/* Pagination buttons for parties */}
                    {Math.ceil(parties.length / partiesPagination.pageSize) > 1 && (
                      <div className="flex items-center justify-center gap-2 pt-3 border-t border-border">
                        <button
                          onClick={() => setPartiesPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
                          disabled={partiesPagination.page === 1}
                          className="p-1.5 rounded-lg border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronLeft className="size-3.5" />
                        </button>
                        <span className="text-xs text-muted-foreground">
                          Page {partiesPagination.page} of {Math.ceil(parties.length / partiesPagination.pageSize)}
                        </span>
                        <button
                          onClick={() => setPartiesPagination((p) => ({ ...p, page: Math.min(Math.ceil(parties.length / partiesPagination.pageSize), p.page + 1) }))}
                          disabled={partiesPagination.page >= Math.ceil(parties.length / partiesPagination.pageSize)}
                          className="p-1.5 rounded-lg border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                          <ChevronRight className="size-3.5" />
                        </button>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground text-center py-4">
                    No parties defined. Add people or organizations involved.
                  </p>
                )}
              </div>

              {/* Responsible Parties Section */}
              <div className="rounded-lg border border-border bg-card p-5">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-sm font-bold flex items-center gap-2">
                    <span className="flex items-center justify-center size-8 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 ring-1 ring-emerald-100 dark:ring-emerald-500/20">
                      <UserCheck className="size-4" />
                    </span>
                    <span className="text-foreground">Responsible Party Assignments</span>
                  </h3>
                  {!isPublishedSsp && (
                    <button
                      onClick={() => setShowAssignRP(true)}
                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-[10px] font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors"
                    >
                      <Plus className="size-3" /> Assign
                    </button>
                  )}
                </div>
                {responsibleParties.length > 0 ? (
                  <div className="divide-y divide-border">
                    {responsibleParties.map((rp) => {
                      const role = roles.find((r) => r.roleId === rp.roleId);
                      return (
                        <div
                          key={rp.id}
                          className="flex items-start gap-3 py-3"
                        >
                          <div className="flex items-center justify-center size-9 rounded-lg bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5">
                            <UserCheck className="size-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-foreground">
                              {role?.title || rp.roleId}
                            </p>
                            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                              {rp.partyUuids.map((uuid) => {
                                const party = parties.find(
                                  (p) => p.partyUuid === uuid,
                                );
                                const isOrg = party?.partyType === "organization";
                                return (
                                  <span
                                    key={uuid}
                                    className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${isOrg
                                      ? "bg-amber-50 text-amber-700 border-amber-100 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20"
                                      : "bg-sky-50 text-sky-700 border-sky-100 dark:bg-sky-500/10 dark:text-sky-400 dark:border-sky-500/20"
                                      }`}
                                  >
                                    {isOrg ? (
                                      <Building2 className="size-2.5" />
                                    ) : (
                                      <Users className="size-2.5" />
                                    )}
                                    {party?.name || uuid.slice(0, 12) + "…"}
                                  </span>
                                );
                              })}
                            </div>
                          </div>
                          <div
                            className={`flex items-center gap-1 flex-shrink-0 ${isPublishedSsp ? "hidden" : ""}`}
                          >
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <button
                                  onClick={() =>
                                    requestDelete(
                                      "Responsible Party Assignment",
                                      rp.id,
                                      role?.title || rp.roleId,
                                      () => handleDeleteRP(rp.id),
                                    )
                                  }
                                  disabled={deletingRPId === rp.id}
                                  className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-500/10 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-50"
                                >
                                  <Trash2 className="size-3.5" />
                                </button>
                              </TooltipTrigger>
                              <TooltipContent>Delete</TooltipContent>
                            </Tooltip>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground text-center py-4">
                    No assignments yet. Map roles to parties.
                  </p>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* ═══ Locations Tab ═══ */}
      {activeTab === "Locations" && (
        <>
          {locationsLoading ? (
            <LoadingSpinner text="Loading locations" />
          ) : (
            <>
              <div className="rounded-lg border border-border bg-card overflow-hidden">
                {locations.length > 0 ? (
                  <div className="divide-y divide-border">
                    {pagLocations.map((loc) => (
                      <div
                        key={loc.id}
                        className="px-5 py-3.5 hover:bg-muted/40 transition-colors"
                      >
                        {editingLocationId === loc.id ? (
                          /* ── Inline edit form ── */
                          <div className="space-y-3">
                            <FormField label="Title" required>
                              <FormInput
                                value={editLocationForm.title}
                                onChange={(v) =>
                                  setEditLocationForm((f) => ({
                                    ...f,
                                    title: v,
                                  }))
                                }
                                placeholder="Location title"
                              />
                            </FormField>
                            <FormField label="Address Lines (one per line)">
                              <FormTextarea
                                value={editLocationForm.addressLines}
                                onChange={(v) =>
                                  setEditLocationForm((f) => ({
                                    ...f,
                                    addressLines: v,
                                  }))
                                }
                                placeholder="Street address"
                              />
                            </FormField>
                            <div className="grid grid-cols-4 gap-3">
                              <FormField label="City">
                                <FormInput
                                  value={editLocationForm.city}
                                  onChange={(v) =>
                                    setEditLocationForm((f) => ({
                                      ...f,
                                      city: v,
                                    }))
                                  }
                                  placeholder="City"
                                />
                              </FormField>
                              <FormField label="State">
                                <FormInput
                                  value={editLocationForm.state}
                                  onChange={(v) =>
                                    setEditLocationForm((f) => ({
                                      ...f,
                                      state: v,
                                    }))
                                  }
                                  placeholder="State"
                                />
                              </FormField>
                              <FormField label="Postal Code">
                                <FormInput
                                  type="number"
                                  value={editLocationForm.postalCode}
                                  onChange={(v) =>
                                    setEditLocationForm((f) => ({
                                      ...f,
                                      postalCode: v,
                                    }))
                                  }
                                  placeholder="Zip"
                                />
                              </FormField>
                              <FormField label="Country">
                                <FormInput
                                  value={editLocationForm.country}
                                  onChange={(v) =>
                                    setEditLocationForm((f) => ({
                                      ...f,
                                      country: v,
                                    }))
                                  }
                                  placeholder="US"
                                />
                              </FormField>
                            </div>
                            <div className="flex items-center gap-2 pt-1">
                              <button
                                onClick={() => setEditingLocationId(null)}
                                className="px-3 py-1.5 rounded-lg border border-border text-xs font-medium hover:bg-accent transition-colors"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={handleUpdateLocation}
                                disabled={
                                  !editLocationForm.title || savingLocation
                                }
                                className="px-3 py-1.5 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-xs font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-50"
                              >
                                {savingLocation ? "Saving" : "Save"}
                              </button>
                            </div>
                          </div>
                        ) : (
                          /* ── Read-only display ── */
                          <div className="flex items-center gap-4">
                            <div className="flex items-center justify-center size-10 rounded-lg bg-rose-50 dark:bg-rose-500/10 text-rose-600 dark:text-rose-400 flex-shrink-0 ring-1 ring-rose-100 dark:ring-rose-500/20">
                              <MapPin className="size-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-foreground">{loc.title}</p>
                              <div className="flex items-center gap-x-3 gap-y-1 mt-1 flex-wrap text-xs">
                                {loc.addressLines.length > 0 && (
                                  <span className="text-muted-foreground">
                                    {loc.addressLines.join(", ")}
                                  </span>
                                )}
                                {(loc.city || loc.state || loc.postalCode) && (
                                  <span className="inline-flex items-center gap-1 text-muted-foreground">
                                    <span className="size-1 rounded-full bg-muted-foreground/50 hidden sm:inline-block" />
                                    {[loc.city, loc.state, loc.postalCode]
                                      .filter(Boolean)
                                      .join(", ")}
                                  </span>
                                )}
                                {loc.country && (
                                  <span className="inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-100 dark:border-emerald-500/20 tracking-wide">
                                    {loc.country}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div
                              className={`flex items-center gap-1 flex-shrink-0 ${isPublishedSsp ? "hidden" : ""}`}
                            >
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={() => {
                                      setEditingLocationId(loc.id);
                                      setEditLocationForm({
                                        title: loc.title,
                                        addressLines:
                                          loc.addressLines.join("\n"),
                                        city: loc.city || "",
                                        state: loc.state || "",
                                        postalCode: loc.postalCode || "",
                                        country: loc.country || "",
                                      });
                                    }}
                                    className="p-1.5 rounded-md hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
                                  >
                                    <Pencil className="size-3.5" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>Edit</TooltipContent>
                              </Tooltip>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={() =>
                                      requestDelete(
                                        "Location",
                                        loc.id,
                                        loc.title,
                                        () => handleDeleteLocation(loc.id),
                                      )
                                    }
                                    disabled={deletingLocationId === loc.id}
                                    className="p-1.5 rounded-md hover:bg-red-50 dark:hover:bg-red-500/10 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-colors disabled:opacity-50"
                                  >
                                    <Trash2 className="size-3.5" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>Delete</TooltipContent>
                              </Tooltip>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-5 text-center py-12">
                    <MapPin className="size-8 text-muted-foreground mb-3 mx-auto" />
                    <p className="text-sm font-medium">No locations defined</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Add offices, data centers, or cloud regions.
                    </p>
                  </div>
                )}
              </div>
              {filtLocations.length > 0 && (
                <div className="flex items-center justify-between gap-2 px-4 py-2 border-t border-[#e2e8f0] dark:border-white/[0.06] bg-[#fafbfc] dark:bg-white/[0.015] flex-shrink-0">
                  <p className="text-[11px] leading-tight text-[#475569] dark:text-white/70">
                    Showing{" "}
                    <span className="font-semibold text-[#334155] dark:text-white/90">
                      {Math.min((tabPage - 1) * tabPageSize + 1, filtLocations.length)}–{Math.min(tabPage * tabPageSize, filtLocations.length)}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold text-[#334155] dark:text-white/90">
                      {filtLocations.length}
                    </span>
                  </p>
                  {Math.ceil(filtLocations.length / tabPageSize) > 1 && (
                    <div className="flex items-center gap-0.5">
                      <button
                        disabled={tabPage === 1}
                        onClick={() => setCurrentTabPagination({ page: Math.max(1, tabPage - 1), pageSize: tabPageSize })}
                        className="p-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronLeft className="size-3" />
                      </button>

                      {getPageWindow(tabPage, Math.ceil(filtLocations.length / tabPageSize)).map((p, idx) =>
                        p === "dots" ? (
                          <span
                            key={`dots-${idx}`}
                            className="w-6 h-6 flex items-center justify-center text-xs text-[#475569] dark:text-white/70 select-none"
                          >
                            …
                          </span>
                        ) : (
                          <button
                            key={p}
                            onClick={() => setCurrentTabPagination({ page: p as number, pageSize: tabPageSize })}
                            className={`w-6 h-6 text-xs font-medium rounded-md transition-colors ${p === tabPage
                              ? "bg-indigo-600 dark:bg-indigo-600 text-white"
                              : "text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04]"
                              }`}
                          >
                            {p}
                          </button>
                        ),
                      )}

                      <button
                        disabled={tabPage >= Math.ceil(filtLocations.length / tabPageSize)}
                        onClick={() => setCurrentTabPagination({ page: Math.min(Math.ceil(filtLocations.length / tabPageSize), tabPage + 1), pageSize: tabPageSize })}
                        className="p-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronRight className="size-3" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* ═══ History Tab ═══ */}
      {activeTab === "History" && (
        <>
          {historyLoading ? (
            <LoadingSpinner text="Loading change history" />
          ) : historyEntries.length > 0 ? (
            <div className="space-y-4">
              {/* Pagination controls */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <label className="text-sm dark:text-white text-foreground whitespace-nowrap">Rows per page</label>
                  <Dropdown
                    value={String(tabPageSize)}
                    onChange={(v) => setTabPageSize(Number(v))}
                    options={[10, 25, 50, 100].map((n) => ({
                      value: String(n),
                      label: String(n),
                    }))}
                    size="sm"
                    align="start"
                    className="!min-w-[80px]"
                  />
                </div>
                <p className="text-sm text-muted-foreground">
                  Showing{" "}
                  <span className="font-semibold">
                    {Math.min((tabPage - 1) * tabPageSize + 1, historyEntries.length)}–{Math.min(tabPage * tabPageSize, historyEntries.length)}
                  </span>{" "}
                  of{" "}
                  <span className="font-semibold">{historyEntries.length}</span>{" "}
                  entries
                </p>
              </div>

              {/* History entries */}
              <div className="rounded-lg border border-border bg-card overflow-hidden">
                <div className="divide-y divide-border">
                  {historyEntries
                    .slice((tabPage - 1) * tabPageSize, tabPage * tabPageSize)
                    .map((entry) => (
                      <div
                        key={entry.id}
                        className="flex items-start gap-4 px-5 py-3.5 hover:bg-muted/40 transition-colors"
                      >
                        <div className="size-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0 mt-0.5">
                          <History className="size-4 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 ">
                              {entry.action}
                            </span>
                            <span className="text-[10px] font-medium text-muted-foreground capitalize">
                              {entry.entityType}
                            </span>
                          </div>
                          <p className="text-sm">{entry.summary}</p>
                          {entry.changedBy && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              by {entry.changedBy}
                            </p>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground flex-shrink-0 whitespace-nowrap">
                          {new Date(entry.changedAt).toLocaleString()}
                        </span>
                      </div>
                    ))}
                </div>
              </div>

              {/* Pagination */}
              {historyEntries.length > 0 && (
                <div className="flex items-center justify-between gap-2 px-4 py-2 border-t border-[#e2e8f0] dark:border-white/[0.06] bg-[#fafbfc] dark:bg-white/[0.015] flex-shrink-0">
                  <p className="text-[11px] leading-tight text-[#475569] dark:text-white/70">
                    Showing{" "}
                    <span className="font-semibold text-[#334155] dark:text-white/90">
                      {Math.min((tabPage - 1) * tabPageSize + 1, historyEntries.length)}–{Math.min(tabPage * tabPageSize, historyEntries.length)}
                    </span>{" "}
                    of{" "}
                    <span className="font-semibold text-[#334155] dark:text-white/90">
                      {historyEntries.length}
                    </span>
                  </p>
                  {Math.ceil(historyEntries.length / tabPageSize) > 1 && (
                    <div className="flex items-center gap-0.5">
                      <button
                        disabled={tabPage === 1}
                        onClick={() => setCurrentTabPagination({ page: Math.max(1, tabPage - 1), pageSize: tabPageSize })}
                        className="p-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronLeft className="size-3" />
                      </button>

                      {getPageWindow(tabPage, Math.ceil(historyEntries.length / tabPageSize)).map((p, idx) =>
                        p === "dots" ? (
                          <span
                            key={`dots-${idx}`}
                            className="w-6 h-6 flex items-center justify-center text-xs text-[#475569] dark:text-white/70 select-none"
                          >
                            …
                          </span>
                        ) : (
                          <button
                            key={p}
                            onClick={() => setCurrentTabPagination({ page: p as number, pageSize: tabPageSize })}
                            className={`w-6 h-6 text-xs font-medium rounded-md transition-colors ${p === tabPage
                              ? "bg-indigo-600 dark:bg-indigo-600 text-white"
                              : "text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04]"
                              }`}
                          >
                            {p}
                          </button>
                        ),
                      )}

                      <button
                        disabled={tabPage >= Math.ceil(historyEntries.length / tabPageSize)}
                        onClick={() => setCurrentTabPagination({ page: Math.min(Math.ceil(historyEntries.length / tabPageSize), tabPage + 1), pageSize: tabPageSize })}
                        className="p-1 rounded-md border border-[#e2e8f0] dark:border-white/[0.07] text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                      >
                        <ChevronRight className="size-3" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-lg border border-border bg-card p-5 text-center py-12">
              <History className="size-8 text-muted-foreground mb-3 mx-auto" />
              <p className="text-sm font-medium">No change history</p>
              <p className="text-xs text-muted-foreground mt-1">
                Changes to this SSP will appear here
              </p>
            </div>
          )}
        </>
      )}

      {/* ═══ Asset Resolution Tab ═══ */}
      {activeTab === "Asset Resolution" && (
        <>
          <div className="rounded-lg border border-border bg-card p-5 space-y-4">
            <div>
              <h3 className="text-sm font-bold text-foreground mb-1">
                Resolve Asset to Controls
              </h3>
              <p className="text-xs text-muted-foreground">
                Given an external identifier (hostname, IP, ARN, etc.), trace:
                Identifier → Inventory Item → Components → Control
                Implementations
              </p>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="e.g. web-prod-01.example.com or 10.0.1.50"
                  value={resolveQuery}
                  onChange={(e) => setResolveQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleResolveAsset();
                  }}
                  className="w-full pl-9 pr-3 py-2 text-sm bg-background border border-border rounded-lg focus:border-indigo-500 dark:focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/10 dark:focus:ring-indigo-500/10 transition-all outline-none placeholder:text-muted-foreground"
                />
              </div>
              <button
                onClick={handleResolveAsset}
                disabled={!resolveQuery || resolving}
                className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-50"
              >
                {resolving ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  "Resolve"
                )}
              </button>
            </div>
          </div>

          {resolveError && (
            <div className="flex items-center gap-3 p-4 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-xl text-sm text-red-700 dark:text-red-400">
              <AlertCircle className="size-4 flex-shrink-0" />
              <span>{resolveError}</span>
            </div>
          )}

          {resolveResult && (
            <div className="space-y-4">
              {/* Inventory Item */}
              <div className="rounded-lg border border-border bg-card p-4">
                <h4 className="text-xs font-black tracking-widest text-muted-foreground mb-3">
                  Inventory Item
                </h4>
                <p className="text-sm font-medium">
                  {resolveResult.inventoryItem.description}
                </p>
                {resolveResult.inventoryItem.assetIdentifiers.length > 0 && (
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {resolveResult.inventoryItem.assetIdentifiers.map((ai) => (
                      <span
                        key={ai.id}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-muted text-[10px] font-mono"
                      >
                        <span className="text-muted-foreground">
                          {ai.identifierType}:
                        </span>{" "}
                        {ai.identifierValue}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Components */}
              {resolveResult.components.length > 0 && (
                <div className="rounded-lg border border-border bg-card p-4">
                  <h4 className="text-xs font-black tracking-widest text-muted-foreground mb-3">
                    Components ({resolveResult.components.length})
                  </h4>
                  <div className="space-y-2">
                    {resolveResult.components.map((comp) => (
                      <div
                        key={comp.id}
                        className="flex items-center gap-3 py-2 border-b border-border last:border-0"
                      >
                        <Server className="size-4 text-muted-foreground flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{comp.title}</p>
                          <p className="text-xs text-muted-foreground capitalize">
                            {comp.componentType}
                          </p>
                        </div>
                        {(comp.displayStatusLabel || comp.displayStatus) && (
                          <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${getDisplayStatusStyle(comp.displayStatus)}`}>
                            {comp.displayStatusLabel || comp.displayStatus}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Control Implementations */}
              {resolveResult.controlImplementations.length > 0 && (
                <div className="rounded-lg border border-border bg-card p-4">
                  <h4 className="text-xs font-black tracking-widest text-muted-foreground mb-3">
                    Control Implementations (
                    {resolveResult.controlImplementations.length})
                  </h4>
                  <div className="space-y-2">
                    {resolveResult.controlImplementations.map((ci) => (
                      <div
                        key={ci.id}
                        className="py-2 border-b border-border last:border-0"
                      >
                        <span className="px-2 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 text-[10px] font-black text-indigo-600 dark:text-indigo-400">
                          {ci.source}
                        </span>
                        {ci.description && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {ci.description}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ═══ MODALS ═══ */}

      {/* OSCAL Export Modal */}
      <Modal
        open={showExport}
        onClose={() => {
          setShowExport(false);
          setExportTargetDocId(null);
        }}
        title="Export"
        description="Choose a file format to export this System Security Plan."
        size="sm"
      >
        <div className="grid grid-cols-3 gap-3">
          {(["json", "xml", "yaml"] as const).map((fmt) => (
            <button
              key={fmt}
              onClick={() => handleExport(fmt)}
              disabled={exportingDoc}
              className="flex flex-col items-center gap-2 p-4 rounded-lg border border-border hover:border-indigo-400 dark:hover:border-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 transition-colors group disabled:opacity-40"
            >
              {exportingDoc ? (
                <Loader2 className="size-6 animate-spin text-muted-foreground" />
              ) : (
                <Upload className="size-6 text-muted-foreground group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors" />
              )}
              <span className="text-sm font-semibold group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors">
                {fmt.toUpperCase()}
              </span>
            </button>
          ))}
        </div>
      </Modal>

      {/* Export Pulse Overlay */}
      {exportingDoc && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4 bg-card rounded-2xl px-10 py-8 shadow-2xl">
            <div className="relative flex items-center justify-center">
              <div className="absolute size-16 rounded-full bg-indigo-500/20 dark:bg-indigo-500/20 animate-ping" />
              <div className="absolute size-12 rounded-full bg-indigo-500/30 dark:bg-indigo-500/30 animate-pulse" />
              <Upload className="relative size-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <p className="text-sm font-semibold text-foreground mt-2">
              Exporting…
            </p>
            <p className="text-xs text-muted-foreground">
              Please wait while we prepare your file
            </p>
          </div>
        </div>
      )}

      {/* ── Import SSP Modal ── */}
      <Modal
        open={showSspImport}
        onClose={() => {
          setShowSspImport(false);
          resetSspImport();
        }}
        title="Import System Security Plan"
        description="Upload an OSCAL SSP document (.json, .xml, .yaml)"
        size="md"
        footer={
          sspImportStep === "select" ? (
            <div className="flex justify-end w-full gap-2">
              <button
                onClick={() => {
                  setShowSspImport(false);
                  resetSspImport();
                }}
                className="px-4 py-2 rounded-lg bg-muted text-sm font-medium hover:bg-accent transition-colors"
              >
                Cancel
              </button>
              {sspImportFile && (
                <button
                  onClick={sspRunImport}
                  disabled={!sspValidateResult || sspValidating}
                  className={`px-4 py-2 rounded-lg text-white text-sm font-bold transition-colors shadow-md ${!sspValidateResult || sspValidating
                    ? "bg-indigo-400 dark:bg-indigo-400 opacity-50 cursor-not-allowed"
                    : "bg-indigo-600 dark:bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-700"
                    }`}
                >
                  Import
                </button>
              )}
            </div>
          ) : sspImportStep === "url" ? (
            <div className="flex justify-end w-full gap-2">
              <button
                onClick={() => {
                  setSspImportStep("select");
                  setSspImportUrl("");
                  setSspImportFile(null);
                  setSspValidateResult(null);
                  setSspImportError(null);
                  setSspValidating(false);
                }}
                className="px-4 py-2 rounded-lg bg-muted text-sm font-medium hover:bg-accent transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={sspRunImport}
                disabled={!sspValidateResult || sspValidating}
                className={`px-4 py-2 rounded-lg text-white text-sm font-bold transition-colors shadow-md ${!sspValidateResult || sspValidating
                  ? "bg-indigo-400 dark:bg-indigo-400 opacity-50 cursor-not-allowed"
                  : "bg-indigo-600 dark:bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-700"
                  }`}
              >
                Import
              </button>
            </div>
          ) : sspImportStep === "findings" ? (
            <div className="flex items-center justify-end w-full gap-3">
              <button
                onClick={() => {
                  setShowSspImport(false);
                  resetSspImport();
                }}
                className="px-3 py-1.5 rounded-md border border-border text-sm hover:bg-accent transition-colors"
              >
                Close
              </button>
              <button
                onClick={resetSspImport}
                className="flex items-center gap-2 px-4 py-1.5 rounded-md bg-foreground text-background text-sm font-medium hover:opacity-90 transition-opacity"
              >
                Try Again
              </button>
            </div>
          ) : null
        }
      >
        {/* Step: Select source */}
        {sspImportStep === "select" && (
          <div className="py-6 flex flex-col items-center gap-6">
            <input
              id="ssp-import-file-input"
              type="file"
              accept=".json,.yaml,.yml,.xml"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setSspImportFile(file);
                  setSspValidateResult(null);
                  setSspImportError(null);
                  sspStartValidation(file);
                }
              }}
            />
            <div className="w-full">
              <p className="text-xs font-medium text-muted-foreground mb-2">
                Import Mode
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setSspImportMode("full")}
                  className={`flex-1 px-4 py-2.5 rounded-lg border text-sm font-medium transition-colors ${sspImportMode === "full"
                    ? "border-indigo-500 dark:border-indigo-500 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400"
                    : "border-border hover:bg-accent text-muted-foreground"
                    }`}
                >
                  <span className="block font-semibold">Full</span>
                  <span className="block text-[11px] mt-0.5 font-normal opacity-70">
                    Replace entire document
                  </span>
                </button>
                <button
                  disabled
                  className="flex-1 px-4 py-2.5 rounded-lg border border-border text-sm font-medium text-muted-foreground opacity-50 cursor-not-allowed"
                  title="Coming soon"
                >
                  <span className="block font-semibold">Delta</span>
                  <span className="block text-[11px] mt-0.5 font-normal opacity-70">
                    Coming soon
                  </span>
                </button>
              </div>
            </div>
            <p className="text-sm font-semibold text-foreground">
              Select a source to add from
            </p>
            <div className="flex items-center justify-center gap-8">
              <button
                onClick={() =>
                  document.getElementById("ssp-import-file-input")?.click()
                }
                className="flex flex-col items-center gap-3 group"
              >
                <div className="size-20 rounded-full bg-muted flex items-center justify-center group-hover:bg-accent transition-colors">
                  <Upload className="size-8 text-muted-foreground group-hover:text-foreground transition-colors" />
                </div>
                <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                  Upload file
                </span>
              </button>
              <button
                onClick={() => setSspImportStep("url")}
                className="flex flex-col items-center gap-3 group"
              >
                <div className="size-20 rounded-full bg-muted flex items-center justify-center group-hover:bg-accent transition-colors">
                  <Link2 className="size-8 text-muted-foreground group-hover:text-foreground transition-colors" />
                </div>
                <span className="text-sm text-muted-foreground group-hover:text-foreground transition-colors">
                  Source link
                </span>
              </button>
            </div>
            {sspImportFile && (
              <div
                className={`rounded-lg border w-full ${sspValidateResult
                  ? "border-emerald-200 dark:border-emerald-500/20 bg-emerald-50 dark:bg-emerald-500/10"
                  : sspImportError
                    ? "border-red-200 dark:border-red-500/20 bg-red-50 dark:bg-red-500/10"
                    : "border-border bg-muted"
                  }`}
              >
                <div className="flex items-center gap-3 px-4 py-3">
                  <FileText
                    className={`size-6 flex-shrink-0 ${sspValidateResult ? "text-emerald-500" : sspImportError ? "text-red-500" : "text-muted-foreground"}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {sspImportFile.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {(sspImportFile.size / 1024).toFixed(1)} KB
                    </p>
                    {sspValidating && (
                      <p className="text-xs text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5 mt-1">
                        <Loader2 className="size-3 animate-spin" /> Validating…
                      </p>
                    )}
                    {!sspValidating && sspValidateResult && (
                      <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5 mt-1">
                        <CheckCircle className="size-3" /> Validated -{" "}
                        {sspValidateResult.documentType}, OSCAL{" "}
                        {sspValidateResult.oscalVersion}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setSspImportFile(null);
                      setSspValidateResult(null);
                      setSspImportError(null);
                      setSspValidating(false);
                    }}
                    className="p-1 rounded hover:bg-accent flex-shrink-0"
                  >
                    <X className="size-4 text-muted-foreground" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Step: URL input */}
        {sspImportStep === "url" && (
          <div className="space-y-4">
            <FormField
              label="Document URL"
              required
              hint="URL must end in .json, .yaml, or .xml"
            >
              <div className="flex gap-2">
                <div className="flex-1">
                  <FormInput
                    placeholder="https://example.com/ssp.json"
                    value={sspImportUrl}
                    onChange={(v) => {
                      setSspImportUrl(v);
                      setSspValidateResult(null);
                      setSspImportError(null);
                    }}
                    disabled={sspValidating}
                  />
                </div>
                <button
                  onClick={sspFetchUrl}
                  disabled={!sspImportUrl.trim() || sspValidating}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${!sspImportUrl.trim() || sspValidating
                    ? "bg-muted text-muted-foreground cursor-not-allowed"
                    : "bg-indigo-600 dark:bg-indigo-600 text-white hover:bg-indigo-700 dark:hover:bg-indigo-700"
                    }`}
                >
                  {sspValidating ? (
                    <span className="flex items-center gap-1.5">
                      <Loader2 className="size-3.5 animate-spin" /> Fetching…
                    </span>
                  ) : (
                    "Fetch"
                  )}
                </button>
              </div>
            </FormField>
            {(sspValidating || sspValidateResult) && (
              <div
                className={`rounded-lg border w-full ${sspValidateResult
                  ? "border-emerald-200 dark:border-emerald-500/20 bg-emerald-50 dark:bg-emerald-500/10"
                  : sspImportError
                    ? "border-red-200 dark:border-red-500/20 bg-red-50 dark:bg-red-500/10"
                    : "border-border bg-muted"
                  }`}
              >
                <div className="flex items-center gap-3 px-4 py-3">
                  <Link2
                    className={`size-6 flex-shrink-0 ${sspValidateResult ? "text-emerald-500" : "text-muted-foreground"}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {sspImportUrl.split("/").pop() || sspImportUrl}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {sspImportUrl}
                    </p>
                    {sspValidating && (
                      <p className="text-xs text-indigo-600 dark:text-indigo-400 flex items-center gap-1.5 mt-1">
                        <Loader2 className="size-3 animate-spin" /> Fetching &
                        validating…
                      </p>
                    )}
                    {!sspValidating && sspValidateResult && (
                      <p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5 mt-1">
                        <CheckCircle className="size-3" /> Validated -{" "}
                        {sspValidateResult.documentType}, OSCAL{" "}
                        {sspValidateResult.oscalVersion}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setSspValidateResult(null);
                      setSspImportError(null);
                      setSspValidating(false);
                    }}
                    className="p-1 rounded hover:bg-accent flex-shrink-0"
                  >
                    <X className="size-4 text-muted-foreground" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Importing spinner */}
        {sspImportStep === "importing" && (
          <div className="py-12 flex flex-col items-center gap-4">
            <Loader2 className="size-10 animate-spin text-indigo-500 dark:text-indigo-500" />
            <p className="text-sm font-medium">Importing document…</p>
            <p className="text-xs text-muted-foreground text-center">
              Uploading and processing your OSCAL SSP. This may take a moment
              for large files.
            </p>
          </div>
        )}

        {/* Error findings */}
        {sspImportStep === "findings" && (
          <div className="space-y-4">
            <div className="flex items-start gap-2 px-3 py-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-700 dark:text-red-400">
              <AlertTriangle className="size-4 mt-0.5 flex-shrink-0" />
              <div className="space-y-1">
                <p className="text-xs">
                  <span className="font-semibold">Error:</span>{" "}
                  {sspImportErrorTitle || "Import Failed"}
                </p>
                <p className="text-xs">
                  <span className="font-semibold">Message:</span>{" "}
                  {sspImportError}
                </p>
              </div>
            </div>
          </div>
        )}
      </Modal>

      {/* ── Import Session Modal ── */}
      <Modal
        open={showSspSession}
        onClose={() => {
          setShowSspSession(false);
          setSspSessionData(null);
        }}
        title={
          sspSessionData
            ? sspSessionData.jobStatus.toLowerCase() === "committed"
              ? "Import Complete"
              : sspSessionData.jobStatus.toLowerCase() === "validated"
                ? "Commit SSP"
                : sspSessionData.jobStatus.toLowerCase() === "failed" ||
                  sspSessionData.jobStatus.toLowerCase() === "aborted"
                  ? "Import Failed"
                  : "Importing SSP"
            : "Import SSP"
        }
        size="md"
        closable={false}
        footer={
          sspSessionData ? (
            <div className="flex justify-end w-full gap-2">
              {sspSessionData.jobStatus.toLowerCase() === "committed" ? (
                <button
                  onClick={() => {
                    setShowSspSession(false);
                    setSspSessionData(null);
                    resetSspImport();
                  }}
                  className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors shadow-md"
                >
                  Done
                </button>
              ) : sspSessionData.jobStatus.toLowerCase() === "failed" ||
                sspSessionData.jobStatus.toLowerCase() === "aborted" ? (
                <button
                  onClick={() => {
                    setShowSspSession(false);
                    setSspSessionData(null);
                    resetSspImport();
                  }}
                  className="px-4 py-2 rounded-lg bg-muted text-sm font-medium hover:bg-accent transition-colors"
                >
                  Close
                </button>
              ) : (
                <>
                  <button
                    onClick={handleSspAbortSession}
                    disabled={sspAborting || sspCommitting}
                    className="px-4 py-2 rounded-lg border border-red-200 dark:border-red-500/20 text-red-600 dark:text-red-400 text-sm font-medium hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors disabled:opacity-40"
                  >
                    {sspAborting ? "Cancelling…" : "Cancel"}
                  </button>
                  <button
                    onClick={handleSspCommitSession}
                    disabled={
                      sspCommitting ||
                      sspAborting ||
                      sspSessionData.jobStatus.toLowerCase() !== "validated"
                    }
                    className={`px-4 py-2 rounded-lg text-white text-sm font-bold transition-colors shadow-md ${sspCommitting ||
                      sspAborting ||
                      sspSessionData.jobStatus.toLowerCase() !== "validated"
                      ? "bg-indigo-400 dark:bg-indigo-400 opacity-50 cursor-not-allowed"
                      : "bg-indigo-600 dark:bg-indigo-600 hover:bg-indigo-700 dark:hover:bg-indigo-700"
                      }`}
                  >
                    {sspCommitting ? "Committing…" : "Commit"}
                  </button>
                </>
              )}
            </div>
          ) : null
        }
      >
        {sspSessionLoading && (
          <div className="py-12 flex flex-col items-center gap-4">
            <Loader2 className="size-10 animate-spin text-indigo-500 dark:text-indigo-500" />
            <p className="text-sm font-medium">Loading session…</p>
          </div>
        )}
        {sspSessionError && (
          <div className="flex items-start gap-2 px-3 py-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-sm text-red-700 dark:text-red-400 mb-4">
            <AlertTriangle className="size-4 mt-0.5 flex-shrink-0" />
            <p className="text-xs">{sspSessionError}</p>
          </div>
        )}
        {sspSessionData && (
          <div className="space-y-5">
            {sspCommitting && (
              <div className="py-6 flex flex-col items-center gap-3">
                <Loader2 className="size-8 animate-spin text-indigo-500 dark:text-indigo-500" />
                <p className="text-sm font-medium">Committing import…</p>
                <div className="w-full max-w-xs h-1.5 rounded-full bg-muted overflow-hidden">
                  <div
                    className="h-full rounded-full bg-indigo-500 dark:bg-indigo-500 animate-pulse"
                    style={{ width: "70%" }}
                  />
                </div>
              </div>
            )}
            {!sspCommitting && (
              <>
                <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-3">
                  <div className="flex items-center justify-between py-1.5 border-b border-border">
                    <span className="text-xs text-muted-foreground">
                      Status
                    </span>
                    <span
                      className={`text-sm font-semibold capitalize ${sspSessionData.jobStatus.toLowerCase() === "committed"
                        ? "text-emerald-600 dark:text-emerald-400"
                        : sspSessionData.jobStatus.toLowerCase() ===
                          "failed" ||
                          sspSessionData.jobStatus.toLowerCase() ===
                          "aborted"
                          ? "text-red-600 dark:text-red-400"
                          : sspSessionData.jobStatus.toLowerCase() ===
                            "validated"
                            ? "text-indigo-600 dark:text-indigo-400"
                            : "text-amber-600 dark:text-amber-400"
                        }`}
                    >
                      {sspSessionData.jobStatus}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-1.5 border-b border-border">
                    <span className="text-xs text-muted-foreground">
                      Job ID
                    </span>
                    <code className="text-xs font-mono text-foreground">
                      {sspSessionData.id}
                    </code>
                  </div>
                  <div className="flex items-center justify-between py-1.5 border-b border-border">
                    <span className="text-xs text-muted-foreground">
                      Doc Type
                    </span>
                    <span className="text-sm font-medium capitalize">
                      {sspSessionData.documentType}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-xs text-muted-foreground">Name</span>
                    <span className="text-sm font-medium">
                      {sspSessionData.sourceFilename}
                    </span>
                  </div>
                </div>
                {sspSessionData.findings.length > 0 && (
                  <div className="rounded-lg border border-border p-4">
                    <h4 className="text-xs font-bold text-muted-foreground mb-2">
                      Findings ({sspSessionData.findings.length})
                    </h4>
                    <div className="space-y-2">
                      {sspSessionData.findings.map((f) => (
                        <div
                          key={f.id}
                          className="flex items-start gap-2 text-xs"
                        >
                          <span
                            className={`px-1.5 py-0.5 rounded font-bold text-[10px] ${f.severity === "info"
                              ? "bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400"
                              : f.severity === "warning"
                                ? "bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400"
                                : "bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400"
                              }`}
                          >
                            {f.severity}
                          </span>
                          <div className="flex-1">
                            <p className="font-medium">{f.title}</p>
                            <p className="text-muted-foreground">{f.detail}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </Modal>

      {/* Add Information Type Modal */}
      <Modal
        open={showAddInfoType}
        onClose={() => setShowAddInfoType(false)}
        title="Add Information Type"
        description="Register an information type processed by this system"
        size="md"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowAddInfoType(false)}
            onSubmit={handleAddInfoType}
            submitLabel={addingInfoType ? "Saving" : "Save"}
            submitDisabled={
              !infoTypeForm.title ||
              !infoTypeForm.confidentiality ||
              !infoTypeForm.integrity ||
              !infoTypeForm.availability ||
              addingInfoType
            }
          />
        }
      >
        <FormField label="Title" required>
          <FormInput
            placeholder="e.g. Personally Identifiable Information"
            value={infoTypeForm.title}
            onChange={(v) => setInfoTypeForm((f) => ({ ...f, title: v }))}
          />
        </FormField>
        <FormField label="Description">
          <FormTextarea
            placeholder="Description of this information type"
            value={infoTypeForm.description}
            onChange={(v) => setInfoTypeForm((f) => ({ ...f, description: v }))}
            rows={2}
          />
        </FormField>
        <div className="grid grid-cols-3 gap-3">
          <FormField label="Confidentiality" required>
            <FormSelect
              options={[
                { value: "low", label: "Low" },
                { value: "moderate", label: "Moderate" },
                { value: "high", label: "High" },
              ]}
              placeholder="Select"
              value={infoTypeForm.confidentiality}
              onChange={(v) =>
                setInfoTypeForm((f) => ({ ...f, confidentiality: v }))
              }
            />
          </FormField>
          <FormField label="Integrity" required>
            <FormSelect
              options={[
                { value: "low", label: "Low" },
                { value: "moderate", label: "Moderate" },
                { value: "high", label: "High" },
              ]}
              placeholder="Select"
              value={infoTypeForm.integrity}
              onChange={(v) => setInfoTypeForm((f) => ({ ...f, integrity: v }))}
            />
          </FormField>
          <FormField label="Availability" required>
            <FormSelect
              options={[
                { value: "low", label: "Low" },
                { value: "moderate", label: "Moderate" },
                { value: "high", label: "High" },
              ]}
              placeholder="Select"
              value={infoTypeForm.availability}
              onChange={(v) =>
                setInfoTypeForm((f) => ({ ...f, availability: v }))
              }
            />
          </FormField>
        </div>
      </Modal>

      {/* Add Component Modal */}
      <Modal
        open={showAddComponent}
        onClose={() => setShowAddComponent(false)}
        title="Add Component"
        description="Add a component to this SSP"
        size="md"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowAddComponent(false)}
            onSubmit={handleAddComponent}
            submitLabel={addingComponent ? "Saving" : "Save"}
            submitDisabled={
              !componentForm.title ||
              !componentForm.componentType ||
              addingComponent
            }
          />
        }
      >
        <FormField label="Component Name" required>
          <FormInput
            placeholder="e.g. API Gateway"
            value={componentForm.title}
            onChange={(v) => setComponentForm((f) => ({ ...f, title: v }))}
          />
        </FormField>
        <FormField label="Component Type" required>
          <FormSelect
            options={COMPONENT_TYPES}
            placeholder="Select type"
            value={componentForm.componentType}
            onChange={(v) =>
              setComponentForm((f) => ({
                ...f,
                componentType: v,
              }))
            }
          />
        </FormField>
        <FormField label="Description">
          <FormTextarea
            placeholder="Description of the component"
            value={componentForm.description}
            onChange={(v) =>
              setComponentForm((f) => ({ ...f, description: v }))
            }
            rows={2}
          />
        </FormField>
        <FormField label="Purpose">
          <FormInput
            placeholder="e.g. Entry point for all API traffic"
            value={componentForm.purpose}
            onChange={(v) => setComponentForm((f) => ({ ...f, purpose: v }))}
          />
        </FormField>
        <FormField label="Status">
          <FormSelect
            options={SYSTEM_STATUSES}
            placeholder="Select status"
            value={componentForm.status || ""}
            onChange={(v) =>
              setComponentForm((f) => ({
                ...f,
                status: v.toLowerCase(),
              }))
            }
          />
        </FormField>
      </Modal>

      {/* Add Inventory Item Modal */}
      <Modal
        open={showAddAsset}
        onClose={() => setShowAddAsset(false)}
        title="Add Inventory Item"
        description="Add a new asset to the system inventory"
        size="md"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowAddAsset(false)}
            onSubmit={handleAddAsset}
            submitLabel={addingAsset ? "Saving" : "Save"}
            submitDisabled={
              !assetForm.componentId || !assetForm.description || addingAsset
            }
          />
        }
      >
        {sspComponents.length === 0 ? (
          <div className="bg-yellow-50 dark:bg-yellow-500/10 border border-yellow-300 dark:border-yellow-500/30 text-yellow-800 dark:text-yellow-300 rounded-md p-3 text-sm">
            No components found. Please create a Component first before adding
            Inventory Items.
          </div>
        ) : (
          <FormField label="Component" required>
            <FormSelect
              options={sspComponents.map((c) => ({
                label: `${c.title} (${c.componentType})`,
                value: c.id,
              }))}
              placeholder="Select a component"
              value={assetForm.componentId}
              onChange={(v) => setAssetForm((f) => ({ ...f, componentId: v }))}
            />
          </FormField>
        )}
        <FormField label="Description" required>
          <FormTextarea
            placeholder="e.g. Production PostgreSQL RDS Instance"
            value={assetForm.description}
            onChange={(v) => setAssetForm((f) => ({ ...f, description: v }))}
            rows={2}
          />
        </FormField>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium">Asset Identifiers</label>
            <button
              type="button"
              onClick={() =>
                setAssetForm((f) => ({
                  ...f,
                  identifiers: [
                    ...f.identifiers,
                    { identifierType: "", identifierValue: "" },
                  ],
                }))
              }
              className="flex items-center gap-1 text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
            >
              <Plus className="size-3" />
              Add Identifier
            </button>
          </div>
          {assetForm.identifiers.map((ident, idx) => (
            <div key={idx} className="flex items-start gap-2">
              <div className="grid grid-cols-2 gap-2 flex-1">
                <FormSelect
                  options={[
                    { value: "hostname", label: "Hostname" },
                    { value: "ip_address", label: "IP Address" },
                    { value: "ipv4-address", label: "IPv4 Address" },
                    { value: "ipv6-address", label: "IPv6 Address" },
                    { value: "fqdn", label: "FQDN" },
                    { value: "mac-address", label: "MAC Address" },
                    { value: "serial-number", label: "Serial Number" },
                    { value: "cloud-arn", label: "Cloud ARN" },
                    { value: "container-id", label: "Container ID" },
                    { value: "uri", label: "URI" },
                    { value: "asset-id", label: "Asset ID" },
                    { value: "other", label: "Other" },
                  ]}
                  placeholder="Select type"
                  value={ident.identifierType}
                  onChange={(v) =>
                    setAssetForm((f) => ({
                      ...f,
                      identifiers: f.identifiers.map((id, i) =>
                        i === idx ? { ...id, identifierType: v } : id,
                      ),
                    }))
                  }
                />
                <FormInput
                  placeholder="e.g. web-prod-01.example.com"
                  value={ident.identifierValue}
                  onChange={(v) =>
                    setAssetForm((f) => ({
                      ...f,
                      identifiers: f.identifiers.map((id, i) =>
                        i === idx ? { ...id, identifierValue: v } : id,
                      ),
                    }))
                  }
                />
              </div>
              {assetForm.identifiers.length > 1 && (
                <button
                  type="button"
                  onClick={() =>
                    setAssetForm((f) => ({
                      ...f,
                      identifiers: f.identifiers.filter((_, i) => i !== idx),
                    }))
                  }
                  className="mt-1.5 p-1.5 rounded-md text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors flex-shrink-0"
                >
                  <Trash2 className="size-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      </Modal>

      {/* Add Leveraged Authorization Modal */}
      <Modal
        open={showAddLevAuth}
        onClose={() => setShowAddLevAuth(false)}
        title="Add Leveraged Authorization"
        description="Reference an authorization from another system that this SSP inherits controls from"
        size="md"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowAddLevAuth(false)}
            onSubmit={handleAddLevAuth}
            submitLabel={addingLevAuth ? "Saving" : "Save"}
            submitDisabled={!levAuthForm.title || addingLevAuth}
          />
        }
      >
        <FormField label="Title" required>
          <FormInput
            placeholder="e.g. AWS GovCloud FedRAMP Authorization"
            value={levAuthForm.title}
            onChange={(v) => setLevAuthForm((f) => ({ ...f, title: v }))}
          />
        </FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Authorizing Party">
            <FormSelect
              options={[
                { label: "— None —", value: "" },
                ...parties.map((p) => ({
                  label: `${p.name} (${p.partyType})`,
                  value: p.partyUuid,
                })),
              ]}
              placeholder="Select a party"
              value={levAuthForm.partyUuid}
              onChange={(v) => setLevAuthForm((f) => ({ ...f, partyUuid: v }))}
            />
          </FormField>
          <FormField label="Date Authorized">
            <FormDatePicker
              value={levAuthForm.dateAuthorized}
              onChange={(v) =>
                setLevAuthForm((f) => ({ ...f, dateAuthorized: v }))
              }
            />
          </FormField>
        </div>
      </Modal>

      {/* Add Control Implementation Modal */}
      <Modal
        open={showAddControlImpl}
        onClose={() => setShowAddControlImpl(false)}
        title="Add Control Implementation"
        description="Document how a specific control is satisfied by this system"
        size="md"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowAddControlImpl(false)}
            onSubmit={handleAddControlImpl}
            submitLabel={addingControlImpl ? "Saving" : "Save"}
            submitDisabled={
              controlImplForm.componentIds.length === 0 ||
              !controlImplForm.source ||
              !controlImplForm.controlId ||
              !controlImplForm.implementationStatus ||
              addingControlImpl
            }
          />
        }
      >
        {/* Component Selection (multi-select) */}
        {sspComponents.length === 0 ? (
          <div className="bg-yellow-50 dark:bg-yellow-500/10 border border-yellow-300 dark:border-yellow-500/30 text-yellow-800 dark:text-yellow-300 rounded-md p-3 text-sm">
            No components found. Please create a Component first before adding
            Control Implementations.
          </div>
        ) : (
          <FormField label="Components" required>
            <div className="rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] bg-[#f5f7fb] dark:bg-white/[0.04] max-h-40 overflow-y-auto">
              {sspComponents.map((c) => {
                const checked = controlImplForm.componentIds.includes(c.id);
                return (
                  <label
                    key={c.id}
                    className="flex items-center gap-2.5 px-3 py-2 hover:bg-black/[0.03] dark:hover:bg-white/[0.03] cursor-pointer transition-colors border-b border-[#e2e8f0]/50 dark:border-white/[0.04] last:border-0"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        setControlImplForm((f) => ({
                          ...f,
                          componentIds: checked
                            ? f.componentIds.filter((id) => id !== c.id)
                            : [...f.componentIds, c.id],
                        }))
                      }
                      className="size-3.5 rounded border-border accent-indigo-600 cursor-pointer"
                    />
                    <span className="text-sm text-foreground">
                      {c.title}{" "}
                      <span className="text-xs text-muted-foreground">
                        ({c.componentType})
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          </FormField>
        )}

        {/* Control ID + Description */}
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Control ID" required>
            <FormSelect
              options={ciControls.map((c) => ({
                label: c.controlId,
                value: c.controlId,
              }))}
              placeholder={
                ciLoadingControls ? "Loading controls" : "Select a control"
              }
              value={controlImplForm.controlId}
              onChange={async (v) => {
                const ctrl = ciControls.find((c) => c.controlId === v);
                setControlImplForm((f) => ({
                  ...f,
                  controlId: v,
                  description: ctrl?.title || "",
                  setParameters: [],
                  statements: [],
                }));
                setCiSelectedControlDetail(null);
                if (ctrl) {
                  setCiLoadingControlDetail(true);
                  try {
                    const res = await getControl(ciCatalogId, ctrl.controlId);
                    setCiSelectedControlDetail(res.data);
                    // Auto-populate parameters from the control
                    if (res.data.params && res.data.params.length > 0) {
                      setControlImplForm((f) => ({
                        ...f,
                        setParameters: res.data.params.map((p) => ({
                          paramId: p.paramId,
                          values: "",
                          remarks: "",
                        })),
                      }));
                    }
                    // Auto-populate all statements (mandatory)
                    const allStmts: {
                      statementId: string;
                      description: string;
                      sortOrder: string;
                    }[] = [];
                    res.data.parts
                      .filter((p) => p.name === "statement")
                      .forEach((part) => {
                        allStmts.push({
                          statementId: part.partId,
                          description: part.prose || "",
                          sortOrder: String(allStmts.length),
                        });
                        part.parts.forEach((sub) => {
                          allStmts.push({
                            statementId: sub.partId,
                            description: sub.prose || "",
                            sortOrder: String(allStmts.length),
                          });
                        });
                      });
                    if (allStmts.length > 0) {
                      setControlImplForm((f) => ({
                        ...f,
                        statements: allStmts,
                      }));
                    }
                  } catch {
                    setCiSelectedControlDetail(null);
                  } finally {
                    setCiLoadingControlDetail(false);
                  }
                }
              }}
            />
          </FormField>
          <FormField label="Description">
            <FormInput
              placeholder="Auto-filled from control"
              value={controlImplForm.description}
              onChange={(v) =>
                setControlImplForm((f) => ({ ...f, description: v }))
              }
            />
          </FormField>
        </div>

        {/* ── Control Detail (from catalog) ── */}
        {ciLoadingControlDetail && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
            <Loader2 className="size-4 animate-spin" />
            Loading control details
          </div>
        )}

        {ciSelectedControlDetail && !ciLoadingControlDetail && (
          <>
            {/* ── Guidance (read-only) ── */}
            {ciSelectedControlDetail.parts.filter((p) => p.name === "guidance")
              .length > 0 && (
                <div className="rounded-lg border border-amber-200 dark:border-amber-500/20 bg-amber-50/50 dark:bg-amber-500/5 p-3 space-y-1.5">
                  <p className="text-xs font-semibold text-amber-800 dark:text-amber-400">
                    Guidance
                  </p>
                  {ciSelectedControlDetail.parts
                    .filter((p) => p.name === "guidance")
                    .map((part) => (
                      <p
                        key={part.id}
                        className="text-xs text-amber-700 dark:text-amber-300/80"
                      >
                        {part.prose}
                      </p>
                    ))}
                </div>
              )}

            {/* ── Statements (read-only, auto-included) ── */}
            {ciSelectedControlDetail.parts.filter((p) => p.name === "statement")
              .length > 0 && (
                <div className="rounded-lg border border-border p-3 space-y-2">
                  <p className="text-xs font-semibold text-foreground">
                    Statements
                  </p>
                  {ciSelectedControlDetail.parts
                    .filter((p) => p.name === "statement")
                    .map((part) => {
                      const allStatements = [
                        { id: part.partId, prose: part.prose },
                        ...part.parts.map((sub) => ({
                          id: sub.partId,
                          prose: sub.prose,
                        })),
                      ];
                      return allStatements.map((stmt) => (
                        <div
                          key={stmt.id}
                          className="flex items-start gap-2.5 p-2.5 rounded-lg border border-indigo-300 dark:border-indigo-500/30 bg-indigo-50/50 dark:bg-indigo-500/5"
                        >
                          <CheckCircle className="size-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <span className="text-[10px] font-bold text-muted-foreground ">
                              {stmt.id}
                            </span>
                            {stmt.prose && (
                              <p className="text-xs text-foreground mt-0.5">
                                {stmt.prose}
                              </p>
                            )}
                          </div>
                        </div>
                      ));
                    })}
                </div>
              )}

            {/* ── Parameters (value only) ── */}
            {ciSelectedControlDetail.params.length > 0 && (
              <div className="rounded-lg border border-border p-3 space-y-3">
                <p className="text-xs font-semibold text-foreground">
                  Parameters
                </p>
                {controlImplForm.setParameters.map((param, idx) => {
                  const catalogParam = ciSelectedControlDetail.params.find(
                    (p) => p.paramId === param.paramId,
                  );
                  return (
                    <div
                      key={param.paramId}
                      className="rounded-lg border border-dashed border-border p-3 space-y-1"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold text-muted-foreground ">
                          {param.paramId}
                        </span>
                        {catalogParam?.label && (
                          <span className="text-[10px] font-medium text-indigo-600 dark:text-indigo-400">
                            {catalogParam.label}
                          </span>
                        )}
                      </div>
                      <FormInput
                        placeholder={
                          catalogParam?.values && catalogParam.values.length > 0
                            ? `e.g. ${catalogParam.values.join(", ")}`
                            : "Enter value"
                        }
                        value={param.values}
                        onChange={(v) =>
                          setControlImplForm((f) => ({
                            ...f,
                            setParameters: f.setParameters.map((p, i) =>
                              i === idx ? { ...p, values: v } : p,
                            ),
                          }))
                        }
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* Implementation Status + Responsible Role IDs */}
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Implementation Status" required>
            <FormSelect
              options={IMPLEMENTATION_STATUSES}
              placeholder="Select status"
              value={controlImplForm.implementationStatus}
              onChange={(v) =>
                setControlImplForm((f) => ({
                  ...f,
                  implementationStatus: v as ImplementationStatus,
                }))
              }
            />
          </FormField>
          <FormField label="Responsible Role IDs" hint="Comma-separated">
            <FormInput
              placeholder="e.g. isso, system-owner"
              value={controlImplForm.responsibleRoleIds}
              onChange={(v) =>
                setControlImplForm((f) => ({ ...f, responsibleRoleIds: v }))
              }
            />
          </FormField>
        </div>
      </Modal>

      {/* Edit Control Implementation Modal */}
      <Modal
        open={showEditControlImpl}
        onClose={() => {
          setShowEditControlImpl(false);
          setEditingControlImplId(null);
        }}
        title="Edit Control Implementation"
        description={`Editing implemented requirement for ${editControlImplForm.controlId}`}
        size="md"
        footer={
          <ModalFooterButtons
            onCancel={() => {
              setShowEditControlImpl(false);
              setEditingControlImplId(null);
            }}
            onSubmit={handleUpdateControlImpl}
            submitLabel={savingControlImpl ? "Saving" : "Save"}
            submitDisabled={
              !editControlImplForm.controlId ||
              !editControlImplForm.implementationStatus ||
              savingControlImpl
            }
          />
        }
      >
        {/* Components (multi-select with per-component description) */}
        <FormField label="Components">
          {sspComponents.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No components available.
            </p>
          ) : (
            <div className="rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] bg-[#f5f7fb] dark:bg-white/[0.04] max-h-72 overflow-y-auto">
              {sspComponents.map((c) => {
                const checked = editControlImplForm.byComponents.some(
                  (bc) => bc.componentUuid === c.componentUuid,
                );
                const bcEntry = editControlImplForm.byComponents.find(
                  (bc) => bc.componentUuid === c.componentUuid,
                );
                return (
                  <div
                    key={c.componentUuid}
                    className="border-b border-[#e2e8f0]/50 dark:border-white/[0.04] last:border-0"
                  >
                    {/* Checkbox row */}
                    <label className="flex items-center gap-2.5 px-3 py-2 hover:bg-black/[0.03] dark:hover:bg-white/[0.03] cursor-pointer transition-colors">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() =>
                          setEditControlImplForm((f) => ({
                            ...f,
                            byComponents: checked
                              ? f.byComponents.filter(
                                (bc) => bc.componentUuid !== c.componentUuid,
                              )
                              : [
                                ...f.byComponents,
                                {
                                  componentUuid: c.componentUuid,
                                  description: "",
                                  implementationStatus: "",
                                  responsibleRoles: "",
                                },
                              ],
                          }))
                        }
                        className="size-3.5 rounded border-border accent-indigo-600 cursor-pointer flex-shrink-0"
                      />
                      <span className="text-sm text-foreground">
                        {c.title}{" "}
                        <span className="text-xs text-muted-foreground">
                          ({c.componentType})
                        </span>
                      </span>
                    </label>

                    {/* Per-component description — shown when checked */}
                    {checked && (
                      <div className="px-3 pb-2.5">
                        <p className="text-[10px] text-muted-foreground mb-1 font-medium tracking-wide">
                          How does <span className="text-indigo-400">{c.title}</span> implement this control?
                        </p>
                        <textarea
                          rows={2}
                          value={bcEntry?.description ?? ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setEditControlImplForm((f) => ({
                              ...f,
                              byComponents: f.byComponents.map((bc) =>
                                bc.componentUuid === c.componentUuid
                                  ? { ...bc, description: val }
                                  : bc,
                              ),
                            }));
                          }}
                          placeholder={`Describe how ${c.title} satisfies this control...`}
                          className="w-full rounded-md border border-[#e2e8f0] dark:border-white/[0.07] bg-background px-2.5 py-1.5 text-xs text-foreground outline-none transition-colors resize-none placeholder:text-muted-foreground hover:border-indigo-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </FormField>

        {/* Control ID + Implementation Status (same line) */}
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Control ID">
            <FormInput
              value={editControlImplForm.controlId}
              onChange={() => { }}
              placeholder="Control ID"
              disabled
            />
          </FormField>
          <FormField label="Implementation Status" required>
            <FormSelect
              options={IMPLEMENTATION_STATUSES}
              value={editControlImplForm.implementationStatus}
              onChange={(v) =>
                setEditControlImplForm((f) => ({
                  ...f,
                  implementationStatus: v as ImplementationStatus,
                }))
              }
              placeholder="Select status"
            />
          </FormField>
        </div>

        {/* Responsible Roles */}
        <FormField label="Responsible Role IDs" hint="Comma-separated role IDs (e.g. isso, system-owner). Stored as OSCAL responsible-roles on this implemented-requirement.">
          <FormInput
            placeholder="e.g. isso, system-owner, system-engineer"
            value={editControlImplForm.responsibleRoleIds}
            onChange={(v) =>
              setEditControlImplForm((f) => ({ ...f, responsibleRoleIds: v }))
            }
          />
        </FormField>

        {/* Overall implementation description */}
        <FormField
          label="Overall Implementation Description"
          hint="OSCAL: implemented-requirement.description — summarises how the system as a whole satisfies this control"
        >
          <FormTextarea
            value={editControlImplForm.description}
            onChange={(v) =>
              setEditControlImplForm((f) => ({ ...f, description: v }))
            }
            placeholder="Describe how this control is satisfied by the system overall..."
            rows={3}
          />
        </FormField>

        {/* Parameters from catalog / profile */}
        {(() => {
          const bc = findBaselineControlForId(
            baselineControls,
            editControlImplForm.controlId,
          );
          const parameterRows = buildParameterRows(
            bc,
            editControlImplForm.setParameters,
          );

          if (parameterRows.length > 0) {
            return (
              <div>
                <p className="text-sm font-medium text-foreground mb-2">
                  Parameters
                </p>
                <div className="rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] overflow-hidden">
                  {parameterRows.map((param, i) => (
                    <div
                      key={param.paramId}
                      className={`px-3 py-2.5 ${i !== parameterRows.length - 1 ? "border-b border-[#e2e8f0] dark:border-white/[0.07]" : ""}`}
                    >
                      {(() => {
                        const sspEntry = editControlImplForm.setParameters.find(
                          (p) => p.paramId === param.paramId,
                        );
                        const sspVal = sspEntry?.values?.trim();
                        const resolvedVal = sspVal
                          ? sspVal
                          : param.componentValues.length
                            ? param.componentValues.join(", ")
                            : param.profileValues.length
                              ? param.profileValues.join(", ")
                              : param.catalogValues.join(", ");
                        const resolvedSource = sspVal
                          ? "SSP override"
                          : param.componentValues.length
                            ? "Component"
                            : param.profileValues.length
                              ? "Profile"
                              : "Catalog default";
                        const sourceColor: Record<string, string> = {
                          "SSP override": "bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400",
                          Component: "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
                          Profile: "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300",
                          "Catalog default": "bg-muted text-muted-foreground",
                        };
                        return (
                          <>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs font-mono text-indigo-400">
                                {param.paramId}
                              </span>
                              {param.label && (
                                <span className="text-xs text-muted-foreground">
                                  — {param.label}
                                </span>
                              )}
                              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${sourceColor[resolvedSource]}`}>
                                {resolvedSource}
                              </span>
                              {resolvedVal && (
                                <span className="text-xs text-foreground font-medium truncate max-w-[160px]" title={resolvedVal}>
                                  {resolvedVal}
                                </span>
                              )}
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                              <div className="rounded-md bg-muted/40 p-2">
                                <span className="block text-muted-foreground mb-0.5">
                                  Catalog default
                                </span>
                                <span className="text-foreground">
                                  {param.catalogValues.length
                                    ? param.catalogValues.join(", ")
                                    : "-"}
                                </span>
                              </div>
                              <div className="rounded-md bg-muted/40 p-2">
                                <span className="block text-muted-foreground mb-0.5">
                                  Profile / Component
                                </span>
                                <span className="text-foreground">
                                  {param.profileValues.length
                                    ? param.profileValues.join(", ")
                                    : param.componentValues.length
                                      ? param.componentValues.join(", ")
                                      : "-"}
                                </span>
                              </div>
                              <div className="rounded-md bg-muted/40 p-2">
                                <span className="block text-muted-foreground mb-0.5 font-medium text-indigo-600 dark:text-indigo-400">
                                  SSP override
                                </span>
                                <input
                                  type="text"
                                  value={sspEntry?.values ?? ""}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setEditControlImplForm((form) => {
                                      const existingIdx = form.setParameters.findIndex(
                                        (p) => p.paramId === param.paramId,
                                      );
                                      const nextParams = [...form.setParameters];
                                      if (existingIdx >= 0) {
                                        nextParams[existingIdx] = { ...nextParams[existingIdx], values: value };
                                      } else {
                                        nextParams.push({ paramId: param.paramId, values: value });
                                      }
                                      return { ...form, setParameters: nextParams };
                                    });
                                  }}
                                  placeholder="Override value (comma-separated)"
                                  onClick={(e) => e.stopPropagation()}
                                  className="mt-1 w-full rounded-md border border-indigo-200 dark:border-indigo-500/30 bg-background px-2 py-1.5 text-xs text-foreground outline-none transition-colors placeholder:text-muted-foreground hover:border-indigo-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                                />
                              </div>
                            </div>
                          </>
                        );
                      })()}
                      {param.constraints.length > 0 && (
                        <div className="text-xs mt-2">
                          <span className="text-muted-foreground">
                            Constraints:{" "}
                          </span>
                          <span className="text-foreground">
                            {param.constraints.join(", ")}
                          </span>
                        </div>
                      )}
                      {param.guidelines.length > 0 && (
                        <div className="text-xs mt-1">
                          <span className="text-muted-foreground">
                            Guidelines:{" "}
                          </span>
                          <span className="text-foreground">
                            {param.guidelines.join("; ")}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          const topParams = (bc?.parameters ?? []).filter((p) =>
            p.paramId?.trim(),
          );
          const detailParams = (bc?.controlDetails?.params ?? []).filter(
            (p) =>
              p.paramId?.trim() &&
              (p.label?.trim() ||
                (p.values ?? []).length > 0 ||
                (p.guidelines ?? []).length > 0),
          );

          if (topParams.length > 0) {
            return (
              <div>
                <p className="text-sm font-medium text-foreground mb-2">
                  Parameters
                </p>
                <div className="rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] overflow-hidden">
                  {topParams.map((p, i) => (
                    <div
                      key={p.paramId}
                      className={`px-3 py-2.5 ${i !== topParams.length - 1 ? "border-b border-[#e2e8f0] dark:border-white/[0.07]" : ""}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-mono text-indigo-400">
                          {p.paramId}
                        </span>
                        {p.label && (
                          <span className="text-xs text-muted-foreground">
                            — {p.label}
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-muted-foreground">
                            Catalog values:{" "}
                          </span>
                          <span className="text-foreground">
                            {p.catalogValues?.length
                              ? p.catalogValues.join(", ")
                              : "—"}
                          </span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">
                            Profile values:{" "}
                          </span>
                          <span className="text-foreground">
                            {p.profileValues?.length
                              ? p.profileValues.join(", ")
                              : "—"}
                          </span>
                        </div>
                        {p.constraints?.length > 0 && (
                          <div className="col-span-2">
                            <span className="text-muted-foreground">
                              Constraints:{" "}
                            </span>
                            <span className="text-foreground">
                              {p.constraints.join(", ")}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          if (detailParams.length > 0) {
            return (
              <div>
                <p className="text-sm font-medium text-foreground mb-2">
                  Parameters
                </p>
                <div className="rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] overflow-hidden">
                  {detailParams.map((p, i) => (
                    <div
                      key={p.paramId}
                      className={`px-3 py-2.5 ${i !== detailParams.length - 1 ? "border-b border-[#e2e8f0] dark:border-white/[0.07]" : ""}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-mono text-indigo-400">
                          {p.paramId}
                        </span>
                        {p.label && (
                          <span className="text-xs text-muted-foreground">
                            — {p.label}
                          </span>
                        )}
                      </div>
                      {p.values && p.values.length > 0 && (
                        <div className="text-xs">
                          <span className="text-muted-foreground">
                            Values:{" "}
                          </span>
                          <span className="text-foreground">
                            {p.values.join(", ")}
                          </span>
                        </div>
                      )}
                      {p.guidelines && p.guidelines.length > 0 && (
                        <div className="text-xs mt-0.5">
                          <span className="text-muted-foreground">
                            Guidelines:{" "}
                          </span>
                          <span className="text-foreground">
                            {p.guidelines.map((g) => g.prose).join("; ")}
                          </span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          return null;
        })()}
      </Modal>

      {/* Add Role Modal */}
      <Modal
        open={showAddRole}
        onClose={() => setShowAddRole(false)}
        title="Add Role"
        description="Define a role for this SSP's metadata"
        size="sm"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowAddRole(false)}
            onSubmit={handleAddRole}
            submitLabel={addingRole ? "Saving" : "Save"}
            submitDisabled={!roleForm.roleId || !roleForm.title || addingRole}
          />
        }
      >
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Role ID" required hint="Machine identifier">
            <FormInput
              placeholder="e.g. isso"
              value={roleForm.roleId}
              onChange={(v) => setRoleForm((f) => ({ ...f, roleId: v }))}
            />
          </FormField>
          <FormField label="Title" required>
            <FormInput
              placeholder="e.g. Information System Security Officer"
              value={roleForm.title}
              onChange={(v) => setRoleForm((f) => ({ ...f, title: v }))}
            />
          </FormField>
        </div>
        <FormField label="Short Name">
          <FormInput
            placeholder="e.g. ISSO"
            value={roleForm.shortName}
            onChange={(v) => setRoleForm((f) => ({ ...f, shortName: v }))}
          />
        </FormField>
        <FormField label="Description">
          <FormTextarea
            placeholder="Role description"
            value={roleForm.description}
            onChange={(v) => setRoleForm((f) => ({ ...f, description: v }))}
            rows={2}
          />
        </FormField>
      </Modal>

      {/* Add Party Modal */}
      <Modal
        open={showAddParty}
        onClose={() => setShowAddParty(false)}
        title="Add Party"
        description="Add a person or organization"
        size="sm"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowAddParty(false)}
            onSubmit={handleAddParty}
            submitLabel={addingParty ? "Saving" : "Save"}
            submitDisabled={!partyForm.name || addingParty}
          />
        }
      >
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Type" required>
            <FormSelect
              options={[
                { value: "person", label: "Person" },
                { value: "organization", label: "Organization" },
              ]}
              value={partyForm.type}
              onChange={(v) =>
                setPartyForm((f) => ({
                  ...f,
                  type: v as "person" | "organization",
                }))
              }
            />
          </FormField>
          <FormField label="Name" required>
            <FormInput
              placeholder="e.g. Jane Doe or Acme Corp"
              value={partyForm.name}
              onChange={(v) => setPartyForm((f) => ({ ...f, name: v }))}
            />
          </FormField>
        </div>
        <FormField label="Short Name">
          <FormInput
            placeholder="e.g. JD or Acme"
            value={partyForm.shortName}
            onChange={(v) => setPartyForm((f) => ({ ...f, shortName: v }))}
          />
        </FormField>
        <FormField label="Email Address">
          <FormInput
            placeholder="e.g. jane@example.com"
            value={partyForm.email}
            onChange={(v) => setPartyForm((f) => ({ ...f, email: v }))}
          />
        </FormField>
      </Modal>

      {/* Assign Responsible Party Modal */}
      <Modal
        open={showAssignRP}
        onClose={() => setShowAssignRP(false)}
        title="Assign Responsible Party"
        description="Map a role to one or more parties"
        size="sm"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowAssignRP(false)}
            onSubmit={handleAssignRP}
            submitLabel={assigningRP ? "Assigning" : "Assign"}
            submitDisabled={!rpForm.roleId || !rpForm.partyUuids || assigningRP}
          />
        }
      >
        <FormField label="Role" required>
          <FormSelect
            options={roles.map((r) => ({
              value: r.roleId,
              label: `${r.title} (${r.roleId})`,
            }))}
            placeholder="Select role"
            value={rpForm.roleId}
            onChange={(v) => setRpForm((f) => ({ ...f, roleId: v }))}
          />
        </FormField>
        <FormField label="Parties" required>
          {parties.length > 0 ? (
            <div className="rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] bg-[#f5f7fb] dark:bg-white/[0.04] max-h-40 overflow-y-auto">
              {parties.map((p) => {
                const selected = rpForm.partyUuids
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean);
                const checked = selected.includes(p.partyUuid);
                return (
                  <label
                    key={p.id}
                    className="flex items-center gap-2.5 px-3 py-2 hover:bg-black/[0.03] dark:hover:bg-white/[0.03] cursor-pointer transition-colors border-b border-[#e2e8f0]/50 dark:border-white/[0.04] last:border-0"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        setRpForm((f) => {
                          const current = f.partyUuids
                            .split(",")
                            .map((s) => s.trim())
                            .filter(Boolean);
                          const next = checked
                            ? current.filter((u) => u !== p.partyUuid)
                            : [...current, p.partyUuid];
                          return { ...f, partyUuids: next.join(", ") };
                        })
                      }
                      className="size-3.5 rounded border-border accent-indigo-600 cursor-pointer"
                    />
                    <span className="text-sm text-foreground">
                      {p.name}{" "}
                      <span className="text-xs text-muted-foreground">
                        ({p.partyType})
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>
          ) : (
            <FormInput
              placeholder="Paste party UUIDs (comma-separated)"
              value={rpForm.partyUuids}
              onChange={(v) => setRpForm((f) => ({ ...f, partyUuids: v }))}
            />
          )}
        </FormField>
      </Modal>

      {/* Add Location Modal */}
      <Modal
        open={showAddLocation}
        onClose={() => setShowAddLocation(false)}
        title="Add Location"
        description="Define an office, data center, or cloud region"
        size="sm"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowAddLocation(false)}
            onSubmit={handleAddLocation}
            submitLabel={addingLocation ? "Saving" : "Save"}
            submitDisabled={!locationForm.title || addingLocation}
          />
        }
      >
        <FormField label="Title" required>
          <FormInput
            placeholder="e.g. AWS US-East-1 Region"
            value={locationForm.title}
            onChange={(v) => setLocationForm((f) => ({ ...f, title: v }))}
          />
        </FormField>
        <FormField label="Address Lines" hint="One per line">
          <FormTextarea
            placeholder="100 Technology Drive"
            value={locationForm.addressLines}
            onChange={(v) =>
              setLocationForm((f) => ({ ...f, addressLines: v }))
            }
            rows={2}
          />
        </FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="City">
            <FormInput
              placeholder="Arlington"
              value={locationForm.city}
              onChange={(v) => setLocationForm((f) => ({ ...f, city: v }))}
            />
          </FormField>
          <FormField label="State">
            <FormInput
              placeholder="VA"
              value={locationForm.state}
              onChange={(v) => setLocationForm((f) => ({ ...f, state: v }))}
            />
          </FormField>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Postal Code">
            <FormInput
              type="number"
              placeholder="22201"
              value={locationForm.postalCode}
              onChange={(v) =>
                setLocationForm((f) => ({ ...f, postalCode: v }))
              }
            />
          </FormField>
          <FormField label="Country">
            <FormInput
              placeholder="US"
              value={locationForm.country}
              onChange={(v) => setLocationForm((f) => ({ ...f, country: v }))}
            />
          </FormField>
        </div>
      </Modal>

      {/* Add System User Modal */}
      <Modal
        open={showAddSystemUser}
        onClose={() => setShowAddSystemUser(false)}
        title="Add System User"
        description="Define a user type and their authorized privileges"
        size="md"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowAddSystemUser(false)}
            onSubmit={handleAddSystemUser}
            submitLabel={addingSystemUser ? "Saving" : "Save"}
            submitDisabled={!systemUserForm.title || addingSystemUser}
          />
        }
      >
        <FormField label="Title" required>
          <FormInput
            placeholder="e.g. Privileged Administrator"
            value={systemUserForm.title}
            onChange={(v) => setSystemUserForm((f) => ({ ...f, title: v }))}
          />
        </FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Short Name">
            <FormInput
              placeholder="e.g. Admin"
              value={systemUserForm.shortName}
              onChange={(v) =>
                setSystemUserForm((f) => ({ ...f, shortName: v }))
              }
            />
          </FormField>
          <FormField label="Role IDs" hint="Comma-separated">
            <FormInput
              placeholder="e.g. system-admin, isso"
              value={systemUserForm.roleIds}
              onChange={(v) => setSystemUserForm((f) => ({ ...f, roleIds: v }))}
            />
          </FormField>
        </div>
        <FormField label="Description">
          <FormTextarea
            placeholder="Description of this user type and access level"
            value={systemUserForm.description}
            onChange={(v) =>
              setSystemUserForm((f) => ({ ...f, description: v }))
            }
            rows={2}
          />
        </FormField>
        <FormField label="Privilege Title">
          <FormInput
            placeholder="e.g. Full System Administration"
            value={systemUserForm.privilegeTitle}
            onChange={(v) =>
              setSystemUserForm((f) => ({ ...f, privilegeTitle: v }))
            }
          />
        </FormField>
        <FormField label="Functions Performed" hint="Comma-separated">
          <FormInput
            placeholder="e.g. System configuration, Code deployment"
            value={systemUserForm.functionsPerformed}
            onChange={(v) =>
              setSystemUserForm((f) => ({ ...f, functionsPerformed: v }))
            }
          />
        </FormField>
      </Modal>

      {/* Add Interconnection Modal */}
      <Modal
        open={showAddInterconnection}
        onClose={() => setShowAddInterconnection(false)}
        title="Add Interconnection"
        description="Define a system interconnection or external integration"
        size="lg"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowAddInterconnection(false)}
            onSubmit={handleAddInterconnection}
            submitLabel={addingInterconnection ? "Saving" : "Save"}
            submitDisabled={!interconnectionForm.title || addingInterconnection}
          />
        }
      >
        <FormField label="Title" required>
          <FormInput
            placeholder="e.g. Stripe Payment Gateway"
            value={interconnectionForm.title}
            onChange={(v) =>
              setInterconnectionForm((f) => ({ ...f, title: v }))
            }
          />
        </FormField>
        <FormField label="Description">
          <FormTextarea
            placeholder="Description of the interconnection"
            value={interconnectionForm.description}
            onChange={(v) =>
              setInterconnectionForm((f) => ({ ...f, description: v }))
            }
            rows={2}
          />
        </FormField>
        <div className="grid grid-cols-2 gap-3">
          <FormField label="Direction" required>
            <FormSelect
              options={[
                { value: "incoming", label: "Incoming" },
                { value: "outgoing", label: "Outgoing" },
                { value: "incoming-outgoing", label: "Incoming-Outgoing" },
              ]}
              placeholder="Select direction"
              value={interconnectionForm.direction}
              onChange={(v) =>
                setInterconnectionForm((f) => ({
                  ...f,
                  direction: v as "incoming" | "outgoing" | "incoming-outgoing",
                }))
              }
            />
          </FormField>
          <FormField label="Remote System Name">
            <FormInput
              placeholder="e.g. Stripe API"
              value={interconnectionForm.remoteSystemName}
              onChange={(v) =>
                setInterconnectionForm((f) => ({
                  ...f,
                  remoteSystemName: v,
                }))
              }
            />
          </FormField>
        </div>
        <FormField label="Responsible Roles">
          <FormInput
            placeholder="e.g. network-admin, system-owner (comma-separated)"
            value={interconnectionForm.responsibleRoles}
            onChange={(v) =>
              setInterconnectionForm((f) => ({ ...f, responsibleRoles: v }))
            }
          />
        </FormField>

        {/* Protocols */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-sm font-medium">Protocols</label>
            <button
              type="button"
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              onClick={() =>
                setInterconnectionForm((f) => ({
                  ...f,
                  protocols: [
                    ...f.protocols,
                    {
                      name: "",
                      portRanges: [{ start: "", end: "", transport: "tcp" }],
                    },
                  ],
                }))
              }
            >
              <Plus className="size-3" /> Add More
            </button>
          </div>
          {interconnectionForm.protocols.map((proto, pi) => (
            <div
              key={pi}
              className="rounded-lg border border-border bg-muted/30 p-3 space-y-2"
            >
              <div className="flex items-center gap-2">
                <FormInput
                  placeholder="Protocol name (e.g. HTTPS, IPSec)"
                  value={proto.name}
                  onChange={(v) =>
                    setInterconnectionForm((f) => {
                      const protocols = [...f.protocols];
                      protocols[pi] = { ...protocols[pi], name: v };
                      return { ...f, protocols };
                    })
                  }
                />
                <button
                  type="button"
                  className="p-1 rounded text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors flex-shrink-0"
                  onClick={() =>
                    setInterconnectionForm((f) => ({
                      ...f,
                      protocols: f.protocols.filter((_, i) => i !== pi),
                    }))
                  }
                >
                  <Trash2 className="size-3.5" />
                </button>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    Port Ranges
                  </span>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-[10px] text-primary hover:underline"
                    onClick={() =>
                      setInterconnectionForm((f) => {
                        const protocols = [...f.protocols];
                        protocols[pi] = {
                          ...protocols[pi],
                          portRanges: [
                            ...protocols[pi].portRanges,
                            { start: "", end: "", transport: "tcp" },
                          ],
                        };
                        return { ...f, protocols };
                      })
                    }
                  >
                    <Plus className="size-2.5" /> Add Port
                  </button>
                </div>
                {proto.portRanges.map((pr, pri) => (
                  <div key={pri} className="flex items-center gap-2">
                    <input
                      type="number"
                      placeholder="Start"
                      className="w-20 rounded-md border border-input bg-background px-2 py-1 text-xs"
                      value={pr.start}
                      onChange={(e) =>
                        setInterconnectionForm((f) => {
                          const protocols = [...f.protocols];
                          const portRanges = [...protocols[pi].portRanges];
                          portRanges[pri] = {
                            ...portRanges[pri],
                            start: e.target.value,
                          };
                          protocols[pi] = { ...protocols[pi], portRanges };
                          return { ...f, protocols };
                        })
                      }
                    />
                    <span className="text-xs text-muted-foreground">-</span>
                    <input
                      type="number"
                      placeholder="End"
                      className="w-20 rounded-md border border-input bg-background px-2 py-1 text-xs"
                      value={pr.end}
                      onChange={(e) =>
                        setInterconnectionForm((f) => {
                          const protocols = [...f.protocols];
                          const portRanges = [...protocols[pi].portRanges];
                          portRanges[pri] = {
                            ...portRanges[pri],
                            end: e.target.value,
                          };
                          protocols[pi] = { ...protocols[pi], portRanges };
                          return { ...f, protocols };
                        })
                      }
                    />
                    <Dropdown
                      size="sm"
                      variant="form"
                      className="!min-w-[80px]"
                      value={pr.transport}
                      onChange={(v) =>
                        setInterconnectionForm((f) => {
                          const protocols = [...f.protocols];
                          const portRanges = [...protocols[pi].portRanges];
                          portRanges[pri] = {
                            ...portRanges[pri],
                            transport: v,
                          };
                          protocols[pi] = { ...protocols[pi], portRanges };
                          return { ...f, protocols };
                        })
                      }
                      options={[
                        { value: "tcp", label: "TCP" },
                        { value: "udp", label: "UDP" },
                      ]}
                    />
                    <button
                      type="button"
                      className="p-0.5 rounded text-muted-foreground hover:text-red-600 transition-colors flex-shrink-0"
                      onClick={() =>
                        setInterconnectionForm((f) => {
                          const protocols = [...f.protocols];
                          protocols[pi] = {
                            ...protocols[pi],
                            portRanges: protocols[pi].portRanges.filter(
                              (_, i) => i !== pri,
                            ),
                          };
                          return { ...f, protocols };
                        })
                      }
                    >
                      <X className="size-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Modal>

      {/* Lifecycle Transition Modal */}
      <Modal
        open={showTransitionModal}
        onClose={() => setShowTransitionModal(false)}
        title={`${currentLifecycleActions.find((a) => a.target === transitionTarget)?.label || "Transition Lifecycle"}`}
        description={`Move SSP to "${transitionTarget.replace(/_/g, " ")}" status`}
        size="sm"
        footer={
          <ModalFooterButtons
            onCancel={() => setShowTransitionModal(false)}
            onSubmit={handleLifecycleTransition}
            submitLabel={
              transitioningLifecycle
                ? "Processing"
                : `Confirm ${currentLifecycleActions.find((a) => a.target === transitionTarget)?.label || transitionTarget.replace(/_/g, " ")}`
            }
            submitDisabled={transitioningLifecycle}
          />
        }
      >
        <p className="text-sm text-foreground mb-3">
          You are about to change the status of{" "}
          <span className="font-semibold text-foreground">
            "{sspDetail?.title || "SSP"}"
          </span>{" "}
          to{" "}
          <span className="font-semibold text-foreground capitalize">
            {transitionTarget.replace(/_/g, " ")}
          </span>
          .
        </p>
        <FormField label="Comment" hint="Optional reason for this transition">
          <FormTextarea
            placeholder="e.g. All controls reviewed and approved by ISSO"
            value={transitionComment}
            onChange={(v) => setTransitionComment(v)}
            rows={3}
          />
        </FormField>
      </Modal>

      {/* Import Component from Library Modal */}
      <Modal
        open={showImportComponent}
        onClose={() => {
          setShowImportComponent(false);
          setSelectedImportComponentIds(new Set());
        }}
        title="Import from Component Library"
        description="Select components to import into this SSP"
        size="md"
        footer={
          <ModalFooterButtons
            onCancel={() => {
              setShowImportComponent(false);
              setSelectedImportComponentIds(new Set());
            }}
            onSubmit={() =>
              handleImportComponents([...selectedImportComponentIds])
            }
            submitLabel={
              importingComponents
                ? "Importing"
                : `Import Selected (${selectedImportComponentIds.size})`
            }
            submitDisabled={
              importingComponents || selectedImportComponentIds.size === 0
            }
          />
        }
      >
        {(() => {
          // Build set of source component IDs already in this SSP
          const alreadyImportedSourceIds = new Set(
            sspComponents
              .map((c) => c.sourceComponentId)
              .filter(Boolean) as string[],
          );
          // Filter out components already imported into this SSP
          const availableComponents = cdefComponents.filter(
            (comp) =>
              !alreadyImportedSourceIds.has(comp.id) &&
              !importedComponentIds.has(comp.id),
          );

          if (cdefLoading) {
            return (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="size-5 text-indigo-600 animate-spin" />
                <span className="ml-3 text-sm text-muted-foreground">
                  Loading components
                </span>
              </div>
            );
          }

          if (cdefComponents.length === 0) {
            return (
              <p className="text-xs text-muted-foreground text-center py-6">
                No components found in the library.
              </p>
            );
          }

          if (availableComponents.length === 0) {
            return (
              <div className="flex flex-col items-center py-8 gap-2">
                <CheckCircle className="size-6 text-emerald-500" />
                <p className="text-sm font-medium text-muted-foreground">
                  All components have been imported
                </p>
                <p className="text-xs text-muted-foreground">
                  {cdefComponents.length} component
                  {cdefComponents.length !== 1 ? "s" : ""} already in this SSP
                </p>
              </div>
            );
          }

          return (
            <div className="space-y-1.5">
              {availableComponents.length < cdefComponents.length && (
                <p className="text-xs text-muted-foreground mb-2">
                  Showing {availableComponents.length} of{" "}
                  {cdefComponents.length} components
                  <span className="text-emerald-600 dark:text-emerald-400 ml-1">
                    ({cdefComponents.length - availableComponents.length}{" "}
                    already imported)
                  </span>
                </p>
              )}
              <div className="flex items-center justify-between px-1 pb-1">
                <span className="text-xs text-muted-foreground">
                  {selectedImportComponentIds.size} selected
                </span>
                <button
                  type="button"
                  onClick={() =>
                    setSelectedImportComponentIds(
                      new Set(
                        selectedImportComponentIds.size ===
                          availableComponents.length
                          ? []
                          : availableComponents.map((comp) => comp.id),
                      ),
                    )
                  }
                  className="text-xs text-indigo-600 dark:text-indigo-400 hover:underline"
                >
                  {selectedImportComponentIds.size ===
                    availableComponents.length
                    ? "Clear all"
                    : "Select all"}
                </button>
              </div>
              {availableComponents.map((comp) => (
                <div
                  key={comp.id}
                  className="flex items-center gap-2 p-2 rounded-lg border border-border"
                >
                  <input
                    type="checkbox"
                    checked={selectedImportComponentIds.has(comp.id)}
                    onChange={() =>
                      setSelectedImportComponentIds((prev) => {
                        const next = new Set(prev);
                        if (next.has(comp.id)) next.delete(comp.id);
                        else next.add(comp.id);
                        return next;
                      })
                    }
                    className="size-4 rounded border-border accent-indigo-600 flex-shrink-0 cursor-pointer"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium truncate">{comp.title}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="text-[10px] font-bold px-1 py-0.5 rounded bg-muted text-muted-foreground capitalize">
                        {comp.componentType}
                      </span>
                      <span className="text-[10px] px-1 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 truncate">
                        {comp.cdefTitle}
                      </span>
                    </div>
                  </div>
                  {selectedImportComponentIds.has(comp.id) && (
                    <span className="flex items-center gap-1 px-2 py-1 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 text-[10px] font-bold text-indigo-600 dark:text-indigo-400 flex-shrink-0">
                      <CheckCircle className="size-3" />
                      Selected
                    </span>
                  )}
                </div>
              ))}
            </div>
          );
        })()}
      </Modal>

      {/* Post-Import Control Status Popup - Split Panel */}
      <Modal
        open={showPostImportPopup}
        onClose={() => {
          closePostImportPopups();
          setPostImportSelectedIdx(null);
          setPostImportSearch("");
        }}
        title={`Controls - ${postImportComponentTitle}`}
        size="lg"
        footer={
          <div className="flex items-center justify-between w-full">
            <span className="text-xs text-muted-foreground">
              {postImportIRs.length - excludedIrIds.size} of{" "}
              {postImportIRs.length} controls selected
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  closePostImportPopups();
                  setPostImportSelectedIdx(null);
                  setPostImportSearch("");
                }}
                className="px-4 py-2 rounded-lg border border-border text-sm font-medium text-foreground hover:bg-accent transition-colors"
              >
                Cancel
              </button>
              {savedIrIds.size === postImportIRs.length - excludedIrIds.size &&
                postImportIRs.length - excludedIrIds.size > 0 ? (
                <button
                  onClick={() => {
                    closePostImportPopups();
                    setPostImportSelectedIdx(null);
                    setPostImportSearch("");
                  }}
                  className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors"
                >
                  Done
                </button>
              ) : (
                <button
                  onClick={handleBulkSaveAll}
                  disabled={
                    bulkSaving ||
                    postImportIRs.length - excludedIrIds.size === 0
                  }
                  className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-50"
                >
                  {bulkSaving ? (
                    <>
                      <Loader2 className="size-3.5 animate-spin inline mr-1.5" />
                      Saving
                    </>
                  ) : (
                    `Save All (${postImportIRs.length - excludedIrIds.size})`
                  )}
                </button>
              )}
            </div>
          </div>
        }
      >
        {postImportLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="size-6 animate-spin text-indigo-600 dark:text-indigo-400" />
            <span className="ml-2 text-sm text-muted-foreground">
              Loading control details
            </span>
          </div>
        ) : (
          <div className="flex gap-3 min-h-[400px]">
            {/* Left: controls list */}
            <div className="w-56 flex-shrink-0 rounded-lg border border-border overflow-hidden flex flex-col">
              <div className="p-2 border-b border-border">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground/50 pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search controls"
                    value={postImportSearch}
                    onChange={(e) => setPostImportSearch(e.target.value)}
                    className="w-full pl-8 pr-2 py-1.5 text-xs bg-muted/50 rounded-md border-0 outline-none placeholder:text-muted-foreground/50 focus:ring-1 focus:ring-indigo-500/30"
                  />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                {postImportIRs
                  .filter(
                    ({ ir, componentTitle }) =>
                      !postImportSearch ||
                      ir.controlId
                        .toLowerCase()
                        .includes(postImportSearch.toLowerCase()) ||
                      componentTitle
                        .toLowerCase()
                        .includes(postImportSearch.toLowerCase()) ||
                      (postImportDescriptions[ir.id] || ir.description || "")
                        .toLowerCase()
                        .includes(postImportSearch.toLowerCase()),
                  )
                  .map(({ ir, componentTitle }) => {
                    const realIdx = postImportIRs.findIndex(
                      (item) => item.ir.id === ir.id,
                    );
                    const isActive = postImportSelectedIdx === realIdx;
                    const isExcluded = excludedIrIds.has(ir.id);
                    return (
                      <button
                        key={ir.id}
                        type="button"
                        onClick={() => handleSelectPostImportControl(realIdx)}
                        className={`w-full text-left px-3 py-2.5 border-b border-border transition-colors ${isExcluded ? "opacity-50" : ""} ${isActive ? "bg-indigo-50 dark:bg-indigo-500/10" : "hover:bg-muted/50"}`}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={!isExcluded}
                            onChange={(e) => {
                              e.stopPropagation();
                              setExcludedIrIds((prev) => {
                                const next = new Set(prev);
                                if (next.has(ir.id)) {
                                  next.delete(ir.id);
                                } else {
                                  next.add(ir.id);
                                }
                                return next;
                              });
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="size-3 rounded border-border accent-indigo-600 flex-shrink-0 cursor-pointer"
                          />
                          {savedIrIds.has(ir.id) ? (
                            <CheckCircle className="size-3 text-emerald-500 flex-shrink-0" />
                          ) : null}
                          <span
                            className={`text-[10px] font-black ${isExcluded ? "line-through text-muted-foreground" : isActive ? "text-indigo-600 dark:text-indigo-400" : "text-muted-foreground"}`}
                          >
                            {ir.controlId?.toUpperCase()}
                          </span>
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-1">
                          {postImportDescriptions[ir.id] ||
                            ir.description ||
                            ir.controlId?.toUpperCase()}
                        </p>
                        <p className="text-[10px] text-indigo-600 dark:text-indigo-400 mt-0.5 line-clamp-1">
                          {componentTitle}
                        </p>
                      </button>
                    );
                  })}
              </div>
              <div className="px-3 py-2 border-t border-border bg-muted/30">
                <span className="text-[10px] text-muted-foreground">
                  {postImportIRs.length - excludedIrIds.size} of{" "}
                  {postImportIRs.length} selected · {savedIrIds.size} saved
                </span>
              </div>
            </div>

            {/* Right: control detail */}
            <div className="flex-1 rounded-lg border border-border overflow-y-auto">
              {postImportSelectedIdx !== null &&
                postImportIRs[postImportSelectedIdx] ? (
                (() => {
                  const { ciId, ir, componentTitle } =
                    postImportIRs[postImportSelectedIdx];
                  const detail = postImportControlDetails[ir.controlId];
                  const isLoadingDetail =
                    postImportLoadingControlId === ir.controlId;
                  const currentStatements = postImportStmtEdits[ir.id] || [];
                  const currentParams = postImportParamEdits[ir.id] || [];
                  return (
                    <div className="p-4 space-y-4">
                      {/* Header */}
                      <div>
                        <div className="flex items-center gap-2 mb-3">
                          <span className="px-2 py-0.5 rounded bg-indigo-50 dark:bg-indigo-500/10 text-[10px] font-black text-indigo-600 dark:text-indigo-400">
                            {ir.controlId?.toUpperCase()}
                          </span>
                          <h4 className="text-sm font-bold text-foreground">
                            {detail?.title ||
                              postImportDescriptions[ir.id] ||
                              ir.description ||
                              ir.controlId?.toUpperCase()}
                          </h4>
                        </div>
                        <p className="text-xs text-indigo-600 dark:text-indigo-400">
                          Component: {componentTitle}
                        </p>
                        <div className="flex items-end gap-3">
                          <div className="flex-1">
                            <label className="text-[10px] font-bold text-muted-foreground mb-1 block">
                              Status
                            </label>
                            <Dropdown
                              size="sm"
                              variant="form"
                              value={postImportStatuses[ir.id] || "planned"}
                              onChange={(v) =>
                                setPostImportStatuses((prev) => ({
                                  ...prev,
                                  [ir.id]: v as ImplementationStatus,
                                }))
                              }
                              options={IMPLEMENTATION_STATUSES.map((s) => ({
                                value: s.value,
                                label: s.label,
                              }))}
                            />
                          </div>
                        </div>
                      </div>

                      <div className="rounded-lg border border-border p-3 space-y-2">
                        <label className="text-xs font-bold text-foreground block">
                          Description
                        </label>
                        <FormTextarea
                          placeholder="Describe how this component satisfies the control"
                          value={postImportDescriptions[ir.id] || ""}
                          onChange={(value) =>
                            setPostImportDescriptions((prev) => ({
                              ...prev,
                              [ir.id]: value,
                            }))
                          }
                          rows={3}
                        />
                      </div>

                      {isLoadingDetail && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                          <Loader2 className="size-4 animate-spin" />
                          Loading control details
                        </div>
                      )}

                      {(detail ||
                        currentStatements.length > 0 ||
                        currentParams.length > 0) && (
                          <>
                            {/* Statements */}
                            {currentStatements.length > 0 && (
                              <div className="rounded-lg border border-border p-3 space-y-2">
                                <p className="text-xs font-bold text-foreground">
                                  Statements
                                </p>
                                {currentStatements.map((stmt) => (
                                  <div
                                    key={stmt.statementId}
                                    className="flex items-start gap-2.5 p-2.5 rounded-lg border border-indigo-300 dark:border-indigo-500/30 bg-indigo-50/50 dark:bg-indigo-500/5"
                                  >
                                    <CheckCircle className="size-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                                    <div className="flex-1 min-w-0">
                                      <span className="text-[10px] font-bold text-muted-foreground ">
                                        {stmt.statementId}
                                      </span>
                                      {stmt.description && (
                                        <p className="text-xs text-foreground mt-0.5">
                                          {stmt.description}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            {/* Guidance */}
                            {detail &&
                              detail.parts.filter((p) => p.name === "guidance")
                                .length > 0 && (
                                <div className="rounded-lg border border-amber-200 dark:border-amber-500/20 bg-amber-50/50 dark:bg-amber-500/5 p-3 space-y-1.5">
                                  <p className="text-xs font-bold text-amber-800 dark:text-amber-400">
                                    Guidance
                                  </p>
                                  {detail.parts
                                    .filter((p) => p.name === "guidance")
                                    .map((part) => (
                                      <p
                                        key={part.id}
                                        className="text-xs text-amber-700 dark:text-amber-300/80"
                                      >
                                        {part.prose}
                                      </p>
                                    ))}
                                </div>
                              )}
                            {/* Parameters */}
                            {currentParams.length > 0 && (
                              <div className="rounded-lg border border-border p-3 space-y-3">
                                <p className="text-xs font-bold text-foreground">
                                  Parameters
                                </p>
                                {currentParams.map((param) => {
                                  const catalogParam = detail?.params?.find(
                                    (p) => p.paramId === param.paramId,
                                  );
                                  return (
                                    <div
                                      key={param.paramId}
                                      className="rounded-lg border border-dashed border-border p-3 space-y-1"
                                    >
                                      <div className="flex items-center gap-2">
                                        <span className="text-[10px] font-bold text-muted-foreground ">
                                          {param.paramId}
                                        </span>
                                        {catalogParam?.label && (
                                          <span className="text-[10px] font-medium text-indigo-600 dark:text-indigo-400">
                                            {catalogParam.label}
                                          </span>
                                        )}
                                      </div>
                                      <input
                                        type="text"
                                        placeholder={
                                          catalogParam?.values?.length
                                            ? `e.g. ${catalogParam.values.join(", ")}`
                                            : "Enter value"
                                        }
                                        value={param.values}
                                        onChange={(e) => {
                                          const val = e.target.value;
                                          setPostImportParamEdits((prev) => {
                                            const params = [
                                              ...(prev[ir.id] || []),
                                            ];
                                            const idx = params.findIndex(
                                              (p) => p.paramId === param.paramId,
                                            );
                                            if (idx >= 0)
                                              params[idx] = {
                                                ...params[idx],
                                                values: val,
                                              };
                                            return { ...prev, [ir.id]: params };
                                          });
                                        }}
                                        className="w-full px-2.5 py-1.5 rounded-lg border border-border bg-card text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-500"
                                      />
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </>
                        )}
                      {!isLoadingDetail &&
                        !detail &&
                        currentStatements.length === 0 &&
                        currentParams.length === 0 && (
                          <div className="rounded-lg border border-dashed border-border p-4 text-xs text-muted-foreground">
                            Select this control to load statements, guidance,
                            and parameters when catalog details are available.
                          </div>
                        )}
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => handleSaveSingleIR(ciId, ir)}
                          disabled={
                            savingIrId === ir.id || excludedIrIds.has(ir.id)
                          }
                          className="px-4 py-2 rounded-lg bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-colors disabled:opacity-50"
                        >
                          {savingIrId === ir.id ? "Saving" : "Save Control"}
                        </button>
                      </div>
                    </div>
                  );
                })()
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-center py-16">
                  <Search className="size-8 text-muted-foreground/30 mb-3" />
                  <p className="text-sm font-medium text-muted-foreground">
                    Select a control
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Click a control from the list to view details
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* Diagram Viewer Modal */}
      {viewingDiagram && (
        <DiagramPreviewModal
          url={previewSupportingEvidenceFile(viewingDiagram.contentEvidenceId)}
          fileName={viewingDiagram.fileName ?? null}
          downloadUrl={downloadSupportingEvidenceFile(
            viewingDiagram.contentEvidenceId,
          )}
          title={viewingDiagram.title}
          onClose={() => setViewingDiagram(null)}
        />
      )}

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        open={!!deleteTarget}
        type="SSP"
        name={
          deleteTarget
            ? sspDocTitles[deleteTarget.documentId] || deleteTarget.documentId
            : ""
        }
        deleting={deleting}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => handleDeleteSSP()}
      />

      {/* Generic Delete Confirmation */}
      <DeleteConfirmDialog
        open={!!pendingDelete}
        type={pendingDelete?.type || "Item"}
        name={pendingDelete?.title || ""}
        deleting={pendingDeleting}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => confirmPendingDelete()}
      />
    </div>
  );
}

/* ── Diagram Preview Modal (matches ContentRegistry PreviewModal) ── */
function DiagramPreviewModal({
  url,
  fileName,
  downloadUrl,
  title,
  onClose,
}: {
  url: string;
  fileName: string | null;
  downloadUrl: string;
  title: string;
  onClose: () => void;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [contentType, setContentType] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [cannotPreview, setCannotPreview] = useState(false);

  useEffect(() => {
    let revoke = "";
    setLoading(true);
    setError(false);
    setBlobUrl(null);
    setCannotPreview(false);
    fetch(url, { credentials: "include", headers: { ...getAuthHeaders() } })
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load");
        const ct = res.headers.get("content-type") ?? "";
        setContentType(ct);
        const isImage = ct.startsWith("image/");
        const isPdf = ct.includes("pdf");
        if (isImage || isPdf) {
          return res.blob().then((blob) => {
            const objUrl = URL.createObjectURL(blob);
            revoke = objUrl;
            setBlobUrl(objUrl);
          });
        }
        // text types — render in iframe
        if (ct.includes("text/")) {
          return res.text().then((text) => {
            const blob = new Blob([text], { type: ct });
            const objUrl = URL.createObjectURL(blob);
            revoke = objUrl;
            setBlobUrl(objUrl);
          });
        }
        setCannotPreview(true);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    return () => {
      if (revoke) URL.revokeObjectURL(revoke);
    };
  }, [url]);

  return (
    <ModalShell open onClose={onClose} variant="preview">
      <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
        <div className="flex items-center gap-2">
          <Eye className="size-4 text-indigo-500 dark:text-indigo-400" />
          <h3 className="text-sm font-bold text-foreground">
            {title}
            {fileName && (
              <span className="font-normal text-muted-foreground ml-2">
                ({fileName})
              </span>
            )}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={downloadUrl}
            className="p-1.5 rounded-md border border-border hover:bg-accent transition-colors text-foreground"
            title="Download"
          >
            <Download className="size-4" />
          </a>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          >
            <X className="size-4" />
          </button>
        </div>
      </div>
      <div className="flex-1 min-h-0 overflow-auto bg-[#f5f7fb] dark:bg-black/20 flex items-center justify-center">
        {loading && (
          <div className="flex flex-col items-center gap-3 p-8">
            <Loader2 className="size-8 text-muted-foreground animate-spin" />
            <p className="text-sm text-muted-foreground">Loading preview…</p>
          </div>
        )}
        {error && (
          <div className="flex flex-col items-center gap-3 p-8 text-center">
            <AlertTriangle className="size-12 text-muted-foreground" />
            <p className="text-sm font-medium text-foreground">
              Unable to load preview
            </p>
            <p className="text-xs text-muted-foreground">
              Use the download button to view the file.
            </p>
          </div>
        )}
        {cannotPreview && (
          <div className="flex flex-col items-center gap-3 p-8 text-center">
            <AlertTriangle className="size-12 text-muted-foreground" />
            <p className="text-sm font-medium text-foreground">
              Preview not available for this file type
            </p>
            <p className="text-xs text-muted-foreground">
              Use the download button to view the file.
            </p>
          </div>
        )}
        {!loading && !error && !cannotPreview && blobUrl && (
          <>
            {contentType.startsWith("image/") && (
              <img
                src={blobUrl}
                alt={title}
                className="max-w-full max-h-full object-contain"
              />
            )}
            {contentType.includes("pdf") && (
              <embed
                src={blobUrl}
                type="application/pdf"
                className="w-full h-full"
              />
            )}
            {!contentType.startsWith("image/") &&
              !contentType.includes("pdf") && (
                <iframe
                  src={blobUrl}
                  className="w-full h-full border-0 bg-white"
                  title={title}
                />
              )}
          </>
        )}
      </div>
    </ModalShell>
  );
}